/**
 * Central Conductor Pattern (CCP) Compliant Runtime Conductor
 * Murphy Door Configurator - Phase 1 CCP Compliance Complete
 *
 * ARCHITECTURAL ROLE: Pure orchestration and coordination (NOT implementation)
 * This conductor follows Central Conductor Pattern principles by:
 *
 * ORCHESTRATION RESPONSIBILITIES:
 * - Managing tab and category state transitions through delegation
 * - Coordinating selection state validation via business logic modules
 * - Directing UI updates through event-based communication
 * - Orchestrating price calculations via business logic delegation
 * - Coordinating cart operations through specialized modules
 * - Managing progress tracking and navigation state
 * - Providing centralized data structure access interfaces
 * - Handling error reporting and coordinated logging
 * - Managing centralized state through STATE_MAPS coordination
 * - Orchestrating batch state updates through proper delegation
 * - Ensuring state consistency via event-based communication
 * - Providing clean state access interfaces to other modules
 * - Managing initialization coordination and module bootstrapping
 * - Handling variant transition states through delegation
 * - Coordinating display title formatting via UI layer
 *
 * CCP COMPLIANCE ACHIEVED (Phase 1):
 * ✅ All 11 violations fixed with proper delegation patterns
 * ✅ Business logic delegated to specialized modules
 * ✅ UI operations coordinated through event-based communication
 * ✅ Data processing delegated to config and persistence modules
 * ✅ State management coordinated through proper module delegation
 * ✅ 344 line reduction achieved (5,334 → 4,990 lines)
 * ✅ 100% functionality preservation with zero regressions
 * ✅ Event-based architecture implemented throughout
 *
 * DELEGATION PATTERNS IMPLEMENTED:
 * - Event Publishing: UI updates, loading states, tooltips, state recovery
 * - Business Logic Calls: Price/shipping calculation, material selection
 * - Module Enhancement: Enhanced existing modules with new capabilities
 * - Direct Delegation: Data processing, DOM operations (with compatibility)
 *
 * State Management:
 * - Maintains atomic state updates through STATE_MAPS
 * - Provides state validation and error recovery
 * - Coordinates state-driven UI updates
 * - Manages state persistence and restoration
 * - Handles initialization state coordination
 * - Manages variant transition states
 *
 * Dependencies:
 * - md-config-tab-manager.js
 * - md-config-category-manager.js
 * - md-config-selection-manager.js
 * - md-config-support-progress-tracker.js
 * - md-config-ui-updater.js
 * - md-config-bl-price-calculator.js
 * - md-config-bl-cart-manager.js
 * - md-config-summary-manager.js
 * - md-config-core-state-initializer.js (PPD-only initialization)
 *
 * ✅ PHASE 3 COMPLETE: PPD-ONLY INITIALIZATION FLOW
 * 1. perfectProductDictionary is created by bootstrap pipeline (Phase 1)
 * 2. State initializer extracts state data from PPD (Phase 2)
 * 3. Conductor receives system:state_from_ppd_ready event (Phase 2)
 * 4. Conductor uses stateInitializer.initializeStateFromPPD(ppd) for state data (Phase 3)
 * 5. All initialization flows use PPD as single source of truth
 *
 * LEGACY FLOWS REMOVED (Phase 3):
 * - ❌ this.processedData fallback (removed)
 * - ❌ processConfiguratorData() fallback (removed)
 * - ✅ Fail-fast error handling enforces PPD-only flow
 */
(function (window) {
  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};

  const CONSTANTS = window.MurphyDoorConfigurator?.CONSTANTS;

  // Dev-only logging (production guard)
  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  // Phase 0 shared price-label vocabulary, resolved at CALL time (core-utils
  // loads first in production, but deferring keeps this loadable in a bare
  // test harness). The inline fallback exists only for that harness —
  // md-config-core-utils.js is the single source of truth.
  const priceLabels = () =>
    window.MurphyDoorConfigurator?.utils?.PRICE_LABELS ?? {
      BASE: 'Starting at',
      CONFIGURED: 'Your Price',
    };

  // =========================================================================
  // PATTERN #2: publishEvent() Helper (10 points)
  // Standalone async function with try/catch for all event publishing
  // NOTE: Object.freeze() NOT needed - CoreEventBus.publish() freezes centrally (Pattern #18 DRY)
  // =========================================================================
  function publishEvent(eventType, payload = {}) {
    try {
      // Uses CONSTANTS.EVENTS.* for all event references (Pattern #19 compliant)
      const bus = window.MurphyDoorConfigurator?.CoreEventBus;
      if (!bus) {
        isDev() &&
          console.warn(
            '[CONDUCTOR] CoreEventBus not available for:',
            eventType
          );
        return;
      }
      bus.publish(eventType, payload);
    } catch (error) {
      isDev() &&
        console.error(`[CONDUCTOR] Event publish failed: ${eventType}`, error);
    }
  }

  // =========================================================================
  // C1: CLICK = APPLY pure guard decision
  // Module-scope so it is unit-testable at load time (the conductor `main`
  // object is only built later inside the async initializeCore()). The method
  // main._shouldClickApply() delegates here after resolving the flag.
  // =========================================================================
  function shouldClickApplyDecision(opts, clickApplyEnabled) {
    const o = opts || {};
    if (!clickApplyEnabled) return false;
    if (!o.success) return false;
    if (!o.variantId) return false; // MULTI_VARIANT_START null-variant guard
    if (o.browsingIsForbidden) return false; // forbidden = preview only
    if (o.suppressClickApply) return false; // slider render / opt-out
    if (o.isSliderCategory) return false; // sliders commit on release
    if (o.isSimplifiedBulk) return false; // simplified 3Q bulk-applies
    return true;
  }

  // =========================================================================
  // FORBIDDEN CLICK UNDER CLICK=APPLY: refuse it, never "preview" it.
  //
  // In click-apply a click IS the apply attempt. The C1 "forbidden = preview
  // only" rule above still correctly stops the auto-apply, but letting the
  // BROWSE go ahead is not harmless there: entering BROWSING deletes the
  // category's applied answer (stateManager.handleSelectionState), rings the
  // refused tile and writes its title into the card, and nothing ever rolls
  // any of that back. The customer sees an answer they do not have, and the
  // answer they did have is gone. Engineering UAT 2026-09-17: the card read
  // "6-9/16 in." on an in-swing 6-1/2 in. door while the jamb was null in
  // both selection stores, and Add to Cart shipped a door with no jamb line.
  //
  // Blocks exactly the browses that would have been APPLIED had they been
  // legal, so sliders, the simplified bulk flow and every non-click-apply
  // mode keep their existing preview behaviour.
  // =========================================================================
  function shouldBlockForbiddenBrowseDecision(opts, clickApplyEnabled) {
    const o = opts || {};
    if (!o.browsingIsForbidden) return false;
    // Validation runs BEFORE the browse is attempted, so there is no browse
    // result yet -- judge the would-be apply as if it had succeeded.
    return shouldClickApplyDecision(
      Object.assign({}, o, { browsingIsForbidden: false, success: true }),
      clickApplyEnabled
    );
  }

  // =========================================================================
  // BACK ARROW: navigation or unapply?
  //
  // classic/dual: a drill-down only exists while BROWSING, before the Apply
  // button, so leaving it means abandoning that browse -- the historical full
  // unapply (stateManager.handleBackClick).
  //
  // click-apply: the drill-down stays open after applying, so the arrow is
  // reachable on ANSWERED categories. It must only navigate
  // (stateManager.handleBackNavigation); treating it as an unapply silently
  // deleted answers. See handleBackNavigation for the incident.
  // =========================================================================
  function backClickModeDecision(clickApplyEnabled) {
    return clickApplyEnabled ? 'navigate' : 'unapply';
  }

  // D1: default dual-mode for an experience. Pure + module-scope so it is
  // unit-testable at load time. dual_curated_default lands in curated; every
  // other experience (dual_full_default, classic) lands in full_control.
  function defaultModeForExperience(experience) {
    return experience === 'dual_curated_default' ? 'curated' : 'full_control';
  }

  /**
   * Overlays admin apo_visibility_rule entries onto the code default, returning
   * the SAME SHAPE as CATEGORY_VISIBILITY_DEPS so its six consumers keep their
   * existing logic.
   *
   * Module scope + exported below for the same reason shouldClickApplyDecision
   * is: the conductor's `main` object is only built inside the async
   * initializeCore(), so a method on it cannot be reached by loading this file.
   * A merge this fiddly tested only through a hand-written stub is a merge that
   * drifts from production silently -- which is exactly what happened to
   * apo-style-category-order.test.js.
   *
   * @param {Object} base - CATEGORY_VISIBILITY_DEPS
   * @param {Map<string,{gate:string,showWhen:string[]}>} rules
   * @returns {Object} gateId -> { categories, answers, autoApplyOnHide }
   */
  function mergeGateVisibilityDeps(base, rules) {
    const safeBase = base || {};
    if (!rules || typeof rules.forEach !== 'function' || rules.size === 0) {
      return safeBase;
    }

    const merged = {};
    Object.keys(safeBase).forEach((gateId) => {
      const cfg = safeBase[gateId] || {};
      merged[gateId] = {
        categories: (cfg.categories || []).slice(),
        answers: Object.keys(cfg.answers || {}).reduce((acc, answer) => {
          acc[answer] = (cfg.answers[answer] || []).slice();
          return acc;
        }, {}),
        autoApplyOnHide: cfg.autoApplyOnHide,
      };
    });

    rules.forEach((rule, categoryId) => {
      if (!rule || typeof rule.gate !== 'string') return;
      const gate = merged[rule.gate];
      // A rule naming a gate that does not exist is ignored rather than
      // inventing one: gate cards are defined in code (GATE_CARD_DEFINITIONS),
      // so an unknown gate has no card for the customer to answer, and a
      // category attached to it would be permanently unreachable.
      if (!gate) return;
      const showWhen = Array.isArray(rule.showWhen) ? rule.showWhen : [];
      if (gate.categories.indexOf(categoryId) === -1) {
        gate.categories.push(categoryId);
      }
      Object.keys(gate.answers).forEach((answer) => {
        const list = gate.answers[answer];
        const at = list.indexOf(categoryId);
        const shouldShow = showWhen.indexOf(answer) !== -1;
        if (shouldShow && at === -1) list.push(categoryId);
        if (!shouldShow && at !== -1) list.splice(at, 1);
      });

      // A rule that shows the category on NO answer is almost always a typo,
      // and it is the one mistake this migration made possible: it is valid
      // JSON, parses cleanly, and permanently hides a card with no error
      // anywhere. Before apo_visibility_rule this logic lived in a frozen
      // constant that got reviewed in a diff; in admin nobody reviews it.
      //
      // Deliberately a warning, not a correction. "Hidden on every answer" is
      // a legitimate way to retire a category without a deploy, so silently
      // overriding it would be worse than saying something. Dev-only: it costs
      // production nothing and the audience is whoever just typed the rule.
      const reachable = Object.keys(gate.answers).some(
        (answer) => gate.answers[answer].indexOf(categoryId) !== -1
      );
      if (!reachable) {
        isDev() &&
          console.warn(
            '[Conductor] apo_visibility_rule makes "' +
              categoryId +
              '" unreachable: showWhen ' +
              JSON.stringify(showWhen) +
              ' matches none of gate "' +
              rule.gate +
              '" answers ' +
              JSON.stringify(Object.keys(gate.answers)) +
              '. The card will never appear. Intentional? If not, check for a typo.'
          );
      }
    });

    return merged;
  }

  /**
   * Pure apo_style ordering resolution -- module scope so it is unit-testable
   * without booting the conductor, for the same reason mergeGateVisibilityDeps
   * above is: main._getApoOrderRank() gathers the live inputs (the admin
   * apo_load_order map, the 1:1 toggle, the two order constants, and the two
   * blended visibility-dependency maps) and delegates here, so the DOM reveal
   * pass and the auto-navigation walk can never disagree, and so this logic
   * is testable against real fixtures instead of a hand-written stub that can
   * silently drift from it -- which is exactly what happened to
   * apo-style-category-order.test.js before this function existed.
   *
   * RESOLUTION ORDER
   * 1. a category a gate controls OTHER than that gate's own
   *    insertBeforeCategory anchor (a "sibling", e.g. Bottom Cabinet Doors),
   *    or a category with a selection_dependency_rule (a "dependent", e.g.
   *    Top/Bottom Knobs & Pulls): rides at its anchor/parent's CURRENT rank
   *    plus a fixed offset (recurses through this same function), IGNORING
   *    its own apo_load_order even if a merchant has set one
   * 2. apo_load_order from the category's metaobject, when set
   * 3. for a gate card ('gate:<id>'), half a step before its own
   *    insertBeforeCategory target's CURRENT rank (recurses through this
   *    same function, so it inherits whichever of 1/2/4 the target used)
   * 4. the category's index in the hardcoded order constant, when nothing above resolves
   *
   * GATE CARDS CAN NEVER GET AN apo_load_order VALUE. They are synthetic
   * (GATE_CARD_DEFINITIONS), not real configurator_tab_categories
   * metaobjects, so step 2 always misses for a 'gate:<id>' sortKey and a gate
   * used to depend entirely on its static array position (step 4) -- fine
   * when it shipped, but that position goes stale the moment admin migrates
   * the gate's real neighbours to apo_load_order, because a hand-picked
   * admin integer and a hardcoded array index are two different numbering
   * spaces with no fixed relationship. Verified live 2026-09-11 on
   * flush-mount-bookcase-door-2-0: a partial migration left 'gate:cabinets'
   * (hardcoded index 11) sandwiched between Top Section (admin value 9) and
   * Bottom Section (admin value 12) instead of immediately before Top
   * Cabinet Doors (admin value 15) -- breaking both the documented "shelf
   * pair stays adjacent" rule and the gate's own adjacency to its dependents.
   * Step 3 fixes this at the source by tracking the target's rank live
   * instead of freezing a position at code-review time.
   *
   * STEP 1 EXISTS FOR THE SAME REASON, ONE LEVEL DEEPER. Unlike a gate, a
   * sibling or dependent category IS a real configurator_tab_categories
   * metaobject and CAN carry its own apo_load_order -- and on
   * flush-mount-bookcase-door-2-0 today, Top Knobs & Pulls (16) and Bottom
   * Knobs & Pulls (18) already do. Left to resolve independently, a knobs
   * category can drift away from the cabinet-door style it only exists to
   * accessorize -- e.g. sorting after Front Casing's own fallback position,
   * putting interior cabinet hardware after the door's front casing choice.
   * A customer can't pick knobs for a door style that doesn't exist yet, so
   * this relationship is a hard dependency, not a merchandising choice (per
   * ux-ui-champion review 2026-09-11): the whole gate + Top/Bottom Cabinet
   * Doors + Top/Bottom Knobs & Pulls cluster moves as one unit, positioned
   * entirely by Top Cabinet Doors' own resolved rank (the gate's
   * insertBeforeCategory target). Only that ONE category's apo_load_order
   * still matters for the cluster's position; every other member's is
   * deliberately ignored for ordering purposes -- its metaobject row and
   * value are untouched, this only changes apo_style sort order.
   *
   * @param {string} sortKey - category id, or 'gate:<id>' for a gate card
   * @param {Object} ctx
   * @param {Map<string, number>} ctx.metaMap - admin apo_load_order, sparse (empty Map if none)
   * @param {boolean} ctx.isOneToOne - conductor._isApo1to1Enabled()
   * @param {Array<{id: string, insertBeforeCategory: string}>} ctx.gateCardDefinitions - CONSTANTS.GATE_CARD_DEFINITIONS
   * @param {Object} [ctx.gateVisibilityDeps] - conductor._getGateVisibilityDeps() (blended CATEGORY_VISIBILITY_DEPS); gateId -> { categories }
   * @param {Object} [ctx.selectionVisibilityDeps] - conductor._getSelectionVisibilityDeps() (blended SELECTION_VISIBILITY_DEPS); categoryId -> { dependsOnCategory }
   * @param {string[]} ctx.oneToOneOrder - CONSTANTS.APO_STYLE_1TO1_CATEGORY_ORDER
   * @param {string[]} ctx.defaultOrder - CONSTANTS.APO_STYLE_DEFAULT_CATEGORY_ORDER
   * @param {Set<string>} [visiting] - INTERNAL recursion-cycle guard, never passed by a caller.
   *   Steps 1a/1b/3 all recurse into a DIFFERENT sortKey that an admin sets
   *   (selection_dependency_rule.dependsOn / a gate's insertBeforeCategory) --
   *   the pipeline validates field SHAPE only (md-config-core-data-pipeline.js),
   *   never that it's acyclic. An admin pointing a dependent's dependsOn at its
   *   own gate-sibling (or any other cross-reference cycle) would recurse
   *   forever with no guard, throwing RangeError out of the Array.sort
   *   comparator in BOTH consumers (the apo_style reveal pass AND the
   *   auto-navigation walk) -- breaking category ordering entirely from one
   *   malformed metaobject entry, with no warning anywhere. A revisited key
   *   falls through to FALLBACK for THAT resolution path only (never throws),
   *   matching mergeGateVisibilityDeps' existing "ignore a malformed rule,
   *   don't crash" precedent. Found and reproduced in ccp-compliance-auditor
   *   review 2026-09-11.
   * @returns {number} rank; unknown keys sort to the end
   */
  function resolveApoOrderRank(sortKey, ctx, visiting) {
    const FALLBACK = Number.MAX_SAFE_INTEGER;
    if (!sortKey) return FALLBACK;
    const seen = visiting || new Set();
    if (seen.has(sortKey)) return FALLBACK;
    seen.add(sortKey);

    const safeCtx = ctx || {};
    const {
      metaMap,
      isOneToOne,
      gateCardDefinitions,
      gateVisibilityDeps,
      selectionVisibilityDeps,
      oneToOneOrder,
      defaultOrder,
    } = safeCtx;

    // Step 1a (dependent): a selection_dependency_rule entry (Top/Bottom
    // Knobs & Pulls) rides a hair after its parent's rank, own apo_load_order
    // ignored on purpose -- see STEP 1 rationale above.
    if (selectionVisibilityDeps && selectionVisibilityDeps[sortKey]) {
      const parentCategory = selectionVisibilityDeps[sortKey].dependsOnCategory;
      if (parentCategory) {
        const parentRank = resolveApoOrderRank(parentCategory, safeCtx, seen);
        if (parentRank !== FALLBACK) {
          return parentRank + 0.5;
        }
      }
    }

    // Step 1b (sibling): a real category a gate controls OTHER than that
    // gate's own insertBeforeCategory anchor (Bottom Cabinet Doors, the
    // anchor's sibling) rides after the anchor too, own apo_load_order
    // ignored -- otherwise it can drift ahead of the anchor and, via 1a
    // above, take its own dependent (Bottom Knobs & Pulls) with it.
    if (gateVisibilityDeps) {
      const gateDefs = Array.isArray(gateCardDefinitions)
        ? gateCardDefinitions
        : [];
      for (let i = 0; i < gateDefs.length; i++) {
        const gateDef = gateDefs[i];
        const dep = gateDef && gateVisibilityDeps[gateDef.id];
        const categories = dep && dep.categories;
        if (
          !Array.isArray(categories) ||
          categories.indexOf(sortKey) === -1 ||
          sortKey === gateDef.insertBeforeCategory
        ) {
          continue;
        }
        const siblingPosition = categories
          .filter((categoryId) => categoryId !== gateDef.insertBeforeCategory)
          .indexOf(sortKey);
        const anchorRank = resolveApoOrderRank(
          gateDef.insertBeforeCategory,
          safeCtx,
          seen
        );
        if (anchorRank !== FALLBACK && siblingPosition !== -1) {
          return anchorRank + siblingPosition + 1;
        }
      }
    }

    if (metaMap && typeof metaMap.get === 'function') {
      const value = metaMap.get(sortKey);
      if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
      }
    }

    if (sortKey.indexOf('gate:') === 0) {
      const gateId = sortKey.slice('gate:'.length);
      const gateDefs = Array.isArray(gateCardDefinitions)
        ? gateCardDefinitions
        : [];
      const gateDef = gateDefs.find((def) => def && def.id === gateId);
      if (gateDef && gateDef.insertBeforeCategory) {
        const targetRank = resolveApoOrderRank(
          gateDef.insertBeforeCategory,
          safeCtx,
          seen
        );
        if (targetRank !== FALLBACK) {
          return targetRank - 0.5;
        }
      }
    }

    const order = isOneToOne ? oneToOneOrder : defaultOrder;
    if (!Array.isArray(order)) return FALLBACK;
    const idx = order.indexOf(sortKey);
    return idx === -1 ? FALLBACK : idx;
  }

  /**
   * sessionStorage key holding a gate answer's PROVENANCE.
   *
   * Deliberately nested under the answer's own key prefix instead of given a
   * sibling prefix: Start Over's sweep in clearState() removes every key
   * matching `startsWith('mdConfiguratorGate_')`, so this suffix is cleared
   * in lockstep with the answer for free. A 'mdConfiguratorGateSrc_' sibling
   * would NOT match that prefix and would silently survive Start Over,
   * re-labelling the next answer written for the gate.
   * @param {string} gateId
   * @returns {string}
   */
  function gateAnswerSourceStorageKey(gateId) {
    return 'mdConfiguratorGate_' + gateId + '__src';
  }

  /**
   * Resolve a gate answer's provenance from its two sources, in priority
   * order: this page load's in-memory label, then the persisted one, then
   * the protected default.
   *
   * Pure on purpose -- callers hand in what they already read, so the
   * precedence rule is unit-testable without a conductor or a storage stub.
   *
   * 'user' is the default for UNKNOWN provenance because it is the safe
   * direction: reconcileRestoredGateAnswers() only ever declines to
   * overwrite a 'user' answer, so guessing 'user' can at worst leave a
   * correct answer alone, while guessing 'reconciliation' could silently
   * overwrite a real customer choice.
   *
   * @param {*} cachedSource - this session's label, if any
   * @param {*} storedSource - the persisted label, if any
   * @returns {'user'|'reconciliation'}
   */
  function resolveGateAnswerSource(cachedSource, storedSource) {
    if (cachedSource === 'user' || cachedSource === 'reconciliation') {
      return cachedSource;
    }
    return storedSource === 'reconciliation' ? 'reconciliation' : 'user';
  }

  /**
   * What a _setGateAnswer() write DOES, given what is already stored.
   *
   *   'changed'    -- a genuinely new answer: persist and publish
   *                   BUSINESS_GATE_ANSWER_CHANGED.
   *   'reaffirmed' -- the SAME answer, clicked by a real customer: publish
   *                   BUSINESS_GATE_ANSWER_REAFFIRMED and nothing else.
   *   'silent'     -- the same answer re-asserted by a reconciliation pass:
   *                   publish nothing.
   *
   * Pure, and exposed for test, for the same reason resolveGateAnswerSource
   * above is: this three-way split IS the bug in issue #1826. The unchanged
   * branch used to be a bare `return`, so a customer re-clicking the answer
   * their gate already held published NOTHING -- and that click was the last
   * escape hatch out of a door stuck below 100% because the gate's own
   * auto-applied fallbacks were missing from the applied map. Publishing
   * CHANGED there would have been wrong in the other direction (the visibility
   * manager, auto-navigation and config_gate_answered all treat it as a real
   * new pick), and a reconciliation pass re-asserting its own earlier guess is
   * not a customer gesture at all -- hence three outcomes, not two.
   *
   * @param {*} storedAnswer - what _getGateAnswer currently returns
   * @param {*} answer - the answer being written
   * @param {'user'|'reconciliation'} source
   * @returns {'changed'|'reaffirmed'|'silent'}
   */
  function gateAnswerWriteOutcome(storedAnswer, answer, source) {
    if (storedAnswer !== answer) return 'changed';
    return source === 'user' ? 'reaffirmed' : 'silent';
  }

  // Exposed for unit testing the guard matrix without booting the conductor.
  window.MurphyDoorConfigurator._conductorTestHelpers = {
    shouldClickApplyDecision,
    shouldBlockForbiddenBrowseDecision,
    backClickModeDecision,
    defaultModeForExperience,
    gateAnswerSourceStorageKey,
    resolveGateAnswerSource,
    gateAnswerWriteOutcome,
    mergeGateVisibilityDeps,
    resolveApoOrderRank,
  };

  // =========================================================================
  // PATTERN #13: Performance Thresholds (10 points)
  // Frozen thresholds for performance monitoring
  // =========================================================================
  const PERFORMANCE_THRESHOLDS = Object.freeze({
    INITIALIZATION: 2000, // ms - full conductor init
    STATE_RECOVERY: 500, // ms - state recovery operations
    DATA_REQUEST: 50, // ms - data request/response handling
    DELEGATION: 100, // ms - delegation to sub-modules
    UI_COORDINATION: 16, // ms - UI update coordination
  });

  // =========================================================================
  // PATTERN #12: Performance Tracking (10 points)
  // Centralized performance tracking helper
  // =========================================================================
  function trackPerformance(operationType, duration) {
    const threshold = PERFORMANCE_THRESHOLDS[operationType];
    if (threshold && duration > threshold) {
      publishEvent(CONSTANTS.EVENTS.PERFORMANCE_SLOW_OPERATION, {
        module: 'md-config-core-conductor',
        operation: operationType,
        duration,
        threshold,
        severity: duration > threshold * 2 ? 'critical' : 'warning',
        timestamp: Date.now(),
      });
    }
  }

  // =========================================================================
  // PATTERN #15: Cleanup Arrays (10 points)
  // Track all event subscriptions for proper cleanup
  // =========================================================================
  const cleanupHandlers = [];

  /* State Constants */
  const STATE_TYPES = {
    TAB: 'tab',
    CATEGORY: 'category',
    DISPLAY: 'display',
    PROGRESS: 'progress',
    PRODUCT: 'product',
    VARIANT: 'variant',
    SELECTION: 'selection',
    IMAGE_LAYER: 'imageLayer',
    FORBIDDEN: 'forbidden',
  };

  const STATE_ACTIONS = {
    TAB_CHANGED: 'tabChanged',
    CATEGORY_CHANGED: 'categoryChanged',
    PRODUCT_SELECTED: 'productSelected',
    VARIANT_SELECTED: 'variantSelected',
    SINGLE_VARIANT_COMPLETE: 'singleVariantComplete',
    MULTI_VARIANT_START: 'multiVariantStart',
    MULTI_VARIANT_COMPLETE: 'multiVariantComplete',
    APPLY_SELECTION: 'applySelection',
    BACK_CLICKED: 'backClicked',
  };

  /* Performance Optimization: Frozen fallback for material preferences */
  const EMPTY_MATERIAL_PREFERENCES = Object.freeze({
    selectedWood: null,
    selectedFinish: null,
    selectedWoodGid: null,
    selectedFinishGid: null,
    preferredCombinations: Object.freeze(new Set()),
    lastUpdated: null,
    initialized: false,
  });

  /* Bootstrap Barrier Pattern: Material Preferences Initialization Queue */
  let isMaterialPreferencesInitialized = false;
  let pendingMaterialPreferenceRequests = [];

  const SELECTION_STATES = {
    BROWSING: 'browsing',
    APPLIED: 'applied',
  };

  const DISPLAY_MODES = {
    STANDARD: 'STANDARD',
    DIRECT_VARIANT: 'DIRECT_VARIANT',
    WOOD_FINISH_FILTERED: 'WOOD_FINISH_FILTERED',
  };

  const TRANSITION_STATES = {
    NONE: 'NONE',
    IN_PROGRESS: 'IN_PROGRESS',
    COMPLETED: 'COMPLETED',
  };

  const NAVIGATION_STATES = {
    INITIAL: 'INITIAL',
    IN_PROGRESS: 'IN_PROGRESS',
    COMPLETED: 'COMPLETED',
  };

  const UNKNOWN_CATEGORY_SORT_ORDER = 999;

  // Dev-only logging helper

  /**
   * Validates core dependencies required by the configurator
   *
   * This method validates:
   * 1. Required modules are available
   * 2. UI dependencies are properly initialized
   * 3. Required DOM elements are present
   * 4. Core component interfaces have required methods
   *
   * If validation passes, a system:dependencies_validated event is published
   * If validation fails, a system:error event with detailed context is published
   *
   * @returns {boolean} True if all dependencies are valid, false otherwise
   */
  const checkCoreDependencies = () => {
    try {
      const startTime = performance.now();

      // Check for core modules
      const requiredModules = {
        uiUpdater: window.MurphyDoorConfigurator.uiUpdater,
        stateManager: window.MurphyDoorConfigurator.stateManager,
        coreEventBus: window.MurphyDoorConfigurator.CoreEventBus,
        config: window.MurphyDoorConfigurator.dataPipeline,
        errorHandler: window.MurphyDoorConfigurator.supportErrorHandler,
        validationManager:
          window.MurphyDoorConfigurator.supportValidationManager,
      };

      // Check each module and collect missing ones
      const missingModules = Object.entries(requiredModules)
        .filter(([, module]) => !module)
        .map(([name]) => name);

      if (missingModules.length > 0) {
        throw new Error(
          `Missing required modules: ${missingModules.join(', ')}`
        );
      }

      // Check UI dependencies
      if (!window.MurphyDoorConfigurator.uiUpdater.DOM) {
        throw new Error('UI Updater DOM dependency not found');
      }

      const configuratorWrapper =
        window.MurphyDoorConfigurator.uiUpdater.DOM.getConfiguratorWrapper();
      if (!configuratorWrapper) {
        throw new Error('Configurator wrapper not found in DOM');
      }

      // Check required DOM elements
      const requiredElements = [
        'murphy-door-config-data',
        'murphy-door-product-data',
      ];

      const missingElements = requiredElements.filter(
        (id) => !document.getElementById(id)
      );
      if (missingElements.length > 0) {
        throw new Error(
          `Missing required DOM elements: ${missingElements.join(', ')}`
        );
      }

      // Validate core component interfaces
      // Check stateManager has required methods
      const requiredStateMethods = [
        'getStateMaps',
        'initializeState',
        'getElementData',
        'recoverState',
      ];
      const missingStateMethods = requiredStateMethods.filter(
        (method) =>
          typeof window.MurphyDoorConfigurator.stateManager[method] !==
          'function'
      );

      if (missingStateMethods.length > 0) {
        throw new Error(
          `State Manager missing required methods: ${missingStateMethods.join(', ')}`
        );
      }

      // Check eventBus has required methods
      const requiredEventMethods = ['publish', 'subscribe', 'unsubscribe'];
      const missingEventMethods = requiredEventMethods.filter(
        (method) =>
          typeof window.MurphyDoorConfigurator.CoreEventBus[method] !==
          'function'
      );

      if (missingEventMethods.length > 0) {
        throw new Error(
          `Event Bus missing required methods: ${missingEventMethods.join(', ')}`
        );
      }

      const validationTime = performance.now() - startTime;

      // Publish successful dependency check
      if (window.MurphyDoorConfigurator.CoreEventBus) {
        window.MurphyDoorConfigurator.CoreEventBus.publish(
          CONSTANTS.EVENTS.SYSTEM_DEPENDENCIES_VALIDATED,
          {
            timestamp: Date.now(),
            modules: Object.keys(requiredModules),
            executionTime: validationTime,
            validatedInterfaces: {
              stateManager: requiredStateMethods,
              eventBus: requiredEventMethods,
            },
          }
        );

        // Track performance if validation took longer than expected
        if (validationTime > 50) {
          window.MurphyDoorConfigurator.CoreEventBus.publish(
            CONSTANTS.EVENTS.PERFORMANCE_INITIALIZATION_SLOW,
            {
              operation: 'dependencies_check',
              executionTime: validationTime,
              threshold: 50,
              timestamp: Date.now(),
            }
          );
        }
      }

      return true;
    } catch (error) {
      // Route error through centralized error handler (DRY: single publication point)
      window.MurphyDoorConfigurator.utils.handleSystemError({
        type: 'DependencyError',
        error: error,
        message: error.message,
        source: 'checkCoreDependencies',
        recoverable: false,
        impact: 'critical',
        details: {
          windowObject: typeof window !== 'undefined',
          murphyDoorConfiguratorObject:
            typeof window.MurphyDoorConfigurator !== 'undefined',
          availableModules: window.MurphyDoorConfigurator
            ? Object.keys(window.MurphyDoorConfigurator)
            : [],
        },
      });
      isDev() && console.error('Dependency check failed:', error);
      return false;
    }
  };

  // Initialize core functionality
  const initializeCore = async () => {
    // 🛡️ SINGLETON GUARD v3: Prevent initializeCore() from running twice.
    // window.__murphyDoorBootstrapComplete is on raw window (NOT MurphyDoorConfigurator namespace)
    // so it survives even if the namespace is wiped or recreated during async section rendering.
    // Root cause: async section rendering reloads all module IIFEs with fresh v= hashes;
    // the entire MurphyDoorConfigurator namespace gets recreated losing all namespace-based flags.
    if (
      window.__murphyDoorBootstrapComplete ||
      window.MurphyDoorConfigurator._initializeCoreComplete ||
      window.MurphyDoorConfigurator.perfectProductDictionary ||
      window.MurphyDoorConfigurator.main
    ) {
      isDev() &&
        console.log(
          '🛡️ CONDUCTOR: initializeCore() skipped - bootstrap already complete (window flag / PPD / main / namespace flag)'
        );
      return false;
    }
    window.MurphyDoorConfigurator._initializeCoreComplete = true;

    try {
      const dependencies = checkCoreDependencies();

      // Initialize state
      const initialState = {
        tabStructure: new Map(),
        categoryStructure: new Map(),
        productStructure: null,
      };

      window.MurphyDoorConfigurator.main = {
        /* Initialization Guard */
        _isInitialized: false,

        /* PPD State Readiness Flags */
        // 🎯 TIMING FIX: Initialize flags here so subscription can set them before initializeConfigurator() runs
        _ppdStateReady: false,
        _ppdStateTimestamp: null,

        /* Celebration State */
        // 🎉 Duplicate celebration protection - ensures confetti fires only once per session
        _celebrationFired: false,

        /* Core References */
        stateManager: window.MurphyDoorConfigurator.stateManager,
        DOM: window.MurphyDoorConfigurator.uiUpdater.DOM,

        /**
         * Claim the one-shot celebration slot to prevent duplicate confetti.
         * CCP delegation: bl-/ui- modules MUST call this instead of mutating
         * the private `_celebrationFired` flag directly (CCP Rule 2). Returns
         * true if the slot was newly claimed, false if it was already claimed.
         * @returns {boolean}
         */
        claimCelebrationSlot() {
          if (this._celebrationFired) return false;
          this._celebrationFired = true;
          return true;
        },

        /* DOM Access Methods */
        /**
         * Gets a category element by its ID
         * @param {string} categoryId - The ID of the category to retrieve
         * @returns {HTMLElement|null} - The category element or null if not found
         */
        getCategoryElement(categoryId) {
          if (!categoryId) {
            isDev() &&
              console.error('getCategoryElement: categoryId is required');
            return null;
          }
          return this.DOM.getCategoryElement(categoryId);
        },

        /* State Access Methods */
        /**
         * Provides access to all state maps through the central conductor
         *
         * This method implements the Central Conductor Pattern by delegating
         * state access to the stateManager module. It provides a unified access point
         * for all modules to retrieve state without direct access to the stateManager.
         *
         * By channeling all state access through the Main module, we maintain
         * proper separation of concerns and module isolation according to the
         * Central Conductor Pattern.
         *
         * @returns {Object} The complete STATE_MAPS object from the stateManager
         */
        getStateMaps() {
          return this.stateManager.getStateMaps();
        },

        /**
         * Returns the tab structure data from the Perfect Product Dictionary (PPD).
         *
         * This method acts as part of the Central Conductor Pattern by providing
         * controlled access to the tab structure configuration. It ensures that
         * other modules don't access the configuration data directly, maintaining
         * proper separation of concerns.
         *
         * CCP COMPLIANT: Reads from PPD directly, no legacy data pipeline access.
         *
         * @returns {Map<number, Object>} A map of tab IDs to tab configuration objects
         * @throws {Error} If the tab structure is not available or properly initialized
         */
        getTabStructure() {
          // ✅ PPD-ONLY FLOW: Use tab structure from STATE_MAPS (extracted from PPD by StateInitializer)
          // Raw ppd.tabStructure doesn't have the enriched data needed for navigation
          // stateManager stores it as STATE_MAPS.core.tabs (see md-config-core-state-manager.js:269)

          const stateMaps = this.stateManager.getStateMaps();

          if (!stateMaps.core || !stateMaps.core.tabs) {
            const errorMessage =
              'Tab structure data not available in STATE_MAPS.core.tabs';
            isDev() && console.error('❌ CONDUCTOR:', errorMessage);
            isDev() && console.log('  stateMaps.core:', stateMaps.core);
            isDev() &&
              console.log('  stateMaps.core.tabs:', stateMaps.core?.tabs);
            this.handleError(errorMessage, new Error(errorMessage));
            return new Map(); // Return empty map instead of null/undefined
          }

          return stateMaps.core.tabs;
        },

        /**
         * 🔥 CCP COMPLIANT: Returns tab readiness status for support modules
         * This method provides safe DOM state information without allowing direct DOM access
         *
         * @returns {Object} Tab readiness status with tabsExist and hasActiveTab properties
         */
        getTabReadinessStatus() {
          try {
            // CCP COMPLIANT: Return cached state from UI module via reactive state synchronization
            return this._tabReadinessStatus;
          } catch (error) {
            isDev() &&
              console.warn(
                '⚠️ Conductor: Error getting tab readiness status',
                error
              );
            return {
              tabsExist: false,
              hasActiveTab: false,
              tabCount: 0,
              error: error.message,
            };
          }
        },

        /**
         * Returns the category structure data from the Perfect Product Dictionary (PPD).
         *
         * This method acts as part of the Central Conductor Pattern by providing
         * controlled access to the category structure configuration. It ensures that
         * other modules don't access the configuration data directly, maintaining
         * proper separation of concerns.
         *
         * CCP COMPLIANT: Reads from PPD directly, no legacy data pipeline access.
         *
         * @returns {Map<number, Object>} A map of category IDs to category configuration objects
         * @throws {Error} If the category structure is not available or properly initialized
         */
        getCategoryStructure() {
          // ✅ PPD-ONLY FLOW: Use category structure from STATE_MAPS (enriched by StateInitializer with displayElements.thumbnails)
          // Raw ppd.categories doesn't have the displayElements structure needed for navigation
          // stateManager stores it as STATE_MAPS.core.categories (see md-config-core-state-manager.js:270)

          const stateMaps = this.stateManager.getStateMaps();

          if (!stateMaps.core || !stateMaps.core.categories) {
            const errorMessage =
              'Category structure data not available in STATE_MAPS.core.categories';
            isDev() && console.error('❌ CONDUCTOR:', errorMessage);
            isDev() && console.log('  stateMaps.core:', stateMaps.core);
            isDev() &&
              console.log(
                '  stateMaps.core.categories:',
                stateMaps.core?.categories
              );
            this.handleError(errorMessage, new Error(errorMessage));
            return new Map(); // Return empty map instead of null/undefined
          }

          return stateMaps.core.categories;
        },

        /**
         * PPD-FIRST: Get category display order for fast sorting
         * Eliminates Map lookups during sort comparisons
         *
         * @param {string} categoryId - Category identifier
         * @returns {number} Display order index (0-based)
         */
        getCategoryDisplayOrder(categoryId) {
          const ppd = this.getPerfectProductDictionary();
          if (!ppd || !ppd.categoryDisplayOrder) {
            isDev() &&
              console.error(
                '❌ CONDUCTOR: categoryDisplayOrder not available in PPD'
              );
            // Fallback to loadOrder from category structure
            const categoryStructure = this.getCategoryStructure();
            const categoryData = categoryStructure.get(categoryId);
            return categoryData?.loadOrder ?? UNKNOWN_CATEGORY_SORT_ORDER;
          }

          const displayOrder = ppd.categoryDisplayOrder.get(categoryId);
          if (displayOrder === undefined) {
            isDev() &&
              console.warn(
                `⚠️ CONDUCTOR: No display order found for category "${categoryId}"`
              );
            return UNKNOWN_CATEGORY_SORT_ORDER; // Put unknown categories at end
          }

          return displayOrder;
        },

        /**
         * Returns the product structure data from the configuration.
         *
         * This method acts as part of the Central Conductor Pattern by providing
         * controlled access to the product structure configuration. It ensures that
         * other modules don't access the configuration data directly, maintaining
         * proper separation of concerns.
         *
         * The product structure contains all product and variant data used throughout
         * the configurator, including relationships between products, categories,
         * and variants.
         *
         * @returns {Object} The complete product structure object with maps for products and variants
         * @throws {Error} If the product structure is not available or properly initialized
         */
        getProductStructure() {
          // ✅ PPD-ONLY FLOW: Use product structure from STATE_MAPS.core.products (extracted from PPD by StateInitializer)
          // Legacy path (config.data.productStructure) is deprecated
          // Note: stateManager stores it as STATE_MAPS.core.products (see md-config-core-state-manager.js:271)

          const stateMaps = this.stateManager.getStateMaps();

          if (!stateMaps.core || !stateMaps.core.products) {
            const errorMessage =
              'Product structure data not available in STATE_MAPS.core.products';
            isDev() && console.error('❌ CONDUCTOR:', errorMessage);
            isDev() && console.log('  stateMaps.core:', stateMaps.core);
            isDev() &&
              console.log(
                '  stateMaps.core.products:',
                stateMaps.core?.products
              );
            this.handleError(errorMessage, new Error(errorMessage));
            return { addOnProducts: new Map() }; // Return empty structure instead of null/undefined
          }

          return stateMaps.core.products;
        },

        /**
         * Get variant by ID from PPD variantMap (O(1) lookup)
         *
         * This method provides O(1) access to variant data from the perfectProductDictionary's
         * pre-built variantMap. The map is constructed during Layer 1 of the data pipeline
         * and provides instant variant lookups without iterating through product arrays.
         *
         * ⚡ PPD-FIRST ARCHITECTURE: Pre-built map from Layer 1
         * 🚀 PERFORMANCE: O(1) Map.get vs O(n) array.find
         *
         * @param {number|string} variantId - Variant ID (string for synthetic, numeric for Shopify)
         * @returns {Object|null} Variant object with product context or null if not found
         *
         * @note Supports both string IDs (CompositeProduct synthetic variants like "generated_123456_0")
         *       and numeric IDs (Shopify variants like 789012). Dual-lookup maintains O(1) performance.
         */
        getVariantById(variantId) {
          const ppd = this.perfectProductDictionary;
          if (!ppd) {
            isDev() && console.warn('⚠️ CONDUCTOR: PPD not available');
            return null;
          }

          // 🔥 FIX: Support both string (CompositeProduct synthetic) and numeric variant IDs
          // Example: "generated_123456_0" (string) or 789012 (number)
          // Try string first (direct match), then numeric (parsed match)
          const stringId = String(variantId);
          const numericId = parseInt(variantId, 10);

          // TODO: Clarify which map is canonical. `variantIdToVariantMap` is the modern PPD source.
          // `variantMap` may be a legacy remnant and should be considered for deprecation.
          // Try new variantMap first (from current modernization), fallback to existing variantIdToVariantMap
          return (
            ppd.variantMap?.get(stringId) ||
            ppd.variantMap?.get(numericId) ||
            ppd.variantIdToVariantMap?.get(stringId) ||
            ppd.variantIdToVariantMap?.get(numericId) ||
            null
          );
        },

        /**
         * Get product by ID from PPD products Map (O(1) lookup)
         *
         * This method provides O(1) access to product data from the perfectProductDictionary's
         * products Map. The map is constructed during the bootstrap pipeline and provides
         * instant product lookups without iterating through product arrays.
         *
         * ⚡ PPD-FIRST ARCHITECTURE: Pre-built map from bootstrap
         * 🚀 PERFORMANCE: O(1) Map.get vs O(n) array.find
         *
         * @param {number|string} productId - Product ID (will be parsed to integer)
         * @returns {Object|null} Product object or null if not found
         */
        getProductById(productId) {
          const ppd = this.perfectProductDictionary;
          if (!ppd?.products) {
            isDev() &&
              console.warn('⚠️ CONDUCTOR: products Map not available in PPD');
            return null;
          }

          // Try both numeric and string keys for compatibility
          const idNum = parseInt(productId, 10);
          return (
            ppd.products.get(idNum) ||
            ppd.products.get(String(productId)) ||
            null
          );
        },

        /**
         * Get virtualized products for a category (includes CompositeProducts + standalone)
         * ✅ PPD-FIRST: O(1) lookup from pre-built map (Phase 1 Layer 2)
         *
         * This method provides access to pre-computed virtualized products for a specific
         * category. Virtualized products include both CompositeProducts (merged from multiple
         * source products with same display_name) and standalone products.
         *
         * The categoryToVirtualizedProductsMap is built during Phase 1 Layer 2 virtualization
         * and provides O(1) category lookups without runtime filtering or searching.
         *
         * @param {string} categoryName - Category name/identifier
         * @returns {Array} Frozen array of virtualized products for the category (empty array if not found)
         */
        getVirtualizedProductsForCategory(categoryName) {
          if (
            !this.perfectProductDictionary?.categoryToVirtualizedProductsMap
          ) {
            isDev() &&
              console.warn(
                '⚠️ CONDUCTOR: categoryToVirtualizedProductsMap not available in PPD'
              );
            return [];
          }

          const products =
            this.perfectProductDictionary.categoryToVirtualizedProductsMap.get(
              categoryName
            );
          if (!products) {
            isDev() &&
              console.warn(
                `⚠️ CONDUCTOR: No virtualized products found for category "${categoryName}"`
              );
            return [];
          }

          return products; // Already frozen in PPD
        },

        /**
         * Get all virtualized products (includes CompositeProducts + standalone)
         * ✅ PPD-FIRST: Direct access to pre-built array (Phase 1 Layer 2)
         *
         * This method returns the complete array of virtualized products created during
         * Phase 1 Layer 2 bootstrap. The array combines CompositeProducts (products with
         * merged variants from same display_name) with standalone products.
         *
         * The virtualizedProducts array is built once during bootstrap and frozen for
         * immutability. This eliminates all runtime virtualization operations.
         *
         * @returns {Array} All virtualized products (empty array if not available)
         */
        getAllVirtualizedProducts() {
          if (!this.perfectProductDictionary?.virtualizedProducts) {
            isDev() &&
              console.warn(
                '⚠️ CONDUCTOR: virtualizedProducts not available in PPD'
              );
            return [];
          }

          return this.perfectProductDictionary.virtualizedProducts;
        },

        /**
         * Get virtualized product by ID
         * ✅ PPD-FIRST: O(1) lookup from pre-built map (Phase 1 Layer 2)
         *
         * This method provides O(1) access to virtualized products by ID using the
         * pre-built virtualizedProductsMap. Works for both CompositeProduct IDs and
         * regular product IDs.
         *
         * The virtualizedProductsMap is built during Phase 1 Layer 2 and provides
         * instant lookups without iterating through product arrays.
         *
         * @param {string|number} productId - Product ID (will be parsed for numeric IDs)
         * @returns {Object|null} Virtualized product object or null if not found
         */
        getVirtualizedProductById(productId) {
          if (!this.perfectProductDictionary?.virtualizedProductsMap) {
            isDev() &&
              console.warn(
                '⚠️ CONDUCTOR: virtualizedProductsMap not available in PPD'
              );
            return null;
          }

          // 🔥 FIX TASK 2: Handle both numeric and string IDs (same pattern as getProductById)
          const map = this.perfectProductDictionary.virtualizedProductsMap;
          const numericId = parseInt(productId, 10);

          return (
            map.get(productId) ||
            (!isNaN(numericId) && map.get(numericId)) ||
            map.get(String(productId)) ||
            null
          );
        },

        /**
         * Get product by variant ID from PPD (O(1) lookup)
         *
         * This method provides O(1) access to product data via variant ID lookup using the
         * perfectProductDictionary's variantIdToProductMap. The map is constructed during
         * the bootstrap pipeline and provides instant product lookups without iteration.
         *
         * ⚡ PPD-FIRST ARCHITECTURE: Pre-built map from bootstrap
         * 🚀 PERFORMANCE: O(1) Map.get vs O(n) array.find
         *
         * @param {number|string} variantId - Variant ID (string for synthetic, numeric for Shopify)
         * @returns {Object|null} Product object or null if not found
         *
         * @note Supports both string IDs (CompositeProduct synthetic variants like "generated_123456_0")
         *       and numeric IDs (Shopify variants like 789012). Dual-lookup maintains O(1) performance.
         */
        getProductByVariantId(variantId) {
          if (!this.perfectProductDictionary?.variantIdToProductMap) {
            isDev() &&
              console.warn(
                '⚠️ CONDUCTOR: variantIdToProductMap not available in PPD'
              );
            return null;
          }

          // 🔥 FIX: Try both string and numeric keys for CompositeProduct compatibility
          const stringId = String(variantId);
          const numericId = parseInt(variantId, 10);
          return (
            this.perfectProductDictionary.variantIdToProductMap.get(stringId) ||
            this.perfectProductDictionary.variantIdToProductMap.get(
              numericId
            ) ||
            null
          );
        },

        /**
         * Get total price of all applied selections
         *
         * This method delegates to the state manager's optimized price calculation method
         * which uses pre-categorized appliedSelections Map for efficient price totaling.
         *
         * Delegates to state manager's optimized method
         * 🚀 PERFORMANCE: Uses pre-categorized appliedSelections Map
         *
         * @returns {number} Total price of all applied selections
         */
        getAppliedSelectionsTotalPrice() {
          if (!this.stateManager) {
            isDev() &&
              console.warn('⚠️ CONDUCTOR: State manager not available');
            return 0;
          }

          return this.stateManager.getAppliedSelectionsTotalPrice();
        },

        /**
         * Check if any selections are applied
         *
         * This method delegates to the state manager's O(1) check method which simply
         * checks the size of the pre-categorized appliedSelections Map.
         *
         * Delegates to state manager's O(1) check
         * 🚀 PERFORMANCE: O(1) Map.size check vs O(n) .some() iteration
         *
         * @returns {boolean} True if any selections are applied
         */
        hasAppliedSelections() {
          if (!this.stateManager) {
            isDev() &&
              console.warn('⚠️ CONDUCTOR: State manager not available');
            return false;
          }

          return this.stateManager.hasAppliedSelections();
        },

        /**
         * Get total price of all browsing selections
         * Delegates to state manager's O(1) iteration over browsingSelections Map
         *
         * @returns {number} Total price of all browsing selections
         */
        getBrowsingSelectionsTotalPrice() {
          if (!this.stateManager) {
            isDev() &&
              console.warn('⚠️ CONDUCTOR: State manager not available');
            return 0;
          }

          return this.stateManager.getBrowsingSelectionsTotalPrice();
        },

        /**
         * Check if any selections are in browsing state
         * Delegates to state manager's O(1) check
         *
         * @returns {boolean} True if any browsing selections exist
         */
        hasBrowsingSelections() {
          if (!this.stateManager) {
            isDev() &&
              console.warn('⚠️ CONDUCTOR: State manager not available');
            return false;
          }

          return this.stateManager.hasBrowsingSelections();
        },

        /**
         * Serialize configurator selections into a base64-encoded config string.
         * Delegates to persistenceManager.serializeState() — CCP-compliant delegation.
         * Used by cart-manager to build the _Configuration_URL cart line item property.
         *
         * @param {Object} selections - The selections object to serialize
         * @returns {string|null} Base64-encoded config string, or null on failure
         */
        serializeConfigurationState(selections) {
          const persistenceManager =
            window.MurphyDoorConfigurator.supportPersistenceManager;
          if (
            !persistenceManager ||
            typeof persistenceManager.serializeState !== 'function'
          ) {
            isDev() &&
              console.warn(
                '⚠️ CONDUCTOR: persistenceManager unavailable for serializeConfigurationState'
              );
            return null;
          }
          return persistenceManager.serializeState(selections);
        },

        /**
         * Decode a base64 config string (the ?config= / preset format) back
         * into a selections object. Mirror of serializeConfigurationState:
         * delegates to persistenceManager.deserializeState(), which publishes
         * ERROR_DESERIALIZE_STATE_FAILED on a bad payload.
         * Used by md-config-support-build-preset.js to decode a preset slide.
         *
         * @param {string} encoded - base64 JSON
         * @returns {Object|null} Decoded selections, or null on failure
         */
        deserializeConfigurationState(encoded) {
          const persistenceManager =
            window.MurphyDoorConfigurator.supportPersistenceManager;
          if (
            !persistenceManager ||
            typeof persistenceManager.deserializeState !== 'function'
          ) {
            isDev() &&
              console.warn(
                '⚠️ CONDUCTOR: persistenceManager unavailable for deserializeConfigurationState'
              );
            return null;
          }
          if (typeof encoded !== 'string' || !encoded) return null;
          const decoded = persistenceManager.deserializeState(encoded);
          return decoded &&
            typeof decoded === 'object' &&
            !Array.isArray(decoded)
            ? decoded
            : null;
        },

        /**
         * STATE_MAPS.buildPreset, created on first use so an older state
         * manager (or a test stub) without the entry still works.
         * @returns {{activeId: (string|null), edited: boolean}|null}
         */
        _getBuildPresetState() {
          const maps =
            this.stateManager &&
            typeof this.stateManager.getStateMaps === 'function'
              ? this.stateManager.getStateMaps()
              : null;
          if (!maps) return null;
          if (!maps.buildPreset || typeof maps.buildPreset !== 'object') {
            maps.buildPreset = { activeId: null, edited: false };
          }
          return maps.buildPreset;
        },

        /**
         * Sets (or clears, with null) the "Build this" preset whose look is
         * currently applied. Any change resets the edited flag. A handle that
         * fails CONSTANTS.BUILD_PRESET_HANDLE_REGEX is rejected: the value
         * becomes a cart property and is client-settable.
         * @param {string|null} handle - 'bp-<12 hex>' or null
         * @returns {boolean} true when stored
         */
        setActiveBuildPresetId(handle) {
          const state = this._getBuildPresetState();
          if (!state) return false;
          if (handle === null || handle === undefined) {
            state.activeId = null;
            state.edited = false;
            return true;
          }
          const pattern = CONSTANTS.BUILD_PRESET_HANDLE_REGEX;
          if (typeof handle !== 'string' || !pattern || !pattern.test(handle)) {
            isDev() &&
              console.warn(
                '⚠️ CONDUCTOR: rejected invalid build preset handle',
                handle
              );
            return false;
          }
          state.activeId = handle;
          state.edited = false;
          return true;
        },

        /** @returns {string|null} the active preset handle */
        getActiveBuildPresetId() {
          const state = this._getBuildPresetState();
          return state ? state.activeId : null;
        },

        /**
         * Marks the active preset as edited by the customer (a manual pick on
         * one of its categories). No-op while no preset is active.
         * @param {boolean} edited
         * @returns {boolean} true when stored
         */
        setBuildPresetEdited(edited) {
          const state = this._getBuildPresetState();
          if (!state || !state.activeId) return false;
          state.edited = edited === true;
          return true;
        },

        /** @returns {boolean} true once the customer changed a preset category */
        isBuildPresetEdited() {
          const state = this._getBuildPresetState();
          return !!(state && state.activeId && state.edited);
        },

        /**
         * Check if any selections exist (browsing OR applied)
         * Used for price label logic ("Your Price" vs "Starting at")
         * Delegates to state manager's O(1) check
         *
         * @returns {boolean} True if any selections exist in any state
         */
        hasAnySelections() {
          if (!this.stateManager) {
            isDev() &&
              console.warn('⚠️ CONDUCTOR: State manager not available');
            return false;
          }

          return this.stateManager.hasAnySelections();
        },

        /**
         * Updates material preferences via state manager delegation
         * Used by material-preference-coordinator for CCP-compliant state updates
         *
         * @param {Object} newPreferences - Complete preferences object to set
         *   Can accept two formats:
         *   1. { wood: {sku, gid}, finish: {sku, gid} } - Compact format (transformed internally)
         *   2. { woodApplied, appliedWoodSku, finishApplied, appliedFinishSku } - State manager format
         */
        updateMaterialPreferencesFromObject(newPreferences) {
          if (!this.stateManager) {
            isDev() &&
              console.error(
                '❌ CONDUCTOR: State manager not available for updateMaterialPreferencesFromObject'
              );
            return;
          }

          if (
            typeof this.stateManager.setMaterialPreferencesFromObject !==
            'function'
          ) {
            isDev() &&
              console.error(
                '❌ CONDUCTOR: State manager setMaterialPreferencesFromObject method not available'
              );
            return;
          }

          // Transform compact format {wood: {sku, gid}, finish: {sku, gid}} to state manager format
          let transformedPreferences = newPreferences;
          if (newPreferences.wood || newPreferences.finish) {
            transformedPreferences = {
              woodApplied: !!newPreferences.wood,
              appliedWoodSku: newPreferences.wood
                ? newPreferences.wood.sku
                : null,
              finishApplied: !!newPreferences.finish,
              appliedFinishSku: newPreferences.finish
                ? newPreferences.finish.sku
                : null,
              lastUpdated: Date.now(),
            };
          }

          this.stateManager.setMaterialPreferencesFromObject(
            transformedPreferences
          );

          // CRITICAL FIX: Trigger material preference coordinator to compute filter state and publish bubble-up data
          // When preferences are updated programmatically (URL recovery, bulk updates), the coordinator
          // needs to be notified to compute the filter state and publish the material:filter_state_updated
          // event, which includes bubble-up variant data for UI transformation.
          if (window.MurphyDoorConfigurator.materialPreferenceCoordinator) {
            window.MurphyDoorConfigurator.materialPreferenceCoordinator.computeAndPublishFilterState();
          }
        },

        /**
         * Store material filter state computed by material-preference-coordinator
         * PPD-First pattern: Filter state is stored centrally for UI modules to access
         * via conductor delegation (no direct module-to-module calls)
         *
         * @param {Map} categoryFilters - Map<categoryId, { compatibleVariantIds, incompatibleVariantIds, allVariantIds, autoApplyVariantId, allGreyedOut }>
         */
        updateMaterialFilterState(categoryFilters) {
          this.materialFilterState = categoryFilters;
          isDev() &&
            console.log(
              `✅ CONDUCTOR: Material filter state updated for ${categoryFilters?.size ?? 0} categories`
            );
        },

        /**
         * Delegates material display update to the UI Updater module
         * CCP Compliant: Phase 2 modules access UI through conductor delegation
         *
         * @param {string} categoryId - Category identifier for material display
         * @param {Object} filterResult - Filter result from material compatibility check
         * @returns {void}
         */
        updateMaterialDisplay(categoryId, filterResult) {
          const uiUpdater = window.MurphyDoorConfigurator.uiUpdater;
          if (!uiUpdater) {
            this.handleError(
              'UI Updater not available for updateMaterialDisplay',
              new Error('UI Updater module is missing'),
              {
                method: 'updateMaterialDisplay',
                severity: 'warning',
                recoverable: true,
              }
            );
            return;
          }
          uiUpdater.updateMaterialDisplay(categoryId, filterResult);
        },

        /**
         * Get material filter state for a specific category
         * Used by ui-updater for string-replacement-based filtering (zero DOM queries)
         *
         * @param {string} categoryId - Category identifier
         * @returns {Object|null} Filter state { compatibleVariantIds, incompatibleVariantIds, allVariantIds, autoApplyVariantId, allGreyedOut } or null
         */
        getMaterialFilterState(categoryId) {
          return this.materialFilterState?.get(categoryId) || null;
        },

        /**
         * Get all material filter state (full Map)
         * Used by modules that need to iterate over all category filter states
         *
         * @returns {Map} Full category filters Map or empty Map
         */
        getAllMaterialFilterState() {
          return this.materialFilterState || new Map();
        },

        /**
         * Retrieves the variant transition state from the state manager.
         *
         * This method acts as part of the Central Conductor Pattern by providing
         * controlled access to the variant transition state. It ensures that
         * other modules don't access the state manager directly, maintaining
         * proper separation of concerns.
         *
         * The variant transition state indicates whether the system is currently
         * in the process of transitioning between variant selections, which is
         * used to prevent certain UI interactions during transitions.
         *
         * @returns {boolean} True if the system is in variant transition, false otherwise
         */
        getVariantTransitionState() {
          if (!this.stateManager) {
            const errorMessage = 'State manager not available';
            this.handleError(errorMessage, new Error(errorMessage), {
              method: 'getVariantTransitionState',
            });
            return false; // Return safe default value
          }

          try {
            return this.stateManager.getStateMaps().core.inVariantTransition;
          } catch (error) {
            this.handleError('Failed to get variant transition state', error, {
              method: 'getVariantTransitionState',
            });
            return false; // Return safe default value
          }
        },

        /**
         * Retrieves data from a DOM element using data attributes through the state manager.
         *
         * This method acts as part of the Central Conductor Pattern by providing
         * controlled access to element data retrieval functionality. It ensures that
         * other modules don't access the state manager directly, maintaining
         * proper separation of concerns.
         *
         * @param {HTMLElement|string} element - The DOM element or element selector
         * @param {string} dataType - The type of data to retrieve from the element
         * @returns {*} The requested data from the element, or null if unavailable
         * @throws {Error} If the element or data type is invalid
         */
        getElementData(element, dataType) {
          if (!element) {
            const errorMessage = 'Element is required for getElementData';
            this.handleError(errorMessage, new Error(errorMessage), {
              method: 'getElementData',
              parameters: { element, dataType },
            });
            return null;
          }

          if (!dataType) {
            const errorMessage = 'Data type is required for getElementData';
            this.handleError(errorMessage, new Error(errorMessage), {
              method: 'getElementData',
              parameters: { element },
            });
            return null;
          }

          try {
            return this.stateManager.getElementData(element, dataType);
          } catch (error) {
            this.handleError(
              `Failed to get element data (${dataType})`,
              error,
              {
                method: 'getElementData',
                parameters: { element, dataType },
              }
            );
            return null;
          }
        },

        /**
         * @deprecated Use getActiveSelectionFromState for performance-critical data access or getActiveSelectionElement for the DOM element. This method relies on slow DOM queries.
         *
         * Retrieves the active selection element for a given category.
         *
         * This method acts as part of the Central Conductor Pattern by providing
         * controlled access to the DOM for active selection elements. It ensures
         * that other modules don't directly access the DOM or stateManager,
         * maintaining proper separation of concerns.
         *
         * The active selection element represents the currently selected product
         * or variant within a category and contains data attributes with selection
         * information. This is a critical part of the selection state management
         * in the configurator.
         *
         * @param {HTMLElement|string} category - The category element or category ID selector
         * @returns {HTMLElement|null} The active selection DOM element, or null if no selection exists or an error occurs
         */
        getActiveSelection(category) {
          isDev() &&
            console.warn(
              'DEPRECATION: getActiveSelection() is deprecated. Use getActiveSelectionFromState() for better performance.'
            );
          if (!category) {
            const errorMessage = 'Category is required for getActiveSelection';
            this.handleError(errorMessage, new Error(errorMessage), {
              method: 'getActiveSelection',
              params: { category },
            });
            return null;
          }

          if (!this.stateManager) {
            const errorMessage = 'State manager is not initialized';
            this.handleError(errorMessage, new Error(errorMessage), {
              method: 'getActiveSelection',
            });
            return null;
          }

          try {
            return this.stateManager.getActiveSelection(category);
          } catch (error) {
            this.handleError(
              `Failed to get active selection for category ${category}`,
              error,
              {
                method: 'getActiveSelection',
                params: {
                  category:
                    typeof category === 'string' ? category : 'HTMLElement',
                },
              }
            );
            return null;
          }
        },

        /**
         * Retrieves active selection from state (O(1) lookup) for a specific category.
         *
         * This method acts as part of the Central Conductor Pattern by delegating to
         * the state manager's getActiveSelectionFromState method. It provides state-based
         * selection retrieval without DOM queries for performance-critical code paths.
         *
         * ARCHITECTURAL NOTE: This is the preferred method for retrieving active selection
         * data as it reads from STATE_MAPS.browsingSelections (O(1)) instead of querying the DOM.
         * The state object contains productId, variantId, and display information already resolved.
         *
         * @param {string} categoryId - The category ID to get active selection for
         * @returns {Object|null} Selection data: { productId, variantId, displayTitle, price, productTitle }
         *                        or null if no active selection found
         */
        getActiveSelectionFromState(categoryId) {
          if (!categoryId) {
            const errorMessage =
              'Category ID is required for getActiveSelectionFromState';
            this.handleError(errorMessage, new Error(errorMessage), {
              method: 'getActiveSelectionFromState',
              params: { categoryId },
            });
            return null;
          }

          if (!this.stateManager) {
            const errorMessage = 'State manager is not initialized';
            this.handleError(errorMessage, new Error(errorMessage), {
              method: 'getActiveSelectionFromState',
            });
            return null;
          }

          try {
            return this.stateManager.getActiveSelectionFromState(categoryId);
          } catch (error) {
            this.handleError(
              `Failed to get active selection from state for category ${categoryId}`,
              error,
              {
                method: 'getActiveSelectionFromState',
                params: { categoryId },
              }
            );
            return null;
          }
        },

        /**
         * Retrieves variant view data for a specific category from the state manager.
         *
         * This method acts as part of the Central Conductor Pattern by providing
         * controlled access to variant view data. It ensures that other modules don't
         * access the state manager directly, maintaining proper separation of concerns.
         *
         * The variant view data contains information about the currently displayed variant
         * in the UI, including references to DOM elements and product IDs.
         *
         * @param {string|HTMLElement} category - The category ID or category DOM element
         * @returns {Object|null} The variant view data object containing productId and variantView,
         *                       or null if unavailable or an error occurs
         */
        getVariantViewData(category) {
          if (!category) {
            const errorMessage =
              'Category ID is required for getVariantViewData';
            this.handleError(errorMessage, new Error(errorMessage), {
              method: 'getVariantViewData',
              parameters: { category },
            });
            return null;
          }

          if (!this.stateManager) {
            const errorMessage = 'State manager is not initialized';
            this.handleError(errorMessage, new Error(errorMessage), {
              method: 'getVariantViewData',
            });
            return null;
          }

          try {
            return this.stateManager.getVariantViewData(category);
          } catch (error) {
            this.handleError(
              `Failed to get variant view data for category ${category}`,
              error,
              {
                method: 'getVariantViewData',
                category:
                  typeof category === 'string' ? category : 'HTMLElement',
              }
            );
            return null;
          }
        },

        /**
         * Retrieves comprehensive selection data for a specific category from the state manager.
         *
         * This method is a core part of the Central Conductor Pattern, providing a standardized
         * interface for accessing selection state from any module while maintaining proper
         * separation of concerns. It delegates the actual state access to the stateManager
         * while handling validation and error management at the conductor level.
         *
         * The method centralizes:
         * - Parameter validation
         * - Dependency checking
         * - Error handling with context
         * - Proper delegation to stateManager
         *
         * @param {string|HTMLElement} category - The category ID or category DOM element
         * @returns {Object|null} The selection data object containing categoryId, productId, variantId, and selectionType,
         *                        or null if unavailable or an error occurs
         */
        getSelectionData(category) {
          try {
            if (!category) {
              window.MurphyDoorConfigurator.errorHandler.handleError(
                'Invalid category parameter',
                new Error('Category parameter is required'),
                {
                  methodName: 'getSelectionData',
                  severity: 'warning',
                  module: 'main',
                  category,
                }
              );
              return null;
            }

            return this.stateManager.getSelectionData(category);
          } catch (error) {
            window.MurphyDoorConfigurator.errorHandler.handleError(
              'Failed to get selection data',
              error,
              {
                methodName: 'getSelectionData',
                severity: 'error',
                module: 'main',
                category,
              }
            );
            return null;
          }
        },

        /**
         * Retrieves all applied selections for image layer updates
         *
         * This method follows the Central Conductor Pattern by:
         * - Providing controlled access to APPLIED state selections only
         * - Ensuring gallery modules filter on confirmed selections
         * - Delegating to stateManager for implementation
         * - Supporting CCP compliance for image layer management
         *
         * @returns {Object} Object containing applied selections with category keys
         *                   or empty object if no applied selections exist
         */
        getAppliedSelections() {
          try {
            if (!this.stateManager) {
              const errorMessage = 'State manager is not initialized';
              this.handleError(errorMessage, new Error(errorMessage), {
                method: 'getAppliedSelections',
              });
              return {};
            }

            const appliedSelections = this.stateManager.getAllSelections();
            isDev() &&
              console.log(
                `✅ GALLERY: Applied selections retrieved for image updates (${Object.keys(appliedSelections).length} selections)`
              );
            return appliedSelections;
          } catch (error) {
            this.handleError('Failed to get applied selections', error, {
              method: 'getAppliedSelections',
              severity: 'error',
              module: 'conductor',
            });
            return {};
          }
        },

        /**
         * Front casing product title -> is it exempt from the "-1 RULE" casing
         * widening? ONE owner for that answer: the trim profile's
         * `wideRuleExempt` flag in md-config-blueprint-geometry.js (Fluted,
         * vendor-milled, always 3-3/4in), keyed by the dimension calculator's
         * trimProfileKey -- the exact resolution the blueprint uses. The space
         * calculator asks HERE rather than calling another BL module (CCP: no
         * peer-to-peer BL calls; the conductor coordinates).
         *
         * @param {string|null} productTitle e.g. 'Fluted Front Casing (Paint Grade) (Single Door)'
         * @returns {boolean|null} true/false for a recognised casing shape,
         *   null when the title is absent or not a known casing profile.
         */
        resolveCasingWideRuleExempt(productTitle) {
          if (typeof productTitle !== 'string' || !productTitle) return null;
          const ns = window.MurphyDoorConfigurator;
          const keyFn = ns.dimensionCalculator?.trimProfileKey;
          const profiles = ns.blueprintGeometry?.trimProfiles;
          if (typeof keyFn !== 'function' || !profiles) return null;
          const key = keyFn('Front Casing', productTitle);
          const profile = key ? profiles[key] : null;
          if (!profile) return null;
          return profile.wideRuleExempt === true;
        },

        /**
         * ⚡ PPD-FIRST OPTIMIZATION: Get applied selections as Map for faster iteration
         *
         * Returns appliedSelections as Map instead of Object for ~10-20% faster iteration.
         * Use this in performance-critical paths like forbidden combination validation.
         *
         * @returns {Map<string, Object>} Map of categoryId → selection data
         */
        getAppliedSelectionsMap() {
          try {
            if (!this.stateManager) {
              const errorMessage = 'State manager is not initialized';
              this.handleError(errorMessage, new Error(errorMessage), {
                method: 'getAppliedSelectionsMap',
              });
              return new Map();
            }

            const selectionsMap = this.stateManager.getAppliedSelectionsMap();
            isDev() &&
              console.log(
                `⚡ CONDUCTOR: Applied selections Map retrieved (${selectionsMap.size} selections)`
              );
            return selectionsMap;
          } catch (error) {
            this.handleError('Failed to get applied selections Map', error, {
              method: 'getAppliedSelectionsMap',
              severity: 'error',
              module: 'conductor',
            });
            return new Map();
          }
        },

        /**
         * Get all browsing selections
         *
         * This method follows the Central Conductor Pattern by:
         * - Providing controlled access to BROWSING state selections only
         * - Ensuring modules can access temporary selections before APPLY
         * - Delegating to stateManager for implementation
         * - Supporting CCP compliance for image layer management
         *
         * @returns {Object} Map of categoryId → selection data
         */
        getBrowsingSelections() {
          try {
            if (!this.stateManager) {
              const errorMessage = 'State manager is not initialized';
              this.handleError(errorMessage, new Error(errorMessage), {
                method: 'getBrowsingSelections',
              });
              return {};
            }

            const state = this.stateManager.getStateMaps();
            const browsing = {};
            state.browsingSelections?.forEach((data, categoryId) => {
              browsing[categoryId] = data;
            });

            isDev() &&
              console.log(
                `✅ CONDUCTOR: Browsing selections retrieved (${Object.keys(browsing).length} selections)`
              );
            return browsing;
          } catch (error) {
            this.handleError('Failed to get browsing selections', error, {
              method: 'getBrowsingSelections',
              severity: 'error',
              module: 'conductor',
            });
            return {};
          }
        },

        /**
         * Returns the simplified 3Q mode browsing progress counters, or null when
         * not in simplified mode. Used by updateProgressBar() to show equal 33%-per-
         * category progress during the 3Q wizard without touching the normal applied-
         * selection counter.
         *
         * @returns {{browsedCount: number, totalCount: number}|null}
         */
        getSimplifiedBrowsingCount() {
          if (
            !this._simplifiedCategoryIds ||
            this._simplifiedCategoryIds.size === 0
          ) {
            return null;
          }
          return {
            browsedCount: this._simplifiedBrowsingSelections.size,
            totalCount: this._simplifiedCategoryIds.size,
          };
        },

        /**
         * Gets the image layer state for a specific category
         *
         * This method follows the Central Conductor Pattern by:
         * - Validating input parameters for correctness
         * - Providing proper error handling with context
         * - Delegating to the stateManager for implementation
         * - Ensuring graceful failure with meaningful defaults
         *
         * The image layer state contains information about:
         * - Visibility status of the layer
         * - Z-index for proper stacking order
         * - Current view (front/back)
         * - Timestamp of last update
         *
         * @param {string|number} categoryId - The ID of the category to get image layer state for
         * @returns {Object|null} The image layer state object or null if not found or on error
         */
        getImageLayerState(categoryId) {
          try {
            if (categoryId === undefined) {
              window.MurphyDoorConfigurator.errorHandler.handleError(
                'Missing categoryId parameter',
                new Error('CategoryId parameter is required'),
                {
                  methodName: 'getImageLayerState',
                  severity: 'warning',
                  module: 'main',
                  parameter: 'categoryId',
                }
              );
              return null;
            }

            return this.stateManager.getImageLayerState(categoryId);
          } catch (error) {
            window.MurphyDoorConfigurator.errorHandler.handleError(
              'Failed to get image layer state',
              error,
              {
                methodName: 'getImageLayerState',
                severity: 'error',
                module: 'main',
                categoryId,
              }
            );
            return null;
          }
        },

        /**
         * Gets the current view (front/back) for door visualization
         *
         * This method is part of the Central Conductor Pattern where view state access
         * is handled through the main conductor rather than directly accessing stateManager.
         * It provides a standardized interface for modules to determine the current visualization view.
         *
         * Returns a string ('front' or 'back') indicating which side of the door is being shown.
         *
         * @returns {string} The current view ('front' or 'back')
         * @throws {Error} If the stateManager is unavailable or view state is inaccessible
         */
        getCurrentView() {
          try {
            if (!this.stateManager) {
              throw new Error('stateManager unavailable');
            }
            return this.stateManager.getCurrentView();
          } catch (error) {
            this.handleError('Failed to get current view', error, {
              method: 'getCurrentView',
              timestamp: Date.now(),
              recoverable: true,
            });
            // Return default view as fallback
            return 'front';
          }
        },

        /**
         * Determines if the configurator is currently showing the front view of the door
         *
         * This method is part of the Central Conductor Pattern where view state access
         * is handled through the main conductor rather than directly accessing stateManager.
         * It provides a standardized boolean interface for modules to check the current view.
         *
         * @returns {boolean} True if showing front view, false if showing back view
         * @throws {Error} If the stateManager is unavailable or view state is inaccessible
         */
        isShowingFrontView() {
          try {
            if (!this.stateManager) {
              throw new Error('stateManager unavailable');
            }
            return this.stateManager.isShowingFrontView();
          } catch (error) {
            this.handleError(
              'Failed to determine front/back view status',
              error,
              {
                method: 'isShowingFrontView',
                timestamp: Date.now(),
                recoverable: true,
              }
            );
            // Default to front view as fallback
            return true;
          }
        },

        /**
         * Gets the transition state for a specific category
         *
         * This method is part of the Central Conductor Pattern where transition state
         * is accessed through the main conductor rather than directly from stateManager.
         * Transition states track the progress of animations and UI transitions between
         * different states (NONE, IN_PROGRESS, COMPLETED).
         *
         * @param {string|number} categoryId - The ID of the category to get transition state for
         * @returns {string} The transition state (one of TRANSITION_STATES constants)
         * @throws {Error} If the stateManager is unavailable or transition state is inaccessible
         */
        getTransitionState(categoryId) {
          try {
            if (!this.stateManager) {
              throw new Error('stateManager unavailable');
            }

            if (categoryId === undefined || categoryId === null) {
              throw new Error('Category ID is required');
            }

            return this.stateManager.getTransitionState(categoryId);
          } catch (error) {
            this.handleError('Failed to get transition state', error, {
              method: 'getTransitionState',
              categoryId,
              timestamp: Date.now(),
              recoverable: true,
            });
            // Return default transition state as fallback
            return (
              window.MurphyDoorConfigurator.TRANSITION_STATES?.NONE || 'NONE'
            );
          }
        },

        /**
         * Gets the navigation state for a specific element
         *
         * This method is part of the Central Conductor Pattern where navigation state
         * is accessed through the main conductor rather than directly from stateManager.
         * Navigation states track the user's progress through the configuration flow
         * (INITIAL, IN_PROGRESS, COMPLETED).
         *
         * @param {string} elementId - The ID of the element (tab or category) to get navigation state for
         * @returns {string} The navigation state (one of NAVIGATION_STATES constants)
         * @throws {Error} If the stateManager is unavailable or navigation state is inaccessible
         */
        getNavigationState(elementId) {
          try {
            if (!this.stateManager) {
              throw new Error('stateManager unavailable');
            }

            if (!elementId) {
              throw new Error('Element ID is required');
            }

            return this.stateManager.getNavigationState(elementId);
          } catch (error) {
            this.handleError('Failed to get navigation state', error, {
              method: 'getNavigationState',
              elementId,
              timestamp: Date.now(),
              recoverable: true,
            });
            // Return default navigation state as fallback
            return (
              window.MurphyDoorConfigurator.NAVIGATION_STATES?.INITIAL ||
              'INITIAL'
            );
          }
        },

        getMaterialRelationships() {
          /**
           * Retrieves material relationships (wood-to-finish mappings) from the state manager.
           *
           * This method follows the Central Conductor Pattern by providing controlled access
           * to the material relationship data stored in the state manager. It handles potential
           * errors gracefully and ensures proper error context for debugging.
           *
           * @returns {Map|null} A Map containing wood-to-finish compatibility mappings or null on error
           */
          try {
            if (!this.stateManager) {
              const errorMessage = 'State manager is not initialized';
              this.handleError(errorMessage, new Error(errorMessage), {
                method: 'getMaterialRelationships',
                severity: 'critical',
                module: 'main',
              });
              return new Map(); // Return empty map as fallback
            }

            const relationships =
              this.stateManager.getStateMaps().materialRelationships;

            if (!relationships) {
              // Log warning but don't fail - might be early in initialization
              isDev() &&
                console.warn('Material relationships not yet initialized');
              return new Map(); // Return empty map as fallback
            }

            return relationships;
          } catch (error) {
            this.handleError('Failed to get material relationships', error, {
              method: 'getMaterialRelationships',
              module: 'main',
              recoverable: true,
            });
            return new Map(); // Return empty map as fallback
          }
        },

        /* Initialization Methods */
        /**
         * Initializes the Murphy Door Configurator system following the Central Conductor Pattern.
         *
         * This method serves as the primary entry point for configurator initialization,
         * orchestrating the complete initialization sequence:
         * 1. Validates the DOM environment
         * 2. Ensures proper initialization timing relative to DOM readiness
         * 3. Publishes initialization events for system monitoring
         * 4. Delegates to initializeConfiguratorCore for the main initialization process
         *
         * Following the Central Conductor Pattern principles:
         * - Acts as the central orchestrator for initialization
         * - Maintains proper event publishing through the event bus
         * - Implements appropriate error handling with context
         * - Ensures DOM-readiness before initialization proceeds
         *
         * @fires system:initialization_started - When initialization begins
         * @throws {Error} When configurator wrapper element is not found in the DOM
         * @returns {void}
         */
        initializeConfigurator() {
          const initStartTime = performance.now();

          try {
            // 🛡️ INITIALIZATION GUARD: Prevent multiple initialization
            if (this._isInitialized) {
              isDev() &&
                console.warn(
                  '🛡️ CONDUCTOR: Initialization already complete - skipping duplicate execution'
                );
              return;
            }
            this._isInitialized = true;

            // PATTERN #3: 5-Stage Event Lifecycle - STAGE 1: START
            publishEvent(CONSTANTS.EVENTS.CONDUCTOR_INITIALIZATION_START, {
              timestamp: performance.now(),
              source: 'md-config-core-conductor.js',
              method: 'initializeConfigurator',
            });

            // Also publish system-level event for backward compatibility
            publishEvent(CONSTANTS.EVENTS.SYSTEM_INITIALIZATION_STARTED, {
              timestamp: performance.now(),
              source: 'md-config-core-conductor.js',
              method: 'initializeConfigurator',
            });

            // PATTERN #3: 5-Stage Event Lifecycle - STAGE 2: VALIDATION
            if (!this.DOM || typeof this.DOM.getElement !== 'function') {
              publishEvent(
                CONSTANTS.EVENTS.CONDUCTOR_INITIALIZATION_VALIDATION,
                {
                  valid: false,
                  errors: ['DOM utility not properly initialized'],
                }
              );
              throw new Error('DOM utility not properly initialized');
            }

            if (!this.DOM.getElement('.murphy-door-configurator-wrapper')) {
              publishEvent(
                CONSTANTS.EVENTS.CONDUCTOR_INITIALIZATION_VALIDATION,
                {
                  valid: false,
                  errors: ['Configurator wrapper not found'],
                }
              );
              throw new Error('Configurator wrapper not found');
            }

            publishEvent(CONSTANTS.EVENTS.CONDUCTOR_INITIALIZATION_VALIDATION, {
              valid: true,
              timestamp: Date.now(),
            });

            // PATTERN #3: 5-Stage Event Lifecycle - STAGE 3: PROGRESS
            publishEvent(CONSTANTS.EVENTS.CONDUCTOR_INITIALIZATION_PROGRESS, {
              stage: 'delegating_to_core',
              timestamp: Date.now(),
            });

            // Direct initialization - DOM readiness handled by initializer.js
            this.initializeConfiguratorCore();
          } catch (error) {
            // PATTERN #3: 5-Stage Event Lifecycle - STAGE 5: FAILED
            const duration = performance.now() - initStartTime;
            publishEvent(CONSTANTS.EVENTS.CONDUCTOR_INITIALIZATION_FAILED, {
              error: error.message,
              duration,
              timestamp: Date.now(),
            });

            // PATTERN #12: Performance Tracking
            trackPerformance('INITIALIZATION', duration);

            this.handleError('Configurator initialization failed', error, {
              method: 'initializeConfigurator',
              module: 'main',
              severity: 'critical',
              recoverable: false,
            });

            // Attempt to show error UI if possible
            try {
              if (window.MurphyDoorConfigurator.uiUpdater) {
                window.MurphyDoorConfigurator.uiUpdater.showErrorMessage(
                  'Unable to initialize configurator. Please refresh the page.'
                );
              }
            } catch (uiError) {
              isDev() && console.error('Failed to display error UI:', uiError);
            }
          }
        },

        /**
         * Core initialization method following the Central Conductor Pattern principles.
         *
         * Responsible for:
         * 1. Verifying the configurator environment is ready
         * 2. Initializing the configurator's core state and structure
         * 3. Setting up initial display modes and states
         * 4. Coordinating initialization across dependent modules
         * 5. Managing state recovery if needed
         * 6. Ensuring proper event publication through the central event bus
         * 7. Providing comprehensive error handling and reporting
         *
         * This method follows Central Conductor design by:
         * - Acting as the central orchestrator for all initialization processes
         * - Delegating specific responsibilities to appropriate modules
         * - Maintaining clear initialization sequence and state transitions
         * - Publishing standardized events for system monitoring
         * - Providing centralized error handling and recovery strategies
         *
         * @fires system:core_initialization_started - When initialization begins
         * @fires system:configuration_loaded - When configuration is successfully loaded
         * @fires system:state_initialized - When state is successfully initialized
         * @fires system:initialization_complete - When initialization is successful
         * @fires system:error - When initialization encounters an error
         *
         * @returns {Promise<void>} - Resolves when initialization is complete
         */
        async initializeConfiguratorCore() {
          const startTime = performance.now();
          const METHOD_SIGNATURE = 'initializeConfiguratorCore';

          // 🚫 CCP INITIALIZATION GUARD - Prevent duplicate initialization flows
          if (
            !window.MurphyDoorConfigurator.stateManager?.canInitialize(
              'conductor'
            )
          ) {
            isDev() &&
              console.warn(
                '🚫 CONDUCTOR GUARD: Blocking duplicate initialization attempt'
              );
            return;
          }

          // 🔒 ACQUIRE INITIALIZATION LOCK - Mark initialization as started
          if (
            !window.MurphyDoorConfigurator.stateManager?.markInitializationStarted(
              'conductor'
            )
          ) {
            isDev() &&
              console.error(
                '🚫 CONDUCTOR GUARD: Failed to acquire initialization lock'
              );
            return;
          }

          isDev() &&
            console.log(
              '🚀 CONDUCTOR GUARD: Initialization lock acquired, proceeding with core initialization'
            );

          // Check for configurator wrapper existence
          if (!document.querySelector('.murphy-door-configurator-wrapper')) {
            this.handleError(
              'Initialization aborted',
              new Error('Configurator wrapper not found'),
              {
                method: METHOD_SIGNATURE,
                module: 'main',
                severity: 'high',
                recoverable: false,
                location: 'environment validation',
              }
            );
            return;
          }

          // Publish initialization started event
          publishEvent(CONSTANTS.EVENTS.SYSTEM_CORE_INITIALIZATION_STARTED, {
            timestamp: performance.now(),
            source: 'md-config-core-conductor.js',
            method: METHOD_SIGNATURE,
          });

          // URL recovery animator initialization moved to after perfect product dictionary is ready

          // Publish loading state event instead of direct UI manipulation
          publishEvent(CONSTANTS.EVENTS.UI_LOADING_STATE_CHANGED, {
            state: 'loading',
            source: 'initialization',
            timestamp: Date.now(),
          });

          try {
            // CCP COMPLIANT: Initialize tab readiness state for reactive synchronization
            this._tabReadinessStatus = {
              tabsExist: false,
              hasActiveTab: false,
              tabCount: 0,
              timestamp: performance.now(),
            };

            // All simplified category IDs (from BUSINESS_SIMPLIFIED_MODE_CATEGORIES_READY).
            // null = simplified mode not yet active.
            this._simplifiedCategoryIds = null;
            // Tracks BROWSING selections per simplified category for Build My Door validation.
            this._simplifiedBrowsingSelections = new Map();
            // Re-entrancy lock for the Build My Door bulk-apply wave (double-click guard).
            this._buildMyDoorInFlight = false;

            // 🎯 NOTE: PPD state flags (_ppdStateReady, _ppdStateTimestamp) initialized in main object
            // 🎯 NOTE: system:state_from_ppd_ready subscription moved to initializeCore()
            // Both moved to fix timing race condition. See initializeCore() function for subscription.

            // CCP COMPLIANT: Subscribe to UI state changes for reactive synchronization
            // PATTERN #15: Track all subscriptions in cleanupHandlers for proper cleanup
            if (window.MurphyDoorConfigurator.CoreEventBus) {
              const unsubTabReadiness =
                window.MurphyDoorConfigurator.CoreEventBus.subscribe(
                  CONSTANTS.EVENTS.UI_TAB_READINESS_CHANGED,
                  (data) => {
                    this._tabReadinessStatus = {
                      tabsExist: data.tabsExist,
                      hasActiveTab: data.hasActiveTab,
                      tabCount: data.tabCount,
                      timestamp: performance.now(),
                    };
                    isDev() &&
                      console.log(
                        '🔄 Conductor: Tab readiness state updated via reactive synchronization',
                        this._tabReadinessStatus
                      );
                  }
                );
              if (unsubTabReadiness) cleanupHandlers.push(unsubTabReadiness);

              // CCP COMPLIANT: Subscribe to cart manager data requests
              const unsubProductStructure =
                window.MurphyDoorConfigurator.CoreEventBus.subscribe(
                  CONSTANTS.EVENTS.DATA_REQUEST_PRODUCT_STRUCTURE,
                  (data) => {
                    isDev() &&
                      console.log(
                        '🎯 Conductor: Handling data:request_product_structure from',
                        data.requester
                      );

                    // FIX: Use perfectProductDictionary for cart manager instead of legacy productStructure
                    const productData = this.getCartCompatibleProductData();

                    // Respond with product data
                    window.MurphyDoorConfigurator.CoreEventBus.publish(
                      CONSTANTS.EVENTS.DATA_PRODUCT_DATA_RESPONSE,
                      {
                        productData: productData,
                        requester: data.requester,
                        timestamp: Date.now(),
                      }
                    );
                  }
                );
              if (unsubProductStructure)
                cleanupHandlers.push(unsubProductStructure);

              const unsubCartMutations =
                window.MurphyDoorConfigurator.CoreEventBus.subscribe(
                  CONSTANTS.EVENTS.DATA_REQUEST_CART_MUTATIONS,
                  (data) => {
                    isDev() &&
                      console.log(
                        '🎯 Conductor: Handling data:request_cart_mutations from',
                        data.requester
                      );
                    // Get cart mutations from product structure or config data
                    const cartMutations =
                      this.getProductStructure()?.cartMutations || {};

                    // Respond with cart mutations
                    window.MurphyDoorConfigurator.CoreEventBus.publish(
                      CONSTANTS.EVENTS.DATA_CART_MUTATIONS_RESPONSE,
                      {
                        cartMutations: cartMutations,
                        requester: data.requester,
                        timestamp: Date.now(),
                      }
                    );
                  }
                );
              if (unsubCartMutations) cleanupHandlers.push(unsubCartMutations);

              // CCP COMPLIANT: Handle product lookup by variant ID
              const unsubProductByVariant =
                window.MurphyDoorConfigurator.CoreEventBus.subscribe(
                  CONSTANTS.EVENTS.DATA_PRODUCT_BY_VARIANT_REQUESTED,
                  (data) => {
                    isDev() &&
                      console.log(
                        '🎯 CONDUCTOR: Product by variant ID requested',
                        data
                      );

                    const product = this.getProductByVariantId(data.variantId);

                    window.MurphyDoorConfigurator.CoreEventBus.publish(
                      CONSTANTS.EVENTS.DATA_PRODUCT_BY_VARIANT_RESPONSE,
                      {
                        product: product,
                        requestId: data.requestId,
                        variantId: data.variantId,
                        category: data.category,
                        requester: data.requester,
                        found: !!product,
                        timestamp: Date.now(),
                      }
                    );
                  }
                );
              if (unsubProductByVariant)
                cleanupHandlers.push(unsubProductByVariant);

              // CCP COMPLIANT: Handle product lookup by product ID
              const unsubProductById =
                window.MurphyDoorConfigurator.CoreEventBus.subscribe(
                  CONSTANTS.EVENTS.DATA_PRODUCT_BY_ID_REQUESTED,
                  (data) => {
                    isDev() &&
                      console.log(
                        '🎯 CONDUCTOR: Product by ID requested',
                        data
                      );

                    const product = this.getProductById(data.productId);

                    window.MurphyDoorConfigurator.CoreEventBus.publish(
                      CONSTANTS.EVENTS.DATA_PRODUCT_BY_ID_RESPONSE,
                      {
                        product: product,
                        requestId: data.requestId,
                        productId: data.productId,
                        requester: data.requester,
                        found: !!product,
                        timestamp: Date.now(),
                      }
                    );
                  }
                );
              if (unsubProductById) cleanupHandlers.push(unsubProductById);

              // CCP COMPLIANT: Handle variant lookup by variant ID
              const unsubVariantById =
                window.MurphyDoorConfigurator.CoreEventBus.subscribe(
                  CONSTANTS.EVENTS.DATA_VARIANT_BY_ID_REQUESTED,
                  (data) => {
                    isDev() &&
                      console.log(
                        '🎯 CONDUCTOR: Variant by ID requested',
                        data
                      );

                    const variant = this.getVariantById(data.variantId);

                    window.MurphyDoorConfigurator.CoreEventBus.publish(
                      CONSTANTS.EVENTS.DATA_VARIANT_BY_ID_RESPONSE,
                      {
                        variant: variant,
                        requestId: data.requestId,
                        variantId: data.variantId,
                        requester: data.requester,
                        found: !!variant,
                        timestamp: Date.now(),
                      }
                    );
                  }
                );
              if (unsubVariantById) cleanupHandlers.push(unsubVariantById);

              // 🚀 PPD-FIRST: Handle cart item templates request
              const unsubCartItemTemplates =
                window.MurphyDoorConfigurator.CoreEventBus.subscribe(
                  CONSTANTS.EVENTS.DATA_CART_ITEM_TEMPLATES_REQUESTED,
                  (data) => {
                    isDev() &&
                      console.log(
                        '🎯 CONDUCTOR: Cart item templates requested',
                        data
                      );

                    const templates = this.getCartItemTemplates();

                    window.MurphyDoorConfigurator.CoreEventBus.publish(
                      CONSTANTS.EVENTS.DATA_CART_ITEM_TEMPLATES_RESPONSE,
                      {
                        templates: templates,
                        requester: data.requester,
                        timestamp: Date.now(),
                      }
                    );
                  }
                );
              if (unsubCartItemTemplates)
                cleanupHandlers.push(unsubCartItemTemplates);

              // 🚀 PPD-FIRST: Handle base product validation request
              const unsubBaseProductValidation =
                window.MurphyDoorConfigurator.CoreEventBus.subscribe(
                  CONSTANTS.EVENTS.DATA_BASE_PRODUCT_VALIDATION_REQUESTED,
                  (data) => {
                    isDev() &&
                      console.log(
                        '🎯 CONDUCTOR: Base product validation requested',
                        data
                      );

                    const validation = this.getBaseProductValidation();

                    window.MurphyDoorConfigurator.CoreEventBus.publish(
                      CONSTANTS.EVENTS.DATA_BASE_PRODUCT_VALIDATION_RESPONSE,
                      {
                        validation: validation,
                        requester: data.requester,
                        timestamp: Date.now(),
                      }
                    );
                  }
                );
              if (unsubBaseProductValidation)
                cleanupHandlers.push(unsubBaseProductValidation);

              // CCP COMPLIANT: Handle state:request_cart_configuration for cart manager
              const unsubCartConfig =
                window.MurphyDoorConfigurator.CoreEventBus.subscribe(
                  CONSTANTS.EVENTS.STATE_REQUEST_CART_CONFIGURATION,
                  (data) => {
                    isDev() &&
                      console.log(
                        '🎯 Conductor: Handling state:request_cart_configuration from',
                        data.requester
                      );

                    // Get current selections from state manager
                    const selections = this.stateManager.getCartConfiguration();
                    isDev() &&
                      console.log(
                        '🎯 Conductor: Retrieved cart configuration:',
                        selections
                      );

                    // Respond with selections via event bus
                    window.MurphyDoorConfigurator.CoreEventBus.publish(
                      CONSTANTS.EVENTS.STATE_SELECTIONS_RESPONSE,
                      {
                        selections: selections,
                        requester: data.requester,
                        timestamp: Date.now(),
                      }
                    );
                  }
                );
              if (unsubCartConfig) cleanupHandlers.push(unsubCartConfig);

              // CCP COMPLIANT: Handle applied selections data request from forbidden validator
              // Responds with current applied selections via PPD-first state manager delegation
              // Golden Standard Pattern #13: Public API Coordinator request/response
              const unsubAppliedSelections =
                window.MurphyDoorConfigurator.CoreEventBus.subscribe(
                  CONSTANTS.EVENTS.DATA_REQUEST_APPLIED_SELECTIONS,
                  (data) => {
                    const startTime = performance.now();
                    try {
                      isDev() &&
                        console.log(
                          '🎯 Conductor: Handling data:request_applied_selections from',
                          data.requester || 'unknown'
                        );

                      // PPD-First: Get applied selections via state manager (O(1) Map access)
                      const appliedSelections = this.getAppliedSelectionsMap();

                      const duration = performance.now() - startTime;
                      if (duration > 50) {
                        isDev() &&
                          console.warn(
                            `⚠️ PERFORMANCE: Applied selections retrieval took ${duration.toFixed(2)}ms (threshold: 50ms)`
                          );
                      }

                      // Publish response (CoreEventBus freezes centrally - Pattern #18 DRY)
                      window.MurphyDoorConfigurator.CoreEventBus.publish(
                        CONSTANTS.EVENTS.DATA_APPLIED_SELECTIONS_RESPONSE,
                        {
                          appliedSelections: appliedSelections,
                          requestId: data.requestId,
                          requester: data.requester,
                          timestamp: Date.now(),
                        }
                      );

                      isDev() &&
                        console.log(
                          '✅ Conductor: Applied selections response published',
                          {
                            selectionsCount:
                              appliedSelections instanceof Map
                                ? appliedSelections.size
                                : Object.keys(appliedSelections || {}).length,
                          }
                        );
                    } catch (error) {
                      isDev() &&
                        console.error(
                          '❌ Conductor: Failed to handle applied selections request',
                          {
                            error: error.message,
                            requester: data.requester,
                          }
                        );
                      // Publish error response so requester doesn't hang
                      window.MurphyDoorConfigurator.CoreEventBus.publish(
                        CONSTANTS.EVENTS.DATA_APPLIED_SELECTIONS_RESPONSE,
                        {
                          appliedSelections: new Map(),
                          requestId: data.requestId,
                          requester: data.requester,
                          error: error.message,
                          timestamp: Date.now(),
                        }
                      );
                    }
                  }
                );
              if (unsubAppliedSelections)
                cleanupHandlers.push(unsubAppliedSelections);

              // ============================================================================
              // PRIVATE HELPER: DRY-compliant forbidden rules request handler
              // Consolidates shared logic between DATA_FORBIDDEN_RULES and
              // DATA_REQUEST_FORBIDDEN_RULES event handlers to eliminate duplication
              // ============================================================================
              const handleForbiddenRulesRequest = (
                data,
                includeRulesKey = false
              ) => {
                const startTime = performance.now();
                try {
                  isDev() &&
                    console.log(
                      '🎯 Conductor: Handling forbidden rules request from',
                      data.requester || 'unknown'
                    );

                  // PPD-First: Get unified forbidden rules via conductor method (O(1) PPD access)
                  const forbiddenRules = this.getUnifiedForbiddenRules();

                  const duration = performance.now() - startTime;
                  if (duration > 50) {
                    isDev() &&
                      console.warn(
                        `⚠️ PERFORMANCE: Forbidden rules retrieval took ${duration.toFixed(2)}ms (threshold: 50ms)`
                      );
                  }

                  // Build response payload - includeRulesKey flag adds compatibility for recovery flow
                  // NOTE: Both `rules` and `forbiddenRules` are provided when includeRulesKey=true
                  // for compatibility with different consumers in forbidden-validator.js:
                  // - `recoverState` flow expects `rules`
                  // - `_executeAppliedValidationAsync` expects `forbiddenRules`
                  const payload = {
                    forbiddenRules: forbiddenRules,
                    requestId: data.requestId,
                    requester: data.requester,
                    timestamp: Date.now(),
                  };
                  if (includeRulesKey) {
                    payload.rules = forbiddenRules;
                  }

                  // Publish response with frozen payload
                  window.MurphyDoorConfigurator.CoreEventBus.publish(
                    CONSTANTS.EVENTS.DATA_FORBIDDEN_RULES_RESPONSE,
                    payload
                  );

                  isDev() &&
                    console.log(
                      '✅ Conductor: Forbidden rules response published',
                      {
                        unifiedRulesCount:
                          forbiddenRules?.unifiedRules?.size || 0,
                      }
                    );
                } catch (error) {
                  isDev() &&
                    console.error(
                      '❌ Conductor: Failed to handle forbidden rules request',
                      {
                        error: error.message,
                        requester: data.requester,
                      }
                    );

                  // Build error response payload with same compatibility logic
                  const emptyRules = {
                    unifiedRules: new Map(),
                    unifiedConditionGroups: new Map(),
                  };
                  const errorPayload = {
                    forbiddenRules: emptyRules,
                    requestId: data.requestId,
                    requester: data.requester,
                    error: error.message,
                    timestamp: Date.now(),
                  };
                  if (includeRulesKey) {
                    errorPayload.rules = emptyRules;
                  }

                  // Publish error response so requester doesn't hang
                  window.MurphyDoorConfigurator.CoreEventBus.publish(
                    CONSTANTS.EVENTS.DATA_FORBIDDEN_RULES_RESPONSE,
                    errorPayload
                  );
                }
              };

              // CCP COMPLIANT: Handle forbidden rules data request from forbidden validator
              // Responds with unified forbidden rules from PPD
              // Golden Standard Pattern #13: Public API Coordinator request/response
              const unsubForbiddenRules =
                window.MurphyDoorConfigurator.CoreEventBus.subscribe(
                  CONSTANTS.EVENTS.DATA_FORBIDDEN_RULES,
                  (data) => handleForbiddenRulesRequest(data, false)
                );
              if (unsubForbiddenRules)
                cleanupHandlers.push(unsubForbiddenRules);

              // CCP COMPLIANT: Handle forbidden rules request via DATA_REQUEST_FORBIDDEN_RULES event
              // (Used by forbidden validator recovery flow)
              // Includes both `rules` and `forbiddenRules` keys for compatibility
              const unsubForbiddenRulesRequest =
                window.MurphyDoorConfigurator.CoreEventBus.subscribe(
                  CONSTANTS.EVENTS.DATA_REQUEST_FORBIDDEN_RULES,
                  (data) => handleForbiddenRulesRequest(data, true)
                );
              if (unsubForbiddenRulesRequest)
                cleanupHandlers.push(unsubForbiddenRulesRequest);

              // CCP COMPLIANT: Subscribe to progress celebration trigger from ui-updater
              // This connects the orphaned event chain: ui-updater detects 100% → publishes
              // UI_PROGRESS_CELEBRATION_TRIGGERED → conductor receives → publishes
              // UI_CONFETTI_CELEBRATION_REQUESTED → confetti-manager receives and fires confetti
              const unsubProgressCelebration =
                window.MurphyDoorConfigurator.CoreEventBus.subscribe(
                  CONSTANTS.EVENTS.UI_PROGRESS_CELEBRATION_TRIGGERED,
                  (eventData) => {
                    if (eventData && eventData.toProgress === 100) {
                      isDev() &&
                        console.log(
                          '🎉 CONDUCTOR: Received UI_PROGRESS_CELEBRATION_TRIGGERED with 100% - delegating to handleProgressUpdate'
                        );
                      this.handleProgressUpdate(eventData.toProgress);
                    }
                  }
                );
              if (unsubProgressCelebration)
                cleanupHandlers.push(unsubProgressCelebration);

              // C1: Subscribe to slider commit-on-release. When click=apply is
              // on, the slider publishes UI_SLIDER_COMMIT_REQUESTED 400ms after
              // motion settles; the conductor applies the committed value via
              // the same one-step APPLY_SELECTION primitive.
              const unsubSliderCommit =
                window.MurphyDoorConfigurator.CoreEventBus.subscribe(
                  CONSTANTS.EVENTS.UI_SLIDER_COMMIT_REQUESTED,
                  (eventData) => {
                    if (
                      !eventData ||
                      !eventData.categoryId ||
                      !eventData.variantId
                    ) {
                      return;
                    }
                    if (!this._isClickApplyEnabled()) return;
                    // Stale-commit guard: the 400ms debounce may fire after the
                    // user backed out of / re-navigated the category. Only apply
                    // if the committed variant is STILL the category's current
                    // browsing selection.
                    try {
                      const sm = this.getStateMaps();
                      const browsing =
                        sm &&
                        sm.browsingSelections &&
                        sm.browsingSelections.get(eventData.categoryId);
                      if (
                        !browsing ||
                        String(browsing.variantId) !==
                          String(eventData.variantId)
                      ) {
                        isDev() &&
                          console.log(
                            '🎚️ CONDUCTOR: Slider commit ignored — category no longer browsing the committed variant',
                            eventData
                          );
                        return;
                      }
                    } catch (guardErr) {
                      // If state can't be read, fall through to apply (the
                      // APPLY_SELECTION path re-validates forbidden rules anyway).
                    }
                    isDev() &&
                      console.log(
                        '🎚️ CONDUCTOR: Slider commit requested',
                        eventData
                      );
                    // A bootstrap seed (the slider accepting its own default
                    // on the customer's behalf) is NOT a manual pick: source
                    // 'slider_seed' maps to provenance 'curated_default'.
                    // auto_applied stays false for BOTH on purpose: a seeded
                    // Width/Height/Door Depth must keep exactly the apply
                    // semantics it had (not in autoAppliedCategories, so no
                    // forbidden auto-unapply / single-compatible replace).
                    // Only the provenance differs.
                    this.handleStateChange('APPLY_SELECTION', {
                      categoryId: eventData.categoryId,
                      productId: eventData.productId,
                      variantId: eventData.variantId,
                      auto_applied: false,
                      isOneStep: true,
                      source:
                        eventData.seed === true
                          ? 'slider_seed'
                          : 'slider_commit',
                    });
                  }
                );
              if (unsubSliderCommit) cleanupHandlers.push(unsubSliderCommit);

              // D1: dual-mode segmented control requests a mode switch.
              const unsubModeChange =
                window.MurphyDoorConfigurator.CoreEventBus.subscribe(
                  CONSTANTS.EVENTS.BUSINESS_CONFIGURATOR_MODE_CHANGE_REQUESTED,
                  (eventData) => {
                    if (eventData && eventData.mode) {
                      this._setConfiguratorMode(eventData.mode);
                    }
                  }
                );
              if (unsubModeChange) cleanupHandlers.push(unsubModeChange);

              // Gate cards (e.g. "Cabinets?"): synthetic question cards request an
              // answer change; the conductor owns the answer as session state (never
              // appliedSelections — a gate answer is UI/orchestration state, not a
              // real cart-relevant selection).
              const unsubGateAnswer =
                window.MurphyDoorConfigurator.CoreEventBus.subscribe(
                  CONSTANTS.EVENTS.BUSINESS_GATE_ANSWER_CHANGE_REQUESTED,
                  (eventData) => {
                    if (eventData && eventData.gateId && eventData.answer) {
                      this._setGateAnswer(eventData.gateId, eventData.answer);
                    }
                  }
                );
              if (unsubGateAnswer) cleanupHandlers.push(unsubGateAnswer);

              // CCP COMPLIANT: Subscribe to rush delivery auto-deselect request from ui-updater
              // When base shipping time changes and crosses the hide boundary (rushHigh < 1),
              // the UI module publishes this event instead of directly mutating selection state.
              // The conductor handles the actual deselection via state manager delegation.
              const unsubRushDelivery =
                window.MurphyDoorConfigurator.CoreEventBus.subscribe(
                  CONSTANTS.EVENTS.UI_RUSH_DELIVERY_DESELECT_REQUESTED,
                  (eventData) => {
                    isDev() &&
                      console.log(
                        '🚚 CONDUCTOR: Received UI_RUSH_DELIVERY_DESELECT_REQUESTED',
                        eventData
                      );

                    try {
                      const deliveryCategory =
                        CONSTANTS.CATEGORY_NAMES.DELIVERY;
                      const stateManager = this.stateManager;

                      if (!stateManager) {
                        isDev() &&
                          console.warn(
                            '⚠️ CONDUCTOR: stateManager not available for rush delivery deselect'
                          );
                        return;
                      }

                      // Check if the Delivery category has an applied selection
                      const appliedSnapshot =
                        stateManager.getStateMaps?.()
                          ?.appliedSelectionsSnapshot;
                      const deliverySelection =
                        appliedSnapshot?.full?.[deliveryCategory];

                      if (!deliverySelection || !deliverySelection.productId) {
                        isDev() &&
                          console.log(
                            '🚚 CONDUCTOR: No applied selection in Delivery category - nothing to deselect'
                          );
                        return;
                      }

                      // Verify the applied product is the rush fee product via PPD displayFormatting
                      const appliedProductId = deliverySelection.productId;
                      const appliedProduct =
                        this.getProductById(appliedProductId);

                      if (!appliedProduct) {
                        isDev() &&
                          console.log(
                            '🚚 CONDUCTOR: Applied product not found in PPD - skipping deselect'
                          );
                        return;
                      }

                      // Check if the applied product has isRushFeeProduct flag via its variant displayFormatting
                      const isRushFee = appliedProduct.variants?.some(
                        (v) => v.displayFormatting?.isRushFeeProduct === true
                      );

                      if (!isRushFee) {
                        isDev() &&
                          console.log(
                            '🚚 CONDUCTOR: Applied Delivery product is NOT rush fee - skipping deselect'
                          );
                        return;
                      }

                      // Clear the rush fee selection via state manager delegation
                      const clearResult =
                        stateManager.handleBackClick(deliveryCategory);

                      if (clearResult) {
                        // Restore thumbnail view to show all products (not variant detail)
                        if (window.MurphyDoorConfigurator.uiUpdater) {
                          window.MurphyDoorConfigurator.uiUpdater.restoreThumbnailView(
                            deliveryCategory,
                            clearResult.clearSelection
                          );
                        }

                        // Publish STATE_SELECTION_CLEARED for downstream coordination
                        window.MurphyDoorConfigurator.CoreEventBus.publish(
                          CONSTANTS.EVENTS.STATE_SELECTION_CLEARED,
                          {
                            categoryId: deliveryCategory,
                            source: 'rush_delivery_auto_deselect',
                            reason: eventData.reason,
                            timestamp: Date.now(),
                          }
                        );

                        // Recalculate price after deselection
                        this.handlePriceUpdates(
                          { categoryId: deliveryCategory },
                          SELECTION_STATES.BROWSING
                        );

                        isDev() &&
                          console.log(
                            '🚚 CONDUCTOR: Rush fee auto-deselected successfully'
                          );
                      }
                    } catch (error) {
                      isDev() &&
                        console.error(
                          '❌ CONDUCTOR: Rush delivery auto-deselect failed:',
                          error
                        );
                    }
                  }
                );
              if (unsubRushDelivery) cleanupHandlers.push(unsubRushDelivery);

              // =========================================================================
              // SUBSCRIPTION: SELECTION_USER_UNAPPLY_REQUESTED
              // Fires when user clicks × on an applied selection thumbnail
              // Clears state, resets UI to non-completed, shows all thumbnails
              //
              // 🚨 `source: 'conductor.user_unapply'` below is a FILTER KEY two
              // consumers match on exactly -- md-config-bl-forbidden-validator.js
              // (:4095, :4150) and md-config-support-persistence-manager.js (:388)
              // both skip a redundant recalculation when they see it. DO NOT
              // change that literal value.
              //
              // But this handler is reached by SIX distinct publish call sites
              // with genuinely different origins -- the real × click
              // (event-handler.js, source 'setupGlobalEventDelegation.
              // unapply_handler'), the summary edit-pencil ('summary_edit'), and
              // four programmatic releases (gate-card-coordinator's
              // resetStaleAutoApply, selection-dependency-coordinator's
              // resetStaleFallback/parentUnapplied, material-remap-coordinator's
              // materialChangeReset, material-preference-coordinator's
              // cascadeUnapply) -- and until now that provenance was discarded
              // here and overwritten with the same hardcoded constant for every
              // caller. `requestSource` forwards the ORIGINAL publisher's
              // `source` (added, not replacing the constant) so a downstream
              // reporter can tell a genuine user unapply from a cascade release
              // without touching the two existing filters above.
              // =========================================================================
              const unsubUserUnapply =
                window.MurphyDoorConfigurator.CoreEventBus.subscribe(
                  CONSTANTS.EVENTS.SELECTION_USER_UNAPPLY_REQUESTED,
                  (eventData) => {
                    const { categoryId } = eventData || {};
                    if (!categoryId) return;
                    const requestSource =
                      (eventData && eventData.source) || 'unknown';

                    const startTime = window.performance
                      ? window.performance.now()
                      : Date.now();

                    try {
                      // 1. Clear state directly via stateManager.handleBackClick() to avoid UI side-effects
                      //    from the full handleStateChange('BACK_CLICKED') path (e.g., restoreThumbnailView).
                      //    NOTE: handleBackClick() ONLY clears state maps - it does NOT publish events or
                      //    reset inVariantTransition. We must handle those responsibilities here.
                      const result =
                        this.stateManager.handleBackClick(categoryId);
                      if (!result) return;

                      // 1a. Update parent tab's visual completion state to reflect the cleared category.
                      //     Without this, the tab retains its --completed CSS class after unapply,
                      //     making it appear complete even though a category within it was cleared.
                      //     Mirrors the same pattern used in clearCategoryState() for cascade resets.
                      if (result.clearSelection) {
                        const tabId =
                          this.stateManager.getCategoryTab(categoryId);
                        if (tabId && window.MurphyDoorConfigurator.uiUpdater) {
                          window.MurphyDoorConfigurator.uiUpdater.updateDisplay(
                            'TAB_STATUS_UPDATE',
                            {
                              tabId: String(tabId),
                              isComplete: false,
                            }
                          );
                        }
                      }

                      // 1b. Reset inVariantTransition flag (mirrors handleStateChange('BACK_CLICKED') behavior)
                      this.stateManager.getStateMaps().core.inVariantTransition = false;

                      // 1c. Publish STATE_SELECTION_CLEARED for downstream coordination
                      //     This triggers: cascade-auto-selection-coordinator, forbidden-validator, ui-updater preview
                      window.MurphyDoorConfigurator.CoreEventBus.publish(
                        CONSTANTS.EVENTS.STATE_SELECTION_CLEARED,
                        {
                          categoryId,
                          source: 'conductor.user_unapply',
                          requestSource,
                          timestamp: Date.now(),
                        }
                      );

                      // 1d. Persist updated state to URL (removes unapplied selection from ?config= param)
                      this.stateManager.persistState();

                      // 2. Publish UI reset event (event-driven, not direct method call)
                      window.MurphyDoorConfigurator.CoreEventBus.publish(
                        CONSTANTS.EVENTS.UI_USER_UNAPPLY_REQUESTED,
                        {
                          categoryId,
                          timestamp: Date.now(),
                          source: 'conductor.user_unapply',
                          requestSource,
                        }
                      );

                      // 3. Recalculate price
                      this.handlePriceUpdates(
                        { categoryId },
                        SELECTION_STATES.BROWSING
                      );

                      // 4. Publish progress update event (event-driven, not direct method call)
                      window.MurphyDoorConfigurator.CoreEventBus.publish(
                        CONSTANTS.EVENTS.UI_PROGRESS_UPDATE_REQUESTED,
                        {
                          source: 'conductor.user_unapply',
                          requestSource,
                          timestamp: Date.now(),
                        }
                      );

                      // 5. Publish completion event
                      window.MurphyDoorConfigurator.CoreEventBus.publish(
                        CONSTANTS.EVENTS.SELECTION_USER_UNAPPLY_COMPLETED,
                        {
                          categoryId,
                          timestamp: Date.now(),
                          source: 'conductor.user_unapply',
                          requestSource,
                        }
                      );

                      const executionTime =
                        (window.performance
                          ? window.performance.now()
                          : Date.now()) - startTime;
                      if (executionTime > 100) {
                        isDev() &&
                          console.warn(
                            `⚠️ CONDUCTOR: User unapply took ${executionTime.toFixed(2)}ms for "${categoryId}"`
                          );
                      }

                      // PATTERN #12: Performance Tracking
                      trackPerformance('DELEGATION', executionTime);
                    } catch (error) {
                      isDev() &&
                        console.error(
                          `❌ CONDUCTOR: User unapply failed for "${categoryId}":`,
                          error
                        );
                      window.MurphyDoorConfigurator.CoreEventBus.publish(
                        CONSTANTS.EVENTS.SELECTION_USER_UNAPPLY_FAILED,
                        {
                          categoryId,
                          error: error.message,
                          timestamp: Date.now(),
                          source: 'conductor.user_unapply',
                          requestSource,
                        }
                      );
                    }
                  }
                );
              if (unsubUserUnapply) cleanupHandlers.push(unsubUserUnapply);

              // SIMPLIFIED MODE: Register all simplified category IDs from the coordinator.
              // Used to track BROWSING selections for "Build My Door" bulk-apply validation.
              const unsubSimplifiedCats =
                window.MurphyDoorConfigurator.CoreEventBus.subscribe(
                  CONSTANTS.EVENTS.BUSINESS_SIMPLIFIED_MODE_CATEGORIES_READY,
                  (eventData) => {
                    this._simplifiedCategoryIds = new Set(
                      eventData?.categoryIds || []
                    );
                    this._simplifiedBrowsingSelections = new Map();
                    this._buildMyDoorInFlight = false;
                    isDev() &&
                      console.log(
                        '[Conductor] Simplified mode categories registered for Build My Door:',
                        [...this._simplifiedCategoryIds]
                      );
                  }
                );
              if (unsubSimplifiedCats)
                cleanupHandlers.push(unsubSimplifiedCats);

              // SIMPLIFIED MODE: "Build My Door" bulk-apply handler.
              // Validates all simplified categories have a BROWSING selection, then
              // applies them all in sequence — triggering the existing completion cascade.
              const unsubBuildMyDoor =
                window.MurphyDoorConfigurator.CoreEventBus.subscribe(
                  CONSTANTS.EVENTS.UI_SIMPLIFIED_BUILD_MY_DOOR_CLICKED,
                  () => {
                    try {
                      if (
                        !this._simplifiedCategoryIds ||
                        this._simplifiedCategoryIds.size === 0
                      )
                        return;

                      // Guard against rapid double-clicks: once a successful apply
                      // wave is scheduled, ignore further clicks until it drains —
                      // otherwise a second click queues a duplicate APPLY_SELECTION
                      // wave that races the first. Failed (incomplete) attempts do
                      // NOT set the flag, so the user can still retry after fixing.
                      if (this._buildMyDoorInFlight) return;

                      const missingCategoryIds = [];
                      this._simplifiedCategoryIds.forEach((catId) => {
                        if (!this._simplifiedBrowsingSelections.has(catId)) {
                          missingCategoryIds.push(catId);
                        }
                      });

                      if (missingCategoryIds.length > 0) {
                        isDev() &&
                          console.log(
                            '[Conductor] Build My Door: missing browsing for:',
                            missingCategoryIds
                          );
                        window.MurphyDoorConfigurator.CoreEventBus.publish(
                          CONSTANTS.EVENTS.UI_ADD_TO_CART_DISABLED_CLICKED,
                          { missingCategoryIds }
                        );
                        return;
                      }

                      isDev() &&
                        console.log(
                          '[Conductor] Build My Door: applying all simplified selections'
                        );
                      this._buildMyDoorInFlight = true;

                      // SIMPLIFIED-MODE ESCAPE — the customer's explicit exit
                      // from the guided 3-question flow into the full
                      // configurator. Published HERE, not from the UI module
                      // that owns the button, for two reasons:
                      //
                      //  1. A Build My Door click is NOT an escape. The early
                      //     return above refuses the click when any simplified
                      //     category still lacks a browsing selection, and that
                      //     refusal keeps the customer inside simplified mode.
                      //     UI_SIMPLIFIED_BUILD_MY_DOOR_CLICKED therefore counts
                      //     attempts, not exits — subscribing an analytics
                      //     emitter to it would inflate the funnel with refusals.
                      //  2. This site sits AFTER the _buildMyDoorInFlight
                      //     double-click guard, so a customer hammering the
                      //     button produces exactly one escape per apply wave.
                      //
                      // `trigger` is a controlled slug, never free text: it is
                      // the affordance identity, and a second future escape
                      // affordance gets its own slug rather than a label.
                      // md-config-support-analytics-reporter.js is the only
                      // subscriber and owns the wire (consent buffer + sample
                      // rate); nothing here talks to Shopify.analytics directly.
                      try {
                        window.MurphyDoorConfigurator.CoreEventBus.publish(
                          CONSTANTS.EVENTS.BUSINESS_SIMPLIFIED_MODE_ESCAPED,
                          {
                            from: 'simplified',
                            trigger: 'build_my_door',
                            timestamp: Date.now(),
                          }
                        );
                      } catch (_e) {
                        /* telemetry must never block the apply wave */
                      }

                      const entries = [
                        ...this._simplifiedBrowsingSelections.entries(),
                      ];
                      entries.forEach(
                        ([categoryId, { productId, variantId }], index) => {
                          setTimeout(() => {
                            try {
                              this.handleStateChange('APPLY_SELECTION', {
                                categoryId,
                                productId,
                                variantId,
                                auto_applied: false,
                              });
                            } finally {
                              // Release the lock once the final selection drains —
                              // in `finally` so a thrown apply can never leave the
                              // button permanently locked until page reload.
                              if (index === entries.length - 1) {
                                this._buildMyDoorInFlight = false;
                              }
                            }
                          }, index * 50);
                        }
                      );
                    } catch (err) {
                      isDev() &&
                        console.error(
                          '[Conductor] Build My Door handler error:',
                          err
                        );
                    }
                  }
                );
              if (unsubBuildMyDoor) cleanupHandlers.push(unsubBuildMyDoor);

              isDev() &&
                console.log(
                  '✅ Conductor: Subscribed to UI tab readiness state changes for CCP compliance'
                );
            }

            // Track performance metrics for initialization steps
            const perfMetrics = {
              stateRecovery: 0,
              dataProcessing: 0,
              validation: 0,
              stateInitialization: 0,
              structureInitialization: 0,
              eventListenerSetup: 0,
              totalTime: 0,
            };

            // ✅ PHASE 3: PPD-ONLY INITIALIZATION - NO FALLBACKS
            // State data MUST come from StateInitializer module (populated via system:state_from_ppd_ready event)
            // Legacy fallback paths (this.processedData, processConfiguratorData()) have been removed
            const dataProcessingStart = performance.now();

            const ppd = window.MurphyDoorConfigurator.perfectProductDictionary;
            const stateInitializer =
              window.MurphyDoorConfigurator.stateInitializer;

            // Fail-fast validation: PPD and StateInitializer must be ready
            if (!ppd || !stateInitializer) {
              throw new Error(
                '❌ CONDUCTOR: PPD initialization failed. Bootstrap phase must complete before conductor initialization.\n' +
                  `   - PPD available: ${!!ppd}\n` +
                  `   - StateInitializer available: ${!!stateInitializer}\n` +
                  '   - Check that bootstrap modules (initializer + data-pipeline) loaded correctly'
              );
            }

            if (!this._ppdStateReady) {
              throw new Error(
                '❌ CONDUCTOR: PPD state not ready. Did not receive system:state_from_ppd_ready event.\n' +
                  '   - StateInitializer should publish this event after processing PPD\n' +
                  '   - Check event subscription in conductor initialization'
              );
            }

            if (!stateInitializer.getStatus().initialized) {
              throw new Error(
                '❌ CONDUCTOR: StateInitializer not initialized. Check StateInitializer module status.\n' +
                  `   - StateInitializer status: ${JSON.stringify(stateInitializer.getStatus())}`
              );
            }

            // 🎯 PPD-ONLY FLOW: Get state data from StateInitializer (already created during ppd_ready event)
            isDev() &&
              console.log(
                '✅ CONDUCTOR: Using PPD-only initialization flow (Phase 3 complete)'
              );
            isDev() &&
              console.log(
                `🔥 PPD has ${ppd.tabStructure?.size || 0} tabs, ${ppd.categories?.size || 0} categories, ${ppd.products?.size || 0} products`
              );

            // 🔧 FIX: Retrieve cached state instead of re-initializing (prevents double initialization race condition)
            // ✨ ARCHITECTURAL CLARITY: Renamed from processedData to stateData (extracted from PPD, not legacy processedData)
            const stateData = stateInitializer.getStateData();

            isDev() &&
              console.log(
                `📊 State data from PPD: ${stateData.tabStructure?.size || 0} tabs, ${stateData.categoryStructure?.size || 0} categories`
              );

            perfMetrics.dataProcessing =
              performance.now() - dataProcessingStart;

            if (!stateData) {
              throw new Error('Failed to get state data from StateInitializer');
            }

            // P3.2 PIPELINE: Initialize perfectProductDictionary via pipeline processor FIRST
            // 🔥 ARCHITECTURAL FIX: Perfect product dictionary must exist before state recovery
            await this.initializePerfectProductDictionary(stateData);

            isDev() &&
              console.log(
                '🧙‍♂️ CONDUCTOR: perfectProductDictionary ready, now attempting state recovery'
              );

            // Step 2: Attempt state recovery AFTER perfectProductDictionary is available
            // 🔥 TIMING FIX: State recovery moved after pipeline completion to eliminate race condition
            const stateRecoveryStart = performance.now();
            const recovered = await this.attemptStateRecovery();
            perfMetrics.stateRecovery = performance.now() - stateRecoveryStart;

            // Continue with normal initialization whether recovery succeeded or not
            if (true) {
              // Always continue - recovery is now non-blocking

              // URL param parser is now event-driven and self-initializing via system:maps_initialized
              // No manual initialization needed here (replaces old URL recovery animator)

              // Publish configuration loaded event
              if (window.MurphyDoorConfigurator.CoreEventBus) {
                window.MurphyDoorConfigurator.CoreEventBus.publish(
                  CONSTANTS.EVENTS.SYSTEM_CONFIGURATION_LOADED,
                  {
                    timestamp: performance.now(),
                    source: 'md-config-core-conductor.js',
                    method: METHOD_SIGNATURE,
                    configSize: Object.keys(stateData).length,
                  }
                );
              }

              // Step 3: Validate initialization data
              const validationStart = performance.now();

              // ✅ ARCHITECTURAL FIX: Always run validation (removed temporary bypass)
              // StateInitializer now guarantees proper structure, so we can always validate
              // 🔧 FIX: Pass stateData to validation (was missing argument, causing validation to fail)
              const validationResult =
                window.MurphyDoorConfigurator.supportValidationManager.validateInitialization(
                  stateData
                );

              // Set performance metrics after validation is complete
              perfMetrics.validation = performance.now() - validationStart;

              // Now check the validation result
              if (
                validationResult &&
                (!validationResult.hasRequiredData ||
                  !validationResult.hasValidStructure)
              ) {
                isDev() &&
                  console.error('Validation failed:', validationResult);
                throw new Error(
                  'Invalid configuration data structure: ' +
                    (validationResult.errorDetails ||
                      'Missing required data or invalid structure')
                );
              }

              // Step 4: Initialize core state structures
              const stateInitStart = performance.now();
              this.initializeState();
              perfMetrics.stateInitialization =
                performance.now() - stateInitStart;

              // Publish state initialized event
              if (window.MurphyDoorConfigurator.CoreEventBus) {
                window.MurphyDoorConfigurator.CoreEventBus.publish(
                  CONSTANTS.EVENTS.SYSTEM_STATE_INITIALIZED,
                  {
                    timestamp: performance.now(),
                    source: 'md-config-core-conductor.js',
                    method: METHOD_SIGNATURE,
                  }
                );
              }

              // Step 6: Initialize display modes for each category
              const DISPLAY_MODES = window.MurphyDoorConfigurator.DISPLAY_MODES;
              const TRANSITION_STATES =
                window.MurphyDoorConfigurator.TRANSITION_STATES;
              const NAVIGATION_STATES =
                window.MurphyDoorConfigurator.NAVIGATION_STATES;

              // Set initial transition and navigation states
              const categories =
                this.stateManager.getStateMaps().categoryStates;
              const tabs = this.stateManager.getStateMaps().tabStates;

              // Set initial tab navigation states
              tabs.forEach((_, tabId) => {
                this.stateManager.setNavigationState(
                  `tab_${tabId}`,
                  NAVIGATION_STATES.INITIAL
                );
              });

              // Step 8: Initialize core system components
              // Remove this line since we're not using it anymore
              // const structureInitStart = performance.now();

              // Initialize progress tracking
              if (!window.MurphyDoorConfigurator.progressTracker) {
                throw new Error('Progress tracker module not available');
              }
              window.MurphyDoorConfigurator.progressTracker.initializeProgress();

              // Publish event BEFORE navigation structure creation
              // This allows TooltipManager to pre-populate cache BEFORE HTML generation
              if (window.MurphyDoorConfigurator.CoreEventBus) {
                window.MurphyDoorConfigurator.CoreEventBus.publish(
                  CONSTANTS.EVENTS.SYSTEM_PRE_STRUCTURE_INITIALIZATION,
                  {
                    timestamp: performance.now(),
                    source: 'md-config-core-conductor.js',
                    conductor: this,
                  }
                );
                isDev() &&
                  console.log(
                    'Published system:pre_structure_initialization event for tooltip cache pre-population'
                  );
              }

              // Initialize navigation structure (reads from PPD directly)
              const structureResult = this.initializeStructure();
              if (!structureResult.success) {
                throw new Error(
                  `Navigation structure initialization failed: ${structureResult.error}`
                );
              }
              // Add structure initialization time to performance metrics
              perfMetrics.structureInitialization =
                structureResult.executionTime;

              // Initialize event listeners
              const eventListenerStart = performance.now();
              this.initializeEventListeners();
              perfMetrics.eventListenerSetup =
                performance.now() - eventListenerStart;

              // Step 9: Initialize pricing and shipping displays
              if (!window.MurphyDoorConfigurator.priceCalculator) {
                throw new Error('Price calculator module not available');
              }
              // 🚀 PPD-FIRST: Pass conductor for CCP compliance and future-proofing
              const basePrice =
                window.MurphyDoorConfigurator.priceCalculator.getBasePrice(
                  this
                );

              // Delegate price update to UI layer through event system
              if (!window.MurphyDoorConfigurator.CoreEventBus) {
                throw new Error('Event bus not available for UI updates');
              }
              // DRY: Use priceCalculator formatting for consistent display
              const formattedBasePrice =
                window.MurphyDoorConfigurator.priceCalculator.formatPriceWithoutCurrency(
                  basePrice
                );
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.UI_UPDATE_REQUESTED,
                {
                  type: 'price',
                  updateData: {
                    // Bootstrap renders the base price by definition, so this
                    // is BASE by construction. Sourced from the shared Phase 0
                    // vocabulary so it cannot drift from the calculator's.
                    label: priceLabels().BASE,
                    amount: basePrice,
                    formattedAmount: formattedBasePrice,
                    // Summary mirrors main display from initialization
                    summaryLabel: priceLabels().BASE,
                    formattedSummary: formattedBasePrice,
                  },
                  source: 'conductor_initialization',
                }
              );

              if (!window.MurphyDoorConfigurator.shippingCalculator) {
                throw new Error('Shipping calculator module not available');
              }
              const baseTime =
                window.MurphyDoorConfigurator.shippingCalculator.getBaseShippingTime();

              // Delegate shipping update to UI layer through event system
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.UI_UPDATE_REQUESTED,
                {
                  type: 'batch',
                  updateData: {
                    shippingTime: {
                      range: baseTime,
                      formattedText:
                        window.MurphyDoorConfigurator.shippingCalculator.formatShippingTime(
                          baseTime
                        ),
                      transitDays:
                        window.MurphyDoorConfigurator.shippingCalculator.getTransitDays(),
                    },
                  },
                  source: 'conductor_initialization',
                }
              );

              // Step 10: URL configuration restoration
              // Now handled by md-config-support-url-param-parser.js which:
              // 1. Detects ?config= parameter
              // 2. Parses and validates against PPD
              // 3. Publishes BUSINESS_CASCADE_APPLY_URL_SELECTIONS event
              // 4. Cascade coordinator applies selections through existing logic

              // Step 11: Auto-navigation now handled by md-config-bl-auto-navigation-coordinator.js
            }

            // Publish loading complete event instead of direct UI manipulation
            window.MurphyDoorConfigurator.CoreEventBus.publish(
              CONSTANTS.EVENTS.UI_LOADING_STATE_CHANGED,
              {
                state: 'ready',
                source: 'initialization_complete',
                timestamp: Date.now(),
              }
            );

            // Calculate total execution time
            perfMetrics.totalTime = performance.now() - startTime;

            // P3.2 SINGLE MASTER DICTIONARY: Already initialized via pipeline processor above

            isDev() &&
              console.log('Configurator initialization completed successfully');

            // 🔓 MARK INITIALIZATION AS COMPLETE - Release the lock
            window.MurphyDoorConfigurator.stateManager?.markInitializationComplete(
              'conductor'
            );
            isDev() &&
              console.log(
                '✅ CONDUCTOR GUARD: Initialization completed and marked successfully'
              );

            // URL param parser is event-driven and self-initializing via system:maps_initialized
            // No manual URL recovery animator initialization needed (deprecated)

            // Initialize persistence manager for event-driven URL updates
            if (window.MurphyDoorConfigurator.supportPersistenceManager) {
              try {
                isDev() &&
                  console.log(
                    '🔄 CONDUCTOR: Initializing persistence manager for event-driven URL updates'
                  );
                window.MurphyDoorConfigurator.supportPersistenceManager.initialize();
                isDev() &&
                  console.log(
                    '✅ CONDUCTOR: Persistence manager initialized successfully'
                  );
              } catch (persistenceError) {
                isDev() &&
                  console.warn(
                    '⚠️ CONDUCTOR: Persistence manager initialization failed, continuing without auto URL updates',
                    persistenceError
                  );
              }
            }

            // Initialize image layer coordinator
            if (window.MurphyDoorConfigurator.imageLayerCoordinator) {
              try {
                isDev() &&
                  console.log(
                    '🧙‍♂️ CONDUCTOR: Initializing image layer coordinator'
                  );
                window.MurphyDoorConfigurator.imageLayerCoordinator.initialize(
                  this
                );
                isDev() &&
                  console.log(
                    '✅ CONDUCTOR: Image layer coordinator initialized successfully'
                  );
              } catch (imageLayerError) {
                isDev() &&
                  console.warn(
                    '⚠️ CONDUCTOR: Image layer coordinator initialization failed, continuing without image layer coordination',
                    imageLayerError
                  );
              }
            }

            // Initialize shipping calculator event subscriptions
            if (
              window.MurphyDoorConfigurator.shippingCalculator &&
              window.MurphyDoorConfigurator.shippingCalculator.initialize
            ) {
              try {
                isDev() &&
                  console.log('🧙‍♂️ CONDUCTOR: Initializing shipping calculator');
                window.MurphyDoorConfigurator.shippingCalculator.initialize();
                isDev() &&
                  console.log(
                    '✅ CONDUCTOR: Shipping calculator initialized successfully'
                  );
              } catch (shippingInitError) {
                isDev() &&
                  console.warn(
                    '⚠️ CONDUCTOR: Shipping calculator initialization failed, continuing without event subscriptions',
                    shippingInitError
                  );
              }
            }

            // Initialize implicit apply coordinator (before auto-nav to ensure event subscriptions are ready)
            if (window.MurphyDoorConfigurator.implicitApplyCoordinator) {
              try {
                isDev() &&
                  console.log(
                    '🧙‍♂️ CONDUCTOR: Initializing implicit apply coordinator'
                  );
                window.MurphyDoorConfigurator.implicitApplyCoordinator.initialize(
                  this
                );
                isDev() &&
                  console.log(
                    '✅ CONDUCTOR: Implicit apply coordinator initialized successfully'
                  );
              } catch (implicitApplyError) {
                isDev() &&
                  console.warn(
                    '⚠️ CONDUCTOR: Implicit apply coordinator initialization failed, continuing without implicit apply',
                    implicitApplyError
                  );
              }
            }

            // Initialize auto-navigation coordinator
            if (window.MurphyDoorConfigurator.autoNavigationCoordinator) {
              try {
                isDev() &&
                  console.log(
                    '🧙‍♂️ CONDUCTOR: Initializing auto-navigation coordinator'
                  );
                window.MurphyDoorConfigurator.autoNavigationCoordinator.initialize(
                  this
                );
                isDev() &&
                  console.log(
                    '✅ CONDUCTOR: Auto-navigation coordinator initialized successfully'
                  );
              } catch (autoNavError) {
                isDev() &&
                  console.warn(
                    '⚠️ CONDUCTOR: Auto-navigation coordinator initialization failed, continuing without auto-navigation',
                    autoNavError
                  );
              }
            }

            // Initialize editor bridge support (Phase 2 CCP module for Super App order editor)
            // Must be initialized BEFORE system:initialization_complete fires so it can subscribe
            // to that event and publish BUSINESS_CASCADE_APPLY_URL_SELECTIONS at Step 12.
            if (window.MurphyDoorConfigurator.editorBridgeSupport) {
              try {
                isDev() &&
                  console.log(
                    '🧙‍♂️ CONDUCTOR: Initializing editor bridge support'
                  );
                window.MurphyDoorConfigurator.editorBridgeSupport.initialize(
                  this
                );
                isDev() &&
                  console.log(
                    '✅ CONDUCTOR: Editor bridge support initialized successfully'
                  );
              } catch (editorBridgeError) {
                isDev() &&
                  console.warn(
                    '⚠️ CONDUCTOR: Editor bridge support initialization failed, continuing without editor bridge pre-population',
                    editorBridgeError
                  );
              }
            }

            // Initialize simplified mode coordinator (3-question flow for FMD2)
            if (window.MurphyDoorConfigurator.simplifiedModeCoordinator) {
              try {
                isDev() &&
                  console.log(
                    '🧙‍♂️ CONDUCTOR: Initializing simplified mode coordinator'
                  );
                window.MurphyDoorConfigurator.simplifiedModeCoordinator.initialize(
                  this
                );
                isDev() &&
                  console.log(
                    '✅ CONDUCTOR: Simplified mode coordinator initialized successfully'
                  );
              } catch (simplifiedModeError) {
                isDev() &&
                  console.warn(
                    '⚠️ CONDUCTOR: Simplified mode coordinator initialization failed, continuing without simplified mode',
                    simplifiedModeError
                  );
              }
            }

            // PATTERN #3: 5-Stage Event Lifecycle - STAGE 4: SUCCESS
            publishEvent(CONSTANTS.EVENTS.CONDUCTOR_INITIALIZATION_SUCCESS, {
              duration: perfMetrics.totalTime,
              recoveredState: !!recovered,
              timestamp: Date.now(),
            });

            // PATTERN #12: Performance Tracking
            trackPerformance('INITIALIZATION', perfMetrics.totalTime);

            // 🎯 CRITICAL: Publish initialization_complete AFTER all coordinators are subscribed
            // This ensures auto-navigation coordinator receives the event
            if (window.MurphyDoorConfigurator.CoreEventBus) {
              publishEvent(CONSTANTS.EVENTS.SYSTEM_INITIALIZATION_COMPLETE, {
                timestamp: performance.now(),
                source: 'md-config-core-conductor.js',
                method: METHOD_SIGNATURE,
                recoveredState: !!recovered,
                executionTime: perfMetrics.totalTime,
                metrics: perfMetrics,
              });

              // Publish performance metrics separately for monitoring systems
              publishEvent(
                CONSTANTS.EVENTS.PERFORMANCE_INITIALIZATION_METRICS,
                {
                  timestamp: performance.now(),
                  source: 'md-config-core-conductor.js',
                  method: METHOD_SIGNATURE,
                  metrics: perfMetrics,
                }
              );

              // Also publish system:initialized event to maintain compatibility
              publishEvent(CONSTANTS.EVENTS.SYSTEM_INITIALIZED, {
                timestamp: performance.now(),
                source: 'md-config-core-conductor.js',
              });

              // Initialize tooltips for all categories
              setTimeout(() => {
                if (this.initializeTooltips) {
                  this.initializeTooltips();
                }
              }, 500); // Short delay to ensure all DOM elements are ready
            }
          } catch (error) {
            // 🚫 RESET INITIALIZATION STATE ON ERROR - Allow retry attempts
            window.MurphyDoorConfigurator.stateManager?.resetInitializationState(
              'conductor-error'
            );
            isDev() &&
              console.error(
                '❌ CONDUCTOR GUARD: Initialization failed, state reset for retry attempts'
              );
            isDev() && console.error('🔥 ACTUAL ERROR:', error);
            isDev() && console.error('🔥 ERROR STACK:', error?.stack);

            // PATTERN #3: 5-Stage Event Lifecycle - STAGE 5: FAILED (in initializeConfiguratorCore)
            const failDuration = performance.now() - startTime;
            publishEvent(CONSTANTS.EVENTS.CONDUCTOR_INITIALIZATION_FAILED, {
              error: error.message,
              duration: failDuration,
              timestamp: Date.now(),
            });

            // PATTERN #12: Performance Tracking
            trackPerformance('INITIALIZATION', failDuration);

            // Publish loading error event instead of direct UI manipulation
            publishEvent(CONSTANTS.EVENTS.UI_LOADING_STATE_CHANGED, {
              state: 'ready',
              source: 'initialization_error',
              error: true,
              timestamp: Date.now(),
            });

            // Enhanced error handling with detailed context
            this.handleError('Initialization failed', error, {
              method: METHOD_SIGNATURE,
              module: 'main',
              severity: 'critical',
              recoverable: false,
              executionTime: failDuration,
              component: 'initializeConfiguratorCore',
              stage: error.message.includes('configuration data')
                ? 'data validation'
                : error.message.includes('modules')
                  ? 'module availability'
                  : 'initialization process',
            });

            // Publish specific error event for monitoring systems
            publishEvent(CONSTANTS.EVENTS.SYSTEM_INITIALIZATION_FAILED, {
              timestamp: performance.now(),
              source: 'md-config-core-conductor.js',
              method: METHOD_SIGNATURE,
              error: {
                message: error.message,
                stack: error.stack,
                type: error.name,
              },
            });

            // Attempt to show error UI if possible
            try {
              if (window.MurphyDoorConfigurator.uiUpdater) {
                window.MurphyDoorConfigurator.uiUpdater.showErrorMessage(
                  'Unable to initialize the configurator. Please refresh the page or contact support.'
                );
              }
            } catch (uiError) {
              isDev() && console.error('Failed to display error UI:', uiError);
            }
          }
        },

        /**
         * Initializes event listeners through the event handler module following the Central Conductor Pattern.
         *
         * This method ensures proper event listener setup:
         * 1. Checks DOM readiness state
         * 2. Sets up event listeners appropriately based on readiness
         * 3. Handles any errors during the initialization process
         * 4. Reports initialization status through the central event bus
         *
         * Following the Central Conductor Pattern principles:
         * - Delegates event management responsibility to the dedicated event handler
         * - Maintains proper event publishing for system monitoring
         * - Implements appropriate error handling with context
         * - Ensures DOM-readiness before event setup proceeds
         *
         * @fires system:event_listeners_initialized - When event listeners are successfully set up
         * @fires system:error - When event listener initialization fails
         * @returns {void}
         */
        initializeEventListeners() {
          const METHOD_SIGNATURE = 'initializeEventListeners';
          const startTime = performance.now();

          try {
            // Publish system event for initialization start
            if (window.MurphyDoorConfigurator.CoreEventBus) {
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.SYSTEM_EVENT_LISTENER_SETUP_STARTED,
                {
                  timestamp: performance.now(),
                  source: 'md-config-core-conductor.js',
                  method: METHOD_SIGNATURE,
                  domState: document.readyState,
                }
              );
            }

            // Validate event handler existence and interface
            if (!window.MurphyDoorConfigurator.eventHandler) {
              throw new Error('Event handler module not found');
            }

            if (
              typeof window.MurphyDoorConfigurator.eventHandler
                .setupEventListeners !== 'function'
            ) {
              throw new Error(
                'Event handler missing required setupEventListeners method'
              );
            }

            // Set up event listeners based on DOM readiness
            if (document.readyState === 'loading') {
              document.addEventListener('DOMContentLoaded', () => {
                try {
                  const handlerStartTime = performance.now();

                  // Delegate to event handler module
                  window.MurphyDoorConfigurator.eventHandler.setupEventListeners();

                  const setupDuration = performance.now() - handlerStartTime;

                  // Publish event listener initialization success event with metrics
                  if (window.MurphyDoorConfigurator.CoreEventBus) {
                    window.MurphyDoorConfigurator.CoreEventBus.publish(
                      CONSTANTS.EVENTS.SYSTEM_EVENT_LISTENERS_INITIALIZED,
                      {
                        timestamp: performance.now(),
                        source: 'md-config-core-conductor.js',
                        method: METHOD_SIGNATURE,
                        domState: 'DOMContentLoaded',
                        metrics: {
                          setupDuration: setupDuration,
                        },
                      }
                    );

                    // Publish separate performance event for monitoring
                    window.MurphyDoorConfigurator.CoreEventBus.publish(
                      CONSTANTS.EVENTS.PERFORMANCE_EVENT_LISTENER_SETUP,
                      {
                        timestamp: performance.now(),
                        method: METHOD_SIGNATURE,
                        executionTime: setupDuration,
                        domState: 'DOMContentLoaded',
                      }
                    );
                  }
                } catch (setupError) {
                  // Enhanced error handling with detailed context
                  this.handleError(
                    'Failed to set up event listeners on DOMContentLoaded',
                    setupError,
                    {
                      method: METHOD_SIGNATURE,
                      module: 'main',
                      severity: 'high',
                      recoverable: false,
                      stage: 'DOMContentLoaded callback',
                      domState: 'loading',
                      errorType: setupError.name || 'SetupError',
                    }
                  );

                  // Publish specific error event for monitoring systems
                  if (window.MurphyDoorConfigurator.CoreEventBus) {
                    window.MurphyDoorConfigurator.CoreEventBus.publish(
                      CONSTANTS.EVENTS.SYSTEM_EVENT_LISTENER_SETUP_FAILED,
                      {
                        timestamp: performance.now(),
                        source: 'md-config-core-conductor.js',
                        method: METHOD_SIGNATURE,
                        domState: 'DOMContentLoaded',
                        error: {
                          message: setupError.message,
                          stack: setupError.stack,
                          type: setupError.name || 'SetupError',
                        },
                      }
                    );
                  }
                }
              });
            } else {
              // DOM already loaded, setup immediately
              const handlerStartTime = performance.now();

              // Delegate to event handler module
              window.MurphyDoorConfigurator.eventHandler.setupEventListeners();

              const setupDuration = performance.now() - handlerStartTime;
              const totalDuration = performance.now() - startTime;

              // Publish event listener initialization success event with metrics
              if (window.MurphyDoorConfigurator.CoreEventBus) {
                window.MurphyDoorConfigurator.CoreEventBus.publish(
                  CONSTANTS.EVENTS.SYSTEM_EVENT_LISTENERS_INITIALIZED,
                  {
                    timestamp: performance.now(),
                    source: 'md-config-core-conductor.js',
                    method: METHOD_SIGNATURE,
                    domState: document.readyState,
                    metrics: {
                      setupDuration: setupDuration,
                      totalDuration: totalDuration,
                    },
                  }
                );

                // Publish separate performance event for monitoring
                window.MurphyDoorConfigurator.CoreEventBus.publish(
                  CONSTANTS.EVENTS.PERFORMANCE_EVENT_LISTENER_SETUP,
                  {
                    timestamp: performance.now(),
                    method: METHOD_SIGNATURE,
                    executionTime: setupDuration,
                    totalTime: totalDuration,
                    domState: document.readyState,
                  }
                );
              }
            }
          } catch (error) {
            const errorDuration = performance.now() - startTime;

            // Enhanced error handling with detailed context
            this.handleError('Event listener initialization failed', error, {
              method: METHOD_SIGNATURE,
              module: 'main',
              severity: 'high',
              recoverable: false,
              stage: 'initialization',
              domState: document.readyState,
              executionTime: errorDuration,
            });

            // Publish specific error event for monitoring systems
            if (window.MurphyDoorConfigurator.CoreEventBus) {
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.SYSTEM_EVENT_LISTENER_SETUP_FAILED,
                {
                  timestamp: performance.now(),
                  source: 'md-config-core-conductor.js',
                  method: METHOD_SIGNATURE,
                  domState: document.readyState,
                  executionTime: errorDuration,
                  error: {
                    message: error.message,
                    stack: error.stack,
                    type: error.name || 'Error',
                  },
                }
              );
            }
          }
        },

        /**
         * Initializes the application state using cached data from StateInitializer
         *
         * Acts as a gateway between the Main conductor and the stateManager, following
         * the Central Conductor Pattern by providing a controlled access point to state
         * initialization. Maintains proper separation of concerns by delegating the actual
         * state initialization to the dedicated stateManager module.
         *
         * Retrieves state data from the StateInitializer cache instead of accepting it
         * as a parameter, enforcing the new caching architecture.
         *
         * @returns {Object} The initialized core state reference
         * @throws {Error} If state initialization fails or StateInitializer not ready
         */
        initializeState() {
          // ✅ PHASE 3: PPD-only state initialization using StateInitializer cache
          isDev() &&
            console.log(
              '✅ STATE INIT: Using cached state data from StateInitializer (Phase 3)'
            );

          // Get StateInitializer and validate it's ready
          const stateInitializer =
            window.MurphyDoorConfigurator.stateInitializer;

          if (!stateInitializer || !stateInitializer.isReady()) {
            this.handleError(
              'StateInitializer not ready or not available',
              new Error(
                'StateInitializer must be initialized before state initialization'
              ),
              {
                method: 'initializeState',
                severity: 'critical',
                recoverable: false,
                context: {
                  hasStateInitializer: !!stateInitializer,
                  isReady: stateInitializer
                    ? stateInitializer.isReady()
                    : false,
                },
              }
            );
            return null;
          }

          // Get state data from StateInitializer cache
          const stateData = stateInitializer.getStateData();

          isDev() &&
            console.log(
              `🔥 State from PPD cache: ${stateData?.tabStructure?.size || 0} tabs, ${stateData?.categoryStructure?.size || 0} categories`
            );

          if (!stateData) {
            this.handleError('Cannot initialize state with empty data', null, {
              method: 'initializeState',
              severity: 'critical',
              recoverable: false,
            });
            return null;
          }

          try {
            const startTime = window.performance
              ? window.performance.now()
              : Date.now();

            // Publish event before state initialization
            window.MurphyDoorConfigurator.CoreEventBus.publish(
              CONSTANTS.EVENTS.SYSTEM_STATE_INITIALIZATION_STARTED,
              {
                timestamp: Date.now(),
              }
            );

            // Check if we need to adapt the data structure
            let dataToUse = stateData;
            if (
              !stateData.tabStructure ||
              !(stateData.tabStructure instanceof Map)
            ) {
              isDev() &&
                console.warn(
                  'State initialization: Structure mismatch detected, adapting data structure'
                );

              // Create a new data structure that matches what's expected
              dataToUse = {
                tabStructure: new Map(),
                categoryStructure: new Map(),
                productStructure: {
                  addOnProducts: new Map(),
                  baseProduct: {},
                },
              };

              // Convert tabs array to Map if available
              if (Array.isArray(stateData.tabs)) {
                stateData.tabs.forEach((tab) => {
                  dataToUse.tabStructure.set(tab.id, tab);
                });
              }

              isDev() &&
                console.log(
                  'Using adapted data structure for state initialization'
                );
            }

            // Delegate to state manager for actual initialization
            const stateResult =
              window.MurphyDoorConfigurator.stateManager.initializeState(
                dataToUse
              );

            // 🎯 AUTO-NAVIGATION FIX: Initialize tab completion indexes AFTER state-manager is initialized
            // This allows auto-navigation to know which category is "first incomplete" on fresh load
            if (
              window.MurphyDoorConfigurator.stateManager
                ?.initializeAllTabCompletionIndexes
            ) {
              window.MurphyDoorConfigurator.stateManager.initializeAllTabCompletionIndexes();
            }

            // Calculate performance metric
            const executionTime =
              (window.performance ? window.performance.now() : Date.now()) -
              startTime;

            // Check for slow initialization
            if (executionTime > 50) {
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.PERFORMANCE_STATE_INITIALIZATION_SLOW,
                {
                  executionTime,
                  dataSize: JSON.stringify(stateData).length,
                  timestamp: Date.now(),
                }
              );
            }

            // Publish success event
            window.MurphyDoorConfigurator.CoreEventBus.publish(
              CONSTANTS.EVENTS.SYSTEM_STATE_INITIALIZED,
              {
                success: true,
                timestamp: Date.now(),
                executionTime,
              }
            );

            return stateResult;
          } catch (error) {
            this.handleError('State initialization failed', error, {
              method: 'initializeState',
              data: {
                dataSize: stateData ? JSON.stringify(stateData).length : 0,
                hasTabStructure:
                  stateData && stateData.tabStructure ? true : false,
                hasCategoryStructure:
                  stateData && stateData.categoryStructure ? true : false,
                hasProductStructure:
                  stateData && stateData.productStructure ? true : false,
              },
              severity: 'critical',
              recoverable: false,
            });

            // Publish error event
            window.MurphyDoorConfigurator.CoreEventBus.publish(
              CONSTANTS.EVENTS.SYSTEM_STATE_INITIALIZATION_FAILED,
              {
                error: error.message,
                timestamp: Date.now(),
              }
            );

            return null;
          }
        },

        /**
         * Initializes the navigation structure by orchestrating the navigation manager.
         * This method is responsible for coordinating the creation of tabs and category elements
         * in the UI based on the Perfect Product Dictionary (PPD).
         *
         * CCP COMPLIANT: Navigation manager reads from PPD directly via conductor delegation.
         * No processedData parameter needed - all data accessed via PPD.
         *
         * @returns {Object} Result object with success status and metadata
         */
        initializeStructure() {
          const METHOD_SIGNATURE = 'initializeStructure';
          const startTime = performance.now();

          try {
            // Validate dependencies

            if (!window.MurphyDoorConfigurator.navigationManager) {
              this.handleError(
                'Navigation manager not available',
                new Error('Navigation manager dependency missing'),
                {
                  method: METHOD_SIGNATURE,
                  module: 'main',
                  severity: 'critical',
                  recoverable: false,
                }
              );
              return {
                success: false,
                error: 'Navigation manager not available',
              };
            }

            // Publish event for structure initialization start
            if (window.MurphyDoorConfigurator.CoreEventBus) {
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.SYSTEM_STRUCTURE_INITIALIZATION_STARTED,
                {
                  timestamp: performance.now(),
                  source: 'md-config-core-conductor.js',
                  method: METHOD_SIGNATURE,
                }
              );
            }

            // Delegate to navigation manager (reads from PPD directly)
            const result =
              window.MurphyDoorConfigurator.navigationManager.createNavigationStructure();

            // Initialize Product Virtualization Layer event listeners
            if (
              window.MurphyDoorConfigurator.navigationManager
                .initializeVirtualizationEventListeners
            ) {
              window.MurphyDoorConfigurator.navigationManager.initializeVirtualizationEventListeners();
            }

            // PENCIL ICON NOTE: Pencil icons are purely visual CSS elements that show on
            // category hover/open states. They don't require JavaScript initialization because
            // they leverage the existing category click handlers in md-config-core-event-handler.js.
            // CSS handles visibility transitions (opacity 0 -> 1 on hover/open).
            // No additional event listeners needed.

            // Calculate execution time
            const executionTime = performance.now() - startTime;

            // Publish completion event
            if (window.MurphyDoorConfigurator.CoreEventBus) {
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.SYSTEM_STRUCTURE_INITIALIZATION_COMPLETED,
                {
                  timestamp: performance.now(),
                  source: 'md-config-core-conductor.js',
                  method: METHOD_SIGNATURE,
                  executionTime: executionTime,
                  success: true,
                }
              );

              // Report slow structure initialization (over 100ms)
              if (executionTime > 100) {
                window.MurphyDoorConfigurator.CoreEventBus.publish(
                  CONSTANTS.EVENTS.PERFORMANCE_STRUCTURE_INIT_SLOW,
                  {
                    timestamp: performance.now(),
                    source: 'md-config-core-conductor.js',
                    method: METHOD_SIGNATURE,
                    executionTime: executionTime,
                    threshold: 100,
                  }
                );
              }
            }

            return { success: true, executionTime };
          } catch (error) {
            // Handle errors
            this.handleError('Structure initialization failed', error, {
              method: METHOD_SIGNATURE,
              module: 'main',
              severity: 'high',
              recoverable: true,
              executionTime: performance.now() - startTime,
            });

            // Publish failure event
            if (window.MurphyDoorConfigurator.CoreEventBus) {
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.SYSTEM_STRUCTURE_INITIALIZATION_FAILED,
                {
                  timestamp: performance.now(),
                  source: 'md-config-core-conductor.js',
                  method: METHOD_SIGNATURE,
                  error: {
                    message: error.message,
                    stack: error.stack,
                    type: error.name,
                  },
                }
              );
            }

            return { success: false, error: error.message };
          }
        },

        /* State Change Handlers */
        /**
         * Handles state changes throughout the configurator
         * This is the central method that orchestrates state transitions and delegates to appropriate handlers
         * Following the Central Conductor Pattern, all state changes flow through this method
         *
         * @param {string} action - The type of state change action
         * @param {Object} data - Data associated with the state change
         * @returns {Object|boolean} Result of the state change operation
         */
        handleStateChange(action, data) {
          const METHOD_SIGNATURE = 'main.handleStateChange';
          const startTime = performance.now();

          try {
            // Validate state change data
            const validState = this.validateStateChange(action, data);

            if (!validState) {
              const errorContext = {
                method: METHOD_SIGNATURE,
                action,
                dataKeys: data ? Object.keys(data) : [],
                severity: 'warning',
                recoverable: true,
              };

              this.handleError(
                `Invalid state change detected for action: ${action}`,
                new Error('State validation failed'),
                errorContext
              );

              // Publish event for monitoring
              if (window.MurphyDoorConfigurator.CoreEventBus) {
                window.MurphyDoorConfigurator.CoreEventBus.publish(
                  CONSTANTS.EVENTS.SYSTEM_VALIDATION_FAILED,
                  {
                    action,
                    timestamp: Date.now(),
                  }
                );
              }

              return false;
            }

            // Handle special cases with dedicated methods
            if (action === 'TAB_CHANGED') {
              const result = this.handleTabChange(action, data);
              return result;
            }

            if (action === 'CATEGORY_TOGGLE') {
              const result = this.handleCategoryToggle(action, data);
              return result;
            }

            if (action === 'PROGRESS_UPDATED') {
              const result = this.handleProgressUpdate(data.progress);
              return result;
            }

            isDev() &&
              console.log(
                `🧙‍♂️ Conductor: Handling state change '${action}' via delegation pattern`
              );

            // Publish state change event for modules to subscribe to
            // This implements Central Conductor Pattern by allowing modules to listen
            if (window.MurphyDoorConfigurator.CoreEventBus) {
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.STATE_CHANGE_COORDINATED,
                {
                  action,
                  data,
                  timestamp: Date.now(),
                  source: 'md-config-core-conductor.js:handleStateChange',
                }
              );
            }

            // Handle state changes through proper delegation to specialized modules
            // Note: This maintains backward compatibility while enabling event-driven architecture
            let result;
            switch (action) {
              case 'INIT_IMAGE_LAYERS':
                result = this.stateManager.initializeImageLayers(data);
                break;

              case 'UPDATE_IMAGE_LAYER':
                result = this.stateManager.updateImageLayerState(
                  data.categoryId,
                  data.layerData
                );
                break;

              case 'SHOW_IMAGE_LAYERS':
                result = this.stateManager.handleShowImageLayers(data);
                if (result && result.success) {
                  window.MurphyDoorConfigurator.uiUpdater.updateDisplay(
                    'IMAGE_LAYERS_SHOWN',
                    {
                      frontView: data.frontView,
                      backView: data.backView,
                    }
                  );
                }
                break;

              case 'SELECTION_BROWSING':
                // C1: track whether this browsing selection is forbidden so the
                // click=apply chokepoint (end of this case) never auto-applies a
                // conflicting option — it only previews it.
                var browsingIsForbidden = false;
                // 🔒 CRITICAL: Validate browsing selection against forbidden rules FIRST
                if (
                  data.variantId &&
                  window.MurphyDoorConfigurator.forbiddenValidator
                ) {
                  const appliedSelections = this.getAppliedSelections();
                  const forbiddenRules = this.getUnifiedForbiddenRules();

                  isDev() &&
                    console.log('🔍 CONDUCTOR: Validating browsing selection', {
                      variantId: data.variantId,
                      categoryId: data.categoryId,
                      hasValidator: Boolean(
                        window.MurphyDoorConfigurator.forbiddenValidator
                      ),
                      hasRules: Boolean(forbiddenRules?.unifiedRules),
                      rulesCount: forbiddenRules?.unifiedRules?.size || 0,
                    });

                  const validationResult =
                    window.MurphyDoorConfigurator.forbiddenValidator.validateItemAgainstSelections(
                      String(data.variantId),
                      appliedSelections,
                      forbiddenRules
                    );

                  if (validationResult.isConflicted) {
                    browsingIsForbidden = true;
                    isDev() &&
                      console.warn(
                        '🚫 CONDUCTOR: Browsing selection is FORBIDDEN',
                        {
                          variantId: data.variantId,
                          conflicts: validationResult.conflicts,
                          conflictCount: validationResult.conflicts.length,
                        }
                      );

                    // Publish forbidden state event for UI to handle
                    if (window.MurphyDoorConfigurator.CoreEventBus) {
                      window.MurphyDoorConfigurator.CoreEventBus.publish(
                        CONSTANTS.EVENTS.UI_SELECTION_FORBIDDEN,
                        {
                          variantId: data.variantId,
                          categoryId: data.categoryId,
                          conflicts: validationResult.conflicts,
                          timestamp: Date.now(),
                        }
                      );
                    }

                    // Under click=apply, refuse the click outright: no browse, no
                    // ring, no title, and the customer's current answer stays
                    // exactly as it was. See shouldBlockForbiddenBrowseDecision.
                    // Every other mode keeps previewing (the apply step blocks).
                    if (
                      shouldBlockForbiddenBrowseDecision(
                        {
                          browsingIsForbidden: true,
                          variantId: data.variantId,
                          suppressClickApply: data.suppressClickApply,
                          isSliderCategory: false,
                          isSimplifiedBulk: !!this._simplifiedCategoryIds?.has(
                            data.categoryId
                          ),
                        },
                        this._isClickApplyEnabled()
                      )
                    ) {
                      // The only way to reach this is a tile that LOOKED
                      // available -- the event handler refuses greyed ones
                      // before they get here. Re-grey the page from current
                      // state so the dead click explains itself instead of
                      // silently doing nothing.
                      this._refreshPreemptiveForbidden(
                        data.categoryId,
                        'conductor.forbidden_click_blocked'
                      );
                      break;
                    }
                  }
                } else {
                  // 🐛 DEBUG: Log why validation was skipped
                  isDev() &&
                    console.warn('⚠️ CONDUCTOR: BROWSING validation SKIPPED', {
                      hasVariantId: Boolean(data.variantId),
                      variantId: data.variantId,
                      hasValidator: Boolean(
                        window.MurphyDoorConfigurator.forbiddenValidator
                      ),
                      validatorType:
                        typeof window.MurphyDoorConfigurator
                          ?.ForbiddenValidator,
                      categoryId: data.categoryId,
                      reason: !data.variantId
                        ? 'NO_VARIANT_ID'
                        : 'NO_VALIDATOR',
                    });
                }

                // 🎥 CUSTOMER VIEW INTENT -- must be recorded BEFORE anything
                // automatic can move the camera.
                //
                // WHY HERE AND NOWHERE ELSE: measured on the dev server, a
                // coordinated SELECTION_BROWSING is published for the
                // customer's OWN selection and for nothing else. A wood click
                // that cascaded 15 further categories produced exactly ONE
                // BROWSING (Wood & Finish) and fifteen bare APPLY_SELECTIONs;
                // page bootstrap produced two APPLY_SELECTIONs (Width, Height)
                // and no BROWSING at all. So this branch IS the customer, which
                // is what makes the intent trustworthy.
                this._recordCustomerViewIntent(data);

                // 🧙‍♂️ EPIC FIX: Check auto-toggle FIRST to prevent PNG layer race condition
                const autoToggleResult = this.handleAutoImageViewToggle(
                  data.productId,
                  data.variantId,
                  data.categoryId
                );
                const willToggleView = autoToggleResult?.action === 'toggled';
                const pngLayerDelay = willToggleView ? 850 : 0; // 850ms delay if view will change

                isDev() &&
                  console.log('🎯 CONDUCTOR: PNG layer timing decision', {
                    willToggleView,
                    pngLayerDelay,
                    toggleResult: autoToggleResult,
                  });

                // 🔥 COMPOSITE PRODUCT FIX: Always pass DOM-sourced categoryId as override.
                // For composite products, the native category in selectionDisplayDataMap
                // may differ from the category where the product is displayed (e.g., "Paint Grade"
                // native to "Wood & Finish" but displayed in "Front Casing").
                // For non-composite products, the override matches the native category (harmless).
                result = this.stateManager.handleSelectionState(
                  data.productId,
                  data.variantId,
                  SELECTION_STATES.BROWSING,
                  pngLayerDelay,
                  data.element,
                  data.categoryId || null
                );

                if (result && result.success) {
                  this.handlePriceUpdates(data, SELECTION_STATES.BROWSING);
                  this.handleShippingUpdates(data, SELECTION_STATES.BROWSING);
                  this.handleSpaceCalculationUpdates(
                    data,
                    SELECTION_STATES.BROWSING
                  );

                  // 🧬 CompositeProduct Enhancement: Pass additional context for CompositeProducts
                  const isCompositeProduct =
                    (data.groupId && data.groupName) || data.isCompositeProduct;
                  if (isCompositeProduct) {
                    isDev() &&
                      console.log(
                        '🎁 Conductor: Detected CompositeProduct selection, passing enhanced context to UI updater',
                        {
                          groupId: data.groupId,
                          groupName: data.groupName,
                          hasCompositeContext: !!data.compositeContext,
                        }
                      );

                    // Use the enhanced composite context if provided, otherwise fall back to basic context
                    const compositeContext = data.compositeContext || {
                      isCompositeProduct: true,
                      groupId: data.groupId,
                      groupName: data.groupName,
                      categoryId: data.categoryId,
                      element: data.element,
                    };

                    window.MurphyDoorConfigurator.uiUpdater
                      .updateSelectionDisplay(
                        result.key,
                        SELECTION_STATES.BROWSING,
                        result.displayTitle,
                        result.price,
                        result.productTitle,
                        compositeContext
                      )
                      .catch((error) => {
                        this.handleError(
                          'Async UI update for selection display failed',
                          error,
                          {
                            method: 'handleStateChange',
                            action: 'SELECTION_BROWSING',
                            severity: 'warning',
                            isComposite: true,
                          }
                        );
                      });
                  } else {
                    window.MurphyDoorConfigurator.uiUpdater
                      .updateSelectionDisplay(
                        result.key,
                        SELECTION_STATES.BROWSING,
                        result.displayTitle,
                        result.price,
                        result.productTitle
                      )
                      .catch((error) => {
                        this.handleError(
                          'Async UI update for selection display failed',
                          error,
                          {
                            method: 'handleStateChange',
                            action: 'SELECTION_BROWSING',
                            severity: 'warning',
                          }
                        );
                      });
                  }
                }

                // HYBRID PATTERN: Conductor is the SINGLE SOURCE OF TRUTH for SELECTION_BROWSING.
                // All duplicate publications from state-manager and selection-coordinator have been removed.
                // This typed event is published AFTER full orchestration (validation, auto-toggle,
                // state handling, price/shipping/space updates, and UI display updates) so subscribers
                // receive a consistent, fully-coordinated state.
                if (window.MurphyDoorConfigurator.CoreEventBus) {
                  // 1. TYPED EVENT: For business logic coordination (ui-updater, forbidden-validator,
                  //    image-layer-coordinator subscribe to this)
                  window.MurphyDoorConfigurator.CoreEventBus.publish(
                    CONSTANTS.EVENTS.SELECTION_BROWSING,
                    {
                      productId: data.productId,
                      categoryId: data.categoryId,
                      variantId: data.variantId,
                      productType: data.productType,
                      validationResult:
                        data.variantId &&
                        window.MurphyDoorConfigurator.forbiddenValidator
                          ? 'validated'
                          : 'skipped',
                      autoToggleResult:
                        typeof autoToggleResult !== 'undefined'
                          ? autoToggleResult
                          : null,
                      isComposite: data.isComposite || false,
                      groupId: data.groupId || null,
                      groupName: data.groupName || null,
                      timestamp: Date.now(),
                      source: 'conductor',
                    }
                  );
                }

                // SIMPLIFIED MODE: Track browsing selections for "Build My Door" validation.
                // All three simplified categories stay in BROWSING state until the user
                // clicks "Build My Door", which bulk-applies them via UI_SIMPLIFIED_BUILD_MY_DOOR_CLICKED.
                if (
                  this._simplifiedCategoryIds?.size > 0 &&
                  data.categoryId &&
                  data.variantId &&
                  result?.success &&
                  this._simplifiedCategoryIds.has(data.categoryId)
                ) {
                  this._simplifiedBrowsingSelections.set(data.categoryId, {
                    productId: data.productId,
                    variantId: data.variantId,
                  });
                  isDev() &&
                    console.log(
                      '[Conductor] Simplified category browsing tracked:',
                      data.categoryId,
                      data.variantId,
                      '(' +
                        this._simplifiedBrowsingSelections.size +
                        '/' +
                        this._simplifiedCategoryIds.size +
                        ' ready)'
                    );
                  // Nudge the progress bar by one 33%-increment. The Map is
                  // already updated above so updateProgressBar (via the override
                  // in ui-updater) will read the correct browsedCount.
                  this.handleProgressUpdate(0);
                }

                // ============================================================
                // C1: CLICK = APPLY CHOKEPOINT
                // When the click_apply experience is enabled, a successful,
                // non-forbidden, non-slider, non-simplified-bulk browsing
                // selection is applied immediately in the same tick — no
                // separate save/apply step. Centralising here (rather than at
                // every click branch) means the composite categoryIdOverride
                // and all browsing context are preserved by construction.
                // MULTI_VARIANT_START never reaches this case (it publishes the
                // browsing EVENT directly), so the null-variant grid-open can't
                // trip it; the data.variantId guard is a second belt.
                // ============================================================
                if (
                  this._shouldClickApply({
                    success: !!(result && result.success),
                    variantId: data.variantId,
                    browsingIsForbidden: browsingIsForbidden,
                    // Sliders flag their own browsing calls (the per-drag path
                    // bypasses this case; the render fallback sets this true).
                    suppressClickApply: data.suppressClickApply,
                    isSliderCategory: false,
                    isSimplifiedBulk: !!this._simplifiedCategoryIds?.has(
                      data.categoryId
                    ),
                  })
                ) {
                  // The nested handleStateChange('APPLY_SELECTION') runs in its
                  // own call frame with its own `result` binding, so it cannot
                  // clobber this case's `result` — no preserve/restore needed.
                  this.handleStateChange('APPLY_SELECTION', {
                    productId: data.productId,
                    variantId: data.variantId,
                    categoryId: data.categoryId,
                    groupId: data.groupId || null,
                    groupName: data.groupName || null,
                    isComposite: data.isComposite || false,
                    compositeContext: data.compositeContext || null,
                    auto_applied: false, // user action → normal animations + provenance
                    isOneStep: true,
                    source: 'click_apply',
                  });
                }

                break;

              case 'MULTI_VARIANT_START':
                // 🧙‍♂️ Enhanced CDD Logging for CompositeProduct Auto-Selection Logic
                isDev() &&
                  console.log(
                    '🎯 CCP DELEGATION: MULTI_VARIANT_START processing (CompositeProduct auto-selection logic)'
                  );
                isDev() &&
                  console.log('📊 MULTI_VARIANT_START DATA:', {
                    productId: data.productId,
                    categoryId: data.categoryId,
                    isComposite: data.isComposite,
                    groupId: data.groupId,
                    groupName: data.groupName,
                    ccpCompliant: true,
                    autoSelectionEligible: true,
                  });

                // 🎥 Opening a drill-down IS the customer engaging with this
                // category, so the camera intent belongs here too -- not only
                // on the tier-2 pick that follows. Without this, clicking a
                // side-neutral category's tile (Locking Systems) left a
                // previous category's intent standing until a variant was
                // actually chosen, and clicking a BACK category's composite
                // tile did not establish 'back' until the tier-2 pick.
                //
                // MULTI_VARIANT_START is only ever reached from a real tile
                // click (the event handler's product-group path); the eager
                // `?config=` tier resume calls generateVariantLayout directly
                // and never routes through here, so this stays customer-only.
                this._recordCustomerViewIntent(data);

                result = this.stateManager.handleMultiVariantStart(data);
                if (result) {
                  // Handle composite products differently - they don't exist in product structure
                  if (data.isComposite === true) {
                    isDev() &&
                      console.log(
                        '🎁 Conductor: CompositeProduct variant view already generated by handleMultiVariantStart - SKIPPING showVariantLayout'
                      );
                    // ✅ COMPOSITE PRODUCT FIX: handleMultiVariantStart() already calls generateCompositeVariantView()
                    // (see md-config-core-state-manager.js lines 922-925)
                    // CompositeProducts don't exist in regular product structure, so showVariantLayout() would fail!
                    // The variant view is already generated, so we're done here.
                  } else {
                    // Standard product handling.
                    //
                    // This used to be an `if (displayMode === 'directVariant')`
                    // / `else` pair whose two bodies were byte-identical, so
                    // the branch decided nothing. Collapsed rather than kept,
                    // because the return value now matters and duplicating the
                    // rollback below into two identical arms is how one of them
                    // eventually drifts.
                    const variantViewOpened =
                      window.MurphyDoorConfigurator.uiUpdater.showVariantLayout(
                        result.categoryId,
                        result.productId,
                        data.element
                      );

                    // showVariantLayout() returns false without rendering
                    // anything when it cannot build a grid -- a missing
                    // category element, a product absent from the PPD, or a
                    // product with only one variant. handleMultiVariantStart()
                    // has ALREADY set variantTransitionStates[category] by this
                    // point, and the SUB-PATH 3 guard in the event handler
                    // (`if (!transitionState)`) treats that flag as "a grid is
                    // open, do nothing". Left set, it silently swallows every
                    // subsequent click in this category -- including clicks on
                    // sibling products that would have opened fine -- for the
                    // rest of the session, with no error surfaced anywhere.
                    //
                    // handleCategoryToggle() and handleBackClick() already clear
                    // this flag, but neither runs in apo_style, where every
                    // category is permanently open and there is no back button
                    // to press. Rolling back here means a failed open costs the
                    // customer one dead click instead of the whole category.
                    if (variantViewOpened === false) {
                      isDev() &&
                        console.warn(
                          '⚠️ CONDUCTOR: showVariantLayout did not open a grid for product ' +
                            result.productId +
                            ' in "' +
                            result.categoryId +
                            '" - clearing variantTransitionState so the category stays clickable'
                        );
                      this.stateManager.clearVariantTransitionState(
                        result.categoryId
                      );
                    }
                  }

                  // HYBRID PATTERN: Publish SELECTION_BROWSING event after variant browser opens
                  // This ensures ui-updater can apply material filtering when browser is displayed
                  if (window.MurphyDoorConfigurator.CoreEventBus) {
                    window.MurphyDoorConfigurator.CoreEventBus.publish(
                      CONSTANTS.EVENTS.SELECTION_BROWSING,
                      {
                        productId: result.productId || data.productId,
                        categoryId: result.categoryId || data.categoryId,
                        variantId: null, // Multi-variant start = browsing mode, no variant selected yet
                        productType: 'multi-variant',
                        validationResult: null, // No validation for browsing state (not applying variant)
                        autoToggleResult: null, // No auto-toggle for browsing state
                        isComposite: data.isComposite || false,
                        groupId: data.groupId || null,
                        groupName: data.groupName || null,
                        timestamp: Date.now(),
                        source: 'conductor-multi-variant-start',
                      }
                    );

                    isDev() &&
                      console.log(
                        '📢 Conductor: Published SELECTION_BROWSING event for MULTI_VARIANT_START',
                        {
                          productId: result.productId || data.productId,
                          categoryId: result.categoryId || data.categoryId,
                        }
                      );
                  }
                }
                break;

              case 'BACK_CLICKED':
                // click-apply only navigates; classic/dual unapply the browse
                // being abandoned. See backClickModeDecision.
                result =
                  backClickModeDecision(this._isClickApplyEnabled()) ===
                  'navigate'
                    ? this.stateManager.handleBackNavigation(data.categoryId)
                    : this.stateManager.handleBackClick(data.categoryId);
                if (result && !result.error) {
                  this.stateManager.getStateMaps().core.inVariantTransition = false;

                  window.MurphyDoorConfigurator.uiUpdater.restoreThumbnailView(
                    result.categoryId,
                    result.clearSelection
                  );

                  // 🚨 A CLEARED ANSWER MUST TELL THE REST OF THE SYSTEM.
                  // handleBackClick() only mutates state maps. This case used to
                  // stop there, so nothing downstream heard about it: the
                  // forbidden rules that depended on the cleared answer were never
                  // recalculated (options it had disabled stayed disabled, options
                  // it had enabled stayed enabled), ?config= kept the stale value,
                  // and progress never dropped -- Add to Cart stayed enabled on a
                  // door with a category missing.
                  //
                  // 🚨 THIS IS THE SUBSET THIS BUG NEEDS, NOT THE FULL UNAPPLY
                  // CONTRACT. The × unapply subscription
                  // (SELECTION_USER_UNAPPLY_REQUESTED) additionally publishes
                  // SELECTION_USER_UNAPPLY_COMPLETED, whose subscribers
                  // invalidate the cart line-item cache, drop ghost image layers,
                  // cascade material preferences and dependent categories, and
                  // auto-navigate. Deliberately NOT fired here: this branch is
                  // classic/dual only (click-apply returns clearSelection:false),
                  // where a drill-down exists only while BROWSING -- before
                  // Apply -- so there is no applied answer for them to tear down.
                  // If a future change makes this branch reachable on an APPLIED
                  // category, wire those events too, or route through the ×
                  // pipeline instead of hand-copying more of it.
                  if (result.clearSelection) {
                    const clearedTabId = this.stateManager.getCategoryTab(
                      result.categoryId
                    );
                    if (clearedTabId) {
                      window.MurphyDoorConfigurator.uiUpdater.updateDisplay(
                        'TAB_STATUS_UPDATE',
                        { tabId: String(clearedTabId), isComplete: false }
                      );
                    }

                    publishEvent(CONSTANTS.EVENTS.STATE_SELECTION_CLEARED, {
                      categoryId: result.categoryId,
                      source: 'conductor.back_clicked',
                      timestamp: Date.now(),
                    });

                    this.stateManager.persistState();

                    publishEvent(CONSTANTS.EVENTS.UI_PROGRESS_UPDATE_REQUESTED, {
                      source: 'conductor.back_clicked',
                      timestamp: Date.now(),
                    });
                  }

                  // Update price after selection is cleared
                  // This ensures price reflects removal of browsing/applied selection
                  this.handlePriceUpdates(data, SELECTION_STATES.BROWSING);
                }
                break;

              case 'APPLY_SELECTION':
                isDev() &&
                  console.log('🧙‍♂️ CONDUCTOR: APPLY_SELECTION case entered', {
                    variantId: data.variantId,
                    categoryId: data.categoryId,
                    productId: data.productId,
                    timestamp: Date.now(),
                  });

                // 🎯 PPD-FIRST: Auto-resolve variantId for single-variant products BEFORE validation
                // This ensures URL-loaded and cascade-applied selections have variantIds for validation
                // NOTE: This logic is intentionally duplicated in md-config-core-state-manager.js
                // as defense-in-depth. If modified, BOTH locations must be updated in sync!
                if (!data.variantId && data.productId) {
                  const ppd = this.getPerfectProductDictionary();
                  const resolvedVariantId = ppd?.productIdToVariantIdMap?.get(
                    data.productId
                  );
                  if (resolvedVariantId) {
                    isDev() &&
                      console.log(
                        `🔄 CONDUCTOR: Auto-resolved variantId ${resolvedVariantId} for product ${data.productId}`
                      );
                    data.variantId = resolvedVariantId;
                  }
                }

                // 🔒 CRITICAL: Validate applied selection against forbidden rules FIRST
                if (
                  data.variantId &&
                  window.MurphyDoorConfigurator.forbiddenValidator
                ) {
                  const appliedSelections = this.getAppliedSelections();
                  const forbiddenRules = this.getUnifiedForbiddenRules();

                  isDev() &&
                    console.log('🔍 CONDUCTOR: Validating APPLIED selection', {
                      variantId: data.variantId,
                      categoryId: data.categoryId,
                      hasValidator: Boolean(
                        window.MurphyDoorConfigurator.forbiddenValidator
                      ),
                      hasRules: Boolean(forbiddenRules?.unifiedRules),
                      rulesCount: forbiddenRules?.unifiedRules?.size || 0,
                      appliedSelectionsCount: appliedSelections?.size || 0,
                    });

                  const validationResult =
                    window.MurphyDoorConfigurator.forbiddenValidator.validateItemAgainstSelections(
                      String(data.variantId),
                      appliedSelections,
                      forbiddenRules
                    );

                  if (validationResult.isConflicted) {
                    isDev() &&
                      console.error(
                        '🚫 CONDUCTOR: APPLIED selection is FORBIDDEN - BLOCKING!',
                        {
                          variantId: data.variantId,
                          conflicts: validationResult.conflicts,
                          conflictCount: validationResult.conflicts.length,
                        }
                      );

                    // Publish forbidden state event for UI to handle
                    if (window.MurphyDoorConfigurator.CoreEventBus) {
                      window.MurphyDoorConfigurator.CoreEventBus.publish(
                        CONSTANTS.EVENTS.UI_SELECTION_FORBIDDEN,
                        {
                          variantId: data.variantId,
                          categoryId: data.categoryId,
                          conflicts: validationResult.conflicts,
                          timestamp: Date.now(),
                        }
                      );
                    }

                    // 🛑 BLOCK the selection - do NOT proceed with applying
                    isDev() &&
                      console.warn(
                        '🛑 CONDUCTOR: Selection BLOCKED due to forbidden rule violation'
                      );

                    // A REJECTED COMMIT CAN LEAVE THE CATEGORY UNANSWERED.
                    // A slider move already entered BROWSING, which deleted
                    // the category from the applied Map; refusing this commit
                    // means nothing puts it back. Without this the check mark,
                    // the Summary row, the progress counter and the cart
                    // snapshot all kept claiming the stop the slider passed
                    // THROUGH, and Add to Cart shipped a door with no Door
                    // Depth (production, 2026-09-29). Only when the customer
                    // is still browsing this category: a rejected pick on a
                    // category that keeps its applied answer is a no-op there.
                    {
                      const rejectedMaps = this.getStateMaps();
                      if (
                        rejectedMaps &&
                        rejectedMaps.browsingSelections &&
                        rejectedMaps.browsingSelections.has(data.categoryId)
                      ) {
                        this._demoteUnappliedCategory(
                          data.categoryId,
                          'conductor.forbidden_commit_rejected'
                        );
                      }
                    }
                    break; // Exit the switch case without applying the selection
                  }
                } else {
                  // 🐛 DEBUG: Log why validation was skipped
                  isDev() &&
                    console.warn('⚠️ CONDUCTOR: APPLY validation SKIPPED', {
                      hasVariantId: Boolean(data.variantId),
                      variantId: data.variantId,
                      hasValidator: Boolean(
                        window.MurphyDoorConfigurator.forbiddenValidator
                      ),
                      validatorType:
                        typeof window.MurphyDoorConfigurator
                          ?.ForbiddenValidator,
                      categoryId: data.categoryId,
                      reason: !data.variantId
                        ? 'NO_VARIANT_ID'
                        : 'NO_VALIDATOR',
                    });
                }

                result = this.stateManager.handleApplySelection(
                  data.categoryId,
                  data.productId,
                  data.variantId
                );

                if (result && result.success) {
                  this.handlePriceUpdates(data, SELECTION_STATES.APPLIED);
                  this.handleShippingUpdates(data, SELECTION_STATES.APPLIED);
                  this.handleSpaceCalculationUpdates(
                    data,
                    SELECTION_STATES.APPLIED
                  );

                  // Handle all progress updates in one coordinated operation
                  const progressUpdate = this.handleSelectionProgress(result);

                  // Batch UI updates through a single coordinated call
                  // Pass auto_applied flag so UI can skip animations for cascade auto-selections
                  this.handleSelectionUIUpdates(
                    result,
                    progressUpdate,
                    data.auto_applied
                  );
                  this.handleProgressUpdate(progressUpdate.totalProgress);

                  // C2: record provenance of this applied selection from
                  // data.source. Drives "Included" tags, the swap-in-place ✕
                  // visibility (C3), and customized/defaulted analytics counts.
                  if (
                    data.categoryId &&
                    typeof this.stateManager.setSelectionProvenance ===
                      'function'
                  ) {
                    this.stateManager.setSelectionProvenance(
                      data.categoryId,
                      this._mapSourceToProvenance(
                        data.source,
                        data.auto_applied
                      )
                    );
                  }

                  // 🔥 CCP COMPLIANCE: Persist state to URL after successful APPLIED state transition
                  isDev() &&
                    console.log(
                      '🧙‍♂️ CONDUCTOR: Delegating state persistence after APPLY_SELECTION',
                      {
                        categoryId: data.categoryId,
                        productId: data.productId,
                        variantId: data.variantId,
                        timestamp: Date.now(),
                      }
                    );

                  const persistSuccess = this.stateManager.persistState();
                  if (!persistSuccess) {
                    isDev() &&
                      console.warn(
                        '⚠️ CONDUCTOR: State persistence failed after APPLY_SELECTION'
                      );
                  }

                  // Clear variantTransitionState so the thumbnail is re-clickable after a future unapply.
                  // handleBackClick() also clears this, but doing it here keeps the state accurate immediately.
                  this.stateManager.clearVariantTransitionState(
                    data.categoryId
                  );

                  // 🎨 MATERIAL FILTERING: Publish selection:variant_applied event for material preference coordinator
                  // This enables bidirectional material filtering when wood_sku/finish_sku variants are applied
                  if (
                    data.variantId &&
                    window.MurphyDoorConfigurator.CoreEventBus
                  ) {
                    const ppd = this.getPerfectProductDictionary();
                    // Parse variantId to number (PPD variantIdToVariantObjectMap uses numeric keys)
                    const numericVariantId =
                      typeof data.variantId === 'string'
                        ? parseInt(data.variantId)
                        : data.variantId;

                    if (
                      ppd &&
                      ppd.variantIdToVariantObjectMap &&
                      ppd.variantIdToVariantObjectMap.has(numericVariantId)
                    ) {
                      const variant =
                        ppd.variantIdToVariantObjectMap.get(numericVariantId);
                      window.MurphyDoorConfigurator.CoreEventBus.publish(
                        CONSTANTS.EVENTS.SELECTION_VARIANT_APPLIED,
                        {
                          variantId: data.variantId,
                          productId: data.productId,
                          categoryId: data.categoryId,
                          variant: variant, // Full variant object with wood_sku/finish_sku
                          auto_applied: !!data.auto_applied, // Flag for cascade auto-selection tracking
                          // Provenance of the APPLY_SELECTION caller (e.g.
                          // 'build_preset', 'url_restoration', 'click_apply').
                          // `source` below names the publisher, not the caller.
                          applySource:
                            typeof data.source === 'string'
                              ? data.source
                              : null,
                          timestamp: Date.now(),
                          source: 'conductor:APPLY_SELECTION',
                        }
                      );
                      isDev() &&
                        console.log(
                          `🎨 CONDUCTOR: Published selection:variant_applied event for variant ${numericVariantId} (wood: ${variant.wood_sku || 'none'}, finish: ${variant.finish_sku || 'none'})`
                        );
                    } else {
                      isDev() &&
                        console.warn(
                          `⚠️ CONDUCTOR: Could not publish selection:variant_applied - variant ${numericVariantId} not found in PPD`
                        );
                    }
                  } else {
                    isDev() &&
                      console.warn(
                        `⚠️ CONDUCTOR: Could not publish selection:variant_applied - missing requirements`,
                        {
                          hasVariantId: !!data.variantId,
                          hasEventBus:
                            !!window.MurphyDoorConfigurator.CoreEventBus,
                        }
                      );
                  }

                  // Auto-navigation now handled by md-config-bl-auto-navigation-coordinator.js

                  // ═══════════════════════════════════════════════════════════════
                  // AUTO_APPLY_OPTION: Evaluate forbidden rules for auto-apply
                  // ═══════════════════════════════════════════════════════════════
                  // SAFEGUARD 1: Skip auto-applied variants to prevent infinite loops.
                  // When a variant is auto-applied (by this very mechanism), we must NOT
                  // re-evaluate AUTO_APPLY rules for it, or we get A -> B -> A -> ...
                  //
                  // DESIGN DECISION (YAGNI + Efficiency):
                  //   This guard enforces SINGLE-LEVEL auto-apply. The cascade coordinator
                  //   (md-config-bl-cascade-auto-selection-coordinator.js) has the architecture
                  //   ready for multi-level cascading (circular detection, chain depth tracking),
                  //   but we intentionally block it here until a real business need arises.
                  //   To enable multi-level cascading in the future:
                  //     1. Remove the `data.auto_applied !== true` condition below
                  //     2. Add MAX_CASCADE_DEPTH enforcement in the cascade coordinator
                  //     3. Write UAT tests for A->B->C chains and circular edge cases
                  if (
                    data.auto_applied !== true &&
                    data.variantId &&
                    window.MurphyDoorConfigurator.forbiddenValidator
                  ) {
                    try {
                      const autoApplyForbiddenRules =
                        this.getUnifiedForbiddenRules();
                      const autoApplySelections =
                        this.getAppliedSelectionsMap();
                      const autoApplyPpd = this.getPerfectProductDictionary();

                      isDev() &&
                        console.log(
                          '🔗 CONDUCTOR: AUTO_APPLY evaluation starting',
                          {
                            variantId: data.variantId,
                            rulesCount:
                              autoApplyForbiddenRules?.unifiedRules?.size || 0,
                            appliedSelectionsCount:
                              autoApplySelections?.size || 0,
                            hasPpd: !!autoApplyPpd?.variantIdToVariantObjectMap,
                          }
                        );

                      if (
                        autoApplyForbiddenRules?.unifiedRules?.size > 0 &&
                        autoApplyPpd?.variantIdToVariantObjectMap
                      ) {
                        const autoApplyResults =
                          window.MurphyDoorConfigurator.forbiddenValidator.evaluateAutoApplyRulesOnApply(
                            String(data.variantId),
                            autoApplySelections,
                            autoApplyForbiddenRules,
                            autoApplyPpd.variantIdToVariantObjectMap
                          );

                        isDev() &&
                          console.log(
                            '🔗 CONDUCTOR: AUTO_APPLY evaluation complete - ' +
                              autoApplyResults.length +
                              ' rules matched',
                            {
                              variantId: data.variantId,
                              matchCount: autoApplyResults.length,
                            }
                          );

                        if (autoApplyResults.length > 0) {
                          isDev() &&
                            console.log(
                              '🔗 CONDUCTOR: AUTO_APPLY_OPTION rules matched: ' +
                                autoApplyResults.length,
                              {
                                variantId: data.variantId,
                                results: autoApplyResults,
                              }
                            );

                          // Publish auto-apply triggered events for each matching rule
                          // The cascade coordinator will handle the actual application
                          autoApplyResults.forEach((autoApplyResult) => {
                            window.MurphyDoorConfigurator.CoreEventBus.publish(
                              CONSTANTS.EVENTS.FORBIDDEN_AUTO_APPLY_TRIGGERED,
                              {
                                ruleId: autoApplyResult.ruleId,
                                ruleName: autoApplyResult.ruleName,
                                conditionVariantId: String(data.variantId),
                                targetVariantId:
                                  autoApplyResult.targetVariantId,
                                targetCategoryId:
                                  autoApplyResult.targetCategoryId,
                                targetProductId:
                                  autoApplyResult.targetProductId,
                                reason: autoApplyResult.reason,
                                priority: autoApplyResult.priority,
                                timestamp: Date.now(),
                                source: 'conductor:auto_apply_evaluation',
                              }
                            );
                          });
                        }

                        // ═══════════════════════════════════════════════════════
                        // ENFORCED_PAIR: opt-in hard pairing for AUTO_APPLY rules
                        // ═══════════════════════════════════════════════════════
                        // Evaluated HERE because this is the one path every
                        // customer apply goes through (click-apply, second-click,
                        // Apply button, implicit apply) with its categoryId, and
                        // SELECTION_VARIANT_APPLIED has already been published
                        // above, so the cascade coordinator's manualSelections is
                        // current. The validator decides (pure), the coordinator
                        // orchestrates, state changes come back through the
                        // conductor (APPLY_SELECTION / clearCategoryForEnforcedPair).
                        this._publishEnforcedPairActions(
                          data.variantId,
                          data.categoryId
                        );
                      }
                    } catch (autoApplyError) {
                      isDev() &&
                        console.error(
                          '❌ CONDUCTOR: AUTO_APPLY evaluation failed:',
                          autoApplyError
                        );
                    }
                  }
                }
                break;

              case 'ADD_TO_CART': {
                isDev() &&
                  console.log('🧙‍♂️ Conductor: ADD_TO_CART case triggered');

                // PRE-CART BROWSING FLUSH GATE
                // Apply all lingering BROWSING selections before reading the cart snapshot so
                // that every user choice (including un-navigated-past categories) is captured
                // in the cart line items and the serialized _Configuration_URL.
                // auto_applied:true suppresses the SAVING badge animation and skips the
                // AUTO_APPLY cascade evaluation guard (single-level cascade is already applied).
                if (this.hasBrowsingSelections()) {
                  const flushStateMaps = this.getStateMaps();
                  const browsingEntries = [
                    ...flushStateMaps.browsingSelections.entries(),
                  ];
                  isDev() &&
                    console.log(
                      `🧙‍♂️ Conductor: Flushing ${browsingEntries.length} browsing selection(s) before ADD_TO_CART`
                    );

                  let flushFailed = false;
                  let enforcedPairAbort = false;
                  let forbiddenRequiredAbort = false;
                  const flushPpd = this.getPerfectProductDictionary();
                  const requiredCategorySet = new Set(
                    (flushPpd &&
                      flushPpd.categoryMetadata &&
                      flushPpd.categoryMetadata.allRequiredCategoryIdsArray) ||
                      []
                  );
                  for (const [categoryId, browsingData] of browsingEntries) {
                    const appliedData =
                      flushStateMaps.appliedSelections.get(categoryId);
                    const alreadyApplied =
                      appliedData &&
                      appliedData.productId === browsingData.productId &&
                      appliedData.variantId === browsingData.variantId;

                    if (!alreadyApplied) {
                      // C1: is this browsing selection a forbidden preview the
                      // user left on screen (click=apply only previews forbidden
                      // options, never applies them)? If so, it will never apply
                      // — skip and clear it rather than abort the whole cart.
                      // Genuine (non-forbidden) apply failures still abort +
                      // notify as before, so no silent data loss.
                      let isForbiddenPreview = false;
                      if (
                        browsingData.variantId &&
                        window.MurphyDoorConfigurator.forbiddenValidator
                      ) {
                        try {
                          const vr =
                            window.MurphyDoorConfigurator.forbiddenValidator.validateItemAgainstSelections(
                              String(browsingData.variantId),
                              this.getAppliedSelections(),
                              this.getUnifiedForbiddenRules()
                            );
                          isForbiddenPreview = !!(vr && vr.isConflicted);
                        } catch (vErr) {
                          isForbiddenPreview = false;
                        }
                      }

                      // 🚨 A FORBIDDEN PREVIEW ON A REQUIRED CATEGORY STOPS
                      // THE ADD. The old "clear it and continue" below ran
                      // handleBackClick(), which ALSO wiped the category's
                      // snapshot entry -- so the cart was built without it.
                      // Production 2026-09-29: Door Depth slider on a
                      // forbidden 5-1/4" stop -> 20 cart lines, no Door Depth.
                      // Leave the preview (and its "Not Available" warning)
                      // on screen, and send the customer to it.
                      if (
                        isForbiddenPreview &&
                        requiredCategorySet.has(categoryId)
                      ) {
                        isDev() &&
                          console.warn(
                            `⚠️ Conductor: ADD_TO_CART stopped - required category "${categoryId}" is showing a forbidden option`
                          );
                        this._demoteUnappliedCategory(
                          categoryId,
                          'conductor:add_to_cart_forbidden_preview'
                        );
                        forbiddenRequiredAbort = true;
                        continue;
                      }

                      if (isForbiddenPreview) {
                        isDev() &&
                          console.warn(
                            `⚠️ Conductor: Flush gate skipping forbidden browsing preview for category "${categoryId}" — clearing it and continuing ADD_TO_CART`
                          );
                        try {
                          this.stateManager.handleBackClick(categoryId);
                        } catch (clearError) {
                          isDev() &&
                            console.warn(
                              '⚠️ Conductor: Failed to clear forbidden browsing preview during flush',
                              clearError
                            );
                        }
                        continue;
                      }

                      isDev() &&
                        console.log(
                          `🧙‍♂️ Conductor: Auto-applying browsing selection for category "${categoryId}" before cart`
                        );
                      const applyResult = this.handleStateChange(
                        'APPLY_SELECTION',
                        {
                          categoryId,
                          productId: browsingData.productId,
                          variantId: browsingData.variantId || null,
                          auto_applied: true,
                          timestamp: window.performance
                            ? window.performance.now()
                            : Date.now(),
                        }
                      );

                      if (!applyResult) {
                        // Genuine apply failure (not forbidden) — abort + notify
                        // so the customer is never silently shorted a component.
                        isDev() &&
                          console.error(
                            `❌ Conductor: Flush gate failed to apply browsing selection for category "${categoryId}" — aborting ADD_TO_CART`
                          );
                        if (window.MurphyDoorConfigurator.CoreEventBus) {
                          window.MurphyDoorConfigurator.CoreEventBus.publish(
                            CONSTANTS.EVENTS.BUSINESS_CART_ADD_FAILED,
                            {
                              reason: 'browsing_flush_failed',
                              categoryId,
                              timestamp: Date.now(),
                              source: 'conductor:add_to_cart_flush',
                            }
                          );
                        }
                        flushFailed = true;
                        break;
                      }

                      // ENFORCED_PAIR: a flushed browsing pick is a real customer
                      // choice, so it gets the same enforcement as a user apply
                      // (applied above with auto_applied:true, which skips the
                      // per-apply evaluation). A CLEAR empties a required
                      // category, so this add-to-cart must stop and point the
                      // customer at it instead of shipping a half-built door.
                      const flushEnforced = this._publishEnforcedPairActions(
                        browsingData.variantId,
                        categoryId
                      );
                      if (
                        flushEnforced.some(
                          (a) =>
                            a.type ===
                            CONSTANTS.FORBIDDEN_RULES.ENFORCED_PAIR_ACTIONS
                              .CLEAR_CATEGORY
                        )
                      ) {
                        enforcedPairAbort = true;
                        break;
                      }
                    }
                  }

                  if (flushFailed) {
                    break;
                  }

                  // Handled by the cart-readiness gate below (the category is
                  // not in the applied Map, so readiness refuses and points
                  // the customer at it). Logged here so the two stop reasons
                  // stay distinguishable in a console capture.
                  if (forbiddenRequiredAbort) {
                    isDev() &&
                      console.warn(
                        '⚠️ Conductor: ADD_TO_CART will stop - a required category is resting on a forbidden option'
                      );
                  }

                  if (enforcedPairAbort) {
                    isDev() &&
                      console.warn(
                        '⚠️ Conductor: ADD_TO_CART stopped - an enforced pair cleared a required category during the browsing flush'
                      );
                    if (window.MurphyDoorConfigurator.CoreEventBus) {
                      // Same event the disabled ATC button publishes: the
                      // auto-navigation coordinator scrolls to and highlights
                      // the first unfinished category (the one just cleared).
                      window.MurphyDoorConfigurator.CoreEventBus.publish(
                        CONSTANTS.EVENTS.UI_ADD_TO_CART_DISABLED_CLICKED,
                        {
                          timestamp: Date.now(),
                          source: 'conductor:enforced_pair_atc_flush',
                        }
                      );
                    }
                    break;
                  }
                }

                // ENFORCED_PAIR add-to-cart guard (defense in depth). Restore,
                // implicit and material cascades apply with auto_applied:true
                // and are never rewritten; a mismatched pair that arrived that
                // way is caught here, before the cart payload is built.
                const enforcedViolations = this._findEnforcedPairViolations();
                if (enforcedViolations.length > 0) {
                  isDev() &&
                    console.warn(
                      '⚠️ Conductor: ADD_TO_CART blocked by enforced pair rule(s)',
                      enforcedViolations
                    );
                  if (window.MurphyDoorConfigurator.CoreEventBus) {
                    window.MurphyDoorConfigurator.CoreEventBus.publish(
                      CONSTANTS.EVENTS.FORBIDDEN_ENFORCED_PAIR_ATC_BLOCKED,
                      {
                        violations: enforcedViolations,
                        timestamp: Date.now(),
                        source: 'conductor:add_to_cart_guard',
                      }
                    );
                  }
                  break;
                }

                // CART-READINESS GATE (post-flush, strict). Every required
                // category must be APPLIED in the Map and agree with the cart
                // snapshot. This is what refuses a door that is missing a
                // category the customer can see is unfinished -- a slider on a
                // forbidden stop, a rejected commit -- regardless of what the
                // counter, the check marks or the button state claim. It uses
                // the same "missing choice" UX as the disabled button: the
                // auto-navigation coordinator scrolls to and flags the first
                // unfinished category.
                {
                  let readiness = this.getCartReadiness();
                  // Applied-but-stale entries: the customer's answer is in the
                  // Map, only the cart snapshot lags. Bring the payload in line
                  // with the answer instead of refusing a finished door.
                  if (
                    readiness.stale.length > 0 &&
                    typeof this.stateManager.resyncSnapshotFromApplied ===
                      'function'
                  ) {
                    this.stateManager.resyncSnapshotFromApplied(
                      readiness.stale
                    );
                    readiness = this.getCartReadiness();
                  }
                  if (!readiness.ready) {
                    isDev() &&
                      console.warn(
                        '⚠️ Conductor: ADD_TO_CART refused - configuration is not cart-ready',
                        readiness
                      );
                    readiness.missing
                      .concat(readiness.stale)
                      .forEach((missingCategoryId) =>
                        this._demoteUnappliedCategory(
                          missingCategoryId,
                          'conductor:add_to_cart_readiness'
                        )
                      );
                    publishEvent(CONSTANTS.EVENTS.UI_PROGRESS_UPDATE_REQUESTED, {
                      source: 'conductor:add_to_cart_readiness',
                      timestamp: Date.now(),
                    });
                    publishEvent(
                      CONSTANTS.EVENTS.BUSINESS_CART_ADD_REFUSED_INCOMPLETE,
                      {
                        source: 'conductor:add_to_cart_readiness',
                        missing: readiness.missing,
                        forbidden: readiness.forbidden,
                        stale: readiness.stale,
                        timestamp: Date.now(),
                      }
                    );
                    publishEvent(
                      CONSTANTS.EVENTS.UI_ADD_TO_CART_DISABLED_CLICKED,
                      {
                        timestamp: Date.now(),
                        source: 'conductor:add_to_cart_readiness',
                        missing: readiness.missing,
                        forbidden: readiness.forbidden,
                        stale: readiness.stale,
                      }
                    );
                    break;
                  }
                }

                // C1: flush the debounced URL writer so the serialized
                // _Configuration_URL captured in the cart reflects the latest
                // applied state (click=apply coalesces writes over 300ms).
                if (
                  window.MurphyDoorConfigurator.supportPersistenceManager &&
                  typeof window.MurphyDoorConfigurator.supportPersistenceManager
                    .flushURLState === 'function'
                ) {
                  window.MurphyDoorConfigurator.supportPersistenceManager.flushURLState();
                }

                const configuration = this.stateManager.getCartConfiguration();
                isDev() &&
                  console.log(
                    '🧙‍♂️ Conductor: Configuration retrieved:',
                    configuration
                  );

                if (!window.MurphyDoorConfigurator.cartManager) {
                  isDev() &&
                    console.error('🚨 Conductor: cartManager not available!');
                  break;
                }

                if (configuration) {
                  isDev() &&
                    console.log('🧙‍♂️ Conductor: Calling cartManager.addToCart');
                  result =
                    window.MurphyDoorConfigurator.cartManager.addToCart(
                      configuration
                    );
                } else {
                  isDev() &&
                    console.error(
                      '🚨 Conductor: No configuration available for cart'
                    );
                }
                break;
              }

              // ARCHITECTURAL IMPROVEMENT: Central Conductor Pattern Implementation
              //
              // This switch statement demonstrates the violation fix:
              // 1. Events are published to allow module subscription (line 2187)
              // 2. Each case delegates to appropriate specialized modules
              // 3. Conductor coordinates but doesn't implement business logic
              // 4. Console logging shows proper delegation patterns
              //
              // Future Migration Path:
              // - Modules can subscribe to CONSTANTS.EVENTS.STATE_CHANGE_COORDINATED events
              // - Switch cases can be gradually replaced with pure event routing
              // - Maintains backward compatibility during transition

              default:
                // For unhandled actions, delegate to state manager
                result = this.stateManager.handleStateChange(action, data);
                break;
            }

            // Track performance metrics
            const executionTime = performance.now() - startTime;
            if (window.MurphyDoorConfigurator.CoreEventBus) {
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.PERFORMANCE_STATE_CHANGE,
                {
                  action,
                  executionTime,
                  timestamp: Date.now(),
                }
              );

              // Notify interested subscribers about the state change
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.SYSTEM_CHANGED,
                {
                  action,
                  success: !!result,
                  timestamp: Date.now(),
                }
              );
            }

            return result;
          } catch (error) {
            // Handle unexpected errors
            const errorContext = {
              method: METHOD_SIGNATURE,
              action,
              data: JSON.stringify(data).substring(0, 100), // Limit size for logging
              severity: 'error',
              recoverable: false,
            };

            this.handleError(
              `Unexpected error handling state change for action: ${action}`,
              error,
              errorContext
            );

            // Publish error event
            if (window.MurphyDoorConfigurator.CoreEventBus) {
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.ERROR_STATE_CHANGE,
                {
                  action,
                  data,
                  error: error.message,
                  stack: error.stack,
                  timestamp: Date.now(),
                  source: METHOD_SIGNATURE,
                }
              );
            }

            return false;
          }
        },

        handleTabChange(action, data) {
          /**
           * Handles tab change events throughout the configurator
           * This method coordinates UI updates and state changes when a user switches tabs
           * Following the Central Conductor Pattern, it delegates state management to stateManager
           * and UI updates to uiUpdater while maintaining orchestration control
           *
           * @param {string} action - The action type (TAB_CHANGED)
           * @param {Object} data - Data associated with the tab change
           * @param {string|number} data.tabId - The ID of the tab to change to
           * @returns {Object|boolean} Result of the tab change operation or false if failed
           */
          const METHOD_SIGNATURE = 'main.handleTabChange';
          const startTime = performance.now();

          try {
            if (!data || !data.tabId) {
              const errorContext = {
                method: METHOD_SIGNATURE,
                action,
                dataKeys: data ? Object.keys(data) : [],
                severity: 'warning',
                recoverable: true,
              };

              this.handleError(
                'Invalid tab change data: tabId is required',
                new Error('Missing required parameter: tabId'),
                errorContext
              );

              return false;
            }

            if (!this.stateManager) {
              const errorContext = {
                method: METHOD_SIGNATURE,
                action,
                tabId: data.tabId,
                severity: 'error',
                recoverable: false,
              };

              this.handleError(
                'State manager not initialized',
                new Error(
                  'Cannot handle tab change: stateManager is not available'
                ),
                errorContext
              );

              return false;
            }

            // Delegate state change to state manager
            const result = this.stateManager.handleStateChange(action, data);

            if (result && result.success) {
              // Skip UI updates if tab is already active (performance optimization)
              // BUT: Always process user clicks even if already active, to handle cases where
              // auto-navigation changed the UI without updating state (fixes tab click bug)
              // NOTE: Any source other than a 'user_click', including an undefined source,
              // will be optimized by skipping UI updates if the tab is already active.
              if (
                result.alreadyActive &&
                data.source !== CONSTANTS.EVENT_SOURCES.USER_CLICK
              ) {
                isDev() &&
                  console.log(
                    `⚡ CONDUCTOR: Tab ${data.tabId} already active (source: ${data.source || 'unknown'}), skipping UI updates for performance`
                  );
                return result; // Return early with success but skip expensive UI operations
              }

              if (
                result.alreadyActive &&
                data.source === CONSTANTS.EVENT_SOURCES.USER_CLICK
              ) {
                isDev() &&
                  console.log(
                    `🔄 CONDUCTOR: Tab ${data.tabId} already active but user clicked - processing UI update to ensure correct display`
                  );
              }
              // Get tab data for UI updates
              let tabData;
              let categories = [];

              try {
                // Handle both numeric and string tab IDs properly
                const tabId = isNaN(parseInt(data.tabId))
                  ? data.tabId
                  : parseInt(data.tabId);
                tabData = this.getTabStructure().get(tabId);
                categories = tabData ? tabData.categories : [];
              } catch (error) {
                const errorContext = {
                  method: METHOD_SIGNATURE,
                  action,
                  tabId: data.tabId,
                  severity: 'warning',
                  recoverable: true,
                };

                this.handleError(
                  'Error retrieving tab data structure',
                  error,
                  errorContext
                );
              }

              // Update UI to reflect tab change
              // skipUIUpdate: navigation-manager already handled DOM activation and categories
              // via activateTab() + UI_TAB_ACTIVATED -> ui-updater subscription path
              if (
                window.MurphyDoorConfigurator.uiUpdater &&
                !data.skipUIUpdate
              ) {
                window.MurphyDoorConfigurator.uiUpdater.updateDisplay(
                  'TAB_CHANGED',
                  {
                    tabId: data.tabId,
                    categories,
                  }
                );
              }

              // Show first incomplete category if available (runs for ALL tab changes including swipe)
              if (
                result.firstIncompleteCategory &&
                window.MurphyDoorConfigurator.uiUpdater
              ) {
                setTimeout(() => {
                  window.MurphyDoorConfigurator.uiUpdater.updateCategoryDisplay(
                    result.firstIncompleteCategory
                  );
                }, 50);
              }

              // Publish event for monitoring
              if (window.MurphyDoorConfigurator.CoreEventBus) {
                window.MurphyDoorConfigurator.CoreEventBus.publish(
                  CONSTANTS.EVENTS.SYSTEM_TAB_CHANGED,
                  {
                    tabId: data.tabId,
                    previousTab: result.previousTab,
                    timestamp: Date.now(),
                  }
                );

                // 🛡️ DISABLED CART FIX: Publish summary tab activation for Add to Cart button setup
                // Pattern #14: Use CONSTANTS.EVENTS (centralized event constants)
                // This triggers UIUpdater's subscription (ui-updater.js:4099) to set up the disabled button
                if (data.tabId === 'summary') {
                  window.MurphyDoorConfigurator.CoreEventBus.publish(
                    CONSTANTS.EVENTS.UI_SUMMARY_TAB_ACTIVATED,
                    {
                      source: 'conductor.handleTabChange',
                      timestamp: Date.now(),
                    }
                  );
                }
              }

              // Update performance metrics
              const endTime = performance.now();
              if (window.MurphyDoorConfigurator.CoreEventBus) {
                window.MurphyDoorConfigurator.CoreEventBus.publish(
                  CONSTANTS.EVENTS.PERFORMANCE_METHOD_EXECUTION,
                  {
                    method: METHOD_SIGNATURE,
                    executionTime: endTime - startTime,
                    timestamp: Date.now(),
                  }
                );
              }
            } else {
              const errorContext = {
                method: METHOD_SIGNATURE,
                action,
                tabId: data.tabId,
                result,
                severity: 'warning',
                recoverable: true,
              };

              this.handleError(
                `Tab change operation failed for tab ${data.tabId}`,
                new Error('State manager returned unsuccessful result'),
                errorContext
              );
            }

            return result;
          } catch (error) {
            const errorContext = {
              method: METHOD_SIGNATURE,
              action,
              dataKeys: data ? Object.keys(data) : [],
              severity: 'error',
              recoverable: true,
            };

            this.handleError(
              'Error during tab change operation',
              error,
              errorContext
            );

            return false;
          }
        },

        /**
         * Handles category toggle state changes and UI updates
         *
         * This method follows the Central Conductor Pattern by:
         * 1. Delegating state change to the stateManager
         * 2. Orchestrating UI updates based on state changes
         * 3. Providing proper error handling and performance monitoring
         *
         * @param {string} action - The action type (CATEGORY_TOGGLE)
         * @param {Object} data - Data for the category toggle
         * @param {string} data.categoryId - The ID of the category to toggle
         * @returns {Object} Result object with success status and category data
         */
        handleCategoryToggle(action, data) {
          try {
            // Record start time for performance monitoring
            const startTime = window.performance.now();

            // Validate input parameters
            if (!data || !data.categoryId) {
              this.handleError(
                'Invalid category toggle data',
                new Error('Missing categoryId'),
                {
                  method: 'main.handleCategoryToggle',
                  action,
                  data,
                  severity: 'warning',
                  recoverable: true,
                }
              );
              return { success: false, error: 'INVALID_PARAMS' };
            }

            // Check if this is a programmatic click
            const isProgrammatic = data.programmatic === true;

            // Analyze category type when toggling
            const categoryType = this.analyzeCategoryType(data.categoryId);
            isDev() &&
              console.log(
                `Category ${data.categoryId} classified as: ${categoryType}`
              );

            // Store category type in state for future use
            if (
              categoryType &&
              categoryType !== 'ERROR' &&
              categoryType !== 'EMPTY'
            ) {
              // Store in state manager via conductor delegation
              if (this.stateManager && this.stateManager.storeCategoryType) {
                this.stateManager.storeCategoryType(
                  data.categoryId,
                  categoryType
                );
              }
              data.categoryType = categoryType;

              // Publish event for category type analysis completion
              if (window.MurphyDoorConfigurator.CoreEventBus) {
                window.MurphyDoorConfigurator.CoreEventBus.publish(
                  CONSTANTS.EVENTS.SYSTEM_CATEGORY_TYPE_ANALYZED,
                  {
                    categoryId: data.categoryId,
                    categoryType,
                    timestamp: Date.now(),
                  }
                );
              }
            }

            // Delegate state change to state manager
            const result = this.stateManager.handleStateChange(action, data);

            // Update UI if state change was successful
            if (result && result.success) {
              window.MurphyDoorConfigurator.uiUpdater.updateDisplay(
                'CATEGORY_TOGGLE',
                {
                  ...data,
                  isProgrammatic,
                  categoryType,
                }
              );

              // Record performance metrics
              const executionTime = window.performance.now() - startTime;
              if (window.MurphyDoorConfigurator.CoreEventBus) {
                window.MurphyDoorConfigurator.CoreEventBus.publish(
                  CONSTANTS.EVENTS.PERFORMANCE_STATE_CHANGE,
                  {
                    action,
                    executionTime,
                    timestamp: Date.now(),
                    module: 'main',
                    method: 'handleCategoryToggle',
                  }
                );
              }
            }

            return result;
          } catch (error) {
            this.handleError('Category toggle failed', error, {
              method: 'main.handleCategoryToggle',
              action,
              data,
              severity: 'error',
              recoverable: true,
            });

            return {
              success: false,
              error: 'TOGGLE_FAILED',
              message: error.message,
            };
          }
        },

        /**
         * ⚡ PPD-FIRST: Get category classification type (O(1) map lookup)
         * Uses pre-built categoryTypeClassificationMap from PPD Layer 4
         *
         * ARCHITECTURAL NOTE: Eliminates manual cache management.
         * Category types are pre-computed during PPD Layer 4 and stored
         * in an immutable frozen Map for instant O(1) lookups.
         *
         * @param {string} categoryId - Category identifier
         * @returns {string} Classification type or 'EMPTY' if not found
         */
        analyzeCategoryType(categoryId) {
          try {
            if (!categoryId) {
              isDev() &&
                console.warn('analyzeCategoryType: No categoryId provided');
              return 'EMPTY';
            }

            const ppd = this.getPerfectProductDictionary();

            if (!ppd) {
              throw new Error('❌ CONDUCTOR: PPD not available');
            }

            if (!ppd.categoryTypeClassificationMap) {
              throw new Error(
                '❌ CONDUCTOR: categoryTypeClassificationMap missing from PPD - data pipeline Layer 4 incomplete'
              );
            }

            // ⚡ O(1) map lookup - no cache needed!
            const categoryType =
              ppd.categoryTypeClassificationMap.get(categoryId);

            if (!categoryType) {
              isDev() &&
                console.warn(
                  `⚠️ CONDUCTOR: No classification found for category "${categoryId}"`
                );
              return 'EMPTY';
            }

            isDev() &&
              console.log(
                `⚡ Category ${categoryId} classified as: ${categoryType} (O(1) PPD lookup - no cache overhead)`
              );

            return categoryType;
          } catch (error) {
            this.handleError('Failed to analyze category type', error, {
              method: 'analyzeCategoryType',
              categoryId,
              severity: 'warning',
              recoverable: true,
            });

            return 'ERROR';
          }
        },

        /**
         * Retrieves products for a given category from the category structure
         *
         * @param {string} categoryId - The category ID to get products for
         * @returns {Array} Array of products or empty array if not found
         */
        getProductsForCategory(categoryId) {
          try {
            const categoryData = this.getCategoryStructure().get(categoryId);

            if (
              !categoryData ||
              !categoryData.displayElements ||
              !categoryData.displayElements.thumbnails
            ) {
              return [];
            }

            return categoryData.displayElements.thumbnails;
          } catch (error) {
            this.handleError('Failed to get products for category', error, {
              method: 'main.getProductsForCategory',
              categoryId,
              severity: 'warning',
              recoverable: true,
            });
            return [];
          }
        },

        /**
         * Retrieves full product objects (with variants) for a given category
         * This returns complete product data instead of just thumbnails
         *
         * @param {string} categoryId - The category ID to get full products for
         * @returns {Array} Array of full product objects with variants
         */
        /**
         * ⚡ PPD-FIRST: Get full products for category (O(1) lookup)
         * Uses pre-built categoryToProductsMap from PPD Layer 4
         *
         * @param {string} categoryId - Category identifier
         * @returns {Array} Frozen array of products for category
         */
        getFullProductsForCategory(categoryId) {
          try {
            const ppd = this.getPerfectProductDictionary();

            if (!ppd?.categoryToProductsMap) {
              throw new Error(
                '❌ CONDUCTOR: categoryToProductsMap missing from PPD - data pipeline Layer 4 incomplete'
              );
            }

            const products = ppd.categoryToProductsMap.get(categoryId);

            if (!products) {
              isDev() &&
                console.warn(
                  `⚠️ CONDUCTOR: No products found for category "${categoryId}"`
                );
              return Object.freeze([]);
            }

            isDev() &&
              console.log(
                `⚡ Retrieved ${products.length} products for category ${categoryId} via O(1) map lookup`
              );

            return products; // Already frozen from PPD Layer 4
          } catch (error) {
            this.handleError(
              'Failed to get full products for category',
              error,
              {
                method: 'main.getFullProductsForCategory',
                categoryId,
                severity: 'warning',
                recoverable: true,
              }
            );
            return Object.freeze([]);
          }
        },

        /**
         * ⚡ PPD-FIRST: Check if product is single-variant (instant property read)
         * Uses pre-computed product.isSingleVariant property from PPD Layer 4
         *
         * ARCHITECTURAL NOTE: This method was 100+ lines of O(n*m) filtering.
         * Now it's a single property read - product.isSingleVariant was pre-computed
         * during PPD Layer 4 when productIdToEnhancedVariantsMap was built.
         *
         * @param {Object} product - Product to check
         * @returns {boolean} True if single variant with "Default Title"
         */
        isSingleVariant(product) {
          try {
            if (!product) {
              isDev() && console.warn('isSingleVariant: No product provided');
              return false;
            }

            // ⚡ O(1) property read - pre-computed in PPD Layer 4
            if (product.isSingleVariant !== undefined) {
              return product.isSingleVariant;
            }

            // FAIL-FAST: If property missing, PPD Layer 4 didn't pre-compute it
            throw new Error(
              `❌ CONDUCTOR: product.isSingleVariant property missing for product ${product.id}. ` +
                'This indicates PPD Layer 4 did not complete pre-computation. ' +
                'Check productIdToEnhancedVariantsMap building in data pipeline.'
            );
          } catch (error) {
            this.handleError(
              'Failed to determine single-variant status',
              error,
              {
                method: 'isSingleVariant',
                productId: product?.id,
                productTitle: product?.title,
                severity: 'error',
                recoverable: true,
              }
            );

            // Safe fallback: Assume multi-variant to prevent incorrect routing
            return false;
          }
        },

        /* Progress Management Methods */
        /**
         * Handles progress tracking and state updates when a selection is applied.
         *
         * This method is a critical part of the Central Conductor Pattern:
         * 1. It delegates progress tracking to the specialized progressTracker module
         * 2. It updates the state through the stateManager with completion information
         * 3. It maintains proper separation of concerns by not directly manipulating DOM
         *
         * The method follows the unidirectional data flow principle:
         * - Data flows from progressTracker -> stateManager -> UI updates
         * - Each module has a single responsibility
         * - The main conductor coordinates but doesn't implement the logic
         *
         * @param {Object} result - The result object from a selection operation
         * @param {string} result.categoryId - The ID of the category being updated
         * @returns {Object} Progress information including category and tab completion status
         * @throws {Error} If progressTracker is unavailable or fails
         */
        handleSelectionProgress(result) {
          const METHOD_SIGNATURE = 'main.handleSelectionProgress';
          const startTime = performance.now();

          try {
            // Parameter validation
            if (!result || !result.categoryId) {
              throw new Error(`Invalid result object or missing categoryId`);
            }

            // Verify progressTracker availability
            if (!window.MurphyDoorConfigurator.progressTracker) {
              throw new Error(`progressTracker module not available`);
            }

            // Delegate progress tracking to the specialized module
            const progress =
              window.MurphyDoorConfigurator.progressTracker.updateCategoryProgress(
                result.categoryId,
                true
              );

            // Update state through the state manager
            this.stateManager.handleStateChange('COMPLETION_UPDATE', {
              categoryId: result.categoryId,
              tabId: progress.tabId,
              categoryComplete: progress.categoryComplete,
              tabComplete: progress.tabComplete,
              totalProgress: progress.totalProgress,
            });

            // Track performance
            const executionTime = performance.now() - startTime;
            if (
              executionTime > 50 &&
              window.MurphyDoorConfigurator.CoreEventBus
            ) {
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.PERFORMANCE_METHOD_SLOW,
                {
                  method: METHOD_SIGNATURE,
                  executionTime,
                  threshold: 50,
                }
              );
            }

            return progress;
          } catch (error) {
            this.handleError(
              `Progress tracking failed for category ${result?.categoryId}`,
              error,
              {
                method: METHOD_SIGNATURE,
                categoryId: result?.categoryId,
                context: result,
                severity: 'high',
                recoverable: false,
              }
            );
            throw error;
          }
        },

        /**
         * Coordinates batch UI updates in response to selection changes.
         *
         * This method is a critical part of the Central Conductor Pattern:
         * 1. It centralizes UI update coordination through a single interface
         * 2. It batches multiple updates to optimize DOM operations
         * 3. It delegates actual DOM manipulation to the specialized uiUpdater module
         *
         * Following the separation of concerns principle:
         * - Main module coordinates but doesn't implement UI updates
         * - Data is transformed into the format expected by the UI module
         * - The UI module handles all DOM operations
         *
         * @param {Object} result - The selection result data
         * @param {string} result.categoryId - The category ID
         * @param {string} result.key - The selection key
         * @param {string} result.displayTitle - The display title for the selection
         * @param {string} result.productTitle - The product title
         * @param {number} result.price - The product price
         * @param {Object} progress - The progress information
         * @param {string|number} progress.tabId - The ID of the tab containing the selection
         * @param {boolean} progress.tabComplete - Whether the tab is now complete
         * @param {number} progress.totalProgress - The total configuration progress percentage
         * @param {boolean} [autoApplied=false] - Whether this selection was auto-applied (skip animations)
         */
        handleSelectionUIUpdates(result, progress, autoApplied) {
          const METHOD_SIGNATURE = 'main.handleSelectionUIUpdates';
          const startTime = performance.now();

          try {
            // Parameter validation
            if (!result || !result.categoryId) {
              throw new Error(`Invalid result object or missing categoryId`);
            }

            if (!progress || typeof progress.totalProgress !== 'number') {
              throw new Error(
                `Invalid progress object or missing totalProgress`
              );
            }

            // Verify uiUpdater availability
            if (!window.MurphyDoorConfigurator.uiUpdater) {
              throw new Error(`uiUpdater module not available`);
            }

            // Delegate batch UI update to UI layer through event system
            // DRY: auto_applied flag propagated from cascade auto-selection coordinator
            // so UI modules can skip animations for auto-applied selections
            window.MurphyDoorConfigurator.CoreEventBus.publish(
              CONSTANTS.EVENTS.UI_UPDATE_REQUESTED,
              {
                type: 'batch',
                updateData: {
                  appliedState: {
                    categoryId: result.categoryId,
                    key: result.key,
                    displayTitle: result.displayTitle,
                    price: result.price,
                    productTitle: result.productTitle,
                    auto_applied: !!autoApplied,
                  },
                  summaryList: {
                    categoryId: result.categoryId,
                    displayTitle: result.displayTitle,
                    productTitle: result.productTitle,
                    price: result.price,
                  },
                  tabStatus: {
                    tabId: String(progress.tabId),
                    isComplete: progress.tabComplete,
                  },
                  progress: progress.totalProgress,
                },
                auto_applied: !!autoApplied,
                source: 'conductor_state_update',
              }
            );
            isDev() &&
              console.log(
                'Conductor: Published batch state update event via delegation'
              );

            // Track performance
            const executionTime = performance.now() - startTime;
            if (
              executionTime > 50 &&
              window.MurphyDoorConfigurator.CoreEventBus
            ) {
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.PERFORMANCE_METHOD_SLOW,
                {
                  method: METHOD_SIGNATURE,
                  executionTime,
                  threshold: 50,
                }
              );
            }
          } catch (error) {
            this.handleError(`UI update failed`, error, {
              method: METHOD_SIGNATURE,
              result,
              progress,
              severity: 'medium',
              recoverable: true,
            });
            // Don't rethrow - allow system to continue functioning
          }
        },

        /**
         * FIXED VIOLATION 1: Price Update Coordination Delegation
         *
         * This method now properly delegates price update coordination to the business logic layer
         * instead of containing the business logic directly in the conductor.
         *
         * The conductor's role is now pure orchestration - it coordinates between modules
         * but doesn't contain business logic itself.
         */
        handlePriceUpdates(data, state) {
          const METHOD_SIGNATURE = 'main.handlePriceUpdates';
          isDev() &&
            console.log(
              `[${METHOD_SIGNATURE}] Delegating price update to business logic layer`,
              { data, state }
            );

          try {
            // Verify module dependencies for delegation
            if (!window.MurphyDoorConfigurator.priceCalculator) {
              throw new Error(
                'Price calculator module not available for delegation'
              );
            }

            if (!this.stateManager) {
              throw new Error('State manager not available for delegation');
            }

            if (!window.MurphyDoorConfigurator.uiUpdater) {
              throw new Error('UI updater module not available for delegation');
            }

            // Prepare dependencies for business logic layer
            const dependencies = {
              SELECTION_STATES,
              stateManager: this.stateManager,
              uiUpdater: window.MurphyDoorConfigurator.uiUpdater,
              eventBus: window.MurphyDoorConfigurator.CoreEventBus,
              conductor: this, // 🚀 PPD-FIRST: Enable conductor delegation
            };

            isDev() &&
              console.log(
                `[${METHOD_SIGNATURE}] Dependencies prepared for delegation`
              );

            // DELEGATE to business logic layer instead of doing the work here!
            const result =
              window.MurphyDoorConfigurator.priceCalculator.coordinatePriceUpdate(
                data,
                state,
                dependencies
              );

            isDev() &&
              console.log(
                `[${METHOD_SIGNATURE}] Price update delegation completed:`,
                { success: result.success }
              );

            return result;
          } catch (error) {
            isDev() &&
              console.error(
                `[${METHOD_SIGNATURE}] Price update delegation failed:`,
                error
              );

            // Use conductor's error handling system
            this.handleError('Price update delegation failed', error, {
              method: METHOD_SIGNATURE,
              data: data
                ? { productId: data.productId, variantId: data.variantId }
                : null,
              state,
              severity: 'medium',
              recoverable: true,
            });

            return {
              success: false,
              error: error.message,
            };
          }
        },

        /**
         * Updates shipping time calculations and UI displays based on product selections.
         *
         * This method is a key part of the Central Conductor Pattern as it:
         * 1. Coordinates shipping calculator and UI updater modules
         * 2. Delegates shipping time calculation to specialized business logic module
         * 3. Maintains separation of concerns with clear module responsibilities
         *
         * The method follows system patterns by:
         * - Using proper dependency validation before module access
         * - Delegating business logic to dedicated shipping calculator
         * - Using UI updater for all DOM manipulations
         * - Publishing performance events for monitoring
         *
         * @param {Object} data - The selection data from state change
         * @param {string} state - Selection state (browsing/applied)
         * @returns {Object} Result with success status and shipping information
         */
        handleShippingUpdates(data, state) {
          const METHOD_SIGNATURE = 'main.handleShippingUpdates';
          isDev() &&
            console.log(
              `[${METHOD_SIGNATURE}] Delegating shipping update to business logic layer`,
              { data, state }
            );

          try {
            // Prepare dependencies for business logic layer
            const dependencies = {
              SELECTION_STATES,
              stateManager: this.stateManager,
              uiUpdater: window.MurphyDoorConfigurator.uiUpdater,
              eventBus: window.MurphyDoorConfigurator.CoreEventBus,
              conductor: this, // 🚀 PPD-FIRST: Enable conductor delegation
            };

            // DELEGATE to business logic layer instead of doing the work here!
            const result =
              window.MurphyDoorConfigurator.shippingCalculator.coordinateShippingUpdate(
                data,
                state,
                dependencies
              );

            isDev() &&
              console.log(
                `[${METHOD_SIGNATURE}] Shipping update delegation completed:`,
                { success: result.success }
              );
            return result;
          } catch (error) {
            // Use conductor's error handling system
            this.handleError('Shipping update delegation failed', error, {
              method: METHOD_SIGNATURE,
              data: data
                ? { productId: data.productId, variantId: data.variantId }
                : null,
              state,
              severity: 'medium',
              recoverable: true,
            });

            return {
              success: false,
              error: error.message,
            };
          }
        },

        /**
         * Updates pass-through space calculations and UI displays based on product selections.
         *
         * This method is a key part of the Central Conductor Pattern as it:
         * 1. Coordinates space calculator and UI updater modules
         * 2. Delegates space calculation to specialized business logic module
         * 3. Maintains separation of concerns with clear module responsibilities
         * 4. Integrates with existing shipping estimate display coordination
         *
         * The method follows system patterns by:
         * - Using proper dependency validation before module access
         * - Delegating business logic to dedicated space calculator
         * - Using UI updater for all DOM manipulations
         * - Publishing performance events for monitoring
         * - Coordinating display state with shipping estimate area
         *
         * @param {Object} data - The selection data from state change
         * @param {string} state - Selection state (browsing/applied)
         * @returns {Object} Result with success status and space information
         */
        handleSpaceCalculationUpdates(data, state) {
          const METHOD_SIGNATURE = 'main.handleSpaceCalculationUpdates';
          isDev() &&
            console.log(
              `🔧 CONDUCTOR DELEGATION: space calculation update to business logic layer`,
              { data, state }
            );

          try {
            // Verify space calculator module availability
            if (!window.MurphyDoorConfigurator.spaceCalculator) {
              isDev() &&
                console.warn(
                  `[${METHOD_SIGNATURE}] Space calculator module not available, skipping calculation`
                );
              return {
                success: false,
                error: 'Space calculator not available',
              };
            }

            // Prepare dependencies for business logic layer
            const dependencies = {
              SELECTION_STATES,
              stateManager: this.stateManager,
              uiUpdater: window.MurphyDoorConfigurator.uiUpdater,
              eventBus: window.MurphyDoorConfigurator.CoreEventBus,
              conductor: this, // Pass conductor reference for delegation
            };

            // DELEGATE to business logic layer instead of doing the work here!
            const result =
              window.MurphyDoorConfigurator.spaceCalculator.coordinateSpaceUpdate(
                data,
                state,
                dependencies
              );

            isDev() &&
              console.log(
                `🔧 CONDUCTOR DELEGATION: space calculation delegation completed:`,
                { success: result.success }
              );
            return result;
          } catch (error) {
            // Use conductor's error handling system
            this.handleError('Space calculation delegation failed', error, {
              method: METHOD_SIGNATURE,
              data: data
                ? { productId: data.productId, variantId: data.variantId }
                : null,
              state,
              severity: 'medium',
              recoverable: true,
            });

            return {
              success: false,
              error: error.message,
            };
          }
        },

        /**
         * 🧙‍♂️ EPIC AUTO-TOGGLE MAGIC: Auto-switch view based on product image availability
         *
         * This method detects if the selected product has either a front or back image,
         * checks which view is currently active, and auto-toggles to the appropriate view
         * to show the user the relevant image for their selection.
         *
         * Follows CCP compliance by:
         * - Using event-driven communication with media gallery
         * - Delegating to conductor methods for data access
         * - Publishing events for UI coordination
         *
         * @param {string|number} productId - The selected product ID
         * @param {string|number} variantId - The selected variant ID (optional)
         */
        /**
         * Record which camera view the CUSTOMER's own selection is about.
         *
         * Read back by media-gallery.js (so a cascade completing right after
         * the click cannot force the camera back to front) and by
         * md-config-bl-image-layer-coordinator.js (so a cascade-applied
         * category's image availability cannot steer the camera at all). Both
         * read it through conductor delegation -- neither derives the rule
         * itself, which is the whole point.
         *
         * A category with NO side preference CLEARS the intent rather than
         * leaving the previous one standing. That is deliberate: it keeps
         * today's behaviour exactly as-is for side-neutral categories
         * (Locking Systems, Width, Delivery ...), where forcing front after a
         * cascade is correct and has always happened.
         *
         * @param {Object} data - the coordinated SELECTION_BROWSING payload
         * @returns {{view: string, categoryId: string, at: number}|null}
         */
        _recordCustomerViewIntent(data) {
          // 🚨 A silently-seeded slider default is NOT a customer action. The
          // Width/Height sliders "click" Use Default at bootstrap through the
          // exact same commit path a real drag takes, so the resulting events
          // are indistinguishable except for this flag -- the same trap
          // media-gallery.js's isUntouchedSliderDefault() already exists to
          // dodge, and the same one UI_SLIDER_DEFAULT_SEED_* was added for.
          // Leave any existing intent alone rather than clearing it: a seed is
          // not the customer changing their mind.
          if (data?.suppressClickApply === true) {
            return this._activeViewIntent || null;
          }

          const utils = window.MurphyDoorConfigurator?.utils;
          if (typeof utils?.getCategoryPreferredView !== 'function') {
            return this._activeViewIntent || null;
          }

          const view = utils.getCategoryPreferredView(data?.categoryId);
          this._activeViewIntent = view
            ? { view, categoryId: data.categoryId, at: Date.now() }
            : null;

          isDev() &&
            console.log(
              `🎥 CONDUCTOR: customer view intent ${
                this._activeViewIntent
                  ? `= ${view} (${data.categoryId})`
                  : `cleared (${data?.categoryId} is side-neutral)`
              }`
            );

          return this._activeViewIntent;
        },

        /**
         * The customer's current camera intent, or null when side-neutral.
         * @returns {{view: string, categoryId: string, at: number}|null}
         */
        getActiveViewIntent() {
          return this._activeViewIntent || null;
        },

        /**
         * Drop the recorded intent because the customer took the camera over
         * by hand (the front/back toggle switch). Their flip outranks any
         * preference we inferred from the category they last clicked, so
         * nothing automatic should pull it back afterwards.
         */
        clearActiveViewIntent() {
          if (!this._activeViewIntent) return;
          isDev() &&
            console.log(
              '🎥 CONDUCTOR: customer view intent cleared (manual view toggle)'
            );
          this._activeViewIntent = null;
        },

        handleAutoImageViewToggle(productId, variantId, categoryId) {
          const METHOD_SIGNATURE = 'main.handleAutoImageViewToggle';
          isDev() &&
            console.log(
              `🧙‍♂️ CONDUCTOR: Auto image view toggle processing for product ${productId}`,
              { productId, variantId, categoryId }
            );

          try {
            // Get perfect product dictionary for enhanced product data
            const perfectDict = this.getPerfectProductDictionary();
            if (!perfectDict || !perfectDict.products) {
              isDev() &&
                console.warn(
                  `[${METHOD_SIGNATURE}] Perfect dictionary not available for image toggle`
                );
              return {
                success: false,
                error: 'Perfect dictionary not available',
              };
            }

            // 🧙‍♂️ EPIC COMPOSITE PRODUCT MAGIC: Handle both regular products and CompositeProducts
            let product = null;

            // First try direct product lookup
            // 🔥 FIX: Support both string (CompositeProduct synthetic) and numeric (Shopify) product IDs
            const stringProductId = String(productId);
            const numericProductId = parseInt(productId, 10);
            product =
              perfectDict.products.get(stringProductId) ||
              perfectDict.products.get(numericProductId);

            if (!product) {
              // 🚀 PPD-FIRST: O(1) CompositeProduct lookup by variant ID
              // 🔥 FIX: Support both string (synthetic) and numeric variant IDs
              const stringVariantId = String(variantId);
              const numericVariantId = parseInt(variantId, 10);
              if (perfectDict.variantIdToCompositeProductMap) {
                product =
                  perfectDict.variantIdToCompositeProductMap.get(
                    stringVariantId
                  ) ||
                  perfectDict.variantIdToCompositeProductMap.get(
                    numericVariantId
                  );
                if (product) {
                  isDev() &&
                    console.log(
                      `🚀 CONDUCTOR: Found CompositeProduct via O(1) variant lookup: ${product.title || product.handle}`
                    );
                }
              }

              if (!product) {
                // 🚀 PPD-FIRST: O(1) CompositeProduct lookup by source product ID
                const numericProductId = parseInt(productId, 10);
                if (
                  !isNaN(numericProductId) &&
                  perfectDict.sourceProductIdToCompositeMap
                ) {
                  product =
                    perfectDict.sourceProductIdToCompositeMap.get(
                      numericProductId
                    );
                  if (product) {
                    isDev() &&
                      console.log(
                        `🚀 CONDUCTOR: Found CompositeProduct via O(1) source product lookup: ${product.title || product.handle}`
                      );
                  }
                }
              }
            }

            if (!product) {
              isDev() &&
                console.warn(
                  `[${METHOD_SIGNATURE}] Product ${productId} not found in dictionary (including CompositeProduct search)`,
                  {
                    productId,
                    variantId,
                    categoryId,
                    hasPPD: !!perfectDict,
                    hasProductsMap: !!perfectDict.products,
                    productsMapSize: perfectDict.products?.size || 0,
                    hasVariantToCompositeMap:
                      !!perfectDict.variantIdToCompositeProductMap,
                    hasSourceToCompositeMap:
                      !!perfectDict.sourceProductIdToCompositeMap,
                  }
                );
              // ✅ DEFENSIVE: Don't throw an error - just return gracefully
              // This allows the configurator to continue functioning even if some products aren't found
              return { success: false, error: 'Product not found' };
            }

            isDev() &&
              console.log(
                `🧙‍♂️ CONDUCTOR: Using product for auto-toggle: ${product.title || product.handle} (type: ${product.type || 'regular'})`
              );

            // Check for front/back image availability
            let hasOnlyFrontImage = false;
            let hasOnlyBackImage = false;
            let hasBothImages = false;

            // ⚡ O(1) VARIANT LOOKUP: Use PPD map instead of O(n) array search
            const numericVariantId = variantId ? parseInt(variantId) : null;
            const enhancedVariant = numericVariantId
              ? perfectDict.variantIdToVariantMap.get(numericVariantId)
              : null;

            const frontImage =
              product.configuratorFrontImage ||
              enhancedVariant?.configuratorFrontImage;
            const backImage =
              product.configuratorBackImage ||
              enhancedVariant?.configuratorBackImage;

            isDev() &&
              console.log(`⚡ CONDUCTOR: O(1) variant image lookup`, {
                variantId: numericVariantId,
                variantFound: !!enhancedVariant,
                lookupMethod: 'ppd.variantIdToVariantMap.get()',
                performanceWin: 'O(1) vs O(n)',
              });

            if (frontImage && backImage) {
              hasBothImages = true;
            } else if (frontImage && !backImage) {
              hasOnlyFrontImage = true;
            } else if (!frontImage && backImage) {
              hasOnlyBackImage = true;
            }

            isDev() &&
              console.log(`🧙‍♂️ CONDUCTOR: Image availability analysis`, {
                productId,
                variantId,
                hasOnlyFrontImage,
                hasOnlyBackImage,
                hasBothImages,
                frontImageUrl: frontImage ? 'available' : 'none',
                backImageUrl: backImage ? 'available' : 'none',
              });

            // 🔥 CATEGORY CONTEXT MAGIC: Check if category suggests a preferred view
            //
            // The rule itself moved to Phase 0 (utils.getCategoryPreferredView)
            // because media-gallery.js and md-config-bl-image-layer-coordinator.js
            // both also move the camera and, while this rule lived only here,
            // neither could tell whether its own toggle was about to overwrite
            // the customer's intent. It did: see the measured stomp timeline in
            // the helper's doc block. Behaviour here is unchanged -- same
            // includes() tests in the same precedence.
            //
            // Guarded rather than called bare: an unguarded call to a missing
            // Phase 0 utility would throw out of the whole BROWSING branch and
            // cost the customer their click, to save a cosmetic camera flip.
            const _viewUtils = window.MurphyDoorConfigurator?.utils;
            const categoryPreferredView =
              typeof _viewUtils?.getCategoryPreferredView === 'function'
                ? _viewUtils.getCategoryPreferredView(categoryId)
                : null;

            if (categoryPreferredView) {
              isDev() &&
                console.log(
                  `🧙‍♂️ CONDUCTOR: Category "${categoryId}" suggests ${categoryPreferredView.toUpperCase()} view preference`
                );
            }

            // Auto-toggle logic:
            // 1. If product has ONLY one type of image, toggle to show that image
            // 2. If category suggests a view preference (Front/Back Casing), toggle to that view
            const shouldToggleForImageAvailability =
              hasOnlyFrontImage || hasOnlyBackImage;
            const shouldToggleForCategoryContext =
              categoryPreferredView !== null;

            if (
              !shouldToggleForImageAvailability &&
              !shouldToggleForCategoryContext
            ) {
              isDev() &&
                console.log(
                  `🧙‍♂️ CONDUCTOR: No auto-toggle needed - product has both images/no images AND no category preference`
                );
              return { success: true, action: 'no_toggle_needed' };
            }

            // Get current view state from media gallery
            const currentView = this.getCurrentView() || 'front';
            let shouldToggle = false;
            let targetView = currentView;
            let toggleReason = '';

            // Priority 1: Image availability (product has only one image type)
            if (hasOnlyBackImage && currentView === 'front') {
              shouldToggle = true;
              targetView = 'back';
              toggleReason = 'back-only image';
              isDev() &&
                console.log(
                  `🧙‍♂️ CONDUCTOR: Auto-toggling from front to back view for back-only image`
                );
            } else if (hasOnlyFrontImage && currentView === 'back') {
              shouldToggle = true;
              targetView = 'front';
              toggleReason = 'front-only image';
              isDev() &&
                console.log(
                  `🧙‍♂️ CONDUCTOR: Auto-toggling from back to front view for front-only image`
                );
            }
            // Priority 2: Category context (Back Casing / Front Casing preference)
            else if (
              categoryPreferredView &&
              categoryPreferredView !== currentView
            ) {
              shouldToggle = true;
              targetView = categoryPreferredView;
              toggleReason = `category preference (${categoryId})`;
              isDev() &&
                console.log(
                  `🧙‍♂️ CONDUCTOR: Auto-toggling from ${currentView} to ${targetView} view for category preference: ${categoryId}`
                );
            }

            if (shouldToggle) {
              // Publish event to trigger view toggle in media gallery
              if (window.MurphyDoorConfigurator?.CoreEventBus) {
                window.MurphyDoorConfigurator.CoreEventBus.publish(
                  CONSTANTS.EVENTS.UI_AUTO_TOGGLE_VIEW,
                  {
                    reason: toggleReason,
                    productId: productId,
                    variantId: variantId,
                    categoryId: categoryId,
                    currentView: currentView,
                    targetView: targetView,
                    hasOnlyFrontImage: hasOnlyFrontImage,
                    hasOnlyBackImage: hasOnlyBackImage,
                    categoryPreferredView: categoryPreferredView,
                    timestamp: Date.now(),
                  }
                );

                isDev() &&
                  console.log(
                    `🧙‍♂️ CONDUCTOR: Published auto-toggle event for ${currentView} → ${targetView} (${toggleReason})`,
                    {
                      productId,
                      variantId,
                      categoryId,
                      toggleReason,
                    }
                  );

                return {
                  success: true,
                  action: 'toggled',
                  fromView: currentView,
                  toView: targetView,
                  reason: toggleReason,
                };
              } else {
                isDev() &&
                  console.warn(
                    `[${METHOD_SIGNATURE}] CoreEventBus not available for auto-toggle`
                  );
                return { success: false, error: 'Event bus not available' };
              }
            } else {
              isDev() &&
                console.log(
                  `🧙‍♂️ CONDUCTOR: No view toggle needed - already showing correct view`
                );
              return { success: true, action: 'already_correct_view' };
            }
          } catch (error) {
            isDev() &&
              console.error(
                `[${METHOD_SIGNATURE}] Auto image view toggle failed:`,
                error
              );

            // Use conductor's error handling system
            this.handleError('Auto image view toggle failed', error, {
              method: METHOD_SIGNATURE,
              productId: productId,
              variantId: variantId,
              severity: 'low',
              recoverable: true,
            });

            return {
              success: false,
              error: error.message,
            };
          }
        },

        /**
         * Updates UI components and navigation based on progress changes.
         *
         * This method is a key part of the Central Conductor Pattern as it:
         * 1. Coordinates multiple module interactions to respond to progress changes
         * 2. Delegates specific actions to specialized modules (UI updates, state tracking)
         * 3. Orchestrates navigation when progress reaches 100%
         *
         * The method follows system patterns by:
         * - Using the uiUpdater module for all DOM manipulations
         * - Delegating state management to progressTracker
         * - Using standardized event flow through handleStateChange
         *
         * @param {number} progress - The current progress percentage (0-100)
         */
        handleProgressUpdate(progress) {
          const METHOD_SIGNATURE = 'main.handleProgressUpdate';
          const startTime = performance.now();

          try {
            // Parameter validation
            if (
              typeof progress !== 'number' ||
              progress < 0 ||
              progress > 100
            ) {
              throw new Error(
                `Invalid progress value: ${progress}. Must be a number between 0-100.`
              );
            }

            // Verify module dependencies
            if (!window.MurphyDoorConfigurator.uiUpdater) {
              throw new Error('uiUpdater module not available');
            }

            if (!window.MurphyDoorConfigurator.progressTracker) {
              throw new Error('progressTracker module not available');
            }

            // Delegate UI updates to uiUpdater module
            window.MurphyDoorConfigurator.uiUpdater.updateProgressBar(progress);

            // Update summary state through progressTracker
            window.MurphyDoorConfigurator.progressTracker.updateSummaryState(
              progress === 100
            );

            // Update summary status in UI
            window.MurphyDoorConfigurator.uiUpdater.updateSummaryStatus();

            // Reset the celebration guard when progress drops below 100% so a
            // re-completion (e.g., user edits a Summary line, picks a new
            // upgrade, cascade brings progress back to 100%) re-fires confetti.
            //
            // This hooks here — NOT on STATE_SELECTION_CLEARED — because that
            // event fires from at least four sources including cascade-internal
            // mid-flow clears (cascade-auto-selection-coordinator's forbidden
            // auto-unapply path) that don't actually drop overall progress
            // below 100. Resetting on STATE_SELECTION_CLEARED would re-arm the
            // flag during an in-flight cascade and produce a spurious second
            // blast. Resetting here uses the conductor's own progress pipeline
            // as the single source of truth: the flag only clears when
            // progress has demonstrably fallen below 100. Diagnosed by
            // ccp-cascade-logic-auditor 2026-06-18.
            if (progress < 100 && this._celebrationFired) {
              this._celebrationFired = false;
            }

            // Handle completion state - summary tab becomes active
            if (progress === 100) {
              // Update tab status through uiUpdater
              window.MurphyDoorConfigurator.uiUpdater.updateDisplay(
                'TAB_STATUS_UPDATE',
                {
                  tabId: 'summary',
                  isComplete: true,
                }
              );

              // Get summary tab element and trigger navigation
              const summaryTab =
                window.MurphyDoorConfigurator.uiUpdater.DOM.getTabElement(
                  'summary'
                );
              if (summaryTab) {
                // Use standard state change flow for tab navigation
                this.handleStateChange('TAB_CHANGED', { tabId: 'summary' });
                window.MurphyDoorConfigurator.utils.simulateClick(summaryTab);
              }

              // 🎉 CELEBRATION: Trigger confetti when configurator reaches 100% completion
              // Guard: Only fire confetti once per session to prevent duplicate celebrations
              if (!this._celebrationFired) {
                this._celebrationFired = true;

                isDev() &&
                  console.log(
                    '🎉 CONDUCTOR: 100% completion achieved - requesting confetti celebration!'
                  );

                if (window.MurphyDoorConfigurator?.CoreEventBus) {
                  window.MurphyDoorConfigurator.CoreEventBus.publish(
                    CONSTANTS.EVENTS.UI_CONFETTI_CELEBRATION_REQUESTED,
                    {
                      trigger: 'configurator_complete',
                      particleCount: 150,
                      timestamp: Date.now(),
                    }
                  );
                }
              } else {
                isDev() &&
                  console.log(
                    '🎉 CONDUCTOR: 100% completion - confetti already fired this session, skipping duplicate'
                  );
              }
            }

            // Track performance
            const executionTime = performance.now() - startTime;
            if (
              executionTime > 50 &&
              window.MurphyDoorConfigurator.CoreEventBus
            ) {
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.PERFORMANCE_METHOD_SLOW,
                {
                  method: METHOD_SIGNATURE,
                  executionTime,
                  threshold: 50,
                }
              );
            }
          } catch (error) {
            this.handleError(`Progress update failed`, error, {
              method: METHOD_SIGNATURE,
              progress,
              severity: 'medium',
              recoverable: true,
            });
            // Don't rethrow - allow system to continue functioning
          }
        },

        findFirstIncompleteTab() {
          const METHOD_SIGNATURE = 'main.findFirstIncompleteTab';
          const startTime = performance.now();

          try {
            // Validate stateManager exists
            if (!this.stateManager) {
              this.handleError(
                'State manager missing',
                new Error(
                  'Cannot find incomplete tab: state manager is not available'
                ),
                {
                  method: METHOD_SIGNATURE,
                  severity: 'error',
                  recoverable: false,
                }
              );
              return null;
            }

            // Delegate to stateManager to maintain proper Central Conductor Pattern
            const result = this.stateManager.findFirstIncompleteTab();

            // Log performance if took longer than threshold
            const endTime = performance.now();
            const executionTime = endTime - startTime;

            if (
              executionTime > 50 &&
              window.MurphyDoorConfigurator.CoreEventBus
            ) {
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.PERFORMANCE_METHOD_SLOW,
                {
                  method: METHOD_SIGNATURE,
                  executionTime,
                  timestamp: Date.now(),
                }
              );
            }

            return result;
          } catch (error) {
            this.handleError('Error finding incomplete tab', error, {
              method: METHOD_SIGNATURE,
              severity: 'warning',
              recoverable: true,
            });
            return null;
          }
        },

        /**
         * Finds the first incomplete category within a specified tab.
         *
         * This method follows the Central Conductor Pattern by delegating functionality
         * to the state manager while providing error handling and performance monitoring.
         * It's used for determining navigation paths and auto-navigation functionality.
         *
         * @param {number|string} tabId - The ID of the tab to search within
         * @param {Set} [categoriesToSkip] - Optional Set of categoryIds to skip (e.g., auto-applied by cascade)
         * @returns {number|null} The ID of the first incomplete category, or null if none found or on error
         */
        findFirstIncompleteCategory(tabId, categoriesToSkip) {
          const METHOD_SIGNATURE = 'main.findFirstIncompleteCategory';
          const startTime = performance.now();

          try {
            // Validate input parameter
            if (tabId === undefined || tabId === null) {
              this.handleError(
                'Invalid tab ID provided',
                new Error('Cannot find incomplete category: tab ID is missing'),
                {
                  method: METHOD_SIGNATURE,
                  severity: 'warning',
                  recoverable: true,
                  tabId,
                }
              );
              return null;
            }

            // Validate stateManager exists
            if (!this.stateManager) {
              this.handleError(
                'State manager missing',
                new Error(
                  'Cannot find incomplete category: state manager is not available'
                ),
                {
                  method: METHOD_SIGNATURE,
                  severity: 'error',
                  recoverable: false,
                }
              );
              return null;
            }

            // Delegate to stateManager to maintain proper Central Conductor Pattern
            // Pass categoriesToSkip for cascade-aware navigation
            const result = this.stateManager.findFirstIncompleteCategory(
              tabId,
              categoriesToSkip
            );

            // Log performance if took longer than threshold
            const endTime = performance.now();
            const executionTime = endTime - startTime;

            if (
              executionTime > 50 &&
              window.MurphyDoorConfigurator.CoreEventBus
            ) {
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.PERFORMANCE_METHOD_SLOW,
                {
                  method: METHOD_SIGNATURE,
                  executionTime,
                  timestamp: Date.now(),
                  tabId: tabId,
                }
              );
            }

            return result;
          } catch (error) {
            this.handleError('Error finding incomplete category', error, {
              method: METHOD_SIGNATURE,
              tabId: tabId,
              severity: 'warning',
              recoverable: true,
            });
            return null;
          }
        },

        /* Progress Tracking Methods */
        /**
         * Checks completion status of a tab or category
         *
         * Follows the Central Conductor Pattern by:
         * - Delegating status checking to the progressTracker module
         * - Providing error handling and dependency validation
         * - Including performance monitoring for slow operations
         * - Using consistent event publication patterns
         *
         * @param {string|number} elementId - ID of the element to check
         * @param {string} type - Type of element ('tab' or 'category')
         * @returns {boolean} - True if element is complete, false otherwise
         */
        checkCompletionStatus(elementId, type) {
          const METHOD_SIGNATURE = 'checkCompletionStatus';
          const startTime = window.performance?.now?.() || Date.now();

          try {
            // Validate parameters
            if (!elementId) {
              throw new Error('Element ID is required');
            }

            if (!type || (type !== 'tab' && type !== 'category')) {
              throw new Error('Type must be either "tab" or "category"');
            }

            // Ensure progressTracker is available
            if (!window.MurphyDoorConfigurator.progressTracker) {
              this.handleError(
                'Progress tracker not available',
                new Error('Progress tracker dependency missing'),
                {
                  method: METHOD_SIGNATURE,
                  module: 'main',
                  severity: 'medium',
                  elementId,
                  type,
                }
              );
              return false;
            }

            // Delegate to appropriate method in progressTracker
            const result =
              type === 'tab'
                ? window.MurphyDoorConfigurator.progressTracker.isTabComplete(
                    elementId
                  )
                : window.MurphyDoorConfigurator.progressTracker.isCategoryComplete(
                    elementId
                  );

            // Performance monitoring
            const endTime = window.performance?.now?.() || Date.now();
            const executionTime = endTime - startTime;

            // Publish performance event if operation was slow (> 10ms)
            if (
              executionTime > 10 &&
              window.MurphyDoorConfigurator.CoreEventBus
            ) {
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.PERFORMANCE_COMPLETION_CHECK_SLOW,
                {
                  timestamp: endTime,
                  source: 'md-config-core-conductor.js',
                  method: METHOD_SIGNATURE,
                  elementId,
                  type,
                  executionTime,
                  threshold: 10,
                }
              );
            }

            return result;
          } catch (error) {
            // Handle errors
            this.handleError(
              `Failed to check ${type} completion status`,
              error,
              {
                method: METHOD_SIGNATURE,
                module: 'main',
                severity: 'medium',
                elementId,
                type,
                recoverable: true,
              }
            );

            // Publish error event
            if (window.MurphyDoorConfigurator.CoreEventBus) {
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.ERROR_COMPLETION_CHECK_FAILED,
                {
                  timestamp: Date.now(),
                  source: 'md-config-core-conductor.js',
                  method: METHOD_SIGNATURE,
                  elementId,
                  type,
                  error: {
                    message: error.message,
                    stack: error.stack,
                  },
                }
              );
            }

            return false;
          }
        },

        /**
         * Checks if the summary tab is complete (all required selections made)
         *
         * Follows the Central Conductor Pattern by:
         * - Delegating summary status checking to the progressTracker module
         * - Providing error handling and dependency validation
         * - Using consistent event publication patterns
         *
         * @returns {boolean} - True if summary is complete, false otherwise
         */
        checkSummaryComplete() {
          const METHOD_SIGNATURE = 'checkSummaryComplete';

          try {
            // Ensure progressTracker is available
            if (!window.MurphyDoorConfigurator.progressTracker) {
              this.handleError(
                'Progress tracker not available',
                new Error('Progress tracker dependency missing'),
                {
                  method: METHOD_SIGNATURE,
                  module: 'main',
                  severity: 'medium',
                }
              );
              return false;
            }

            // Delegate to progressTracker
            return window.MurphyDoorConfigurator.progressTracker.isSummaryComplete();
          } catch (error) {
            // Handle errors
            this.handleError(
              'Failed to check summary completion status',
              error,
              {
                method: METHOD_SIGNATURE,
                module: 'main',
                severity: 'medium',
                recoverable: true,
              }
            );

            // Publish error event
            if (window.MurphyDoorConfigurator.CoreEventBus) {
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.ERROR_SUMMARY_CHECK_FAILED,
                {
                  timestamp: Date.now(),
                  source: 'md-config-core-conductor.js',
                  method: METHOD_SIGNATURE,
                  error: {
                    message: error.message,
                    stack: error.stack,
                  },
                }
              );
            }

            return false;
          }
        },

        /**
         * How many categories still need an answer (0 when complete).
         *
         * CCP delegation: UI modules must not reach progressTracker directly.
         * Mirrors checkSummaryComplete()'s shape deliberately -- the two answer
         * the same question ("is this door finished, and if not, by how much"),
         * and both bottom out in the SAME two counters inside progressTracker,
         * so the Add to Cart's disabled state and the remaining count cannot
         * disagree.
         *
         * Returns 0 rather than throwing when the tracker is unavailable: the
         * consumer is a status hint beside the Add to Cart, and 0 makes it hide
         * itself. Showing a wrong number there is worse than showing nothing.
         *
         * @returns {number} categories remaining; 0 when complete or unknown
         */
        getRemainingCategoryCount() {
          const METHOD_SIGNATURE = 'getRemainingCategoryCount';
          try {
            const tracker = window.MurphyDoorConfigurator.progressTracker;
            if (!tracker || typeof tracker.getRemainingCategoryCount !== 'function') {
              return 0;
            }
            return tracker.getRemainingCategoryCount();
          } catch (error) {
            this.handleError(
              'Failed to read remaining category count',
              error,
              {
                method: METHOD_SIGNATURE,
                module: 'main',
                severity: 'low',
                recoverable: true,
              }
            );
            return 0;
          }
        },

        /**
         * Is the APPLIED state actually cart-ready? The single answer every Add
         * to Cart gate reads: the button's disabled state (ui-updater), the
         * ADD_TO_CART case below, and cart-manager's last-line check before
         * POSTing /cart/add.js.
         *
         * 🚨 DERIVED FROM THE APPLIED MAP, NOT FROM A COUNTER OR A DOM CLASS.
         * Live 2026-09-29 (flush-mount-bookcase-door-2-0, apo_style): a slider
         * resting on a forbidden Door Depth stop had NO applied Door Depth, yet
         * the progress counter, the card's check mark and the button all said
         * complete, and the cart received 20 lines with no Door Depth at all.
         * Every one of those was a cached claim; the Map was the only store
         * that told the truth. Measured on production the same day: after a
         * full fill all 20 required categories (Width/Height and the hidden,
         * gate-auto-applied cabinet categories included) are in the Map, so
         * reading it here cannot strand a finished door.
         *
         * Result fields:
         *   missing   - required categories with no applied answer
         *   forbidden - the subset of `missing` whose on-screen browse (a slider
         *               stop, a previewed option) is forbidden and so can never
         *               be flushed into the cart
         *   pending   - the subset of `missing` whose browse is ALLOWED: the
         *               ADD_TO_CART flush applies these before the cart is read
         *   stale     - applied in the Map, but the cart snapshot disagrees
         *               (the snapshot is the cart payload; see the "TWO
         *               Applied-Selection Stores" footgun)
         *
         * `options.allowPending` (UI only) treats `pending` as not blocking,
         * because the flush will apply them. Every post-flush check is strict.
         *
         * Fails OPEN when the PPD has no required-category list: that is a
         * data-pipeline defect, and failing closed there would disable every
         * Add to Cart on the site. It is logged in dev.
         *
         * @param {{allowPending?: boolean}} [options]
         * @returns {{ready: boolean, known: boolean, missing: string[], forbidden: string[], pending: string[], stale: string[]}}
         */
        getCartReadiness(options) {
          const allowPending = !!(options && options.allowPending);
          const missing = [];
          const forbidden = [];
          const pending = [];
          const stale = [];
          const freeze = (ready, known) =>
            Object.freeze({
              ready,
              known,
              missing: Object.freeze(missing.slice()),
              forbidden: Object.freeze(forbidden.slice()),
              pending: Object.freeze(pending.slice()),
              stale: Object.freeze(stale.slice()),
            });

          try {
            const ppd = this.getPerfectProductDictionary();
            const required =
              ppd &&
              ppd.categoryMetadata &&
              ppd.categoryMetadata.allRequiredCategoryIdsArray;
            if (!Array.isArray(required) || required.length === 0) {
              isDev() &&
                console.warn(
                  '⚠️ CONDUCTOR: getCartReadiness has no required-category list; failing open'
                );
              return freeze(true, false);
            }

            const map = this.stateManager.getAppliedSelectionsMap();
            const cartConfig = this.stateManager.getCartConfiguration();
            const cartSelections = (cartConfig && cartConfig.selections) || {};
            const browsing = this.stateManager.getStateMaps().browsingSelections;

            const sameSelection = (a, b) => {
              if (!a || !b) return false;
              if (a.variantId != null || b.variantId != null) {
                return String(a.variantId) === String(b.variantId);
              }
              return String(a.productId) === String(b.productId);
            };

            required.forEach((categoryId) => {
              const applied = map.get(categoryId);
              if (!applied) {
                missing.push(categoryId);
                const browse = browsing && browsing.get(categoryId);
                if (browse) {
                  const verdict = browse.variantId
                    ? this.isVariantPreemptivelyForbidden(
                        String(browse.variantId)
                      )
                    : null;
                  if (verdict && verdict.isForbidden === true) {
                    forbidden.push(categoryId);
                  } else {
                    pending.push(categoryId);
                  }
                }
                return;
              }
              if (!sameSelection(applied, cartSelections[categoryId])) {
                stale.push(categoryId);
              }
            });

            const blocking = allowPending
              ? missing.filter((id) => pending.indexOf(id) === -1)
              : missing;
            return freeze(blocking.length === 0 && stale.length === 0, true);
          } catch (error) {
            this.handleError('Failed to evaluate cart readiness', error, {
              method: 'getCartReadiness',
              module: 'main',
              severity: 'medium',
              recoverable: true,
            });
            // Cannot prove the payload is whole: refuse. The ATC gate then
            // points the customer at the first unfinished category.
            return freeze(false, false);
          }
        },

        /**
         * A category left UNAPPLIED while still browsed (a slider resting on a
         * forbidden stop, a rejected commit, a forbidden preview at Add to
         * Cart): make every "answered" claim agree with the applied Map.
         *
         * Delegates the state half to stateManager.demoteUnappliedCategory()
         * (which keeps the browse), then publishes the same coordination the
         * other clear paths use -- STATE_SELECTION_CLEARED drives the progress
         * counter, the URL, preemptive forbidden recalculation and ui-updater's
         * check mark / Summary row reset -- and asks for a progress repaint so
         * the "N choices left" counter and the Add to Cart state recompute.
         *
         * No-op (returns false) when the category is still applied, so it is
         * safe to call from any rejection path.
         *
         * @param {string} categoryId
         * @param {string} source - who demoted it (for the event payload)
         * @returns {boolean} true when the category was demoted
         */
        _demoteUnappliedCategory(categoryId, source) {
          try {
            if (
              !categoryId ||
              !this.stateManager ||
              typeof this.stateManager.demoteUnappliedCategory !== 'function'
            ) {
              return false;
            }
            if (this.stateManager.getAppliedSelectionsMap().has(categoryId)) {
              return false;
            }
            const demoted =
              this.stateManager.demoteUnappliedCategory(categoryId);
            // The snapshot can already be clean while the progress counter
            // (and with it the check mark) still claims the category -- they
            // are separate caches. Publish when EITHER still claims it; stay
            // silent for a category nothing ever called answered, so a
            // rejected first-time pick does not fire a clear for nothing.
            const tracker = window.MurphyDoorConfigurator.progressTracker;
            const progressClaims = !!(
              tracker &&
              typeof tracker.isCategoryComplete === 'function' &&
              tracker.isCategoryComplete(categoryId)
            );
            if (!demoted && !progressClaims) return false;

            const tabId = this.stateManager.getCategoryTab(categoryId);
            if (tabId && window.MurphyDoorConfigurator.uiUpdater) {
              window.MurphyDoorConfigurator.uiUpdater.updateDisplay(
                'TAB_STATUS_UPDATE',
                { tabId: String(tabId), isComplete: false }
              );
            }

            publishEvent(CONSTANTS.EVENTS.STATE_SELECTION_CLEARED, {
              categoryId,
              source: source || 'conductor.demote_unapplied',
              browsingPreserved: true,
              timestamp: Date.now(),
            });

            if (demoted) this.stateManager.persistState();

            publishEvent(CONSTANTS.EVENTS.UI_PROGRESS_UPDATE_REQUESTED, {
              source: source || 'conductor.demote_unapplied',
              timestamp: Date.now(),
            });
            return true;
          } catch (error) {
            this.handleError('Failed to demote unapplied category', error, {
              method: '_demoteUnappliedCategory',
              module: 'main',
              severity: 'medium',
              recoverable: true,
              categoryId,
            });
            return false;
          }
        },

        /**
         * Calculates and returns the total progress percentage
         *
         * Follows the Central Conductor Pattern by:
         * - Delegating progress calculation to the progressTracker module
         * - Providing error handling and dependency validation
         * - Using consistent event publication patterns
         *
         * @returns {number} - Progress percentage (0-100)
         */
        calculateTotalProgress() {
          const METHOD_SIGNATURE = 'calculateTotalProgress';

          try {
            // Ensure progressTracker is available
            if (!window.MurphyDoorConfigurator.progressTracker) {
              this.handleError(
                'Progress tracker not available',
                new Error('Progress tracker dependency missing'),
                {
                  method: METHOD_SIGNATURE,
                  module: 'main',
                  severity: 'medium',
                }
              );
              return 0;
            }

            // Delegate to progressTracker and return the calculated value
            const progress =
              window.MurphyDoorConfigurator.progressTracker.calculateTotalProgress();

            // Publish progress update event
            if (window.MurphyDoorConfigurator.CoreEventBus) {
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.SYSTEM_PROGRESS_UPDATED,
                {
                  progress,
                  timestamp: Date.now(),
                }
              );
            }

            return progress;
          } catch (error) {
            // Handle errors
            this.handleError('Failed to calculate total progress', error, {
              method: METHOD_SIGNATURE,
              module: 'main',
              severity: 'medium',
              recoverable: true,
            });

            // Publish error event
            if (window.MurphyDoorConfigurator.CoreEventBus) {
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.ERROR_PROGRESS_CALCULATION_FAILED,
                {
                  timestamp: Date.now(),
                  source: 'md-config-core-conductor.js',
                  method: METHOD_SIGNATURE,
                  error: {
                    message: error.message,
                    stack: error.stack,
                  },
                }
              );
            }

            return 0;
          }
        },

        /* Progress Type Methods */
        /**
         * Determines if a product is a single-variant type product based on modern classification.
         * This method checks the product structure for the given product ID and returns
         * whether it's marked as a single-variant product.
         *
         * Following the Central Conductor Pattern, this method:
         * 1. Delegates data access to the product structure via getProductStructure()
         * 2. Provides proper error handling for invalid inputs
         * 3. Returns a consistent boolean value regardless of input quality
         *
         * @param {number|string} productId - The ID of the product to check
         * @returns {boolean} True if the product is a single-variant type, false otherwise
         */
        isOneStepProduct(productId) {
          try {
            // Validate productId input
            if (productId === null || productId === undefined) {
              return false;
            }

            // Get product structure using conductor method
            const productStructure = this.getProductStructure();

            // Safety check for product structure
            if (!productStructure || !productStructure.addOnProducts) {
              this.handleError(
                'Invalid product structure in isOneStepProduct',
                null,
                {
                  methodSignature: 'isOneStepProduct',
                  productId,
                }
              );
              return false;
            }

            // Parse productId to integer for consistent Map access
            const parsedId = parseInt(productId, 10);

            // Handle NaN case from invalid string parsing
            if (isNaN(parsedId)) {
              return false;
            }

            // Get product and check its type using modern classification
            const product = productStructure.addOnProducts.get(parsedId);
            return product?.type === 'single-variant';
          } catch (error) {
            // Centralized error handling following conductor pattern
            this.handleError('Error in isOneStepProduct method', error, {
              methodSignature: 'isOneStepProduct',
              productId,
            });
            return false;
          }
        },

        /* Validation Methods */
        validateStateChange(action, data) {
          if (!action || !data) {
            return {
              isValid: false,
              error: 'Invalid action or data for state change',
            };
          }

          if (!window.MurphyDoorConfigurator.supportValidationManager) {
            isDev() &&
              console.warn(
                'Validation Manager not available, skipping validation'
              );
            return { isValid: true };
          }

          try {
            // Delegate to validation manager for specialized validation
            return window.MurphyDoorConfigurator.supportValidationManager.validateStateChange(
              action,
              data
            );
          } catch (error) {
            const errorContext = {
              method: 'validateStateChange',
              action,
              dataKeys: data ? Object.keys(data) : [],
              severity: 'error',
              recoverable: true,
            };

            this.handleError(
              'Error during state change validation',
              error,
              errorContext
            );

            if (window.MurphyDoorConfigurator.CoreEventBus) {
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.ERROR_VALIDATION,
                {
                  action,
                  error: error.message,
                  stack: error.stack,
                  timestamp: Date.now(),
                }
              );
            }

            return { isValid: false, error: error.message };
          }
        },

        /* HTML Generation Methods */
        /**
         * REMOVED: Legacy generateThumbnailsHTML method
         *
         * This method was causing race conditions with the Product Virtualization Layer.
         * All thumbnail generation now goes through generateThumbnailsHTMLWithProducts()
         * to ensure consistent CompositeProduct handling.
         *
         * P2.3: Remove Conflicting Thumbnail Generation - LEGACY METHOD ELIMINATED
         */

        /**
         * PHASE 2.H.1: Product Virtualization Layer - Enhanced thumbnail generation
         *
         * Pure delegation to navigation manager - NO caching, NO logic
         * Caching should be handled by UI module if needed, NOT conductor
         *
         * @param {string} categoryId - Category identifier
         * @param {Array} virtualizedProducts - Array of Product | CompositeProduct objects
         * @returns {string} Generated HTML for virtualized product thumbnails
         */
        generateThumbnailsHTMLWithProducts(categoryId, virtualizedProducts) {
          try {
            if (!categoryId) {
              this.handleError(
                'Missing categoryId in generateThumbnailsHTMLWithProducts',
                { categoryId }
              );
              return '';
            }

            if (
              !virtualizedProducts ||
              !Array.isArray(virtualizedProducts) ||
              virtualizedProducts.length === 0
            ) {
              isDev() &&
                console.warn(
                  `Conductor: No virtualized products provided for ${categoryId}`
                );
              return '';
            }

            // Pure delegation - no caching, no logic
            if (
              !window.MurphyDoorConfigurator.navigationManager
                ?.generateThumbnailsHTMLWithProducts
            ) {
              isDev() &&
                console.error(
                  'Conductor: navigationManager.generateThumbnailsHTMLWithProducts method not available'
                );
              return '';
            }

            isDev() &&
              console.log(
                `✅ Conductor: Pure delegation to navigationManager for ${categoryId} with ${virtualizedProducts.length} products`
              );

            return window.MurphyDoorConfigurator.navigationManager.generateThumbnailsHTMLWithProducts(
              categoryId,
              virtualizedProducts
            );
          } catch (error) {
            this.handleError(
              'Error in generateThumbnailsHTMLWithProducts delegation',
              error,
              { categoryId, productCount: virtualizedProducts?.length || 0 }
            );
            return '';
          }
        },

        /**
         * Generates HTML for a variant thumbnail by formatting the display title and delegating
         * the actual HTML generation to the navigationManager.
         *
         * This method follows the Central Conductor Pattern by:
         * 1. Formatting display data through utils module
         * 2. Delegating HTML generation to the specialized navigation module
         * 3. Maintaining proper separation of concerns
         *
         * @param {Object} variantData - Data for the variant to render
         * @param {string} variantData.id - Unique identifier for the variant
         * @param {string} variantData.title - Title of the variant
         * @param {number} variantData.price - Price of the variant
         * @param {Object} variantData.image - Image data for the variant
         * @param {string} variantData.image.src - Source URL for the variant image
         * @param {string} variantData.image.alt - Alt text for the variant image
         * @returns {string} Generated HTML for the variant thumbnail
         */
        generateVariantHTML(variantData) {
          try {
            if (!variantData || !variantData.id) {
              this.handleError('Invalid variant data in generateVariantHTML', {
                variantData,
              });
              return '';
            }

            // 🔥 CCP COMPLIANCE FIX: Use normalized variant title from perfect product dictionary
            // instead of raw variantData.title which bypasses our normalization system
            let normalizedTitle = variantData.title; // fallback to raw title

            // Try to get the enhanced/normalized title from perfect product dictionary
            const ppd = this.getPerfectProductDictionary();
            isDev() &&
              console.log(
                `🔍 PPD DEBUG: Checking PPD lookup for variant ${variantData.id}, product ${variantData.productId}`
              );
            isDev() &&
              console.log(
                `🔍 PPD DEBUG: PPD available: ${!!ppd}, has products: ${!!(ppd && ppd.products)}`
              );

            // 🚀 PPD-FIRST: O(1) variant lookup instead of O(n) array search
            if (!ppd?.variantIdToVariantMap) {
              isDev() &&
                console.warn(
                  `⚠️ generateVariantHTML: variantIdToVariantMap not available in PPD - using raw variant title`
                );
              normalizedTitle = variantData.title;
            } else {
              const numericVariantId = parseInt(variantData.id, 10);
              if (isNaN(numericVariantId)) {
                isDev() &&
                  console.warn(
                    `⚠️ generateVariantHTML: Invalid non-numeric variantId "${variantData.id}" - using raw title`
                  );
                normalizedTitle = variantData.title;
              } else {
                // O(1) direct variant lookup from PPD map
                const enhancedVariant =
                  ppd.variantIdToVariantMap.get(numericVariantId);

                if (enhancedVariant?.title) {
                  normalizedTitle = enhancedVariant.title;
                  isDev() &&
                    console.log(
                      `⚡ generateVariantHTML: O(1) map lookup - normalized title "${normalizedTitle}"`
                    );
                } else {
                  isDev() &&
                    console.warn(
                      `⚠️ generateVariantHTML: Variant ${variantData.id} not found in PPD variantIdToVariantMap - using raw title`
                    );
                  normalizedTitle = variantData.title;
                }
              }
            }

            const displayTitle = this.formatDisplayTitle(
              normalizedTitle,
              variantData.price
            );

            return window.MurphyDoorConfigurator.navigationManager.generateVariantThumbnailHTML(
              variantData,
              displayTitle
            );
          } catch (error) {
            this.handleError('Error generating variant HTML', error, {
              variantData,
            });
            return '';
          }
        },

        /* Formatting Methods */
        /**
         * ⚡ ARCHITECTURAL VIOLATION: This method performs runtime display formatting
         * ALL display formatting MUST be pre-extracted in PPD Layer 1
         * Use variant.displayFormatting.baseDisplay instead
         * @throws {Error} Always throws to enforce PPD-first architecture
         */
        formatDisplayTitle(title, price) {
          throw new Error(
            '🔥 CRITICAL ARCHITECTURAL VIOLATION: Runtime display formatting is PROHIBITED in PPD-First architecture!\n' +
              '✅ CORRECT PATTERN: Use variant.displayFormatting.baseDisplay (pre-extracted in PPD Layer 1)\n' +
              '❌ LEGACY PATTERN: formatDisplayTitle() runtime string concatenation\n' +
              'This is a PPD-First architecture violation - all formatting must happen during bootstrap Phase 1.\n' +
              `Attempted to format: "${title}" with price: "${price}"`
          );
        },

        /* Error Handling */
        /**
         * Centralized error handling method that logs errors and publishes error events
         * through the error handler.
         *
         * This method follows the Central Conductor Pattern by channeling all error
         * handling through a consistent interface, maintaining proper separation of concerns.
         *
         * @param {string} message - Human-readable error message
         * @param {Error|Object} error - Error object or details
         * @param {Object} [context] - Additional contextual information about the error
         * @returns {Object} Error details including message, error object, and context
         */
        handleError(message, error, context = {}) {
          // Add default error metadata
          const errorContext = {
            timestamp: Date.now(),
            // Use the source from context if provided, otherwise default to md-config-core-conductor.js
            source: context.source || 'md-config-core-conductor.js',
            severity: 'error',
            ...context,
          };

          // Add method signature if provided
          if (context.method) {
            errorContext.methodSignature = `md-config-core-conductor.js:${context.method}`;
          }

          // Delegate to error handler if available
          if (window.MurphyDoorConfigurator.errorHandler) {
            window.MurphyDoorConfigurator.errorHandler.logError(
              message,
              error,
              errorContext
            );
          } else {
            isDev() &&
              console.error(
                '[Murphy Door Configurator]',
                message,
                error,
                errorContext
              );
          }

          // Route through centralized error handler (DRY: single publication point)
          window.MurphyDoorConfigurator.utils.handleSystemError({
            type: errorContext.type || 'ConductorError',
            error: error,
            message: message,
            source: errorContext.methodSignature || 'conductor.handleError',
            recoverable: errorContext.recoverable !== false,
            impact: errorContext.severity === 'high' ? 'critical' : 'medium',
            details: errorContext,
          });

          return { message, error, context: errorContext };
        },

        /* State Recovery Methods */
        /**
         * Attempts to recover application state from persistent storage
         *
         * This method coordinates state recovery through the stateManager by:
         * 1. Delegating state retrieval to stateManager.recoverState()
         * 2. Publishing state recovery events for monitoring
         * 3. Updating UI with recovery status
         * 4. Initializing system with recovered state
         * 5. Measuring performance and reporting slow recoveries
         *
         * Following the Central Conductor Pattern, this method:
         * - Acts as the orchestrator for recovery flow
         * - Delegates actual recovery work to specialized modules
         * - Coordinates events between modules
         * - Manages error handling across the system
         * - Provides performance monitoring for the operation
         *
         * @returns {Promise<boolean>} True if state was successfully recovered, false otherwise
         */
        async attemptStateRecovery() {
          const METHOD_SIGNATURE =
            'md-config-core-conductor.js:attemptStateRecovery';

          isDev() &&
            console.log(
              '🧙‍♂️ Conductor: Delegating state recovery to persistence manager'
            );

          try {
            // Publish recovery attempt event - conductor coordinates through events
            if (window.MurphyDoorConfigurator.CoreEventBus) {
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.STATE_RECOVERY_REQUESTED,
                {
                  timestamp: Date.now(),
                  source: METHOD_SIGNATURE,
                }
              );
            }

            // Delegate to persistence manager - this is the proper Central Conductor Pattern
            const persistenceManager =
              window.MurphyDoorConfigurator.supportPersistenceManager;
            if (!persistenceManager) {
              isDev() &&
                console.warn(
                  '🔮 Conductor: Persistence manager not available for recovery delegation'
                );
              return false;
            }

            isDev() &&
              console.log(
                '🔮 Conductor: Calling persistence manager recoverState()'
              );
            const recovery = await persistenceManager.recoverState();

            if (recovery && recovery.state) {
              isDev() &&
                console.log(
                  '🧙‍♂️ Conductor: Recovery successful via delegation:',
                  recovery.source
                );

              // Publish success event - let other modules handle their own recovery logic
              if (window.MurphyDoorConfigurator.CoreEventBus) {
                window.MurphyDoorConfigurator.CoreEventBus.publish(
                  CONSTANTS.EVENTS.STATE_RECOVERY_SUCCEEDED,
                  {
                    source: recovery.source,
                    state: recovery.state,
                    timestamp: Date.now(),
                  }
                );
              }

              // URL-based recovery is now handled by md-config-support-url-param-parser.js
              // which detects ?config=, parses, validates against PPD, and publishes
              // BUSINESS_CASCADE_APPLY_URL_SELECTIONS for the cascade coordinator.
              // The conductor recovery path only handles non-URL sources (session, etc.)
              if (recovery.source === 'url') {
                isDev() &&
                  console.log(
                    '🔗 CONDUCTOR: URL recovery is handled by url-param-parser module via cascade coordinator, skipping conductor recovery path'
                  );
                // Still initialize event listeners - the url-param-parser handles selections
                this.initializeEventListeners();
                return true;
              } else {
                // Use instant recovery for non-URL sources (session storage, etc.)
                isDev() &&
                  console.log(
                    '🔮 CONDUCTOR: Using instant recovery for non-URL source:',
                    recovery.source
                  );
                this.initializeFromRecoveredState(recovery.state);
                return true;
              }
            } else {
              isDev() &&
                console.log(
                  '🔮 Conductor: No recovery data available via delegation'
                );

              // Publish failure event
              if (window.MurphyDoorConfigurator.CoreEventBus) {
                window.MurphyDoorConfigurator.CoreEventBus.publish(
                  CONSTANTS.EVENTS.STATE_RECOVERY_FAILED,
                  {
                    reason: 'no_recovery_data',
                    timestamp: Date.now(),
                    source: METHOD_SIGNATURE,
                  }
                );
              }
              return false;
            }
          } catch (error) {
            isDev() &&
              console.error(
                '🔥 Conductor: State recovery delegation failed:',
                error
              );

            // Handle error through centralized error handler
            this.handleError('State recovery delegation failed', error, {
              method: 'attemptStateRecovery',
              severity: 'error',
              recoverable: true,
            });

            // Publish error event
            if (window.MurphyDoorConfigurator.CoreEventBus) {
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.STATE_RECOVERY_FAILED,
                {
                  error,
                  timestamp: Date.now(),
                  source: METHOD_SIGNATURE,
                }
              );
            }

            return false;
          }
        },

        /**
         * Re-initializes the application with recovered state
         *
         * This method coordinates the re-initialization process by:
         * 1. Reinitializing the progress tracker
         * 2. Setting up event listeners
         * 3. Initiating navigation based on recovered state
         * 4. Publishing initialization events
         *
         * Following the Central Conductor Pattern, this method:
         * - Coordinates initialization across modules
         * - Delegates specific tasks to specialized modules
         * - Manages the initialization sequence
         *
         * @param {Object} state - The recovered state object
         */
        initializeFromRecoveredState(state) {
          try {
            isDev() &&
              console.log(
                '🔮 CONDUCTOR: Initializing UI from recovered state',
                {
                  state,
                  tabStructureExists: !!this.getTabStructure,
                  categoryStructureExists: !!this.getCategoryStructure,
                }
              );

            // Publish initialization started event
            if (window.MurphyDoorConfigurator.CoreEventBus) {
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.STATE_RECOVERY_INITIALIZATION_STARTED,
                {
                  timestamp: Date.now(),
                  source:
                    'md-config-core-conductor.js:initializeFromRecoveredState',
                }
              );
            }

            /**
             * SYSTEM:READY EVENT DEPENDENCY DOCUMENTATION
             *
             * EVENT SOURCE: md-config-core-conductor.js::initializeConfiguratorCore()
             * EVENT NAME: CONSTANTS.EVENTS.SYSTEM_INITIALIZED (line ~1553)
             *
             * PUBLICATION CONDITIONS:
             * The CONSTANTS.EVENTS.SYSTEM_INITIALIZED event fires when the conductor completes its core
             * initialization sequence, specifically after:
             * 1. Perfect product dictionary initialization
             * $(echo "$(sed -n "5518,5523p" /Users/murphydoor/Git/murphy-door-shopify/assets/md-config-core-conductor.js | grep -o "^[^0-9]*[0-9]." | sed "s/[^0-9]//g")-1" | bc). Perfect product dictionary initialization
             * $(echo "$(sed -n "5518,5523p" /Users/murphydoor/Git/murphy-door-shopify/assets/md-config-core-conductor.js | grep -o "^[^0-9]*[0-9]." | sed "s/[^0-9]//g")-1" | bc). State recovery attempt
             * $(echo "$(sed -n "5518,5523p" /Users/murphydoor/Git/murphy-door-shopify/assets/md-config-core-conductor.js | grep -o "^[^0-9]*[0-9]." | sed "s/[^0-9]//g")-1" | bc). Configuration validation
             * $(echo "$(sed -n "5518,5523p" /Users/murphydoor/Git/murphy-door-shopify/assets/md-config-core-conductor.js | grep -o "^[^0-9]*[0-9]." | sed "s/[^0-9]//g")-1" | bc). Tab/category structure initialization
             * $(echo "$(sed -n "5518,5523p" /Users/murphydoor/Git/murphy-door-shopify/assets/md-config-core-conductor.js | grep -o "^[^0-9]*[0-9]." | sed "s/[^0-9]//g")-1" | bc). Event listener setup
             * $(echo "$(sed -n "5518,5523p" /Users/murphydoor/Git/murphy-door-shopify/assets/md-config-core-conductor.js | grep -o "^[^0-9]*[0-9]." | sed "s/[^0-9]//g")-1" | bc). Initial pricing/shipping display updates
             *
             * SUBSCRIPTION PATTERN:
             * We subscribe with {once: true} because this is a one-time deferred initialization
             * scenario. If tab structure is not ready when state recovery runs, we wait for
             * the system:initialized event to signal that all core structures are in place,
             * then retry the recovery operation exactly once.
             *
             * TIMEOUT SAFETY MECHANISM:
             * A 15-second timeout is set as a failsafe. If system:initialized never fires
             * (indicating a critical initialization failure), the timeout prevents:
             * - Memory leaks from hanging event subscriptions
             * - Infinite wait states that block user interaction
             * - Silent failures that are hard to diagnose
             *
             * The timeout logs detailed error messages to help diagnose why initialization
             * failed and cleans up the event subscription to prevent resource leaks.
             *
             * ARCHITECTURAL NOTE:
             * This pattern handles a race condition where state recovery (from localStorage/URL)
             * may attempt to restore selections before the tab/category structure is fully
             * initialized. The deferred retry ensures selections are applied only after the
             * DOM structure exists to receive them.
             */

            // CRITICAL: Verify tab structure exists before proceeding
            // State recovery happens AFTER normal initialization, so tabs should exist
            // If they don't, we need to wait for system:ready event
            const tabStructure = this.getTabStructure();
            if (!tabStructure || tabStructure.size === 0) {
              isDev() &&
                console.warn(
                  '⚠️ CONDUCTOR: Tab structure not ready during state recovery - deferring initialization'
                );

              // Set up one-time listener for when system is ready
              let timeoutId;
              const handleSystemReady = () => {
                // Clear timeout if event fires
                clearTimeout(timeoutId);
                isDev() &&
                  console.log(
                    '🔮 CONDUCTOR: System ready - completing deferred state recovery'
                  );
                // Retry initialization now that tab structure should exist
                this.initializeFromRecoveredState(state);
              };

              // Set up 15-second timeout safety mechanism
              timeoutId = setTimeout(() => {
                isDev() &&
                  console.error(
                    '❌ CONDUCTOR: Deferred state recovery timeout - system:initialized event never fired after 15 seconds'
                  );
                isDev() &&
                  console.error(
                    '🔍 CONDUCTOR: State recovery failed - tab structure never initialized'
                  );
                // Unsubscribe from event to prevent memory leak
                if (window.MurphyDoorConfigurator.CoreEventBus) {
                  window.MurphyDoorConfigurator.CoreEventBus.unsubscribe(
                    CONSTANTS.EVENTS.SYSTEM_INITIALIZED,
                    handleSystemReady
                  );
                }
              }, 15000);

              // Subscribe to system ready event
              if (window.MurphyDoorConfigurator.CoreEventBus) {
                window.MurphyDoorConfigurator.CoreEventBus.subscribe(
                  CONSTANTS.EVENTS.SYSTEM_INITIALIZED,
                  handleSystemReady,
                  { once: true }
                );
              }

              return; // Exit early, will retry when ready
            }

            // CRITICAL: Apply recovered selections to state manager BEFORE UI initialization
            isDev() &&
              console.log(
                '🔥 CONDUCTOR: Applying recovered selections to state manager'
              );
            let selectionsApplied = 0;

            for (const [categoryId, selection] of Object.entries(state)) {
              try {
                isDev() &&
                  console.log(
                    `🔮 CONDUCTOR: Restoring selection for category "${categoryId}"`,
                    selection
                  );

                // Use handleSelectionState directly during recovery to bypass UI dependencies
                // 🔥 COMPOSITE PRODUCT FIX: Pass categoryId override during recovery
                // to ensure composite products are stored under correct category key
                const result = this.stateManager.handleSelectionState(
                  selection.productId,
                  selection.variantId,
                  'applied', // Set as applied state during recovery
                  0, // pngLayerDelay
                  null, // element
                  categoryId // categoryIdOverride - ensures correct category for composite products
                );

                if (result && result.success) {
                  selectionsApplied++;
                  isDev() &&
                    console.log(
                      `✅ CONDUCTOR: Successfully restored selection for "${categoryId}"`
                    );
                } else {
                  isDev() &&
                    console.warn(
                      `⚠️ CONDUCTOR: Failed to restore selection for "${categoryId}"`,
                      result
                    );
                }
              } catch (selectionError) {
                isDev() &&
                  console.error(
                    `🔥 CONDUCTOR: Error restoring selection for "${categoryId}"`,
                    selectionError
                  );
              }
            }

            isDev() &&
              console.log(
                `🎉 CONDUCTOR: Applied ${selectionsApplied}/${Object.keys(state).length} recovered selections`
              );

            // Re-initialize necessary components with recovered state
            // Delegate to specific modules for their areas of responsibility
            // The configurator structure should now exist, so progress tracker can initialize
            try {
              window.MurphyDoorConfigurator.progressTracker.initializeProgress();
            } catch (progressError) {
              isDev() &&
                console.warn(
                  '🔥 CONDUCTOR: Progress tracker initialization failed during recovery',
                  progressError
                );
              // Continue with other initialization even if progress tracker fails
            }

            this.initializeEventListeners();
            // Auto-navigation now handled by md-config-bl-auto-navigation-coordinator.js

            // Publish initialization complete event
            if (window.MurphyDoorConfigurator.CoreEventBus) {
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.STATE_RECOVERY_INITIALIZED,
                {
                  timestamp: Date.now(),
                  source:
                    'md-config-core-conductor.js:initializeFromRecoveredState',
                }
              );
            }
          } catch (error) {
            // Handle error through centralized error handler
            this.handleError('Recovery initialization failed', error, {
              method: 'initializeFromRecoveredState',
              severity: 'error',
              recoverable: false,
            });

            // Publish error event
            if (window.MurphyDoorConfigurator.CoreEventBus) {
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.STATE_RECOVERY_INITIALIZATION_FAILED,
                {
                  error,
                  timestamp: Date.now(),
                  source:
                    'md-config-core-conductor.js:initializeFromRecoveredState',
                }
              );
            }
          }
        },

        /* Material Relationship Methods */
        /**
         * Handles material selection by delegating to business logic layer
         *
         * VIOLATION 8 FIX: This method now properly delegates material selection
         * business logic to the dedicated material selector module instead of
         * implementing the logic directly in the conductor.
         *
         * @param {Object} data - The material selection data
         * @param {string|number} data.categoryId - The category ID
         * @param {string|number} [data.woodId] - The selected wood ID, if any
         * @param {string|number} [data.finishId] - The selected finish ID, if any
         * @returns {boolean} Success or failure of the material selection operation
         */
        handleMaterialSelection(data) {
          const METHOD_SIGNATURE = 'handleMaterialSelection';

          isDev() &&
            console.log(
              `🧙‍♂️ Conductor: Delegating material selection to business logic layer`,
              {
                data,
                timestamp: Date.now(),
                source: METHOD_SIGNATURE,
              }
            );

          try {
            // Validate material selector is available
            const materialSelector =
              window.MurphyDoorConfigurator.materialSelector;
            if (!materialSelector) {
              this.handleError(
                'Material Selector business logic module not available',
                {
                  method: METHOD_SIGNATURE,
                  severity: 'error',
                  recoverable: false,
                  data,
                }
              );
              return false;
            }

            // Delegate material selection to business logic layer
            const result = materialSelector.coordinateMaterialSelection(
              data,
              this
            );

            if (result.success) {
              isDev() &&
                console.log(
                  `🔮 Conductor: Material selection delegation completed successfully:`,
                  result.data
                );
              return true;
            } else {
              isDev() &&
                console.warn(
                  `🔮 Conductor: Material selection delegation failed:`,
                  result.error
                );

              // For validation errors, still return true but log the issue
              if (result.isValidationError) {
                return true; // Allow the system to continue with validation feedback
              }

              return false;
            }
          } catch (error) {
            this.handleError('Material selection delegation failed', error, {
              method: METHOD_SIGNATURE,
              severity: 'error',
              recoverable: true,
              data,
            });
            return false;
          }
        },

        /**
         * ⚡ PPD-FIRST: Get material preferences (delegates to state manager)
         * Retrieves current user material selections from state manager
         *
         * ARCHITECTURAL NOTE: PPD holds the SCAFFOLD structure, but dynamic
         * user selections are runtime state managed by state-manager.
         * This method delegates to state manager which holds the current
         * material preferences state (wood/finish selections).
         *
         * BOOTSTRAP BARRIER PATTERN: If material preferences are not yet initialized,
         * this method queues the request and returns a Promise that resolves when
         * the system:maps_initialized event fires.
         *
         * @returns {Object|Promise<Object>} Material preferences { selectedWood, selectedFinish, appliedWoodSku, appliedFinishSku }
         *                                    Returns Promise if initialization pending, Object if ready
         */
        getMaterialPreferences() {
          const startTime = window.performance
            ? window.performance.now()
            : Date.now();
          const PERFORMANCE_THRESHOLD = 100; // ms

          try {
            // Publish operation started event
            if (window.MurphyDoorConfigurator.CoreEventBus) {
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.CONDUCTOR_MATERIAL_PREFERENCES_REQUESTED,
                {
                  timestamp: startTime,
                  initialized: isMaterialPreferencesInitialized,
                }
              );
            }

            if (!this.stateManager) {
              throw new Error(
                '❌ CONDUCTOR: State manager not available for getting material preferences'
              );
            }

            // BOOTSTRAP BARRIER: If not initialized yet, queue the request
            if (!isMaterialPreferencesInitialized) {
              const requestId = `mp_req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
              isDev() &&
                console.log(
                  `⏳ CONDUCTOR: Material preferences not yet initialized - queueing request ${requestId}`
                );

              // Return a Promise that will be resolved when system:maps_initialized fires
              return new Promise((resolve) => {
                pendingMaterialPreferenceRequests.push({
                  resolve,
                  requestId,
                  requestTime: startTime,
                });
              });
            }

            // ⚡ Delegate to state manager which holds dynamic runtime state
            // (PPD only holds the immutable scaffold - user selections are dynamic!)
            const preferences = this.stateManager.getMaterialPreferences();

            if (!preferences) {
              isDev() &&
                console.warn(
                  '⚠️ CONDUCTOR: Material preferences initialized but returned null - returning safe fallback'
                );
              return EMPTY_MATERIAL_PREFERENCES;
            }

            // Performance tracking
            const endTime = window.performance
              ? window.performance.now()
              : Date.now();
            const executionTime = endTime - startTime;

            if (executionTime > PERFORMANCE_THRESHOLD) {
              isDev() &&
                console.warn(
                  `⚠️ CONDUCTOR PERFORMANCE: getMaterialPreferences took ${executionTime.toFixed(2)}ms (threshold: ${PERFORMANCE_THRESHOLD}ms)`
                );

              if (window.MurphyDoorConfigurator.CoreEventBus) {
                window.MurphyDoorConfigurator.CoreEventBus.publish(
                  CONSTANTS.EVENTS.PERFORMANCE_OPERATION_SLOW,
                  {
                    operation: 'getMaterialPreferences',
                    executionTime: executionTime.toFixed(2),
                    threshold: PERFORMANCE_THRESHOLD,
                  }
                );
              }
            }

            // Publish operation completed event
            if (window.MurphyDoorConfigurator.CoreEventBus) {
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.CONDUCTOR_MATERIAL_PREFERENCES_COMPLETED,
                {
                  timestamp: endTime,
                  executionTime: executionTime.toFixed(2),
                  hasPreferences: !!preferences,
                }
              );
            }

            return preferences;
          } catch (error) {
            // Publish operation failed event
            if (window.MurphyDoorConfigurator.CoreEventBus) {
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.CONDUCTOR_MATERIAL_PREFERENCES_FAILED,
                {
                  error: error.message,
                  timestamp: window.performance
                    ? window.performance.now()
                    : Date.now(),
                }
              );
            }

            this.handleError('Failed to get material preferences', error, {
              method: 'getMaterialPreferences',
              severity: 'warning',
              recoverable: true,
            });

            // Safe fallback: Return frozen empty preferences constant
            return EMPTY_MATERIAL_PREFERENCES;
          }
        },

        /**
         * CCP DELEGATION: Get filtered variants for a product based on material preferences
         * Delegates to materialPreferenceCoordinator to get ONLY matching variants
         *
         * @param {string} productId - Product ID to filter variants for
         * @param {Object} preferences - Optional material preferences override
         * @returns {Array} Array of filtered variant objects matching material preferences
         */
        getFilteredVariantsForProduct(productId, preferences = null) {
          try {
            const coordinator =
              window.MurphyDoorConfigurator.materialPreferenceCoordinator;
            if (!coordinator) {
              isDev() &&
                console.warn(
                  '⚠️ CONDUCTOR: materialPreferenceCoordinator not available'
                );
              return [];
            }
            return coordinator.getFilteredVariants(productId, preferences);
          } catch (error) {
            this.handleError('Failed to get filtered variants', error, {
              method: 'getFilteredVariantsForProduct',
              productId,
              severity: 'warning',
              recoverable: true,
            });
            return [];
          }
        },

        /**
         * CCP DELEGATION: Get sorted variants for a product based on material preferences
         * Delegates to materialPreferenceCoordinator to get ALL variants sorted with matches first
         *
         * @param {string} productId - Product ID to sort variants for
         * @param {Object} preferences - Optional material preferences override
         * @returns {Array} Array of all variants sorted with matching materials first
         */
        getSortedVariantsForProduct(productId, preferences = null) {
          try {
            const coordinator =
              window.MurphyDoorConfigurator.materialPreferenceCoordinator;
            if (!coordinator) {
              isDev() &&
                console.warn(
                  '⚠️ CONDUCTOR: materialPreferenceCoordinator not available'
                );
              return [];
            }
            return coordinator.getSortedVariants(productId, preferences);
          } catch (error) {
            this.handleError('Failed to get sorted variants', error, {
              method: 'getSortedVariantsForProduct',
              productId,
              severity: 'warning',
              recoverable: true,
            });
            return [];
          }
        },

        /**
         * CCP DELEGATION: Lazily compute sorted variant orders for a single product (post-bootstrap)
         *
         * sorted variant orders are not pre-built during bootstrap (deferred to eliminate O(W×F) cost).
         * This conductor method owns the relationship between the Phase 2 coordinator and the Phase 1
         * filter builder, computing on first access and caching in the coordinator's lazy cache.
         *
         * @param {string} productId - Product ID to compute sorted orders for
         * @returns {Object|null} Frozen sorted orders structure, or null if unavailable
         */
        getLazySortedOrdersForProduct(productId) {
          try {
            const ppd = this.getPerfectProductDictionary();
            if (!ppd) return null;

            const product = ppd.products?.get(productId);
            if (!product?.variants?.length) return null;

            const filterBuilder =
              window.MurphyDoorConfigurator.materialFilterBuilder;
            if (!filterBuilder?.computeSortedOrdersForProduct) {
              isDev() &&
                console.warn(
                  '⚠️ CONDUCTOR: materialFilterBuilder not available for lazy sorted orders'
                );
              return null;
            }

            return filterBuilder.computeSortedOrdersForProduct(
              product.variants,
              productId
            );
          } catch (error) {
            this.handleError('Failed to compute lazy sorted orders', error, {
              method: 'getLazySortedOrdersForProduct',
              productId,
              severity: 'warning',
              recoverable: true,
            });
            return null;
          }
        },

        /**
         * CCP DELEGATION: Sort category products based on material preferences
         * Delegates to sortingManager to sort products with material matches first
         *
         * @param {string} categoryId - Category ID to sort products for
         * @param {Object} preferences - Optional material preferences override
         * @returns {Array} Array of sorted product objects with material matches prioritized
         */
        sortCategoryProductsByMaterial(categoryId, preferences = null) {
          try {
            const sortingManager = window.MurphyDoorConfigurator.sortingManager;
            if (!sortingManager) {
              isDev() &&
                console.warn('⚠️ CONDUCTOR: sortingManager not available');
              return [];
            }
            return sortingManager.sortCategoryProducts(categoryId, preferences);
          } catch (error) {
            this.handleError('Failed to sort category products', error, {
              method: 'sortCategoryProductsByMaterial',
              categoryId,
              severity: 'warning',
              recoverable: true,
            });
            return [];
          }
        },

        // ==============================================================
        // C1: CLICK = APPLY helpers
        // ==============================================================

        /**
         * Reads the click_apply experience flag off the configurator wrapper's
         * data-click-apply attribute (Liquid-injected from the section setting).
         * The wrapper is resolved via the UI-layer delegate
         * getConfiguratorWrapper() (CCP: no direct DOM query in the conductor).
         * Cached after first resolution. Also true when the C2+ configurator
         * experience is a curated/dual mode (those imply one-step apply).
         * @returns {boolean}
         */
        _isClickApplyEnabled() {
          // Only cache the positive result: caching `false` before the Liquid
          // wrapper is queryable (early bootstrap / template race) would lock
          // the feature off for the whole session. Re-deriving on a false read
          // is a cheap attribute lookup.
          if (this._clickApplyEnabledCache === true) {
            return true;
          }
          let enabled = false;
          try {
            const wrapper =
              typeof this.getConfiguratorWrapper === 'function'
                ? this.getConfiguratorWrapper()
                : null;
            if (wrapper && typeof wrapper.getAttribute === 'function') {
              enabled = wrapper.getAttribute('data-click-apply') === 'true';
              // C2+: curated / full-control experiences always click-apply.
              const exp = wrapper.getAttribute('data-configurator-experience');
              if (
                exp === 'dual_curated_default' ||
                exp === 'dual_full_default'
              ) {
                enabled = true;
              }
            }
          } catch (error) {
            isDev() &&
              console.warn(
                '[Conductor] _isClickApplyEnabled read failed:',
                error
              );
          }
          this._clickApplyEnabledCache = enabled;
          return enabled;
        },

        /**
         * Gate cards (e.g. "Cabinets?") are purely implied by the apo_style
         * experience -- there is no separate opt-in setting. Reads the
         * wrapper's data-configurator-experience attribute, mirroring
         * _isClickApplyEnabled's read-from-wrapper-attribute pattern.
         * @returns {boolean}
         */
        _areGateCardsEnabled() {
          if (this._gateCardsEnabledCache === true) {
            return true;
          }
          let enabled = false;
          try {
            const wrapper =
              typeof this.getConfiguratorWrapper === 'function'
                ? this.getConfiguratorWrapper()
                : null;
            if (wrapper && typeof wrapper.getAttribute === 'function') {
              enabled =
                wrapper.getAttribute('data-configurator-experience') ===
                'apo_style';
            }
          } catch (error) {
            isDev() &&
              console.warn(
                '[Conductor] _areGateCardsEnabled read failed:',
                error
              );
          }
          this._gateCardsEnabledCache = enabled;
          return enabled;
        },

        // ==============================================================
        // D1: DUAL-MODE (curated | full_control)
        // The mode is UI/session state (persisted to sessionStorage, NOT the
        // URL). Curated = the "Most Popular" 3-question path; full_control = the
        // full 4-tab configurator. Both ride the same underlying state, so
        // switching loses no selections.
        // ==============================================================

        /**
         * Current configurator mode. Resolves (once, cached) from sessionStorage,
         * else the default implied by the experience: dual_full_default →
         * 'full_control', anything else curated → 'curated', classic → 'full_control'.
         * @returns {'curated'|'full_control'}
         */
        _getConfiguratorMode() {
          if (this._configuratorModeCache) return this._configuratorModeCache;
          let mode = null;
          try {
            if (window.sessionStorage) {
              mode = window.sessionStorage.getItem('mdConfiguratorMode');
            }
          } catch (error) {
            mode = null;
          }
          if (mode !== 'curated' && mode !== 'full_control') {
            mode = defaultModeForExperience(this._getConfiguratorExperience());
          }
          this._configuratorModeCache = mode;
          return mode;
        },

        /**
         * Sets the mode, persists it to sessionStorage, and publishes
         * BUSINESS_CONFIGURATOR_MODE_CHANGED so the nav-manager / ui-updater
         * reflect the new tab visibility. No-op if unchanged.
         * @param {'curated'|'full_control'} mode
         */
        _setConfiguratorMode(mode) {
          if (mode !== 'curated' && mode !== 'full_control') return;
          if (this._getConfiguratorMode() === mode) return;
          this._configuratorModeCache = mode;
          try {
            if (window.sessionStorage) {
              window.sessionStorage.setItem('mdConfiguratorMode', mode);
            }
          } catch (error) {
            /* sessionStorage unavailable (private mode) — mode still applies for
               this session via the cache. */
          }
          if (window.MurphyDoorConfigurator.CoreEventBus) {
            window.MurphyDoorConfigurator.CoreEventBus.publish(
              CONSTANTS.EVENTS.BUSINESS_CONFIGURATOR_MODE_CHANGED,
              { mode: mode, timestamp: Date.now(), source: 'conductor' }
            );
          }
          // Switching to curated hides the detailed tabs, so land the user on
          // Summary (the pre-built door review). No state is lost — full_control
          // simply re-reveals the tabs.
          if (mode === 'curated') {
            try {
              this.handleStateChange('TAB_CHANGED', { tabId: 'summary' });
            } catch (navErr) {
              isDev() &&
                console.warn(
                  '[Conductor] curated-mode summary nav failed:',
                  navErr
                );
            }
          }
        },

        // ==============================================================
        // GATE CARDS (e.g. "Cabinets?") — synthetic question cards that
        // reveal/hide real category cards. The answer is UI/session state
        // (sessionStorage, keyed per gate), never appliedSelections — same
        // tier as _getConfiguratorMode/_setConfiguratorMode above, just
        // keyed per-gate instead of a single global mode.
        // ==============================================================

        /**
         * Current answer for a gate, or null if unanswered. Resolves (once
         * per gate, cached) from sessionStorage.
         * @param {string} gateId
         * @returns {string|null}
         */
        _getGateAnswer(gateId) {
          if (!this._gateAnswerCache) this._gateAnswerCache = {};
          if (
            Object.prototype.hasOwnProperty.call(this._gateAnswerCache, gateId)
          ) {
            return this._gateAnswerCache[gateId];
          }
          let answer = null;
          try {
            if (window.sessionStorage) {
              answer = window.sessionStorage.getItem(
                'mdConfiguratorGate_' + gateId
              );
            }
          } catch (error) {
            answer = null;
          }
          this._gateAnswerCache[gateId] = answer;
          return answer;
        },

        /**
         * Provenance of a gate's current answer -- 'user' (a real click, the
         * default) or 'reconciliation' (self-derived by
         * gate-card-coordinator's reconcileRestoredGateAnswers from whatever
         * the applied-selections map already held at the time it ran).
         *
         * Resolves memory cache -> sessionStorage -> 'user'. Unknown
         * provenance still defaults to the PROTECTED label, never the
         * re-derivable one -- that fallback is the safe direction (it can
         * only ever decline to overwrite something), and it is what a gate
         * answered before this key existed reads as.
         *
         * 🚨 The sessionStorage half is load-bearing, not a cache. The
         * provenance used to be memory-only while the ANSWER was persisted,
         * so a self-derived answer came back from a later load in the same
         * tab wearing the protected 'user' label -- and
         * reconcileRestoredGateAnswers() refuses to correct a 'user' answer,
         * by design. That made a premature inference permanent AND
         * self-perpetuating: it is re-persisted on every subsequent load,
         * so once a tab is poisoned it stays poisoned.
         *
         * Measured live on production 2026-09-17 (?config= restore of a
         * top_only door, apo_style). The two-pass design from PR #1769 is
         * working -- it is the PERSISTENCE of pass one that was wrong:
         *   4805ms business:gate_answer_changed [cabinets=none]      <- pass 1, premature
         *   4971ms business:cascade_auto_selection_started [url_restoration]
         *   6854ms business:gate_answer_changed [cabinets=top_only]  <- pass 2, corrects it
         * Pass 1 fires ~166ms BEFORE the restoration cascade even starts, so
         * it necessarily derives from an empty map. Its wrong answer was
         * written to sessionStorage with no provenance; a reload inside that
         * ~2s window (or any later load in that tab) then read 'none' as a
         * customer's own choice. Reproduced end-to-end: the same URL that
         * restores correctly in a clean session restored to gate='none' with
         * source='user' when the stale key was present.
         *
         * @param {string} gateId
         * @returns {'user'|'reconciliation'}
         */
        _getGateAnswerSource(gateId) {
          if (!this._gateAnswerSourceCache) this._gateAnswerSourceCache = {};
          if (
            Object.prototype.hasOwnProperty.call(
              this._gateAnswerSourceCache,
              gateId
            ) &&
            this._gateAnswerSourceCache[gateId]
          ) {
            return this._gateAnswerSourceCache[gateId];
          }
          let stored = null;
          try {
            if (window.sessionStorage) {
              stored = window.sessionStorage.getItem(
                gateAnswerSourceStorageKey(gateId)
              );
            }
          } catch (error) {
            stored = null;
          }
          const source = resolveGateAnswerSource(
            this._gateAnswerSourceCache[gateId],
            stored
          );
          this._gateAnswerSourceCache[gateId] = source;
          return source;
        },


        /**
         * Sets a gate's answer, persists it, and publishes
         * BUSINESS_GATE_ANSWER_CHANGED so the visibility manager and the
         * gate-card auto-apply coordinator react. Validates against the
         * gate's own allowlist in CATEGORY_VISIBILITY_DEPS — the only point
         * of truth for valid answers, so new gates need zero new validation
         * code. No-op if the gate is unknown or the answer is unchanged.
         * @param {string} gateId
         * @param {string} answer
         */
        /**
         * Returns a gate to UNANSWERED and re-asks the question.
         *
         * Deliberately NOT _setGateAnswer(gateId, 'none'). 'none' is a real
         * answer that auto-applies the "No Cabinet Doors" fallbacks, which
         * satisfies validateCartConfiguration and lets the customer check out
         * without ever revisiting the question. Clearing leaves the gate's
         * required categories unapplied, so Add to Cart stays blocked until
         * they answer -- the hard block Daniel specified 2026-08-25.
         *
         * gate-card-coordinator's handleGateAnswerChanged intentionally
         * ignores a null answer (its guard requires one), which is what keeps
         * the fallbacks from firing here. The visibility manager and
         * auto-navigation both already treat a null answer as "hide the
         * dependent categories", so the customer lands on the gate card
         * itself rather than on a category the gate is hiding.
         *
         * @param {string} gateId
         */
        _clearGateAnswer(gateId) {
          const gateDeps = this._getGateVisibilityDeps();
          if (!gateDeps || !gateDeps[gateId]) return;
          if (this._getGateAnswer(gateId) == null) return;
          if (!this._gateAnswerCache) this._gateAnswerCache = {};
          this._gateAnswerCache[gateId] = null;
          if (this._gateAnswerSourceCache) {
            delete this._gateAnswerSourceCache[gateId];
          }
          try {
            if (window.sessionStorage) {
              window.sessionStorage.removeItem('mdConfiguratorGate_' + gateId);
              // Provenance is cleared in lockstep with the answer it
              // describes -- a surviving '__src' key would label the NEXT
              // answer written for this gate with the previous one's
              // provenance.
              window.sessionStorage.removeItem(
                gateAnswerSourceStorageKey(gateId)
              );
            }
          } catch (error) {
            /* sessionStorage unavailable (private mode) — the cleared answer
               still applies for this session via the cache. */
          }
          if (window.MurphyDoorConfigurator.CoreEventBus) {
            window.MurphyDoorConfigurator.CoreEventBus.publish(
              CONSTANTS.EVENTS.BUSINESS_GATE_ANSWER_CHANGED,
              {
                gateId: gateId,
                answer: null,
                cleared: true,
                timestamp: Date.now(),
                source: 'conductor',
              }
            );
          }
        },

        /**
         * @param {string} gateId
         * @param {string} answer
         * @param {{source?: 'user'|'reconciliation'}} [meta] - Provenance for
         *   _getGateAnswerSource. Omit for a real customer click (defaults to
         *   'user', the protected label); gate-card-coordinator's
         *   reconcileRestoredGateAnswers passes {source:'reconciliation'} so
         *   its own possibly-premature inference can be corrected by a later
         *   pass without ever touching a genuine customer answer.
         */
        _setGateAnswer(gateId, answer, meta) {
          const gateDeps = this._getGateVisibilityDeps();
          const gateConfig = gateDeps ? gateDeps[gateId] : null;
          if (!gateConfig || !gateConfig.answers[answer]) return;
          if (!this._gateAnswerSourceCache) this._gateAnswerSourceCache = {};
          const source = (meta && meta.source) || 'user';
          this._gateAnswerSourceCache[gateId] = source;
          // Persist provenance BEFORE the unchanged-answer early return, so
          // the stored label tracks the strongest claim ever made about this
          // answer. A real click that re-confirms the same answer a
          // reconciliation pass already guessed must still UPGRADE it to the
          // protected 'user' label -- returning first would leave it
          // correctable forever. See _getGateAnswerSource for why this is
          // persisted at all rather than kept in memory.
          try {
            if (window.sessionStorage) {
              window.sessionStorage.setItem(
                gateAnswerSourceStorageKey(gateId),
                source
              );
            }
          } catch (error) {
            /* sessionStorage unavailable (private mode) — provenance still
               applies for this session via the cache above. */
          }
          // 🚨 THREE outcomes, not two -- see gateAnswerWriteOutcome above for
          // why an unchanged answer must publish REAFFIRMED (a customer
          // re-click is the only signal that can restore a gate's missing
          // fallbacks, issue #1826) and must NOT publish CHANGED (every
          // subscriber of that event treats it as a real new pick).
          const writeOutcome = gateAnswerWriteOutcome(
            this._getGateAnswer(gateId),
            answer,
            source
          );
          if (writeOutcome !== 'changed') {
            if (
              writeOutcome === 'reaffirmed' &&
              window.MurphyDoorConfigurator.CoreEventBus &&
              CONSTANTS.EVENTS.BUSINESS_GATE_ANSWER_REAFFIRMED
            ) {
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.BUSINESS_GATE_ANSWER_REAFFIRMED,
                {
                  gateId: gateId,
                  answer: answer,
                  timestamp: Date.now(),
                  source: 'conductor',
                }
              );
            }
            return;
          }
          if (!this._gateAnswerCache) this._gateAnswerCache = {};
          this._gateAnswerCache[gateId] = answer;
          try {
            if (window.sessionStorage) {
              window.sessionStorage.setItem(
                'mdConfiguratorGate_' + gateId,
                answer
              );
            }
          } catch (error) {
            /* sessionStorage unavailable (private mode) — answer still
               applies for this session via the cache. */
          }
          if (window.MurphyDoorConfigurator.CoreEventBus) {
            window.MurphyDoorConfigurator.CoreEventBus.publish(
              CONSTANTS.EVENTS.BUSINESS_GATE_ANSWER_CHANGED,
              {
                gateId: gateId,
                answer: answer,
                timestamp: Date.now(),
                source: 'conductor',
              }
            );
          }
        },

        /**
         * Maps an APPLY_SELECTION data.source to a provenance token (C2).
         * @param {string} source
         * @param {boolean} autoApplied
         * @returns {'curated_default'|'cascade_redefault'|'url'|'user'|'auto_apply'}
         */
        _mapSourceToProvenance(source, autoApplied) {
          switch (source) {
            case 'curated_defaults':
              return 'curated_default';
            case 'cascade_redefault':
              return 'cascade_redefault';
            case 'url':
            case 'url_restoration':
            // A "Build this" preset is a restore of a saved configuration: same
            // provenance as ?config= (never asks before being overwritten).
            case 'build_preset':
              return 'url';
            case 'simplified_mode':
              return 'auto_apply';
            // The slider's bootstrap seed of its own default (horizontal-
            // slider seedSliderDefault): accepted for the customer, not by
            // them. Was 'user' via 'slider_commit' until 2026-10-02.
            case 'slider_seed':
              return 'curated_default';
            case 'click_apply':
            case 'slider_commit':
              return 'user';
            default:
              return autoApplied ? 'auto_apply' : 'user';
          }
        },

        /**
         * Returns the configured experience string ('classic' |
         * 'dual_curated_default' | 'dual_full_default') from the wrapper's
         * data-configurator-experience attribute, via the UI-layer delegate
         * (CCP: no direct DOM query in the conductor). Phase-2 modules read the
         * experience through this rather than touching the DOM.
         * @returns {string}
         */
        _getConfiguratorExperience() {
          try {
            const wrapper =
              typeof this.getConfiguratorWrapper === 'function'
                ? this.getConfiguratorWrapper()
                : null;
            if (wrapper && typeof wrapper.getAttribute === 'function') {
              return (
                wrapper.getAttribute('data-configurator-experience') ||
                'classic'
              );
            }
          } catch (error) {
            isDev() &&
              console.warn(
                '[Conductor] _getConfiguratorExperience read failed:',
                error
              );
          }
          return 'classic';
        },

        /**
         * Returns the configured analytics sample rate (0-100, an integer
         * percentage of sessions the reporter should emit for) from the same
         * wrapper element's data-analytics-sample-rate attribute, via the
         * UI-layer delegate (CCP: no direct DOM query in the conductor).
         * Clamped to [0, 100]; defaults to 100 (emit for every session) when
         * the attribute is missing, unparsable, or the wrapper can't be
         * reached -- fail open on the sampling knob so a misconfigured
         * attribute never silently zeroes out telemetry.
         * @returns {number}
         */
        getAnalyticsSampleRate() {
          try {
            const wrapper =
              typeof this.getConfiguratorWrapper === 'function'
                ? this.getConfiguratorWrapper()
                : null;
            if (wrapper && typeof wrapper.getAttribute === 'function') {
              const raw = wrapper.getAttribute('data-analytics-sample-rate');
              const parsed = parseInt(raw, 10);
              if (!isNaN(parsed)) {
                return Math.max(0, Math.min(100, parsed));
              }
            }
          } catch (error) {
            isDev() &&
              console.warn(
                '[Conductor] getAnalyticsSampleRate read failed:',
                error
              );
          }
          return 100;
        },

        // Reads the "1:1 APO" presentation toggle (apo_style-only, set via the
        // apo_1to1_mode section setting). OFF (false) is the default: customization
        // options clustered first, measurements/Locking Systems clustered last,
        // progress bar + tools border shown. ON (true): the literal MageWorx step
        // order, no progress bar, no tools border — see APO_STYLE_1TO1_CATEGORY_ORDER
        // vs APO_STYLE_DEFAULT_CATEGORY_ORDER in md-config-core-constants.js.
        _isApo1to1Enabled() {
          try {
            const wrapper =
              typeof this.getConfiguratorWrapper === 'function'
                ? this.getConfiguratorWrapper()
                : null;
            if (wrapper && typeof wrapper.getAttribute === 'function') {
              return wrapper.getAttribute('data-apo-1to1') === 'true';
            }
          } catch (error) {
            isDev() &&
              console.warn('[Conductor] _isApo1to1Enabled read failed:', error);
          }
          return false;
        },

        /**
         * apo_style card order as set per category in Shopify admin
         * (configurator_tab_categories.apo_load_order), surfaced from the PPD.
         *
         * SPARSE BY DESIGN. Only categories an admin has actually filled in
         * appear. Callers must fall back to CONSTANTS.APO_STYLE_*_CATEGORY_ORDER
         * per category, never treat a miss as "order 0" -- that is what lets the
         * admin migration land one row at a time without changing behaviour for
         * the rows not done yet.
         *
         * @returns {Map<string, number>} category id -> order (empty Map if none)
         */
        getApoCategoryOrder() {
          try {
            const ppd = this.perfectProductDictionary;
            const map = ppd && ppd.apoCategoryOrder;
            if (map && typeof map.get === 'function') return map;
          } catch (error) {
            isDev() &&
              console.warn(
                '[Conductor] getApoCategoryOrder read failed:',
                error
              );
          }
          return new Map();
        },

        /**
         * Admin-set apo_visibility_rule per category, as built by the pipeline.
         * Sparse: only categories with a rule appear.
         *
         * @returns {Map<string, {gate: string, showWhen: string[]}>}
         */
        getApoVisibilityRules() {
          try {
            const ppd = this.perfectProductDictionary;
            const map = ppd && ppd.apoVisibilityRules;
            if (map && typeof map.get === 'function') return map;
          } catch (error) {
            isDev() &&
              console.warn(
                '[Conductor] getApoVisibilityRules read failed:',
                error
              );
          }
          return new Map();
        },

        /**
         * Provenance of a category's applied selection -- 'curated_default' |
         * 'cascade_redefault' | 'url' | 'user' | 'auto_apply', or null when
         * nothing is applied. Delegation so Phase 2 modules can tell an
         * auto-applied selection from one the customer actually chose without
         * reaching into the state manager (CCP Rule 3).
         * @param {string} categoryId
         * @returns {string|null}
         */
        getSelectionProvenance(categoryId) {
          try {
            if (
              this.stateManager &&
              typeof this.stateManager.getSelectionProvenance === 'function'
            ) {
              return this.stateManager.getSelectionProvenance(categoryId);
            }
          } catch (error) {
            isDev() &&
              console.warn(
                '[Conductor] getSelectionProvenance read failed:',
                error
              );
          }
          return null;
        },

        /**
         * Raw admin overlay from the selection_dependency_rule metafield.
         * Sparse: only dependent categories with a rule appear.
         *
         * @returns {Map<string, {dependsOnCategory: string, hiddenWhenProductHandle: string, autoApplyVariantTitleOnHide: string}>}
         */
        getSelectionDependencyRules() {
          try {
            const ppd = this.perfectProductDictionary;
            const map = ppd && ppd.selectionDependencyRules;
            if (map && typeof map.get === 'function') return map;
          } catch (error) {
            isDev() &&
              console.warn(
                '[Conductor] getSelectionDependencyRules read failed:',
                error
              );
          }
          return new Map();
        },

        /**
         * CONSTANTS.DERIVED_PRODUCT_RULES entry for a category, or null.
         * @param {string} categoryId
         * @returns {{sourceCategory: string}|null}
         */
        getDerivedProductRule(categoryId) {
          const rules = CONSTANTS.DERIVED_PRODUCT_RULES || {};
          return (categoryId && rules[categoryId]) || null;
        },

        /**
         * The product of a derived category that matches the source
         * category's CURRENT applied selection, e.g. the "Lipped Shelves"
         * Extra Shelves product while Shelf Style is on any Lipped product.
         * Null when the category has no rule, nothing is applied on the
         * source, or the match is not exactly one product.
         *
         * @param {string} categoryId - the derived category
         * @returns {string|null} product id
         */
        resolveDerivedProductId(categoryId) {
          try {
            const rule = this.getDerivedProductRule(categoryId);
            if (!rule) return null;
            const applied = this.getAppliedSelectionsMap().get(
              rule.sourceCategory
            );
            if (!applied || applied.variantId == null) return null;

            let label = '';
            const sourceProducts =
              this.getVirtualizedProductsForCategory(rule.sourceCategory) || [];
            for (const sourceProduct of sourceProducts) {
              const sourceVariant = (sourceProduct.variants || []).find(
                (v) => v && String(v.id) === String(applied.variantId)
              );
              if (sourceVariant) {
                label = [
                  sourceProduct.display_name,
                  sourceProduct.title,
                  sourceVariant.source_product_title,
                ]
                  .filter(Boolean)
                  .join(' | ');
                break;
              }
            }
            const match =
              window.MurphyDoorConfigurator.utils?.matchDerivedProduct?.(
                this.getVirtualizedProductsForCategory(categoryId) || [],
                label
              );
            return match && match.id != null ? String(match.id) : null;
          } catch (error) {
            isDev() &&
              console.warn(
                '[Conductor] resolveDerivedProductId failed:',
                error
              );
            return null;
          }
        },

        /**
         * For a derived category's applied variant, the SAME-TITLED variant in
         * the product the source now implies ("3 Extra Shelves" on Standard ->
         * "3 Extra Shelves" on Lipped). Null when it already sits on that
         * product, or nothing resolves.
         *
         * @param {string} categoryId - the derived category
         * @param {string|number} variantId - its applied variant
         * @returns {{productId: string, variantId: string}|null}
         */
        getDerivedSiblingSelection(categoryId, variantId) {
          try {
            const targetProductId = this.resolveDerivedProductId(categoryId);
            if (!targetProductId || variantId == null) return null;
            const products =
              this.getVirtualizedProductsForCategory(categoryId) || [];
            let title = null;
            let currentProductId = null;
            products.forEach((product) => {
              const variant = (product.variants || []).find(
                (v) => v && String(v.id) === String(variantId)
              );
              if (variant) {
                title = variant.title;
                currentProductId = String(product.id);
              }
            });
            if (title == null || currentProductId === targetProductId) {
              return null;
            }
            const target = products.find(
              (product) => String(product.id) === targetProductId
            );
            const sibling = ((target && target.variants) || []).find(
              (v) => v && v.title === title
            );
            return sibling
              ? { productId: targetProductId, variantId: String(sibling.id) }
              : null;
          } catch (error) {
            isDev() &&
              console.warn(
                '[Conductor] getDerivedSiblingSelection failed:',
                error
              );
            return null;
          }
        },

        /**
         * Admin-authored note shown under a category's slider (metaobject
         * `slider_note`), e.g. Extra Shelves: "Every door ships with 3
         * adjustable shelves...". Empty string when none.
         * @param {string} categoryId
         * @returns {string}
         */
        getCategorySliderNote(categoryId) {
          const ppd = this.getPerfectProductDictionary();
          const notes = ppd && ppd.categorySliderNotes;
          return (notes && notes.get && notes.get(categoryId)) || '';
        },

        /**
         * THE single source of parent->dependent selection rules ("set the
         * cabinet doors to none and the knobs for that cabinet follow").
         * Every consumer calls this instead of reading
         * CONSTANTS.SELECTION_VISIBILITY_DEPS directly, so an admin rule and
         * the code default can never disagree between the four sites that
         * need it (selection-dependency-coordinator, gate-card-coordinator,
         * auto-navigation-coordinator, ui-gate-visibility-manager). Split
         * sources here would mean the DOM hides a card per the constant while
         * the auto-apply fires per the metafield.
         *
         * @returns {Object} dependentCategoryId -> rule
         */
        _getSelectionVisibilityDeps() {
          const base = CONSTANTS.SELECTION_VISIBILITY_DEPS || {};
          let rules;
          try {
            rules = this.getSelectionDependencyRules();
          } catch (error) {
            return base;
          }
          if (!rules || rules.size === 0) return base;

          // Memoised on the rules Map identity, matching
          // _getGateVisibilityDeps. The PPD is frozen for the session, so this
          // recomputes once, not once per applied selection.
          if (
            this._selectionVisibilityDepsCache &&
            this._selectionVisibilityDepsCacheKey === rules
          ) {
            return this._selectionVisibilityDepsCache;
          }

          const merged = {};
          Object.keys(base).forEach((categoryId) => {
            merged[categoryId] = Object.assign({}, base[categoryId]);
          });
          // A metafield rule REPLACES the constant for that dependent category
          // rather than being deep-merged field by field. The pipeline already
          // dropped any rule missing a field, so every rule that reaches here
          // is complete -- and a field-wise merge could otherwise pair an
          // admin's new parent category with the old constant's variant title.
          rules.forEach((rule, categoryId) => {
            if (
              !rule ||
              !rule.dependsOnCategory ||
              !rule.hiddenWhenProductHandle ||
              !rule.autoApplyVariantTitleOnHide
            ) {
              return;
            }
            merged[categoryId] = {
              dependsOnCategory: rule.dependsOnCategory,
              hiddenWhenProductHandle: rule.hiddenWhenProductHandle,
              autoApplyVariantTitleOnHide: rule.autoApplyVariantTitleOnHide,
            };
          });

          this._selectionVisibilityDepsCache = merged;
          this._selectionVisibilityDepsCacheKey = rules;
          return merged;
        },

        /**
         * THE single source of gate-driven category visibility. Every consumer
         * calls this instead of reading CONSTANTS.CATEGORY_VISIBILITY_DEPS, so
         * an admin rule and the code default can never disagree between the
         * six sites that need this (gate-visibility-manager, gate-card-
         * coordinator, auto-navigation-coordinator, and _setGateAnswer below).
         *
         * Returns the SAME SHAPE as the constant -- { categories, answers,
         * autoApplyOnHide } keyed by gate id -- specifically so those consumers
         * keep their existing logic and only change where the object comes
         * from. A different shape would have meant rewriting six call sites'
         * internals to migrate four rules.
         *
         * MERGE IS PER-CATEGORY, not per-gate. A category with a metafield rule
         * takes it; every other category keeps exactly what the constant says.
         * Doing it per-gate would mean the first populated category silently
         * dropped its un-migrated siblings out of that gate's list.
         *
         * autoApplyOnHide deliberately stays in code: those are product
         * HANDLES, and a typo in admin would silently break the auto-apply
         * rather than just misplace a card (Daniel's call 2026-08-18).
         *
         * @returns {Object} gateId -> { categories, answers, autoApplyOnHide }
         */
        _getGateVisibilityDeps() {
          const base = CONSTANTS.CATEGORY_VISIBILITY_DEPS || {};
          let rules;
          try {
            rules = this.getApoVisibilityRules();
          } catch (error) {
            return base;
          }
          if (!rules || rules.size === 0) return base;

          // Memoised on the rules Map identity. The PPD is frozen for the
          // session, so this recomputes once, not per gate answer.
          if (
            this._gateVisibilityDepsCache &&
            this._gateVisibilityDepsCacheKey === rules
          ) {
            return this._gateVisibilityDepsCache;
          }

          const merged = mergeGateVisibilityDeps(base, rules);
          this._gateVisibilityDepsCache = merged;
          this._gateVisibilityDepsCacheKey = rules;
          return merged;
        },

        /**
         * THE single source of apo_style card ordering. Both consumers -- the
         * reveal pass in md-config-ui-navigation-manager.js and the walk in
         * md-config-bl-auto-navigation-coordinator.js -- must call this rather
         * than reading the constants themselves, or the order cards RENDER in
         * can drift from the order auto-navigation VISITS them in.
         *
         * @param {string} sortKey - category id, or 'gate:<id>' for a gate card
         * @returns {number} rank; unknown keys sort to the end
         *
         * Gathers the live inputs (admin apo_load_order map, the 1:1 toggle,
         * the two order constants, and the two blended visibility-dependency
         * maps -- via _getGateVisibilityDeps()/_getSelectionVisibilityDeps()
         * rather than the raw CONSTANTS, so an admin apo_visibility_rule or
         * selection_dependency_rule overlay is honoured here exactly as it is
         * everywhere else those maps are consulted) and delegates to the pure
         * resolveApoOrderRank() above -- see its JSDoc for the full
         * resolution-order rationale, including why gate cards and their
         * dependent/sibling categories need a dedicated derivation step.
         */
        _getApoOrderRank(sortKey) {
          let metaMap;
          try {
            metaMap = this.getApoCategoryOrder();
          } catch (error) {
            isDev() &&
              console.warn(
                '[Conductor] _getApoOrderRank metafield read failed:',
                error
              );
            metaMap = new Map();
          }

          const isOneToOne =
            typeof this._isApo1to1Enabled === 'function' &&
            this._isApo1to1Enabled();

          return resolveApoOrderRank(sortKey, {
            metaMap,
            isOneToOne,
            gateCardDefinitions: CONSTANTS.GATE_CARD_DEFINITIONS,
            gateVisibilityDeps: this._getGateVisibilityDeps(),
            selectionVisibilityDeps: this._getSelectionVisibilityDeps(),
            oneToOneOrder: CONSTANTS.APO_STYLE_1TO1_CATEGORY_ORDER,
            defaultOrder: CONSTANTS.APO_STYLE_DEFAULT_CATEGORY_ORDER,
          });
        },

        /**
         * Pure decision for the click=apply chokepoint. Extracted so the guard
         * matrix (forbidden / slider / simplified-bulk / null-variant / flag)
         * is unit-testable without booting the whole conductor. Slider
         * categories are signalled by the slider itself via suppressClickApply
         * (its per-drag path bypasses this case entirely), so no DOM lookup is
         * needed here.
         * @returns {boolean}
         */
        _shouldClickApply(opts) {
          return shouldClickApplyDecision(opts, this._isClickApplyEnabled());
        },

        /**
         * Recompute preemptive forbidden state from the CURRENT applied answers
         * and publish it, so every tile on the page is greyed (or un-greyed)
         * to match reality right now.
         *
         * Delegates the calculation to forbiddenValidator and the DOM work to
         * the ui-updater's UI_UPDATE_PREEMPTIVE_FORBIDDEN handler -- the same
         * payload shape the validator's own recalculation passes publish.
         * Best-effort by design: it repairs a stale grid, and a failure here
         * must never break the click path that called it.
         *
         * @param {string} triggerCategoryId - Category whose action prompted this
         * @param {string} source - Caller label for event tracing
         */
        _refreshPreemptiveForbidden(triggerCategoryId, source) {
          try {
            const validator = window.MurphyDoorConfigurator.forbiddenValidator;
            if (
              !validator ||
              typeof validator.calculatePreemptiveForbiddenItems !== 'function'
            ) {
              return;
            }
            const appliedSelections = this.getAppliedSelectionsMap();
            const forbiddenRules = this.getUnifiedForbiddenRules();
            // 🚨 NEVER publish an empty disable map from an unloaded rule set.
            // The ui-updater's handler treats the payload as authoritative and
            // CLEARS stale forbidden classes, so publishing "nothing is
            // forbidden" before the rules exist would un-grey the whole page --
            // the very failure this method is called to repair.
            if (!appliedSelections?.size || !forbiddenRules?.unifiedRules?.size) {
              return;
            }

            const itemsToDisable = validator.calculatePreemptiveForbiddenItems(
              appliedSelections,
              forbiddenRules
            );

            publishEvent(CONSTANTS.EVENTS.UI_UPDATE_PREEMPTIVE_FORBIDDEN, {
              itemsToDisable: Array.from(itemsToDisable.entries()),
              appliedSelectionsCount: appliedSelections.size,
              rulesProcessed: forbiddenRules.unifiedRules.size,
              triggerCategoryId,
              timestamp: Date.now(),
              source,
            });
          } catch (error) {
            isDev() &&
              console.warn(
                '[CONDUCTOR] _refreshPreemptiveForbidden failed:',
                error
              );
          }
        },

        /**
         * Gets the configurator wrapper element by delegating to UI layer
         * Central Conductor Pattern - Orchestrates DOM operations through UI layer
         * Used by the error handler to attach error messages
         *
         * @returns {HTMLElement} The configurator wrapper element
         */
        getConfiguratorWrapper() {
          const METHOD_SIGNATURE = 'main.getConfiguratorWrapper';
          const startTime = window.performance.now();

          isDev() &&
            console.log(
              '🎯 Conductor: Delegating DOM wrapper request to UI layer'
            );

          try {
            // Delegate to UI layer through existing DOM reference for immediate response
            // This maintains backwards compatibility while following delegation pattern
            if (
              window.MurphyDoorConfigurator.uiUpdater?.DOM
                ?.getConfiguratorWrapper
            ) {
              const wrapper =
                window.MurphyDoorConfigurator.uiUpdater.DOM.getConfiguratorWrapper();

              // Track performance if slow
              const executionTime = window.performance.now() - startTime;
              const PERFORMANCE_THRESHOLD = 50; // ms

              if (
                executionTime > PERFORMANCE_THRESHOLD &&
                window.MurphyDoorConfigurator.CoreEventBus
              ) {
                window.MurphyDoorConfigurator.CoreEventBus.publish(
                  CONSTANTS.EVENTS.PERFORMANCE_DOM_OPERATION_SLOW,
                  {
                    executionTime,
                    threshold: PERFORMANCE_THRESHOLD,
                    operation: 'getConfiguratorWrapper_delegated',
                    timestamp: Date.now(),
                    source: METHOD_SIGNATURE,
                  }
                );
              }

              isDev() &&
                console.log('✅ Conductor: DOM wrapper delegation successful');
              return wrapper;
            } else {
              throw new Error(
                'UI Updater DOM dependency not available for delegation'
              );
            }
          } catch (error) {
            isDev() &&
              console.error(
                '❌ Conductor: DOM wrapper delegation failed:',
                error
              );

            // Handle delegation errors
            if (this.handleError) {
              this.handleError(
                'Failed to delegate configurator wrapper request',
                {
                  error,
                  method: METHOD_SIGNATURE,
                  severity: 'error',
                  recoverable: true,
                }
              );
            } else {
              isDev() &&
                console.error(
                  'Failed to access configurator wrapper via delegation:',
                  error
                );
            }

            // Publish error event if event bus is available
            if (window.MurphyDoorConfigurator.CoreEventBus) {
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.SYSTEM_DOM_ERROR,
                {
                  message: 'Failed to delegate configurator wrapper request',
                  error: error.message,
                  timestamp: Date.now(),
                  method: METHOD_SIGNATURE,
                }
              );
            }

            return undefined;
          }
        },

        /**
         * Initialize tooltips for all categories
         * This is called after all modules are initialized
         */
        initializeTooltips() {
          isDev() &&
            console.log(
              '🧙‍♂️ Conductor: Delegating tooltip initialization to UI layer'
            );

          try {
            if (window.MurphyDoorConfigurator.CoreEventBus) {
              isDev() &&
                console.log(
                  '🔮 Conductor: Publishing tooltip initialization request via event system'
                );

              // Delegate tooltip initialization to UI layer through event system
              // This follows the Central Conductor Pattern - conductor coordinates, doesn't implement
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.UI_TOOLTIPS_INITIALIZATION_REQUESTED,
                {
                  timestamp: Date.now(),
                  source: 'md-config-core-conductor.js:initializeTooltips',
                  requestedCategories: 'all',
                }
              );

              isDev() &&
                console.log(
                  '🧙‍♂️ Conductor: Tooltip initialization request sent via delegation'
                );
            }
          } catch (error) {
            isDev() &&
              console.error(
                '🔥 Conductor: Error delegating tooltip initialization:',
                error
              );
          }
        },

        /**
         * Handles grouped product click events by delegating to business logic layer
         *
         * CENTRAL CONDUCTOR PATTERN: This method now properly delegates complex
         * business logic to the specialized groupedProductHandler module instead
         * of containing the logic directly (CCP violation fix).
         *
         * @param {Object} eventData - Event data from ui:product_group_clicked
         */
        handleGroupedProductClick(eventData) {
          const METHOD_SIGNATURE = 'main.handleGroupedProductClick';

          try {
            isDev() &&
              console.info(
                `🎯 ${METHOD_SIGNATURE}: Delegating grouped product processing to business logic layer`,
                {
                  groupId: eventData.groupId,
                  groupName: eventData.groupName,
                  categoryId: eventData.categoryId,
                }
              );

            // CENTRAL CONDUCTOR PATTERN: Delegate to specialized business logic module
            if (!window.MurphyDoorConfigurator.groupedProductHandler) {
              isDev() &&
                console.error(
                  `${METHOD_SIGNATURE}: groupedProductHandler business logic module not available`
                );

              this.handleError(
                'Grouped product handler module unavailable',
                new Error('groupedProductHandler module not loaded'),
                {
                  method: METHOD_SIGNATURE,
                  module: 'conductor',
                  severity: 'error',
                  recoverable: false,
                  groupId: eventData?.groupId,
                }
              );
              return;
            }

            // Delegate all complex business logic to the business layer
            const processingResult =
              window.MurphyDoorConfigurator.groupedProductHandler.processGroupedProductSelection(
                eventData,
                this
              );

            if (processingResult.success) {
              isDev() &&
                console.info(
                  `🧙‍♂️ ${METHOD_SIGNATURE}: Business logic delegation completed successfully`,
                  {
                    groupName: processingResult.groupName,
                    selectedVariantId: processingResult.bestVariant?.id,
                    materialScore: processingResult.materialScore,
                    delegatedTo:
                      'groupedProductHandler.processGroupedProductSelection',
                  }
                );
            } else {
              isDev() &&
                console.warn(
                  `⚠️ ${METHOD_SIGNATURE}: Business logic processing failed`,
                  {
                    reason: processingResult.reason,
                    error: processingResult.error,
                  }
                );
            }
          } catch (error) {
            isDev() &&
              console.error(
                `${METHOD_SIGNATURE}: Error in grouped product delegation:`,
                error
              );

            this.handleError('Error in grouped product delegation', error, {
              method: METHOD_SIGNATURE,
              module: 'conductor',
              severity: 'error',
              recoverable: true,
              groupId: eventData?.groupId,
              categoryId: eventData?.categoryId,
            });
          }
        },

        /**
         * P3.2 SINGLE MASTER DICTIONARY: Access perfectProductDictionary via CCP delegation
         * Returns the unified product dictionary containing all processed data
         *
         * @returns {Object} perfectProductDictionary - The unified data structure
         */
        getPerfectProductDictionary() {
          if (this.perfectProductDictionary) {
            return this.perfectProductDictionary;
          }

          isDev() &&
            console.warn(
              'getPerfectProductDictionary: perfectProductDictionary not yet available'
            );
          return null;
        },

        /**
         * Get direct reference to perfectProductDictionary for O(1) map access
         *
         * ARCHITECTURAL NOTE: This provides direct access to PPD maps for performance-critical
         * code paths where method call overhead should be eliminated. Prefer this over wrapper
         * methods when O(1) transparency is important.
         *
         * @returns {Object} perfectProductDictionary with all pre-built maps
         * @throws {Error} If PPD not initialized
         */
        getPPD: function () {
          if (!this.perfectProductDictionary) {
            throw new Error(
              'PPD not initialized - conductor.getPPD() called before bootstrap'
            );
          }
          return this.perfectProductDictionary;
        },

        /**
         * SLIDER UI DELEGATION: Get slider metadata for a category
         *
         * Returns pre-built slider metadata from PPD Layer 4.6 for categories
         * that use horizontal slider UI (CONSTANTS.CATEGORY_TYPES.SLIDER_CATEGORIES:
         * Width, Height, Door Depth).
         *
         * @param {string} categoryId - Category identifier
         * @returns {Object|null} Slider metadata with sorted variants, bidirectional maps, defaults
         */
        getSliderMetadata: function (categoryId) {
          const ppd = this.getPerfectProductDictionary();
          if (!ppd || !ppd.sliderCategoriesMap) return null;
          const base = ppd.sliderCategoriesMap.get(categoryId) || null;
          if (!base || !this.getDerivedProductRule(categoryId)) return base;
          return this._getDerivedSliderView(categoryId, base);
        },

        /**
         * DERIVED_PRODUCT_RULES view of a slider: only the stops of the
         * product the source implies (Extra Shelves -> the Shelf Style's
         * Standard or Lipped product), in the same frozen shape as the PPD
         * metadata so the slider needs no special case. Falls back to the
         * product owning the configured default when nothing resolves yet,
         * so the slider never shows both styles' "3 Extra Shelves" twice.
         * Memoised per (category, product); the PPD is frozen per session.
         *
         * @param {string} categoryId
         * @param {Object} base - PPD slider metadata
         * @returns {Object} filtered slider metadata
         */
        _getDerivedSliderView(categoryId, base) {
          try {
            const defaultStop =
              base.sortedVariants[base.defaultIndex] || base.sortedVariants[0];
            const productId =
              this.resolveDerivedProductId(categoryId) ||
              (defaultStop && String(defaultStop.productId));
            if (!productId) return base;

            if (!this._derivedSliderViews) this._derivedSliderViews = new Map();
            const key = categoryId + '|' + productId;
            if (this._derivedSliderViews.has(key)) {
              return this._derivedSliderViews.get(key);
            }

            const stops = base.sortedVariants.filter(
              (stop) => String(stop.productId) === productId
            );
            if (stops.length === 0) return base;

            const variantIdToIndexMap = new Map();
            const indexToVariantIdMap = new Map();
            stops.forEach((stop, index) => {
              variantIdToIndexMap.set(stop.variantId, index);
              indexToVariantIdMap.set(index, stop.variantId);
            });
            // Same default POSITION in the matched product: the configured
            // default is "0 Extra Shelves" on one style, so the other style
            // defaults to its own "0 Extra Shelves".
            let defaultIndex = stops.findIndex(
              (stop) => defaultStop && stop.title === defaultStop.title
            );
            if (defaultIndex < 0) defaultIndex = 0;

            const view = Object.freeze(
              Object.assign({}, base, {
                sortedVariants: Object.freeze(stops),
                variantIdToIndexMap: variantIdToIndexMap,
                indexToVariantIdMap: indexToVariantIdMap,
                defaultIndex: defaultIndex,
                defaultVariantId: stops[defaultIndex].variantId,
                minValue: stops[0].numericValue,
                maxValue: stops[stops.length - 1].numericValue,
                totalSteps: stops.length,
              })
            );
            this._derivedSliderViews.set(key, view);
            return view;
          } catch (error) {
            isDev() &&
              console.warn('[Conductor] _getDerivedSliderView failed:', error);
            return base;
          }
        },

        /**
         * SLIDER UI DELEGATION: Check if a category should use horizontal slider UI
         *
         * @param {string} categoryId - Category identifier
         * @returns {boolean} True if category should render as slider
         */
        shouldUseCategorySlider: function (categoryId) {
          const metadata = this.getSliderMetadata(categoryId);
          return metadata !== null && metadata.useSliderUI === true;
        },

        /**
         * CART COMPATIBILITY: Transform perfectProductDictionary into cart-compatible structure
         *
         * The cart manager expects a structure with:
         * - baseProduct: The base door product with variants
         * - addOnProducts: Map/Object of add-on products (enhanced)
         *
         * This method bridges the architecture gap between the enhanced Map-based
         * perfectProductDictionary and the cart manager's data expectations.
         *
         * @returns {Object} Cart-compatible product data structure
         */
        getCartCompatibleProductData() {
          const ppd = this.getPerfectProductDictionary();
          const legacy = this.getProductStructure();

          if (!ppd) {
            isDev() &&
              console.warn(
                '🎯 getCartCompatibleProductData: perfectProductDictionary not available, falling back to legacy structure'
              );
            return legacy;
          }

          isDev() &&
            console.log(
              '🎯 getCartCompatibleProductData: Converting perfectProductDictionary to cart-compatible format'
            );

          // Build cart-compatible structure with enhanced parent product
          let enhancedParentProduct = legacy?.baseProduct || null;

          // CRITICAL FIX: Legacy baseProduct has 'variant' (singular), but cart manager expects 'variants' (array)
          if (
            enhancedParentProduct &&
            enhancedParentProduct.variant &&
            !enhancedParentProduct.variants
          ) {
            enhancedParentProduct = {
              ...enhancedParentProduct,
              variants: [enhancedParentProduct.variant], // Convert single variant to array
            };
            isDev() &&
              console.log(
                '🎯 getCartCompatibleProductData: Enhanced parent product with variants array:',
                {
                  originalVariant: legacy.baseProduct.variant?.id,
                  enhancedVariants: enhancedParentProduct.variants.map(
                    (v) => v.id
                  ),
                }
              );
          }

          const cartData = {
            // Use enhanced parent product with proper variants array
            baseProduct: enhancedParentProduct,

            // Convert Map-based products to cart-compatible addOnProducts
            addOnProducts: ppd.products || new Map(),

            // Include material relationships for cart item naming
            materialRelationships: ppd.materialRelationships || new Map(),

            // Include category data for cart categorization
            categories: ppd.categories || new Map(),

            // Include metadata for debugging
            metadata: {
              source: 'perfectProductDictionary',
              productsCount: ppd.products ? ppd.products.size : 0,
              categoriesCount: ppd.categories ? ppd.categories.size : 0,
              timestamp: Date.now(),
            },
          };

          isDev() &&
            console.log('🎯 getCartCompatibleProductData: Transformed data:', {
              hasParentProduct: !!cartData.baseProduct,
              baseProductVariants: cartData.baseProduct?.variants?.length || 0,
              addOnProductsType:
                cartData.addOnProducts instanceof Map ? 'Map' : 'Object',
              addOnProductsSize:
                cartData.addOnProducts instanceof Map
                  ? cartData.addOnProducts.size
                  : Object.keys(cartData.addOnProducts).length,
              materialRelationshipsSize:
                cartData.materialRelationships instanceof Map
                  ? cartData.materialRelationships.size
                  : Object.keys(cartData.materialRelationships).length,
            });

          return cartData;
        },

        /**
         * P3.2 SINGLE MASTER DICTIONARY: Get category classification via CCP delegation
         *
         * @param {string} categoryId - The category ID to get classification for
         * @returns {Object} Category classification data with type, displayStrategy, routingIntelligence
         */
        getCategoryClassification(categoryId) {
          const ppd = this.getPerfectProductDictionary();
          if (ppd && ppd.categories) {
            return ppd.categories.get(categoryId);
          }

          isDev() &&
            console.warn(
              `getCategoryClassification: Category ${categoryId} not found in perfectProductDictionary`
            );
          return null;
        },

        /**
         * 🚀 UX FLAGS DELEGATION: Get pre-computed UX behavior flags for a category
         *
         * Returns UX behavior flags from PPD Layer 4 that enable O(1) runtime decisions
         * about navigation flow, button visibility, and interaction patterns.
         *
         * UX Flags provided:
         * - requiresThumbnails: Should show product thumbnails
         * - autoSelectProduct: Should auto-select the only product
         * - hasDirectVariant: Category has direct variant access
         * - showVariantBrowser: Should show variant browser/grid
         * - requiresApplyButton: Should show APPLY button (fixes "No Back Casing" bug)
         * - isSingleStep: One-step selection (auto-apply)
         * - supportsMaterialFilter: Material filtering system support (future)
         * - materialFilterEnabled: Material filter currently enabled (future)
         *
         * @param {string} categoryId - The category ID to get UX flags for
         * @returns {Object|null} UX flags object or null if category not found
         *
         * @example
         * const uxFlags = conductor.getCategoryUXFlags('back-casing');
         * if (uxFlags?.requiresApplyButton) {
         *   renderApplyButton();
         * }
         */
        getCategoryUXFlags(categoryId) {
          const ppd = this.getPerfectProductDictionary();
          if (ppd && ppd.categories) {
            const categoryData = ppd.categories.get(categoryId);
            if (categoryData && categoryData.uxFlags) {
              isDev() &&
                console.log(
                  `🎯 CONDUCTOR: Delegating UX flags for category "${categoryId}"`,
                  categoryData.uxFlags
                );
              return categoryData.uxFlags;
            }
          }

          isDev() &&
            console.warn(
              `getCategoryUXFlags: Category ${categoryId} not found in perfectProductDictionary or missing uxFlags`
            );
          return null;
        },

        /**
         * FINISH-FIRST DELEGATION: Get the Wood & Finish inverted index (Concept A)
         *
         * Returns the pre-built finishSku → [woods] re-pivot from PPD Layer 4.7,
         * used exclusively by md-config-ui-finish-first-selector.js to render the
         * inverted (finish-first) Wood & Finish flow in simplified (3Q) mode.
         *
         * Structure:
         * - finishToWoods: Map<finishSku, [{ finishSku, finishName, woodSku,
         *     woodName, productId, variantId, variantTitle, price, image }]>
         * - finishOrder: [finishSku] (stable first-seen order for swatch rendering)
         * - finishMeta: Map<finishSku, { finishSku, finishName, sampleImage, woodCount }>
         *
         * @returns {Object|null} woodFinishInvertedIndex, or null if unavailable
         *
         * @example
         * const idx = conductor.getWoodFinishInvertedIndex();
         * idx.finishOrder.forEach((sku) => renderSwatch(idx.finishMeta.get(sku)));
         */
        getWoodFinishInvertedIndex() {
          const ppd = this.getPerfectProductDictionary();
          if (ppd && ppd.woodFinishInvertedIndex) {
            return ppd.woodFinishInvertedIndex;
          }

          isDev() &&
            console.warn(
              'getWoodFinishInvertedIndex: woodFinishInvertedIndex not available in perfectProductDictionary'
            );
          return null;
        },

        /**
         * P3.2 SINGLE MASTER DICTIONARY: Get product from dictionary via CCP delegation
         *
         * @param {string} productId - The product ID to retrieve
         * @returns {Object} Product data from perfectProductDictionary
         */
        getProductFromDictionary(productId) {
          const ppd = this.getPerfectProductDictionary();
          if (ppd && ppd.products) {
            return ppd.products.get(productId);
          }

          isDev() &&
            console.warn(
              `getProductFromDictionary: Product ${productId} not found in perfectProductDictionary`
            );
          return null;
        },

        /**
         * P3.2 CCP DELEGATION: Get product by selection key (product_id or variant_id)
         *
         * This method provides a unified interface for retrieving products based on
         * selection keys that can represent either direct product IDs or variant IDs.
         *
         * @param {string} key - Selection key in format "product_123" or "variant_456"
         * @returns {Object|null} Product object from perfectProductDictionary or null if not found
         */
        /**
         * ⚡ PPD-FIRST: Get product by selection key (O(1) lookup)
         * Uses pre-built variantIdToProductMap from PPD Layer 4
         *
         * @param {string} key - Selection key (format: "product_{id}" or "variant_{id}")
         * @returns {Object|null} Product object or null if not found
         */
        getProductBySelectionKey(key) {
          try {
            if (!key || typeof key !== 'string') {
              isDev() &&
                console.warn(
                  'getProductBySelectionKey: Invalid key parameter',
                  key
                );
              return null;
            }

            const [type, id] = key.split('_');

            if (!type || !id) {
              isDev() &&
                console.warn(
                  `getProductBySelectionKey: Malformed key "${key}" (expected format: "product_{id}" or "variant_{id}")`
                );
              return null;
            }

            const ppd = this.getPerfectProductDictionary();

            if (!ppd) {
              throw new Error('❌ CONDUCTOR: PPD not available');
            }

            if (type === 'product') {
              // Direct product lookup (O(1))
              return ppd.products.get(id) || null;
            } else if (type === 'variant') {
              // O(1) variant→product lookup using pre-built map
              if (!ppd.variantIdToProductMap) {
                throw new Error(
                  '❌ CONDUCTOR: variantIdToProductMap missing from PPD - data pipeline Layer 4 incomplete'
                );
              }

              const product = ppd.variantIdToProductMap.get(parseInt(id));

              if (!product) {
                isDev() &&
                  console.warn(
                    `⚠️ CONDUCTOR: No product found for variant ${id}`
                  );
                return null;
              }

              isDev() &&
                console.log(
                  `⚡ Found product for variant ${id} via O(1) map lookup`
                );
              return product;
            } else {
              isDev() &&
                console.warn(
                  `getProductBySelectionKey: Unknown key type "${type}" in key "${key}"`
                );
              return null;
            }
          } catch (error) {
            this.handleError('Failed to get product by selection key', error, {
              method: 'getProductBySelectionKey',
              key,
              severity: 'error',
              recoverable: true,
            });
            return null;
          }
        },

        /**
         * P3.2 CCP DELEGATION: Get product image URL by product ID
         *
         * Retrieves image URL from the productIdToImageMap built in Layer 4 of the data pipeline.
         * This provides a reliable way for Phase 2 UI modules to get image URLs for both
         * regular products and CompositeProducts without accessing raw Shopify data.
         *
         * @param {string|number} productId - The product ID to get image URL for
         * @returns {string|null} Image URL or null if not found
         */
        getProductImageUrl(productId) {
          const ppd = this.getPerfectProductDictionary();
          if (!ppd || !ppd.productIdToImageMap) {
            isDev() &&
              console.error(
                '❌ CONDUCTOR: productIdToImageMap not available in perfectProductDictionary - data pipeline failed'
              );
            return null;
          }

          const imageUrl = ppd.productIdToImageMap.get(productId);
          if (!imageUrl) {
            isDev() &&
              console.error(
                `❌ CONDUCTOR: No image URL found for product ${productId} - productIdToImageMap incomplete`
              );
            return null;
          }

          return imageUrl;
        },

        /**
         * P3.2 CCP DELEGATION: Get composite product by group ID
         *
         * Retrieves a CompositeProduct from the compositeGroupIdToProductMap built in Layer 4
         * of the data pipeline. This provides O(1) lookup performance for Phase 2 modules
         * to access composite products without raw data access.
         *
         * @param {string} groupId - The composite group ID to lookup
         * @returns {Object|null} CompositeProduct object or null if not found
         */
        getCompositeProductByGroupId(groupId) {
          const ppd = this.getPerfectProductDictionary();
          if (!ppd || !ppd.compositeGroupIdToProductMap) {
            isDev() &&
              console.error(
                '❌ CONDUCTOR: compositeGroupIdToProductMap not available in PPD - data pipeline failed'
              );
            return null;
          }

          let compositeProduct = ppd.compositeGroupIdToProductMap.get(groupId);

          // 🚨 SEPARATOR-RUN FALLBACK -- DO NOT REMOVE WITHOUT READING THIS.
          // A composite group id is built TWO different ways, and they disagree
          // for any category whose name contains a non-alphanumeric character:
          //   `id`             -> generateGroupId()/slugify(), which COLLAPSES runs
          //                       ("Wood & Finish" -> wood-finish)
          //   `data-group-id`  -> per-character replace, NO collapse
          //                       ("Wood & Finish" -> wood---finish)   <- the map key
          // A tile click derives the key from the PRODUCT ID (the collapsed form)
          // whenever the tile carries no data-group-id attribute, so on those
          // categories the exact lookup always misses -- the drill-down silently
          // never opens and the error log is isDev()-gated, so production shows
          // nothing at all. Measured live 2026-09-17: all 7 Wood & Finish species
          // dead, customers unable to pick a wood or finish; every other composite
          // category coincidentally matched because its name has no "&".
          //
          // Matching on the collapsed form bridges the two builders WITHOUT
          // changing any key, so a PPD already cached in a customer's IndexedDB
          // (built under the old format) keeps resolving too.
          //
          // The collapsed match REFUSES ambiguity: collapsing runs is
          // many-to-one, so two distinct keys can collapse alike. Returning the
          // first hit would silently resolve the WRONG composite -- a customer
          // charged for and shipped a finish they never chose -- so an
          // ambiguous key degrades to the (visible) dead drill-down instead.
          // Measured 2026-09-17 on the live PPD: 42 keys, 42 distinct collapsed
          // forms, zero collisions, so this is dormant today.
          if (!compositeProduct) {
            const collapseRuns = (value) =>
              String(value).replace(/-{2,}/g, '-');
            const wanted = collapseRuns(groupId);
            let ambiguous = false;
            for (const [key, value] of ppd.compositeGroupIdToProductMap) {
              if (collapseRuns(key) !== wanted) continue;
              if (compositeProduct) {
                ambiguous = true;
                break;
              }
              compositeProduct = value;
            }
            if (ambiguous) compositeProduct = null;
          }

          if (!compositeProduct) {
            isDev() &&
              console.error(
                `❌ CONDUCTOR: No composite product found for group ID ${groupId} - invalid group-id or PPD incomplete`
              );
            return null;
          }

          return compositeProduct;
        },

        /**
         * P3.2 CCP DELEGATION: Get composite product by variant ID (O(1) lookup)
         *
         * Retrieves a CompositeProduct from the variantIdToCompositeProductMap built in Layer 4
         * Uses O(1) map lookup instead of O(n×m) product iteration
         *
         * @param {number|string} variantId - Variant ID (string for synthetic, numeric for Shopify)
         * @returns {Object|null} CompositeProduct object or null if not found
         *
         * @note Supports both string IDs (CompositeProduct synthetic variants like "generated_123456_0")
         *       and numeric IDs (Shopify variants like 789012). Dual-lookup maintains O(1) performance.
         */
        getCompositeProductByVariantId(variantId) {
          const ppd = this.getPerfectProductDictionary();
          if (!ppd || !ppd.variantIdToCompositeProductMap) {
            isDev() &&
              console.warn(
                '⚠️ CONDUCTOR: variantIdToCompositeProductMap not available in PPD - data pipeline Layer 4 incomplete'
              );
            return null;
          }

          // 🔥 FIX: Support both string (CompositeProduct synthetic) and numeric (Shopify) variant IDs
          const stringId = String(variantId);
          const numericId = parseInt(variantId, 10);
          const compositeProduct =
            ppd.variantIdToCompositeProductMap.get(stringId) ||
            ppd.variantIdToCompositeProductMap.get(numericId);

          if (!compositeProduct) {
            isDev() &&
              console.log(
                `⚡ CONDUCTOR: No composite product found for variant ID ${variantId}`
              );
            return null;
          }

          isDev() &&
            console.log(
              `⚡ CONDUCTOR: Found composite via O(1) variant lookup: ${compositeProduct.title}`
            );
          return compositeProduct;
        },

        /**
         * P3.2 CCP DELEGATION: Get composite product by source product ID (O(1) lookup)
         *
         * Retrieves a CompositeProduct from the sourceProductIdToCompositeMap built in Layer 4
         * Uses O(1) map lookup instead of O(n) product iteration
         *
         * @param {number} sourceProductId - The source product ID to lookup
         * @returns {Object|null} CompositeProduct object or null if not found
         */
        getCompositeProductBySourceId(sourceProductId) {
          const ppd = this.getPerfectProductDictionary();
          if (!ppd || !ppd.sourceProductIdToCompositeMap) {
            isDev() &&
              console.warn(
                '⚠️ CONDUCTOR: sourceProductIdToCompositeMap not available in PPD - data pipeline Layer 4 incomplete'
              );
            return null;
          }

          const numericSourceId = parseInt(sourceProductId, 10);
          if (isNaN(numericSourceId)) {
            isDev() &&
              console.warn(
                `⚠️ CONDUCTOR: Invalid source product ID ${sourceProductId}`
              );
            return null;
          }

          const compositeProduct =
            ppd.sourceProductIdToCompositeMap.get(numericSourceId);
          if (!compositeProduct) {
            isDev() &&
              console.log(
                `⚡ CONDUCTOR: No composite product found for source product ID ${sourceProductId}`
              );
            return null;
          }

          isDev() &&
            console.log(
              `⚡ CONDUCTOR: Found composite via O(1) source lookup: ${compositeProduct.title}`
            );
          return compositeProduct;
        },

        /**
         * P3.2 CCP DELEGATION: Get dimension category index (O(1) lookups)
         *
         * Returns the pre-built dimension category index from PPD Layer 4
         * Enables O(1) dimension category classification instead of O(n×m) iteration
         *
         * @returns {Object|null} Dimension category index or null if not available
         */
        getDimensionCategoryIndex() {
          const ppd = this.getPerfectProductDictionary();
          if (!ppd || !ppd.dimensionCategoryIndex) {
            isDev() &&
              console.warn(
                '⚠️ CONDUCTOR: dimensionCategoryIndex not available in PPD - data pipeline Layer 4 incomplete'
              );
            return null;
          }

          return ppd.dimensionCategoryIndex;
        },

        /**
         * CCP DELEGATION: Get product ID for a variant ID (O(1) lookup)
         * @param {number|string} variantId - The variant ID to lookup (will be coerced to number)
         * @returns {string|number|null} - The product ID, or null if not found
         */
        /**
         * @deprecated Use getProductIdForVariantId() for consistency. This method will be removed in a future version.
         */
        getProductIdForVariant(variantId) {
          isDev() &&
            console.warn(
              '⚠️ DEPRECATION: getProductIdForVariant() is deprecated. Use getProductIdForVariantId() instead.'
            );
          return this.getProductIdForVariantId(variantId);
        },

        /**
         * Get full product object for a variant ID using pre-built PPD map (O(1) lookup)
         * This ELIMINATES the O(n²) search pattern where we iterate through all products
         * and all their variants to find which product owns a variant.
         *
         * @param {string|number} variantId - The variant ID to look up (will be coerced to number)
         * @returns {Object|null} Full product object from PPD, or null if not found
         * @throws {Error} If PPD or variantIdToProductMap is not available
         */
        getProductForVariantId: function (variantId) {
          const METHOD = 'getProductForVariantId';

          if (!this.perfectProductDictionary) {
            isDev() &&
              console.error(`❌ CONDUCTOR [${METHOD}]: PPD not initialized`);
            throw new Error(
              'PPD not available - cannot lookup variant→product'
            );
          }

          if (!this.perfectProductDictionary.variantIdToProductMap) {
            isDev() &&
              console.error(
                `❌ CONDUCTOR [${METHOD}]: variantIdToProductMap not available in PPD`
              );
            throw new Error(
              'variantIdToProductMap not built in PPD - architectural violation'
            );
          }

          if (variantId === null || variantId === undefined) {
            isDev() &&
              console.warn(
                `⚠️ CONDUCTOR [${METHOD}]: Received null or undefined variantId`
              );
            return null;
          }

          // 🔥 FIX: Support both string (CompositeProduct synthetic) and numeric (Shopify) variant IDs
          const stringId = String(variantId);
          const numericId = parseInt(variantId, 10);
          const product =
            this.perfectProductDictionary.variantIdToProductMap.get(stringId) ||
            this.perfectProductDictionary.variantIdToProductMap.get(numericId);

          if (!product) {
            isDev() &&
              console.warn(
                `⚠️ CONDUCTOR [${METHOD}]: Variant ID ${variantId} not found in PPD index`
              );
            return null;
          }

          isDev() &&
            console.log(
              `✅ CONDUCTOR [${METHOD}]: O(1) lookup successful - variant ${variantId} → product ${product.id}`
            );
          return product;
        },

        /**
         * Get product ID for a variant ID using pre-built PPD map (O(1) lookup)
         * Lighter-weight alternative to getProductForVariantId when you only need the ID
         *
         * @param {string|number} variantId - The variant ID to look up (will be coerced to number)
         * @returns {string|null} Product ID string, or null if not found
         */
        getProductIdForVariantId: function (variantId) {
          const METHOD = 'getProductIdForVariantId';

          // ✅ CRITICAL: Throw error if PPD not initialized (bootstrap failure)
          if (!this.perfectProductDictionary) {
            isDev() &&
              console.error(`❌ CONDUCTOR [${METHOD}]: PPD not initialized`);
            throw new Error(
              'PPD not available - cannot lookup variant→product ID'
            );
          }

          // ✅ CRITICAL: Throw error if map not built (architectural violation)
          if (!this.perfectProductDictionary.variantIdToProductIdMap) {
            isDev() &&
              console.error(
                `❌ CONDUCTOR [${METHOD}]: variantIdToProductIdMap not available in PPD`
              );
            throw new Error(
              'variantIdToProductIdMap not built in PPD - architectural violation'
            );
          }

          if (variantId === null || variantId === undefined) {
            isDev() &&
              console.warn(
                `⚠️ CONDUCTOR [${METHOD}]: Received null or undefined variantId`
              );
            return null;
          }

          // 🔥 FIX: Support both string (CompositeProduct synthetic) and numeric (Shopify) variant IDs
          const stringId = String(variantId);
          const numericId = parseInt(variantId, 10);
          const productId =
            this.perfectProductDictionary.variantIdToProductIdMap.get(
              stringId
            ) ||
            this.perfectProductDictionary.variantIdToProductIdMap.get(
              numericId
            );

          // ✅ CORRECT: Return null for data lookup miss (variant not in index)
          if (!productId) {
            isDev() &&
              console.warn(
                `⚠️ CONDUCTOR [${METHOD}]: Variant ${variantId} not found in index`
              );
            return null;
          }

          isDev() &&
            console.log(
              `✅ CONDUCTOR [${METHOD}]: O(1) lookup - variant ${variantId} → product ${productId}`
            );
          return productId;
        },

        /**
         * Private helper: Handle missing shipping data with standardized error response.
         * DRY Pattern: Eliminates duplicate error handling in getVariantShippingTime() and getProductMaxShippingTime().
         *
         * @private
         * @param {string} type - Data type: 'variant' or 'product'
         * @param {string|number} id - The ID of the missing data
         * @returns {{low: number, high: number}} Zero shipping time fallback
         */
        _handleMissingShippingData: function (type, id) {
          // Log warning in development
          isDev() &&
            console.warn(
              `⚠️ CONDUCTOR: ${type.charAt(0).toUpperCase() + type.slice(1)} shipping time not found for ID ${id}`
            );

          // Publish error event if CoreEventBus available
          // Pattern #18: Freeze payload to prevent subscriber mutations
          if (window.MurphyDoorConfigurator.CoreEventBus) {
            window.MurphyDoorConfigurator.CoreEventBus.publish(
              CONSTANTS.EVENTS.ERROR_SHIPPING_DATA_MISSING,
              {
                type: type,
                id: id,
                timestamp: Date.now(),
              }
            );
          }

          // Return zero shipping time fallback
          return { low: 0, high: 0 };
        },

        /**
         * Get pre-computed shipping time for a variant from the PPD.
         * ⚡ PPD-FIRST ARCHITECTURE: O(1) lookup from variantShippingTimeMap.
         *
         * @param {number|string} variantId - Variant ID (string for synthetic, numeric for Shopify)
         * @returns {{low: number, high: number}} The pre-computed shipping time range in weeks, or {low: 0, high: 0} if not found.
         *
         * @note Supports both string IDs (CompositeProduct synthetic variants like "generated_123456_0")
         *       and numeric IDs (Shopify variants like 789012). Dual-lookup maintains O(1) performance.
         */
        getVariantShippingTime: function (variantId) {
          if (!this.perfectProductDictionary?.variantShippingTimeMap) {
            isDev() &&
              console.warn(
                '⚠️ CONDUCTOR: variantShippingTimeMap not available in PPD'
              );
            return { low: 0, high: 0 };
          }

          // 🔥 FIX: Support both string (CompositeProduct synthetic) and numeric (Shopify) variant IDs
          const stringId = String(variantId);
          const numericId = parseInt(variantId, 10);
          const time =
            this.perfectProductDictionary.variantShippingTimeMap.get(
              stringId
            ) ||
            this.perfectProductDictionary.variantShippingTimeMap.get(numericId);

          if (!time) {
            return this._handleMissingShippingData('variant', variantId);
          }

          return time;
        },

        /**
         * Get pre-computed maximum shipping time for a product from the PPD.
         * ⚡ PPD-FIRST ARCHITECTURE: Pure O(1) lookup from productMaxShippingTimeMap. All fallbacks are resolved during PPD creation.
         *
         * @param {number|string} productId - The ID of the product to look up (can be a numeric ID or a string for composite products).
         * @returns {{low: number, high: number}} The pre-computed maximum shipping time range in weeks, or {low: 0, high: 0} if not found.
         */
        getProductMaxShippingTime: function (productId) {
          if (!this.perfectProductDictionary?.productMaxShippingTimeMap) {
            isDev() &&
              console.warn(
                '⚠️ CONDUCTOR: productMaxShippingTimeMap not available in PPD'
              );
            return { low: 0, high: 0 };
          }

          // Pure O(1) lookup - all fallbacks resolved in PPD Layer 4
          // Normalize to string for consistent key type (handles both numeric and composite IDs)
          const time =
            this.perfectProductDictionary.productMaxShippingTimeMap.get(
              String(productId)
            );

          if (!time) {
            return this._handleMissingShippingData('product', productId);
          }

          return time;
        },

        /**
         * CCP DELEGATION: Get category fallback image URL
         *
         * Retrieves the fallback image URL for a category from the categoryFallbackImages
         * map built in Layer 4 of the data pipeline. This eliminates runtime object construction
         * in UI modules for static configuration data.
         *
         * @param {string} categoryId - The category identifier
         * @returns {string|null} Fallback image URL or generic fallback
         */
        getCategoryFallbackImage(categoryId) {
          const ppd = this.getPerfectProductDictionary();
          if (!ppd || !ppd.categoryFallbackImages) {
            isDev() &&
              console.error(
                '❌ CONDUCTOR: categoryFallbackImages not available in PPD'
              );
            return 'https://cdn.shopify.com/s/files/1/0532/4911/4973/files/murphy-door-placeholder.jpg?v=1722540000';
          }

          // Try exact match first, then generic fallback
          return (
            ppd.categoryFallbackImages.get(categoryId) ||
            ppd.categoryFallbackImages.get('_generic')
          );
        },

        /**
         * P3.2 CCP DELEGATION: Get virtualized products for a category
         *
         * Returns products from PPD that belong to the specified category.
         * This includes CompositeProducts created in Layer 4 virtualization.
         * Products are already fully virtualized and ready for navigation/display.
         *
         * @param {string} categoryId - Category identifier
         * @returns {Array} Array of product objects (may include CompositeProducts)
         */
        /**
         * ⚡ PPD-FIRST: Get virtualized products for category (O(1) lookup)
         * Uses pre-built categoryToProductsMap from PPD Layer 4
         * NO FALLBACK - Fails fast if PPD incomplete
         *
         * @param {string} categoryId - Category identifier
         * @returns {Array} Frozen array of virtualized products
         */
        getVirtualizedProductsForCategory(categoryId) {
          try {
            const ppd = this.getPerfectProductDictionary();

            if (!ppd) {
              throw new Error('❌ CONDUCTOR: PPD not available');
            }

            // FAIL-FAST: No fallback allowed - PPD MUST have this map
            if (!ppd.categoryToProductsMap) {
              throw new Error(
                '❌ CONDUCTOR: categoryToProductsMap missing from PPD - data pipeline Layer 4 incomplete. ' +
                  'This is a CRITICAL architectural violation - Phase 1 must complete before Phase 2 runs.'
              );
            }

            const products = ppd.categoryToProductsMap.get(categoryId);

            if (!products) {
              isDev() &&
                console.warn(
                  `⚠️ CONDUCTOR: No products found for category "${categoryId}"`
                );
              return Object.freeze([]);
            }

            isDev() &&
              console.log(
                `⚡ getVirtualizedProductsForCategory: Found ${products.length} products for category "${categoryId}" via O(1) map lookup`
              );

            return products; // Already frozen from PPD Layer 4
          } catch (error) {
            this.handleError(
              'Failed to get virtualized products for category',
              error,
              {
                method: 'getVirtualizedProductsForCategory',
                categoryId,
                severity: 'critical',
                recoverable: false, // NO FALLBACK - must fix PPD
              }
            );
            return Object.freeze([]);
          }
        },

        /**
         * ⚡ PPD-FIRST: Get unified forbidden rules structure (ONLY access method)
         *
         * MODERNIZED: Returns ONLY the unified metaobject-driven forbidden rules.
         * Legacy forbiddenRulesById and itemToForbiddenRuleIds have been REMOVED.
         *
         * Returns object containing:
         * - unifiedRules: Map of ruleId → unified rule object (with targetVariants Set, conditionGroups array, logicOperator)
         * - unifiedConditionGroups: Map of groupId → condition group object
         * - conflictRules: Map of ruleId → conflict-only rules (excludes AUTO_APPLY_OPTION), pre-partitioned at bootstrap
         * - autoApplyRules: Map of ruleId → AUTO_APPLY_OPTION rules only, pre-partitioned at bootstrap
         *
         * @returns {Object} Unified forbidden rules structure
         */
        getUnifiedForbiddenRules() {
          const ppd = this.getPerfectProductDictionary();
          return {
            unifiedRules: ppd?.unifiedRules || new Map(),
            unifiedConditionGroups: ppd?.unifiedConditionGroups || new Map(),
            conflictRules: ppd?.conflictRules || null,
            autoApplyRules: ppd?.autoApplyRules || null,
          };
        },

        /**
         * @deprecated REMOVED - Legacy Maps no longer exist in PPD.
         * Use getUnifiedForbiddenRules() instead.
         * @throws {Error} Always throws - enforces unified system usage
         */
        getForbiddenRulesById() {
          throw new Error(
            'CRITICAL: getForbiddenRulesById() is REMOVED. Legacy Maps no longer exist in PPD. ' +
              'Use getUnifiedForbiddenRules() for the unified metaobject-driven validation system.'
          );
        },

        /**
         * @deprecated REMOVED - Legacy Maps no longer exist in PPD.
         * Use getUnifiedForbiddenRules() instead.
         * @throws {Error} Always throws - enforces unified system usage
         */
        getItemForbiddenRuleIds() {
          throw new Error(
            'CRITICAL: getItemForbiddenRuleIds() is REMOVED. Legacy Maps no longer exist in PPD. ' +
              'Use getUnifiedForbiddenRules() for the unified metaobject-driven validation system.'
          );
        },

        /**
         * @deprecated REMOVED - Legacy array access was already prohibited.
         * Use getUnifiedForbiddenRules() instead.
         * @throws {Error} Always throws - enforces unified system usage
         */
        getForbiddenRules() {
          throw new Error(
            'CRITICAL: getForbiddenRules() is REMOVED. ' +
              'Use getUnifiedForbiddenRules() for the unified metaobject-driven validation system.'
          );
        },

        /**
         * ⚡ UNIFIED SYSTEM: Validate if an item conflicts with currently applied selections
         *
         * Uses the unified metaobject-driven forbidden rules system exclusively.
         * Evaluates condition groups with logic operators (ALL/ANY/NONE) to determine conflicts.
         *
         * @param {string} targetItemId - Product/variant GID to check
         * @param {Object} appliedSelections - Current applied selections { categoryId: { appliedItem: {...} } }
         * @returns {Object} { isValid: boolean, conflicts: Array<{ ruleId, description, conflictingItems }> }
         */
        validateForbiddenCombination(targetItemId, appliedSelections) {
          // Guard clause: handle nullish appliedSelections gracefully
          if (!appliedSelections) {
            return { isValid: true, conflicts: [] };
          }

          const forbiddenValidator =
            window.MurphyDoorConfigurator.forbiddenValidator;
          if (!forbiddenValidator) {
            isDev() &&
              console.warn('Forbidden validator not available for validation.');
            return { isValid: true, conflicts: [] };
          }

          // Get the full set of rules via the conductor's own helper
          const forbiddenRules = this.getUnifiedForbiddenRules();

          // Delegate the entire validation to the business logic module
          const validationResult =
            forbiddenValidator.validateItemAgainstSelections(
              String(targetItemId),
              appliedSelections,
              forbiddenRules
            );

          // Adapt the result to the expected format of this method
          return {
            isValid: !validationResult.isConflicted,
            conflicts: validationResult.conflicts || [],
          };
        },

        /**
         * CCP DELEGATION: Check if a variant is preemptively forbidden.
         *
         * Delegates to forbiddenValidator.isVariantPreemptivelyForbidden() for O(1) in-memory
         * lookup. Used by UI modules (e.g., horizontal slider) that need to check forbidden
         * state without direct module-to-module coupling.
         *
         * @param {string} variantId - The variant ID to check
         * @returns {Object|null} Forbidden info {isForbidden, reasons, reasonText} or null if not forbidden
         */
        isVariantPreemptivelyForbidden(variantId) {
          if (!variantId) return null;

          const forbiddenValidator =
            window.MurphyDoorConfigurator.forbiddenValidator;
          if (
            forbiddenValidator &&
            typeof forbiddenValidator.isVariantPreemptivelyForbidden ===
              'function'
          ) {
            return forbiddenValidator.isVariantPreemptivelyForbidden(variantId);
          }

          return null;
        },

        /**
         * P3.3 CCP DELEGATION: Get material compatibility index for O(1) lookups
         *
         * Returns the pre-built material compatibility index from PPD Layer 4.
         * This enables O(1) material compatibility checks instead of O(n×m) iterations.
         *
         * Index structure:
         * - woodCompatibilityMap: Map<woodSku, Set<variantId>>
         * - finishCompatibilityMap: Map<finishSku, Set<variantId>>
         * - variantMaterialScoreBase: Map<variantId, {hasWood, hasFinish, tagCount, woodSku, finishSku}>
         *
         * @returns {Object|null} Material compatibility index or null if not available
         */
        getMaterialCompatibilityIndex() {
          const ppd = this.getPerfectProductDictionary();
          if (!ppd || !ppd.materialCompatibilityIndex) {
            isDev() &&
              console.warn(
                'getMaterialCompatibilityIndex: materialCompatibilityIndex not available in PPD'
              );
            return null;
          }

          return ppd.materialCompatibilityIndex;
        },

        /**
         * ⚡ PPD-FIRST: Get material combination validation map for O(1) validation
         *
         * Returns the pre-built validation map from PPD Layer 4 that enables O(1)
         * wood+finish combination validation instead of O(n) materialRelationships searches.
         *
         * Map structure: Map<combinationKey, boolean>
         * - Key format: "woodSku::finishSku"
         * - Value: true (pre-validated combination)
         *
         * @returns {Map|null} Material validation map or null if not available
         */
        getMaterialCombinationValidationMap() {
          const ppd = this.getPerfectProductDictionary();
          if (!ppd?.materialCombinationValidationMap) {
            isDev() &&
              console.warn(
                '⚠️ CONDUCTOR: materialCombinationValidationMap not available in PPD - validation will fail'
              );
            return new Map(); // Empty map = no valid combinations
          }

          return ppd.materialCombinationValidationMap;
        },

        /**
         * ⚡ PPD-FIRST: Filter compatible variants by material selections (O(1) lookups)
         *
         * Uses pre-built materialCompatibilityIndex from PPD Layer 4 for O(1) filtering
         * instead of O(n×m) runtime iterations. Returns enhanced filter result with
         * single-variant detection included.
         *
         * @param {string} categoryId - Category identifier
         * @param {string|null} woodId - Selected wood GID (optional)
         * @param {string|null} finishId - Selected finish GID (optional)
         * @returns {Object} Enhanced filter result with compatible variants and metadata
         */
        filterCompatibleVariantsByMaterial(categoryId, woodId, finishId) {
          const ppd = this.getPerfectProductDictionary();

          if (!ppd?.materialCompatibilityIndex) {
            this.handleError(
              'PPD materialCompatibilityIndex missing - cannot filter variants',
              new Error(
                'Data pipeline may have failed to build material compatibility index'
              ),
              {
                method: 'filterCompatibleVariantsByMaterial',
                severity: 'critical',
                recoverable: false,
              }
            );
            return {
              compatibleVariants: [],
              isSingleVariant: false,
              singleVariantId: null,
              totalCompatible: 0,
              woodName: null,
              finishName: null,
            };
          }

          const {
            woodCompatibilityMap,
            finishCompatibilityMap,
            variantMaterialScoreBase,
          } = ppd.materialCompatibilityIndex;

          // Get category variants
          const categoryVariants =
            ppd.categoryToCompatibleVariantsMap?.get(categoryId);
          if (!categoryVariants || categoryVariants.size === 0) {
            isDev() &&
              console.warn(
                `⚠️ CONDUCTOR: No variants found for category "${categoryId}"`
              );
            return {
              compatibleVariants: [],
              isSingleVariant: false,
              singleVariantId: null,
              totalCompatible: 0,
              woodName: null,
              finishName: null,
            };
          }

          let compatibleVariantIds;

          // O(1) filtering based on material selections
          if (woodId && finishId) {
            // Both materials selected - O(1) Set intersection
            const woodVariants = woodCompatibilityMap.get(woodId);
            const finishVariants = finishCompatibilityMap.get(finishId);

            if (!woodVariants || !finishVariants) {
              compatibleVariantIds = [];
            } else {
              // Set intersection: variants in both wood AND finish sets AND category
              compatibleVariantIds = [...woodVariants].filter(
                (v) => finishVariants.has(v) && categoryVariants.has(v)
              );
            }
          } else if (woodId) {
            // Only wood selected - O(1) lookup
            const woodVariants = woodCompatibilityMap.get(woodId);
            compatibleVariantIds = woodVariants
              ? [...woodVariants].filter((v) => categoryVariants.has(v))
              : [];
          } else if (finishId) {
            // Only finish selected - O(1) lookup
            const finishVariants = finishCompatibilityMap.get(finishId);
            compatibleVariantIds = finishVariants
              ? [...finishVariants].filter((v) => categoryVariants.has(v))
              : [];
          } else {
            // No materials selected - return all category variants
            compatibleVariantIds = [...categoryVariants];
          }

          // Get material names from PPD
          let woodName = null;
          let finishName = null;

          if (woodId && compatibleVariantIds.length > 0) {
            const firstVariant = variantMaterialScoreBase.get(
              compatibleVariantIds[0]
            );
            woodName = firstVariant?.woodSku === woodId ? 'Wood' : null;
          }

          if (finishId && compatibleVariantIds.length > 0) {
            const firstVariant = variantMaterialScoreBase.get(
              compatibleVariantIds[0]
            );
            finishName = firstVariant?.finishSku === finishId ? 'Finish' : null;
          }

          // Build enhanced result with single-variant detection
          const result = {
            compatibleVariants: compatibleVariantIds,
            isSingleVariant: compatibleVariantIds.length === 1,
            singleVariantId:
              compatibleVariantIds.length === 1
                ? compatibleVariantIds[0]
                : null,
            totalCompatible: compatibleVariantIds.length,
            woodName: woodName,
            finishName: finishName,
          };

          isDev() &&
            console.log(
              `✅ PPD-FIRST: Filtered ${result.totalCompatible} compatible variants for category "${categoryId}" ` +
                `(wood: ${woodId || 'none'}, finish: ${finishId || 'none'}) via O(1) lookups`
            );

          return result;
        },

        /**
         * P3.4 CCP DELEGATION: Get parent tab for category (O(1) lookup)
         *
         * Returns the parent tab ID for a given category using PPD Layer 4's
         * categoryToParentTabMap. Eliminates O(n²) tab searching in navigation manager.
         *
         * @param {string} categoryId - Category identifier
         * @returns {string|null} Parent tab ID or null if not found
         */
        getParentTabForCategory(categoryId) {
          const ppd = this.getPerfectProductDictionary();
          if (!ppd || !ppd.categoryToParentTabMap) {
            isDev() &&
              console.error(
                '❌ CONDUCTOR: categoryToParentTabMap not available in PPD'
              );
            return null;
          }

          if (!categoryId) {
            isDev() &&
              console.warn(
                '⚠️ CONDUCTOR: categoryId parameter is required for getParentTabForCategory'
              );
            return null;
          }

          const parentTabId = ppd.categoryToParentTabMap.get(categoryId);

          if (!parentTabId) {
            isDev() &&
              console.warn(
                `⚠️ CONDUCTOR: No parent tab found for category "${categoryId}"`
              );
            return null;
          }

          isDev() &&
            console.log(
              `✅ PPD-FIRST: getParentTabForCategory found parent tab "${parentTabId}" for category "${categoryId}" via O(1) lookup`
            );

          return parentTabId;
        },

        /**
         * Evaluate whether an implicit apply should fire before navigating
         * to a new category or tab.
         *
         * Called from event-handler when user clicks a category button or tab button.
         * Checks if there is a BROWSING selection in the currently open category,
         * whether the target is "forward" in PPD sort order, and whether the
         * BROWSING value differs from the APPLIED value.
         *
         * NOTE: DOM queries (for browsing thumbnail) are NOT performed here per CCP rules.
         * The event-handler (which owns DOM access) resolves the thumbnail element separately
         * using the browsingCategoryId returned by this method.
         *
         * @param {string} targetId - The tab ID or category ID that was clicked
         * @param {string} targetType - 'tab' or 'category'
         * @returns {Object} Eligibility result with shape:
         *   { eligible, skipReason, browsingCategoryId, applyPayload }
         */
        evaluateImplicitApplyEligibility(targetId, targetType) {
          const METHOD = 'evaluateImplicitApplyEligibility';
          const noResult = (skipReason) => ({
            eligible: false,
            skipReason,
            browsingCategoryId: null,
            applyPayload: null,
          });

          try {
            // 1. Check if any browsing selections exist
            if (!this.hasBrowsingSelections()) {
              return noResult('no_browsing');
            }

            // 2. Find which category has a browsing selection
            const stateMaps = this.getStateMaps();
            const browsingSelections = stateMaps.browsingSelections;

            if (!browsingSelections || browsingSelections.size === 0) {
              return noResult('no_browsing');
            }

            // Get the currently open category (most likely the one with browsing state)
            const openCategoryId = stateMaps.core.openCategory;

            // Find the browsing category - prefer the open category if it has a browsing selection
            let browsingCategoryId = null;
            let browsingData = null;

            if (openCategoryId && browsingSelections.has(openCategoryId)) {
              browsingCategoryId = openCategoryId;
              browsingData = browsingSelections.get(openCategoryId);
            } else {
              // Fall back to first browsing selection
              for (const [catId, data] of browsingSelections) {
                browsingCategoryId = catId;
                browsingData = data;
                break;
              }
            }

            if (!browsingCategoryId || !browsingData) {
              return noResult('no_browsing');
            }

            // 3. Check if BROWSING === APPLIED (same product+variant selected)
            const appliedSelections = stateMaps.appliedSelections;
            const appliedData = appliedSelections
              ? appliedSelections.get(browsingCategoryId)
              : null;

            if (appliedData) {
              const sameProduct =
                appliedData.productId === browsingData.productId;
              const sameVariant =
                appliedData.variantId === browsingData.variantId;
              if (sameProduct && sameVariant) {
                return noResult('browsing_equals_applied');
              }
            }

            // 4. Determine if target is "forward" in sort order
            let isForward = false;

            if (targetType === 'category') {
              // Use PPD sortedCategories for category order
              const sortedCategories = this.getSortedCategories();
              let browsingIndex = -1;
              let targetIndex = -1;

              for (let i = 0; i < sortedCategories.length; i++) {
                const catId = sortedCategories[i][0];
                if (catId === browsingCategoryId) browsingIndex = i;
                if (catId === targetId) targetIndex = i;
              }

              if (browsingIndex === -1 || targetIndex === -1) {
                isDev() &&
                  console.warn(
                    `[${METHOD}] Could not find category indexes for forward check`
                  );
                return noResult('going_backwards');
              }

              isForward = targetIndex > browsingIndex;
            } else if (targetType === 'tab') {
              // Use PPD sortedTabsByLoadOrder for tab order
              const ppd = this.getPerfectProductDictionary();
              const sortedTabs = ppd?.sortedTabsByLoadOrder;

              if (!sortedTabs) {
                isDev() &&
                  console.warn(
                    `[${METHOD}] sortedTabsByLoadOrder not available`
                  );
                return noResult('going_backwards');
              }

              // Find current tab (the tab containing the browsing category)
              const currentTabId = stateMaps.core.currentTab;
              let currentTabIndex = -1;
              let targetTabIndex = -1;

              for (let i = 0; i < sortedTabs.length; i++) {
                const tabId = sortedTabs[i][0];
                if (tabId === currentTabId) currentTabIndex = i;
                if (tabId === targetId) targetTabIndex = i;
              }

              if (currentTabIndex === -1 || targetTabIndex === -1) {
                isDev() &&
                  console.warn(
                    `[${METHOD}] Could not find tab indexes for forward check`
                  );
                return noResult('going_backwards');
              }

              isForward = targetTabIndex > currentTabIndex;
            }

            if (!isForward) {
              return noResult('going_backwards');
            }

            // 5. Build the apply payload (DOM queries handled by event-handler per CCP rules)
            const applyPayload = {
              categoryId: browsingCategoryId,
              productId: browsingData.productId,
              variantId: browsingData.variantId || null,
              timestamp: window.performance
                ? window.performance.now()
                : Date.now(),
            };

            // 6. Collect other BROWSING selections to apply silently before the primary animated apply.
            // These are from categories the user already visited and left in BROWSING state without
            // explicitly saving. auto_applied:true suppresses SAVING animation and skips AUTO_APPLY
            // cascade re-evaluation (guard at ADD_TO_CART flush gate line prevents infinite loop).
            const silentItems = [];
            for (const [catId, catData] of browsingSelections) {
              if (catId === browsingCategoryId) continue; // primary is handled with animation
              const catApplied = stateMaps.appliedSelections
                ? stateMaps.appliedSelections.get(catId)
                : null;
              const catAlreadyApplied =
                catApplied &&
                catApplied.productId === catData.productId &&
                catApplied.variantId === catData.variantId;
              if (!catAlreadyApplied) {
                silentItems.push({
                  browsingCategoryId: catId,
                  applyPayload: {
                    categoryId: catId,
                    productId: catData.productId,
                    variantId: catData.variantId || null,
                    auto_applied: true,
                    timestamp: window.performance
                      ? window.performance.now()
                      : Date.now(),
                  },
                });
              }
            }

            isDev() &&
              console.log(`[${METHOD}] Eligible for implicit apply`, {
                browsingCategoryId,
                targetId,
                targetType,
                silentItemsCount: silentItems.length,
              });

            return {
              eligible: true,
              skipReason: null,
              browsingCategoryId,
              applyPayload,
              silentItems,
            };
          } catch (error) {
            isDev() &&
              console.error(`[${METHOD}] Error evaluating eligibility`, error);
            return noResult('error');
          }
        },

        /**
         * PPD-FIRST: Get pre-sorted categories array
         * Eliminates O(n log n) sorting at runtime in navigation manager
         *
         * @returns {Array<[categoryId, categoryData]>} Sorted category entries
         */
        getSortedCategories() {
          const ppd = this.getPerfectProductDictionary();

          if (!ppd?.sortedCategories) {
            throw new Error(
              'CRITICAL: PPD missing sortedCategories - data pipeline Layer 4 failed to pre-sort categories. This indicates a bootstrap failure.'
            );
          }

          isDev() &&
            console.log(
              `✅ PPD-FIRST: getSortedCategories returning ${ppd.sortedCategories.length} pre-sorted categories`
            );
          return ppd.sortedCategories;
        },

        /**
         * 🚀 PPD-FIRST: Get cart item templates from PPD
         *
         * Returns pre-built cart item templates for O(1) cart item preparation.
         * Eliminates runtime O(n) async lookups by using Phase 1 pre-built templates.
         *
         * @returns {Map} Cart item templates map (variantId → template)
         */
        getCartItemTemplates: function () {
          if (!this.perfectProductDictionary?.cartItemTemplates) {
            isDev() &&
              console.warn(
                '⚠️ CONDUCTOR: cartItemTemplates not available in PPD'
              );
            return new Map();
          }
          return this.perfectProductDictionary.cartItemTemplates;
        },

        /**
         * 🚀 PPD-FIRST: Get base product validation from PPD
         *
         * Returns pre-validated base product structure from Phase 1 bootstrap.
         * Eliminates runtime validation logic from cart operations.
         *
         * @returns {Object} Base product validation {error, selectedVariant, validatedAt}
         */
        getBaseProductValidation: function () {
          if (!this.perfectProductDictionary?.baseProductValidation) {
            isDev() &&
              console.warn(
                '⚠️ CONDUCTOR: baseProductValidation not available in PPD'
              );
            return {
              error: { hasError: true, message: 'Validation not available' },
              selectedVariant: null,
              validatedAt: null,
            };
          }
          return this.perfectProductDictionary.baseProductValidation;
        },

        /**
         * PPD-FIRST: Get material-aware products for a category and view (O(1) lookup)
         *
         * Returns products that have been filtered for material awareness based on current
         * material preferences (wood/finish selections). This eliminates runtime filtering
         * by using pre-computed maps from PPD Layer 4.
         *
         * @param {string} categoryName - Category identifier
         * @param {string} view - View type ('grid' or 'list')
         * @returns {Array} Array of material-aware product objects
         */
        getMaterialAwareProductsForCategory(categoryName, view) {
          const ppd = this.getPerfectProductDictionary();

          if (!ppd?.categoryToMaterialAwareProductsMap) {
            this.handleError(
              'PPD map `categoryToMaterialAwareProductsMap` is missing.',
              new Error(
                'Data pipeline may have failed to build a critical performance map.'
              ),
              {
                method: 'getMaterialAwareProductsForCategory',
                severity: 'warning',
                recoverable: true,
              }
            );
            return this.getVirtualizedProductsForCategory(categoryName);
          }

          const materialProducts =
            ppd.categoryToMaterialAwareProductsMap.get(categoryName);
          if (!materialProducts) {
            isDev() &&
              console.warn(
                `⚠️ CONDUCTOR: No material-aware products found for category "${categoryName}"`
              );
            return [];
          }

          const viewProducts = materialProducts[view] || [];
          isDev() &&
            console.log(
              `✅ PPD-FIRST: getMaterialAwareProductsForCategory found ${viewProducts.length} products for "${categoryName}" (${view}) via O(1) lookup`
            );

          return viewProducts;
        },

        /**
         * PPD-FIRST: Get products with images for a category and view (O(1) lookup)
         *
         * Returns products that have valid image URLs, eliminating runtime image validation.
         * Uses pre-computed categoryToProductsWithImagesMap from PPD Layer 4.
         *
         * @param {string} categoryName - Category identifier
         * @param {string} view - View type ('grid' or 'list')
         * @returns {Array} Array of products with validated images
         */
        getProductsWithImagesForCategory(categoryName, view) {
          const ppd = this.getPerfectProductDictionary();

          if (!ppd?.categoryToProductsWithImagesMap) {
            this.handleError(
              'PPD map `categoryToProductsWithImagesMap` is missing.',
              new Error(
                'Data pipeline may have failed to build a critical performance map.'
              ),
              {
                method: 'getProductsWithImagesForCategory',
                severity: 'warning',
                recoverable: true,
              }
            );
            return this.getVirtualizedProductsForCategory(categoryName);
          }

          const imageProducts =
            ppd.categoryToProductsWithImagesMap.get(categoryName);
          if (!imageProducts) {
            isDev() &&
              console.warn(
                `⚠️ CONDUCTOR: No products with images found for category "${categoryName}"`
              );
            return [];
          }

          const viewProducts = imageProducts[view] || [];
          isDev() &&
            console.log(
              `✅ PPD-FIRST: getProductsWithImagesForCategory found ${viewProducts.length} products for "${categoryName}" (${view}) via O(1) lookup`
            );

          return viewProducts;
        },

        /**
         * Gets door dimensions from current applied selections
         * Used by space calculator for pass-through space calculations
         * Extracts from "Width", "Door Handing", and "Door Depth" categories (via PPD dimensionCategoryIndex)
         *
         * @returns {Object|null} Door dimensions object with width, depth, handling or null if not available
         */
        getDoorDimensions() {
          const METHOD_SIGNATURE = 'conductor.getDoorDimensions()';
          isDev() &&
            console.log(
              `🔧 ${METHOD_SIGNATURE}: Extracting door dimensions from applied selections`
            );

          try {
            // Get current applied selections
            const appliedSelections = this.getAppliedSelections();
            if (
              !appliedSelections ||
              Object.keys(appliedSelections).length === 0
            ) {
              isDev() &&
                console.warn(
                  `${METHOD_SIGNATURE}: No applied selections available`
                );
              return null;
            }

            isDev() &&
              console.log(
                `🔧 ${METHOD_SIGNATURE}: Applied selections:`,
                appliedSelections
              );

            // 🚀 PPD-FIRST: O(1) direct category lookups instead of O(n×m) iteration
            const ppd = this.getPerfectProductDictionary();
            const dimensionIndex = ppd?.dimensionCategoryIndex;

            if (!dimensionIndex) {
              throw new Error(
                `❌ CONDUCTOR: dimensionCategoryIndex not available in PPD. This is a CRITICAL architectural violation.`
              );
            }

            // O(1) direct lookups - no iteration needed
            let width = null;
            let depth = null;
            let handling = null;

            // Direct access using pre-indexed category names
            if (appliedSelections[dimensionIndex.widthCategory]) {
              width = this.extractDimensionFromSelection(
                appliedSelections[dimensionIndex.widthCategory],
                'width',
                dimensionIndex.widthCategory
              );
              isDev() &&
                console.log(
                  `⚡ ${METHOD_SIGNATURE}: Found width via O(1) lookup:`,
                  width
                );
            }

            if (appliedSelections[dimensionIndex.depthCategory]) {
              depth = this.extractDimensionFromSelection(
                appliedSelections[dimensionIndex.depthCategory],
                'depth',
                dimensionIndex.depthCategory
              );
              isDev() &&
                console.log(
                  `⚡ ${METHOD_SIGNATURE}: Found depth via O(1) lookup:`,
                  depth
                );
            }

            if (appliedSelections[dimensionIndex.handlingCategory]) {
              handling = this.extractDimensionFromSelection(
                appliedSelections[dimensionIndex.handlingCategory],
                'handling',
                dimensionIndex.handlingCategory
              );
              isDev() &&
                console.log(
                  `⚡ ${METHOD_SIGNATURE}: Found handling via O(1) lookup:`,
                  handling
                );
            }

            // The variant enhancer reduces handing to swing only when it builds
            // extractedDimensions ('Left Hand Inswing' -> 'in-swing'), which is
            // all the space math needs. The pass-through DIAGRAM also needs the
            // handedness, because there is a distinct drawing per left/right, so
            // capture the raw variant title alongside it.
            //
            // Additive and nullable: every existing consumer reads
            // width/depth/handling and is unaffected. Resolved here rather than
            // in the space calculator because the conductor owns PPD access.
            let handlingLabel = null;
            try {
              const handlingSelection =
                appliedSelections[dimensionIndex.handlingCategory];
              if (handlingSelection?.variantId) {
                const variantMap = ppd?.variantIdToVariantMap;
                if (variantMap) {
                  const variant =
                    variantMap.get(String(handlingSelection.variantId)) ||
                    variantMap.get(
                      parseInt(handlingSelection.variantId, 10)
                    );
                  handlingLabel = variant?.title || null;
                }
              }
            } catch (labelError) {
              // Never let a cosmetic lookup break dimension extraction — the
              // diagram falls back to its neutral placeholder without a label.
              isDev() &&
                console.warn(
                  `${METHOD_SIGNATURE}: handlingLabel lookup failed:`,
                  labelError
                );
            }

            // Jamb depth, as the raw variant title ('6-9/16 in.'), for the
            // space calculator's in-swing wall clearance (engineering sheet B23,
            // rev. 2026-09-25). Same additive/nullable contract as handlingLabel:
            // the space calculator parses it and falls back to the standard
            // 4-9/16in jamb when it is absent or unparseable.
            let jambLabel = null;
            try {
              const jambCategory = dimensionIndex.jambCategory || 'Jamb Depth';
              const jambSelection = appliedSelections[jambCategory];
              if (jambSelection?.variantId) {
                const variantMap = ppd?.variantIdToVariantMap;
                if (variantMap) {
                  const variant =
                    variantMap.get(String(jambSelection.variantId)) ||
                    variantMap.get(parseInt(jambSelection.variantId, 10));
                  jambLabel = variant?.title || null;
                }
              }
            } catch (jambError) {
              isDev() &&
                console.warn(
                  `${METHOD_SIGNATURE}: jambLabel lookup failed:`,
                  jambError
                );
            }

            // Front casing, for the space calculator's in-swing pass-through
            // (engineering sheet B24, FINAL 2026-09-25): W - (jamb + 0.5) -
            // (casingWidth - 1.5), where a Fluted casing is always 3-3/4in.
            //
            // WHICH CASING: the FRONT one. The sheet has a single "Casing Style"
            // input that feeds the front dimensions, and the blueprint's own
            // "-1 RULE" casing geometry for the front face is keyed on the Front
            // Casing selection too -- so both surfaces answer from the same
            // selection. Same additive/nullable contract as jambLabel: absent ->
            // null, and the space calculator treats null as Standard.
            let casingLabel = null;
            let casingWideRuleExempt = null;
            try {
              const casingCategory =
                dimensionIndex.frontCasingCategory || 'Front Casing';
              const casingSelection =
                this.getAppliedSelectionsMap().get(casingCategory);
              casingLabel = casingSelection?.productTitle || null;
              casingWideRuleExempt =
                this.resolveCasingWideRuleExempt(casingLabel);
            } catch (casingError) {
              isDev() &&
                console.warn(
                  `${METHOD_SIGNATURE}: casingLabel lookup failed:`,
                  casingError
                );
            }

            // Construct door dimensions object
            const doorDimensions = {
              width: width,
              depth: depth,
              handling: handling,
              handlingLabel: handlingLabel,
              jambLabel: jambLabel,
              casingLabel: casingLabel,
              casingWideRuleExempt: casingWideRuleExempt,
            };

            isDev() &&
              console.log(
                `⚡ ${METHOD_SIGNATURE}: Final door dimensions extracted via O(1) lookups:`,
                doorDimensions
              );

            // Return dimensions if we have at least some data
            if (width || depth || handling) {
              return doorDimensions;
            }

            isDev() &&
              console.warn(
                `${METHOD_SIGNATURE}: No door dimension data found in applied selections`
              );
            return null;
          } catch (error) {
            isDev() &&
              console.error(
                `${METHOD_SIGNATURE}: Failed to extract door dimensions:`,
                error
              );

            // Handle extraction errors
            this.handleError('Door dimensions extraction failed', error, {
              method: METHOD_SIGNATURE,
              severity: 'warning',
              recoverable: true,
            });

            return null;
          }
        },

        /**
         * CONDUCTOR DELEGATION: both swings' space diagrams for the APPLIED
         * width/depth/jamb/casing, before a Door Handing is chosen.
         *
         * The published BUSINESS_SPACE_CALCULATION_COMPLETED diagram needs a
         * handing; this is the what-if a surface uses to state "if out-swing /
         * if in-swing" up front (md-config-ui-metrics-dashboard.js). Same
         * formulas, same owner (md-config-bl-space-calculator.js
         * computeSwingScenarios), no events published -- a UI module reaches
         * the BL layer only through here.
         *
         * @returns {{inSwing: Object|null, outSwing: Object|null}}
         */
        getSwingScenarioSpaceData() {
          const none = { inSwing: null, outSwing: null };
          try {
            const calc = window.MurphyDoorConfigurator.spaceCalculator;
            if (!calc || typeof calc.computeSwingScenarios !== 'function') {
              return none;
            }
            return calc.computeSwingScenarios(this.getDoorDimensions()) || none;
          } catch (error) {
            isDev() &&
              console.warn(
                'conductor.getSwingScenarioSpaceData() failed:',
                error
              );
            return none;
          }
        },

        /**
         * Helper method to extract dimension value from a selection
         * Handles both product titles and variant titles for dimension extraction
         *
         * @param {Object} selection - Selection object with productId/variantId
         * @param {string} dimensionType - Type of dimension ('width', 'depth', 'handling')
         * @param {string} categoryId - Category ID for logging
         * @returns {*} Extracted dimension value or null
         *
         * @note Supports both string IDs (CompositeProduct synthetic variants like "generated_123456_0")
         *       and numeric IDs (Shopify variants like 789012). Dual-lookup maintains O(1) performance.
         */
        extractDimensionFromSelection(selection, dimensionType, categoryId) {
          const METHOD_SIGNATURE = 'conductor.extractDimensionFromSelection()';

          try {
            // 🔥 FIX: Support both string (CompositeProduct synthetic) and numeric (Shopify) variant IDs
            // No need to reject string variant IDs - they're valid for CompositeProducts

            let product = null;

            // Handle case where productId is null but variantId exists
            if (!selection.productId && selection.variantId) {
              isDev() &&
                console.log(
                  `${METHOD_SIGNATURE}: ProductId is null, using O(1) variant→product lookup for variant ${selection.variantId}`
                );

              // ⚡ PPD-FIRST: O(1) variant→product lookup using pre-built map
              // ANTI-PATTERN #1 FIX: Eliminates O(n×m) nested loop search
              const ppd = this.getPerfectProductDictionary();
              if (!ppd?.variantIdToProductMap) {
                throw new Error(
                  '❌ CONDUCTOR: variantIdToProductMap missing from PPD - data pipeline Layer 4 incomplete. ' +
                    'This is a CRITICAL architectural violation.'
                );
              }

              // 🔥 FIX: Support both string (synthetic) and numeric variant IDs for dual lookup
              const stringId = String(selection.variantId);
              const numericId = parseInt(selection.variantId, 10);
              product =
                ppd.variantIdToProductMap.get(stringId) ||
                ppd.variantIdToProductMap.get(numericId);

              if (product) {
                isDev() &&
                  console.log(
                    `⚡ ${METHOD_SIGNATURE}: O(1) variant→product lookup successful - variant ${selection.variantId} → product ${product.id}`
                  );
              } else {
                isDev() &&
                  console.warn(
                    `⚠️ ${METHOD_SIGNATURE}: Variant ${selection.variantId} not found in PPD index`
                  );
              }
            } else {
              // Get product from dictionary using productId
              product = this.getProductFromDictionary(selection.productId);
            }

            if (!product) {
              isDev() &&
                console.warn(
                  `${METHOD_SIGNATURE}: Product not found for ${categoryId} (productId: ${selection.productId}, variantId: ${selection.variantId})`
                );
              return null;
            }

            let titleToCheck = product.title;

            // 🚀 PPD-FIRST: O(1) variant lookup instead of O(n) array search
            if (selection.variantId) {
              const ppd = this.getPerfectProductDictionary();

              if (!ppd?.variantIdToVariantMap) {
                isDev() &&
                  console.warn(
                    `⚠️ ${METHOD_SIGNATURE}: variantIdToVariantMap not available in PPD - using product title only`
                  );
                titleToCheck = product.title;
              } else {
                // 🔥 FIX: Support both string (CompositeProduct synthetic) and numeric (Shopify) variant IDs
                const stringId = String(selection.variantId);
                const numericId = parseInt(selection.variantId, 10);
                // O(1) direct variant lookup with dual lookup pattern
                const variant =
                  ppd.variantIdToVariantMap.get(stringId) ||
                  ppd.variantIdToVariantMap.get(numericId);

                if (variant?.title) {
                  titleToCheck = variant.title;
                  isDev() &&
                    console.log(
                      `⚡ ${METHOD_SIGNATURE}: O(1) map lookup - using variant title "${titleToCheck}"`
                    );
                } else {
                  isDev() &&
                    console.warn(
                      `⚠️ ${METHOD_SIGNATURE}: Variant ${selection.variantId} not found in PPD variantIdToVariantMap`
                    );
                  titleToCheck = product.title;
                }
              }
            }

            // ⚡ PPD-FIRST: Use pre-extracted dimensions from variant object (O(1) property read)
            // Get the variant object (already looked up above via O(1) map access)
            let variant = null;
            if (selection.variantId) {
              const ppd = this.getPerfectProductDictionary();
              if (ppd?.variantIdToVariantMap) {
                // 🔥 FIX: Support both string (CompositeProduct synthetic) and numeric (Shopify) variant IDs
                const stringId = String(selection.variantId);
                const numericId = parseInt(selection.variantId, 10);
                variant =
                  ppd.variantIdToVariantMap.get(stringId) ||
                  ppd.variantIdToVariantMap.get(numericId);
              }
            }

            // Try pre-extracted dimensions first (instant O(1) access)
            if (variant?.extractedDimensions) {
              const extractedValue = variant.extractedDimensions[dimensionType];

              // Numeric dimensions (width, height, depth)
              if (typeof extractedValue === 'number') {
                isDev() &&
                  console.log(
                    `⚡ ${METHOD_SIGNATURE}: O(1) pre-extracted dimension access - ${dimensionType}=${extractedValue} (numeric)`
                  );
                return extractedValue;
              }

              // String dimensions (handling: 'in-swing', 'out-swing', etc.)
              if (extractedValue) {
                isDev() &&
                  console.log(
                    `⚡ ${METHOD_SIGNATURE}: O(1) pre-extracted dimension access - ${dimensionType}="${extractedValue}" (string)`
                  );
                return extractedValue;
              }
            }

            // Fallback to legacy string parsing if extractedDimensions missing
            isDev() &&
              console.warn(
                `⚠️ ${METHOD_SIGNATURE}: extractedDimensions.${dimensionType} not available for variant ${selection.variantId} - using legacy string parsing`
              );
            isDev() &&
              console.log(
                `🔧 ${METHOD_SIGNATURE}: Extracting ${dimensionType} from "${titleToCheck}" in category ${categoryId}`
              );

            // Legacy extraction (should rarely happen if PPD properly built)
            switch (dimensionType) {
              case 'width':
                const widthMatch = titleToCheck.match(
                  /(\d+)(?:\s*(?:inch|in|"|'))?/i
                );
                return widthMatch ? parseInt(widthMatch[1]) : null;

              case 'depth':
                const depthMatch = titleToCheck.match(
                  /(\d+(?:-\d+\/\d+)?(?:\.\d+)?)/
                );
                if (!depthMatch) return null;

                const value = depthMatch[1];
                // Parse fraction to number (replicate _parseDimensionValue logic)
                if (value.includes('-') && value.includes('/')) {
                  const parts = value.split('-');
                  const integerPart = parseInt(parts[0], 10);
                  const fractionParts = parts[1].split('/');
                  if (fractionParts.length === 2) {
                    const numerator = parseInt(fractionParts[0], 10);
                    const denominator = parseInt(fractionParts[1], 10);
                    if (
                      !isNaN(integerPart) &&
                      !isNaN(numerator) &&
                      !isNaN(denominator) &&
                      denominator !== 0
                    ) {
                      return integerPart + numerator / denominator;
                    }
                  }
                }
                const numericValue = parseFloat(value);
                return isNaN(numericValue) ? null : numericValue;

              case 'handling':
                const handlingLower = titleToCheck.toLowerCase();
                if (
                  handlingLower.includes('in-swing') ||
                  handlingLower.includes('inswing')
                ) {
                  return 'in-swing';
                } else if (
                  handlingLower.includes('out-swing') ||
                  handlingLower.includes('outswing')
                ) {
                  return 'out-swing';
                } else if (handlingLower.includes('left')) {
                  return 'left';
                } else if (handlingLower.includes('right')) {
                  return 'right';
                }
                return null; // Return null for consistency with PPD behavior

              default:
                isDev() &&
                  console.warn(
                    `${METHOD_SIGNATURE}: Unknown dimension type: ${dimensionType}`
                  );
                return null;
            }
          } catch (error) {
            isDev() &&
              console.error(
                `${METHOD_SIGNATURE}: Failed to extract ${dimensionType}:`,
                error
              );
            return null;
          }
        },

        /* ─── DOM & Viewport Access Methods ─── */

        /**
         * Provides viewport dimensions via conductor delegation.
         * CCP Compliant: Prevents direct window.innerWidth/innerHeight access
         * from business logic, UI, and support modules.
         *
         * Used by: md-config-ui-confetti-manager.js, md-config-ui-updater.js
         * for fallback confetti coordinate calculation when progress bar is not found.
         *
         * @returns {{ width: number, height: number }} Viewport dimensions object
         */
        getViewportDimensions() {
          try {
            return {
              width: window.innerWidth || 0,
              height: window.innerHeight || 0,
            };
          } catch (error) {
            isDev() &&
              console.error(
                'CONDUCTOR: getViewportDimensions() failed:',
                error
              );
            return { width: 0, height: 0 };
          }
        },

        /**
         * P3.2 SINGLE MASTER DICTIONARY: Initialize the pipeline and create perfectProductDictionary
         * This method integrates the new pipeline processor with existing initialization
         *
         * NOTE: In PPD flow, PPD is already created by initializer, so this typically skips recreation
         * @param {Object} stateData - The state data extracted from PPD by StateInitializer (for legacy compat only)
         * @returns {Promise<boolean>} Success status
         */
        async initializePerfectProductDictionary(stateData) {
          try {
            isDev() &&
              console.log(
                '🔥 P3.2: Initializing Single Master Dictionary from existing state data'
              );

            // Check if PPD already provided by initializer
            if (
              this.perfectProductDictionary &&
              this.perfectProductDictionary.products &&
              this.perfectProductDictionary.products.size > 0
            ) {
              isDev() &&
                console.log(
                  '✅ P3.2: perfectProductDictionary already provided by initializer - skipping recreation'
                );
              isDev() &&
                console.log(
                  `📊 Using existing PPD: ${this.perfectProductDictionary.products.size} products, ${this.perfectProductDictionary.categories.size} categories`
                );
              return true;
            }

            // LEGACY PATH: This code path should never execute in modern PPD-First flow
            // PPD is always created by initializer before conductor initialization
            isDev() &&
              console.error(
                '❌ CONDUCTOR: initializePerfectProductDictionary called but PPD not provided by initializer',
                'This indicates a bootstrap sequence issue - PPD should be created first'
              );
            return false;
          } catch (error) {
            isDev() &&
              console.error(
                '❌ P3.2: Failed to initialize perfectProductDictionary:',
                error
              );
            return false;
          }
        },

        /* Persistence Operations Delegation */
        /**
         * Delegate persistence operations to persistence manager module
         *
         * ARCHITECTURAL ROLE: CCP-compliant delegation pattern
         * - Conductor orchestrates persistence operations
         * - Delegates actual implementation to persistence manager
         * - Publishes coordination events via CoreEventBus
         * - Provides centralized error handling
         *
         * @param {string} operation - Operation type ('generateShareableURL', 'updateURLState', 'clearState', 'clearIdbCache', 'copyToClipboard')
         * @param {Object} data - Operation data (state, options, etc.)
         * @returns {Object} Operation result with success status and data
         */
        delegatePersistenceOperation(operation, data = {}) {
          try {
            isDev() &&
              console.log(
                '🔄 CONDUCTOR: Persistence operation delegation initiated',
                { operation, data }
              );

            // Validate persistence manager availability
            const persistenceManager =
              window.MurphyDoorConfigurator.supportPersistenceManager;
            if (!persistenceManager) {
              isDev() &&
                console.warn(
                  '⚠️ CONDUCTOR: Persistence manager module not available'
                );
              return {
                success: false,
                error: 'Persistence manager unavailable',
              };
            }

            // Publish coordination event
            if (window.MurphyDoorConfigurator.CoreEventBus) {
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.SYSTEM_PERSISTENCE_OPERATION_STARTED,
                {
                  operation,
                  timestamp: performance.now(),
                }
              );
            }

            const result = { success: true };

            // Delegate to appropriate persistence manager method
            switch (operation) {
              case 'generateShareableURL':
                const shareableURL = persistenceManager.generateShareableURL(
                  data.state
                );
                result.url = shareableURL;
                result.success = !!shareableURL;
                isDev() &&
                  console.log('📤 CONDUCTOR: Shareable URL generated', {
                    url: shareableURL,
                  });
                break;

              case 'updateURLState':
                persistenceManager.updateURLState(data.state);
                isDev() && console.log('🔗 CONDUCTOR: URL state updated');
                break;

              case 'clearState':
                // Clear URL parameter
                const url = new URL(window.location);
                url.searchParams.delete('config');
                const urlUtils = window.MurphyDoorConfigurator?.utils;
                // 🚨 GUARDED because the storage clear BELOW must run even if
                // this throws. Browsers rate-limit replaceState/pushState to
                // ~100 calls per 30s and throw SecurityError past that (see the
                // throttle comment in md-config-support-persistence-manager.js).
                // That ceiling is genuinely reachable here: the configurator's
                // own URL writer is debounced at 300ms, so 30s of rapid
                // selection is ~100 writes on its own, before this one.
                //
                // Unguarded, a throw escapes the whole case handler and SKIPS
                // the "Defense-in-depth" block below -- so Start Over would
                // leave localStorage/sessionStorage selections intact and the
                // next visit would restore them. That is precisely the ghost
                // selection this code exists to prevent, and it would appear
                // only under rapid interaction, which is the hardest condition
                // to reproduce from a bug report.
                //
                // A failed URL write is cosmetic (the params clear on the next
                // successful write or navigation); a skipped storage clear is
                // not. Losing the URL tidy-up to keep the clear is the right
                // trade, so this deliberately swallows rather than rethrows.
                try {
                  if (urlUtils?.replaceStateQuietly) {
                    urlUtils.replaceStateQuietly({}, '', url);
                  } else {
                    window.history.replaceState({}, '', url);
                  }
                } catch (urlError) {
                  isDev() &&
                    console.warn(
                      '⚠️ CONDUCTOR: clearState URL write failed (storage clear still runs):',
                      urlError
                    );
                }

                // Defense-in-depth: Clear all persistent storage entries
                // This ensures no recovery path can restore old selections
                try {
                  // Clear state manager recovery entry
                  localStorage.removeItem('murphyDoorConfigState');
                  // Clear persistence manager session/recovery entries
                  sessionStorage.removeItem('md_recovery');
                  const sessionKeysToRemove = [];
                  for (let i = 0; i < sessionStorage.length; i++) {
                    const key = sessionStorage.key(i);
                    if (
                      key &&
                      (key.startsWith('md_session') ||
                        key.startsWith('md_config') ||
                        // Gate answers (e.g. "Cabinets?") -- doesn't match
                        // either prefix above, so without this it silently
                        // survived Start Over: answer the gate once, hit
                        // Start Over, and the next run never re-asks it.
                        key.startsWith('mdConfiguratorGate_'))
                    ) {
                      sessionKeysToRemove.push(key);
                    }
                  }
                  sessionKeysToRemove.forEach(function (key) {
                    sessionStorage.removeItem(key);
                  });
                  // In-memory cache mirrors sessionStorage for gate answers
                  // (_getGateAnswer/_setGateAnswer) -- clear it too or a
                  // stale cached answer survives even though storage is gone.
                  this._gateAnswerCache = {};
                  // Same reasoning for the provenance cache (_setGateAnswer's
                  // meta.source, read by _getGateAnswerSource): _clearGateAnswer
                  // already clears both in lockstep for a single gate, so Start
                  // Over -- which clears every gate at once -- must not leave
                  // this one behind and drift out of sync with it.
                  this._gateAnswerSourceCache = {};
                } catch (storageError) {
                  isDev() &&
                    console.warn(
                      '⚠️ CONDUCTOR: Storage cleanup error (non-fatal):',
                      storageError
                    );
                }

                isDev() &&
                  console.log(
                    '🗑️ CONDUCTOR: State cleared from URL and all storage'
                  );
                break;

              case 'copyToClipboard':
                if (navigator.clipboard && data.url) {
                  navigator.clipboard
                    .writeText(data.url)
                    .then(() => {
                      result.clipboardSuccess = true;
                      isDev() &&
                        console.log(
                          '📋 CONDUCTOR: URL copied to clipboard successfully'
                        );
                    })
                    .catch((error) => {
                      result.clipboardSuccess = false;
                      result.clipboardError = error;
                      isDev() &&
                        console.warn(
                          '📋 CONDUCTOR: Clipboard operation failed:',
                          error
                        );
                    });
                } else {
                  result.clipboardSuccess = false;
                  result.clipboardError = 'Clipboard API not available';
                }
                break;

              case 'clearIdbCache':
                // Clear IndexedDB PPD cache entries
                if (window.MurphyDoorConfigurator.idbCache) {
                  window.MurphyDoorConfigurator.idbCache.clearAll();
                  isDev() &&
                    console.log('🗑️ CONDUCTOR: IndexedDB PPD cache cleared');
                } else {
                  isDev() &&
                    console.log('🗑️ CONDUCTOR: No IndexedDB cache to clear');
                }
                break;

              default:
                isDev() &&
                  console.warn(
                    '⚠️ CONDUCTOR: Unknown persistence operation:',
                    operation
                  );
                result.success = false;
                result.error = 'Unknown operation';
            }

            // Publish completion event
            if (window.MurphyDoorConfigurator.CoreEventBus) {
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.SYSTEM_PERSISTENCE_OPERATION_COMPLETED,
                {
                  operation,
                  success: result.success,
                  timestamp: performance.now(),
                }
              );
            }

            return result;
          } catch (error) {
            isDev() &&
              console.error(
                '❌ CONDUCTOR: Persistence operation delegation failed:',
                error
              );

            // Publish error event
            if (window.MurphyDoorConfigurator.CoreEventBus) {
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.SYSTEM_PERSISTENCE_OPERATION_FAILED,
                {
                  operation,
                  error: error.message,
                  timestamp: performance.now(),
                }
              );
            }

            return { success: false, error: error.message };
          }
        },

        // =====================================================================
        // CCP DELEGATION: clearCategoryState(categoryId)
        // Resolves documented CCP debt in cascade-auto-selection-coordinator.js
        // Provides programmatic state clearing without UI navigation side-effects.
        // NOTE: Does update the tab completion indicator (TAB_STATUS_UPDATE) to reflect
        // incomplete state — "no UI navigation side-effects" means no restoreThumbnailView,
        // no price update, and no auto-navigation; the tab indicator IS updated.
        // =====================================================================
        /**
         * Clears selection state for a category programmatically.
         *
         * This method provides a clean CCP delegation path for programmatic state clearing,
         * decoupled from user-initiated back navigation (handleStateChange('BACK_CLICKED')).
         * It updates the tab completion indicator to show incomplete state, but does NOT
         * trigger navigation side-effects (no restoreThumbnailView, no price recalculation).
         * It wraps stateManager.handleBackClick() and publishes STATE_SELECTION_CLEARED
         * for downstream coordination.
         *
         * @param {string} categoryId - Category identifier to clear
         * @returns {Object|null} Result from stateManager.handleBackClick(), or null on failure
         */
        clearCategoryState(categoryId) {
          if (!categoryId) {
            isDev() &&
              console.error('clearCategoryState: categoryId is required');
            return null;
          }

          if (
            !this.stateManager ||
            typeof this.stateManager.handleBackClick !== 'function'
          ) {
            isDev() &&
              console.error(
                'clearCategoryState: stateManager.handleBackClick not available'
              );
            return null;
          }

          const startTime = performance.now();

          try {
            const result = this.stateManager.handleBackClick(categoryId);

            const duration = performance.now() - startTime;
            trackPerformance('DELEGATION', duration);

            // Update tab UI indicator to reflect incomplete state.
            // When cascade resets a category, the parent tab's visual indicator must be
            // updated to show as incomplete (not blue/complete) so the user knows attention
            // is needed. This fires before any auto-apply attempt so the UI accurately
            // reflects state during the cascade (may briefly flash for auto-applied ones).
            if (result && result.clearSelection) {
              const tabId = this.stateManager.getCategoryTab(categoryId);
              if (tabId && window.MurphyDoorConfigurator.uiUpdater) {
                window.MurphyDoorConfigurator.uiUpdater.updateDisplay(
                  'TAB_STATUS_UPDATE',
                  {
                    tabId: String(tabId),
                    isComplete: false,
                  }
                );
              }
            }

            isDev() &&
              console.log(
                `[CONDUCTOR] clearCategoryState("${categoryId}") completed in ${duration.toFixed(2)}ms`,
                { success: !!(result && result.clearSelection) }
              );

            return result;
          } catch (error) {
            isDev() &&
              console.error(
                `[CONDUCTOR] clearCategoryState("${categoryId}") failed:`,
                error
              );
            return null;
          }
        },

        // =====================================================================
        // ENFORCED_PAIR: evaluate one customer choice and publish the actions
        // Shared by the APPLY_SELECTION path (user applies) and the ADD_TO_CART
        // browsing flush. The validator decides; the cascade coordinator acts.
        // =====================================================================
        /**
         * @param {string} variantId - Variant the customer chose
         * @param {string} categoryId - Category it was applied in
         * @returns {Array<Object>} the actions that were published (empty if none)
         */
        _publishEnforcedPairActions(variantId, categoryId) {
          const validator = window.MurphyDoorConfigurator.forbiddenValidator;
          const bus = window.MurphyDoorConfigurator.CoreEventBus;
          if (
            !variantId ||
            !bus ||
            !validator ||
            typeof validator.evaluateEnforcedPairActions !== 'function'
          ) {
            return [];
          }
          try {
            const rules = this.getUnifiedForbiddenRules();
            const ppd = this.getPerfectProductDictionary();
            if (!rules?.unifiedRules?.size || !ppd?.variantIdToVariantObjectMap) {
              return [];
            }
            const actions = validator.evaluateEnforcedPairActions(
              String(variantId),
              categoryId,
              this.getAppliedSelectionsMap(),
              rules,
              ppd.variantIdToVariantObjectMap
            );
            const ACTIONS = CONSTANTS.FORBIDDEN_RULES.ENFORCED_PAIR_ACTIONS;
            actions.forEach((action) => {
              if (action.type === ACTIONS.FORCE_APPLY) {
                bus.publish(CONSTANTS.EVENTS.FORBIDDEN_AUTO_APPLY_TRIGGERED, {
                  ruleId: action.ruleId,
                  ruleName: action.ruleName,
                  ruleDescription: action.ruleDescription,
                  conditionVariantId: String(variantId),
                  conditionCategoryId: categoryId,
                  targetVariantId: action.targetVariantId,
                  targetCategoryId: action.targetCategoryId,
                  targetProductId: action.targetProductId,
                  reason: action.ruleDescription,
                  priority: action.priority,
                  enforcedPair: true,
                  timestamp: Date.now(),
                  source: 'conductor:enforced_pair_evaluation',
                });
              } else if (action.type === ACTIONS.CLEAR_CATEGORY) {
                bus.publish(
                  CONSTANTS.EVENTS.FORBIDDEN_ENFORCED_PAIR_CLEAR_TRIGGERED,
                  {
                    ruleId: action.ruleId,
                    ruleName: action.ruleName,
                    clearCategoryId: action.clearCategoryId,
                    clearedVariantId: action.clearedVariantId,
                    triggerCategoryId: action.triggerCategoryId,
                    triggerVariantId: action.triggerVariantId,
                    timestamp: Date.now(),
                    source: 'conductor:enforced_pair_evaluation',
                  }
                );
              }
            });
            return actions;
          } catch (error) {
            isDev() &&
              console.error(
                '❌ CONDUCTOR: ENFORCED_PAIR evaluation failed:',
                error
              );
            return [];
          }
        },

        /**
         * ENFORCED_PAIR add-to-cart audit via the validator (pure check).
         * @returns {Array<Object>} violations (empty when none or no enforced rules)
         */
        _findEnforcedPairViolations() {
          const validator = window.MurphyDoorConfigurator.forbiddenValidator;
          if (
            !validator ||
            typeof validator.findEnforcedPairViolations !== 'function'
          ) {
            return [];
          }
          try {
            const ppd = this.getPerfectProductDictionary();
            return validator.findEnforcedPairViolations(
              this.getAppliedSelectionsMap(),
              this.getUnifiedForbiddenRules(),
              ppd?.variantIdToVariantObjectMap
            );
          } catch (error) {
            isDev() &&
              console.error(
                '❌ CONDUCTOR: ENFORCED_PAIR add-to-cart audit failed:',
                error
              );
            return [];
          }
        },

        // =====================================================================
        // CCP DELEGATION: clearCategoryForEnforcedPair(categoryId, meta)
        // Programmatic clear with the FULL side-effect set a customer-visible
        // clear needs (mirrors the rush-delivery auto-deselect and the user
        // unapply paths): state clear + URL persist + UI reset + downstream
        // STATE_SELECTION_CLEARED + price + progress. clearCategoryState() alone
        // does none of the publishing or price work.
        // =====================================================================
        /**
         * @param {string} categoryId - Category to clear
         * @param {Object} [meta] - { sourceRuleId, triggerCategoryId }
         * @returns {boolean} true when a selection was actually cleared
         */
        /**
         * CCP DELEGATION (read-only): is `categoryId` held empty by an
         * ENFORCED_PAIR reverse clear until the customer chooses? Owned by the
         * cascade auto-selection coordinator; false when it is not loaded.
         * @param {string} categoryId
         * @returns {boolean}
         */
        isEnforcedPairHeld(categoryId) {
          try {
            const coordinator =
              window.MurphyDoorConfigurator.cascadeAutoSelectionCoordinator;
            return !!(
              coordinator &&
              typeof coordinator.isEnforcedPairHeld === 'function' &&
              coordinator.isEnforcedPairHeld(categoryId)
            );
          } catch (error) {
            return false;
          }
        },

        clearCategoryForEnforcedPair(categoryId, meta = {}) {
          const bus = window.MurphyDoorConfigurator.CoreEventBus;
          const source =
            CONSTANTS.FORBIDDEN_RULES?.ENFORCED_PAIR_CLEAR_SOURCE ||
            'forbidden_enforced_pair';
          if (!categoryId) return false;

          try {
            const result = this.clearCategoryState(categoryId);
            if (!result || !result.clearSelection) return false;

            if (this.stateManager?.getStateMaps?.()?.core) {
              this.stateManager.getStateMaps().core.inVariantTransition = false;
            }
            this.stateManager?.persistState?.();

            if (bus) {
              // DOM first, so cascade subscribers see a UI in sync with state
              bus.publish(CONSTANTS.EVENTS.UI_USER_UNAPPLY_REQUESTED, {
                categoryId,
                auto: true,
                source,
                timestamp: Date.now(),
              });
              bus.publish(CONSTANTS.EVENTS.STATE_SELECTION_CLEARED, {
                categoryId,
                source,
                sourceRuleId: meta.sourceRuleId || null,
                triggerCategoryId: meta.triggerCategoryId || null,
                timestamp: Date.now(),
              });
            }

            this.handlePriceUpdates({ categoryId }, SELECTION_STATES.BROWSING);

            if (bus) {
              bus.publish(CONSTANTS.EVENTS.UI_PROGRESS_UPDATE_REQUESTED, {
                source,
                timestamp: Date.now(),
              });
            }
            return true;
          } catch (error) {
            isDev() &&
              console.error(
                `[CONDUCTOR] clearCategoryForEnforcedPair("${categoryId}") failed:`,
                error
              );
            return false;
          }
        },

        // =====================================================================
        // CCP DELEGATION: evaluateAutoApplyRulesOnClear()
        // Resolves documented CCP debt in cascade-auto-selection-coordinator.js
        // Routes forbidden validator evaluation through conductor delegation
        // =====================================================================
        /**
         * Evaluates which auto-apply rules should be unapplied after a category selection is cleared.
         *
         * This method provides a CCP delegation path for forbidden validator evaluation,
         * eliminating direct module-to-module access from cascade-auto-selection-coordinator
         * to forbidden-validator.
         *
         * @param {string} clearedCategoryId - The category that was just cleared
         * @param {Map} activeAutoApplyRules - Map of currently active auto-apply rule IDs to rule data
         * @param {Map} currentSelections - Current applied selections Map
         * @param {Object} forbiddenRules - Unified forbidden rules from PPD
         * @returns {Array} Array of rule objects that should be unapplied, empty array on failure
         */
        evaluateAutoApplyRulesOnClear(
          clearedCategoryId,
          activeAutoApplyRules,
          currentSelections,
          forbiddenRules
        ) {
          const forbiddenValidator =
            window.MurphyDoorConfigurator.forbiddenValidator;

          if (
            !forbiddenValidator ||
            typeof forbiddenValidator.evaluateAutoApplyRulesOnClear !==
              'function'
          ) {
            isDev() &&
              console.warn(
                '[CONDUCTOR] evaluateAutoApplyRulesOnClear: forbiddenValidator not available'
              );
            return [];
          }

          const startTime = performance.now();

          try {
            const result = forbiddenValidator.evaluateAutoApplyRulesOnClear(
              clearedCategoryId,
              activeAutoApplyRules,
              currentSelections,
              forbiddenRules
            );

            const duration = performance.now() - startTime;
            trackPerformance('DELEGATION', duration);

            isDev() &&
              console.log(
                `[CONDUCTOR] evaluateAutoApplyRulesOnClear("${clearedCategoryId}") completed in ${duration.toFixed(2)}ms`,
                { rulesToUnapply: result?.length || 0 }
              );

            return result || [];
          } catch (error) {
            isDev() &&
              console.error(
                `[CONDUCTOR] evaluateAutoApplyRulesOnClear("${clearedCategoryId}") failed:`,
                error
              );
            return [];
          }
        },

        // =====================================================================
        // PATTERN #15: Cleanup method (10 points)
        // Unsubscribes all tracked event handlers and resets state
        // =====================================================================
        cleanup() {
          isDev() && console.log('[CONDUCTOR] Cleaning up resources');

          // Unsubscribe all tracked event handlers
          cleanupHandlers.forEach((unsubscribe) => {
            try {
              if (typeof unsubscribe === 'function') unsubscribe();
            } catch (e) {
              isDev() && console.warn('[CONDUCTOR] Cleanup handler error:', e);
            }
          });
          cleanupHandlers.length = 0;

          // Reset initialization state
          this._isInitialized = false;

          publishEvent(CONSTANTS.EVENTS.CONDUCTOR_CLEANUP, {
            module: 'md-config-core-conductor',
            timestamp: Date.now(),
          });
        },
      };

      // P2.3: Add conductor alias to fix race condition
      // All new virtualization code expects .conductor but main object is .main
      window.MurphyDoorConfigurator.conductor =
        window.MurphyDoorConfigurator.main;
      isDev() &&
        console.log(
          '✅ Conductor alias created: MurphyDoorConfigurator.conductor → MurphyDoorConfigurator.main'
        );

      // 🎯 TIMING FIX: Subscribe to PPD state ready event BEFORE initializeConfigurator() is called
      // This subscription MUST be outside initializeConfiguratorCore() to avoid race condition where
      // StateInitializer publishes the event before conductor subscribes to it.
      // Event flow: StateInitializer publishes → Conductor subscribes HERE → initializeConfigurator() called
      // PATTERN #15: Track early subscriptions in cleanupHandlers
      if (window.MurphyDoorConfigurator.CoreEventBus) {
        const unsub1 = window.MurphyDoorConfigurator.CoreEventBus.subscribe(
          CONSTANTS.EVENTS.SYSTEM_STATE_FROM_PPD_READY,
          (eventData) => {
            isDev() &&
              console.log(
                '🔥 CONDUCTOR: Received system:state_from_ppd_ready event',
                eventData
              );
            // Mark that PPD state is ready for use
            window.MurphyDoorConfigurator.main._ppdStateReady = true;
            window.MurphyDoorConfigurator.main._ppdStateTimestamp =
              eventData.timestamp;
            isDev() &&
              console.log(
                '✅ CONDUCTOR: PPD state is ready for initialization flow'
              );
          }
        );
        if (unsub1) cleanupHandlers.push(unsub1);
        isDev() &&
          console.info(
            '✅ Conductor: Subscribed to system:state_from_ppd_ready event (EARLY SUBSCRIPTION)'
          );
      }

      // Subscribe to material preferences initialization
      if (window.MurphyDoorConfigurator.CoreEventBus) {
        const unsub2 = window.MurphyDoorConfigurator.CoreEventBus.subscribe(
          CONSTANTS.EVENTS.SYSTEM_MAPS_INITIALIZED,
          (eventData) => {
            isDev() &&
              console.log(
                '🔥 CONDUCTOR: Received system:maps_initialized event - material preferences ready',
                eventData
              );

            // Mark material preferences as initialized
            isMaterialPreferencesInitialized = true;

            // Process all queued material preference requests
            if (pendingMaterialPreferenceRequests.length > 0) {
              isDev() &&
                console.log(
                  `✅ CONDUCTOR: Processing ${pendingMaterialPreferenceRequests.length} queued material preference requests`
                );

              pendingMaterialPreferenceRequests.forEach(
                ({ resolve, requestId, requestTime }) => {
                  const preferences =
                    window.MurphyDoorConfigurator.main.stateManager.getMaterialPreferences();
                  const responseTime = window.performance
                    ? window.performance.now()
                    : Date.now();
                  const waitTime = responseTime - requestTime;

                  isDev() &&
                    console.log(
                      `📦 CONDUCTOR: Resolving queued material preference request ${requestId} (waited ${waitTime.toFixed(2)}ms)`
                    );

                  resolve(preferences || EMPTY_MATERIAL_PREFERENCES);
                }
              );

              // Clear the queue
              pendingMaterialPreferenceRequests = [];
            }

            isDev() &&
              console.log(
                '✅ CONDUCTOR: Material preferences initialization complete'
              );
          }
        );
        if (unsub2) cleanupHandlers.push(unsub2);
        isDev() &&
          console.info(
            '✅ Conductor: Subscribed to system:maps_initialized event for material preferences'
          );
      }

      // Subscribe to grouped product click events
      if (window.MurphyDoorConfigurator.CoreEventBus) {
        const unsub3 = window.MurphyDoorConfigurator.CoreEventBus.subscribe(
          CONSTANTS.EVENTS.UI_PRODUCT_GROUP_CLICKED,
          window.MurphyDoorConfigurator.main.handleGroupedProductClick.bind(
            window.MurphyDoorConfigurator.main
          )
        );
        if (unsub3) cleanupHandlers.push(unsub3);
        isDev() &&
          console.info(
            'Conductor: Subscribed to ui:product_group_clicked events'
          );
      } else {
        isDev() &&
          console.warn(
            'Conductor: CoreEventBus not available, grouped product events will not be handled'
          );
      }

      return true;
    } catch (error) {
      isDev() && console.error('Core initialization failed:', error);
      return false;
    }
  };

  // 🚀 CRITICAL FIX: Wait for dependencies before calling initializeCore
  // ES6 modules load asynchronously, so we need to poll for dependencies
  const waitForDependenciesAndInitialize = () => {
    const requiredModules = [
      'uiUpdater',
      'stateManager',
      'CoreEventBus',
      'dataPipeline',
      'supportErrorHandler',
      'supportValidationManager',
    ];

    const allDependenciesReady = requiredModules.every(
      (moduleName) => window.MurphyDoorConfigurator?.[moduleName]
    );

    if (allDependenciesReady) {
      isDev() &&
        console.log('🎯 CONDUCTOR: All dependencies ready, initializing core');
      initializeCore();
    } else {
      const missing = requiredModules.filter(
        (moduleName) => !window.MurphyDoorConfigurator?.[moduleName]
      );
      isDev() &&
        console.log(
          `⏳ CONDUCTOR: Waiting for dependencies: ${missing.join(', ')}`
        );
      setTimeout(waitForDependenciesAndInitialize, 10);
    }
  };

  // CRITICAL FIX v3: Guard against Shopify async section rendering re-loading this script.
  // Using BOTH window-level (__murphyDoorBootstrapComplete) AND namespace-level flag.
  // The window-level flag survives namespace recreation; namespace flag handles same-version reloads.
  if (
    window.__murphyDoorBootstrapComplete ||
    window.MurphyDoorConfigurator._conductorScriptLoaded
  ) {
    isDev() &&
      console.log(
        '🛡️ CONDUCTOR: Script re-loaded (async section rendering) - bootstrap already complete, skipping duplicate initialization'
      );
  } else {
    window.MurphyDoorConfigurator._conductorScriptLoaded = true;
    waitForDependenciesAndInitialize();
  }

  // Initialization coordination handled by md-config-core-initializer.js
  // Removed standalone DOMContentLoaded listener to prevent triple initialization
})(window);
